import '@total-typescript/ts-reset';
import * as AspectRatioPrimitive from '@radix-ui/react-aspect-ratio';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import React__default, { forwardRef, createContext, useMemo, useEffect, useState, useRef, useCallback, useContext, cloneElement, useSyncExternalStore, useLayoutEffect as useLayoutEffect$1 } from 'react';
import cn$1, { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Slot } from '@radix-ui/react-slot';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { isMobile } from 'react-device-detect';
import { useTranslation as useTranslation$1 } from 'react-i18next';
import { useObjectRef, mergeRefs, mergeProps, useLayoutEffect, filterDOMProps, useResizeObserver } from '@react-aria/utils';
import { useIsSSR, useButton, useFocusRing, useHover, mergeProps as mergeProps$1, useLocale, useCollator, ListKeyboardDelegate, useListBox, FocusScope, useListBoxSection, useOption, usePopover, Overlay as Overlay$1, DismissButton, useFilter, useComboBox, useTimeField, useDateSegment } from 'react-aria';
import { useListState, useOverlayTriggerState, useComboBoxState, useTimeFieldState } from 'react-stately';
import ReactDOM, { createPortal } from 'react-dom';
import { useControllableState } from '@mozioinc/hooks';
import { Command as Command$1 } from 'cmdk';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { format, parseISO } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Time } from '@internationalized/date';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import * as SelectPrimitive from '@radix-ui/react-select';
import { useMediaQuery } from 'usehooks-ts';
import { toThemeVars } from './server/theme.mjs';
export { defaultTheme, extendPalette, extendTheme } from './server/theme.mjs';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { ToastAction } from '@radix-ui/react-toast';
import * as SliderPrimitive from '@radix-ui/react-slider';

const AspectRatio = AspectRatioPrimitive.Root;

const cn = (...inputs) => {
  return twMerge(clsx(inputs));
};

const badgeVariants = cva(
  "inline-flex items-center w-max rounded-sm border px-1 py-1.5 text-xs font-normal",
  {
    variants: {
      variant: {
        primary: "border-transparent bg-primary text-primary-contrast",
        secondary: "border-transparent bg-secondary text-secondary-contrast",
        error: "border-transparent bg-error-300 text-white hover:bg-error-400",
        outline: "text-neutral-800",
        ring: "bg-primary mb-1 text-primary-contrast border-white border-1 ring-1 ring-primary ring-offset-1"
      }
    },
    defaultVariants: {
      variant: "primary"
    }
  }
);
const Badge = forwardRef(
  ({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(badgeVariants({ variant }), className),
      ...props
    }
  )
);
Badge.displayName = "Badge";

const BUTTON_VARIANT = {
  SOLID: "solid",
  OUTLINE: "outline",
  GHOST: "ghost"
};
const BUTTON_COLOR = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
  ERROR: "error",
  SUCCESS: "success",
  WARNING: "warning",
  WHITE: "white",
  NEUTRAL: "neutral"
};
const BUTTON_SIZE = {
  XS: "xs",
  SM: "sm",
  MD: "md",
  LG: "lg"
};
const buttonVariants = cva(
  "inline-flex gap-2 items-center justify-center duration-200 rounded disabled:pointer-events-none disabled:opacity-30",
  {
    variants: {
      color: {
        [BUTTON_COLOR.PRIMARY]: "",
        [BUTTON_COLOR.SECONDARY]: "",
        [BUTTON_COLOR.ERROR]: "",
        [BUTTON_COLOR.SUCCESS]: "",
        [BUTTON_COLOR.WARNING]: "",
        [BUTTON_COLOR.WHITE]: "",
        [BUTTON_COLOR.NEUTRAL]: ""
      },
      variant: {
        [BUTTON_VARIANT.SOLID]: "",
        [BUTTON_VARIANT.OUTLINE]: "border bg-transparent",
        [BUTTON_VARIANT.GHOST]: "bg-transparent"
      },
      size: {
        [BUTTON_SIZE.XS]: "px-2 py-1 text-xs",
        [BUTTON_SIZE.SM]: "px-3 py-1 text-sm",
        [BUTTON_SIZE.MD]: "px-4 py-2 text-base",
        [BUTTON_SIZE.LG]: "px-5 py-3 text-base"
      }
    },
    compoundVariants: [
      {
        color: BUTTON_COLOR.PRIMARY,
        variant: BUTTON_VARIANT.SOLID,
        class: "bg-primary text-primary-contrast hover:bg-primary-400"
      },
      {
        color: BUTTON_COLOR.SECONDARY,
        variant: BUTTON_VARIANT.SOLID,
        class: "bg-secondary text-secondary-contrast hover:bg-secondary-400"
      },
      {
        color: BUTTON_COLOR.ERROR,
        variant: BUTTON_VARIANT.SOLID,
        class: "bg-error text-white hover:bg-error-400"
      },
      {
        color: BUTTON_COLOR.SUCCESS,
        variant: BUTTON_VARIANT.SOLID,
        class: "bg-success text-white hover:bg-success-400"
      },
      {
        color: BUTTON_COLOR.WARNING,
        variant: BUTTON_VARIANT.SOLID,
        class: "bg-warning text-white hover:bg-warning-400"
      },
      {
        color: BUTTON_COLOR.WHITE,
        variant: BUTTON_VARIANT.SOLID,
        class: "bg-white text-black hover:bg-neutral-100"
      },
      {
        color: BUTTON_COLOR.NEUTRAL,
        variant: BUTTON_VARIANT.SOLID,
        class: "bg-neutral text-black hover:bg-neutral-600"
      },
      {
        color: BUTTON_COLOR.PRIMARY,
        variant: [BUTTON_VARIANT.OUTLINE, BUTTON_VARIANT.GHOST],
        class: "text-primary border-primary hover:bg-primary-100/20"
      },
      {
        color: BUTTON_COLOR.SECONDARY,
        variant: [BUTTON_VARIANT.OUTLINE, BUTTON_VARIANT.GHOST],
        class: "text-secondary border-secondary hover:bg-secondary-100/20"
      },
      {
        color: BUTTON_COLOR.ERROR,
        variant: [BUTTON_VARIANT.OUTLINE, BUTTON_VARIANT.GHOST],
        class: "text-error border-error hover:bg-error-100/20"
      },
      {
        color: BUTTON_COLOR.SUCCESS,
        variant: [BUTTON_VARIANT.OUTLINE, BUTTON_VARIANT.GHOST],
        class: "text-success border-success hover:bg-success-100/20"
      },
      {
        color: BUTTON_COLOR.WARNING,
        variant: [BUTTON_VARIANT.OUTLINE, BUTTON_VARIANT.GHOST],
        class: "text-warning border-warning hover:bg-warning-100/20"
      },
      {
        color: BUTTON_COLOR.WHITE,
        variant: [BUTTON_VARIANT.OUTLINE, BUTTON_VARIANT.GHOST],
        class: "text-white border-white hover:bg-neutral-100/20"
      },
      {
        color: BUTTON_COLOR.NEUTRAL,
        variant: [BUTTON_VARIANT.OUTLINE, BUTTON_VARIANT.GHOST],
        class: "text-black border-neutral hover:bg-neutral-100/20"
      }
    ],
    defaultVariants: {
      variant: BUTTON_VARIANT.SOLID,
      size: BUTTON_SIZE.MD,
      color: BUTTON_COLOR.PRIMARY
    }
  }
);
const Button = forwardRef(
  ({ className, variant, size, color, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        className: cn(buttonVariants({ color, variant, size, className })),
        ref,
        ...props,
        children
      }
    );
  }
);
Button.displayName = "Button";
const iconButtonVariants = cva("", {
  variants: {
    size: {
      [BUTTON_SIZE.XS]: "px-0 py-0 text-base",
      [BUTTON_SIZE.SM]: "px-0 py-0 text-lg",
      [BUTTON_SIZE.MD]: "px-1 py-1 text-xl",
      [BUTTON_SIZE.LG]: "px-2 py-2 text-2xl"
    }
  },
  defaultVariants: {
    size: BUTTON_SIZE.MD
  }
});
const IconButton = forwardRef(
  ({ className, variant, size, color, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        className: cn(
          buttonVariants({ color, variant, className }),
          iconButtonVariants({ size })
        ),
        ref,
        ...props,
        children
      }
    );
  }
);
IconButton.displayName = "IconButton";

var _style;
function _extends$D() { _extends$D = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$D.apply(this, arguments); }
var SvgSpinner = function SvgSpinner(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$D({
    width: "1em",
    height: "1em",
    stroke: "currentColor",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _style || (_style = /*#__PURE__*/React.createElement("style", null, "@keyframes spinner_zKoa{to{transform:rotate(360deg)}}@keyframes spinner_YpZS{0%{stroke-dasharray:0 150;stroke-dashoffset:0}47.5%{stroke-dasharray:42 150;stroke-dashoffset:-16}95%,to{stroke-dasharray:42 150;stroke-dashoffset:-59}}")), /*#__PURE__*/React.createElement("g", {
    style: {
      transformOrigin: "center",
      animation: "spinner_zKoa 2s linear infinite"
    }
  }, /*#__PURE__*/React.createElement("circle", {
    cx: 12,
    cy: 12,
    r: 9.5,
    fill: "none",
    strokeWidth: 3,
    style: {
      strokeLinecap: "round",
      animation: "spinner_YpZS 1.5s ease-in-out infinite"
    }
  })));
};

const Loading = forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "text-primary grid place-items-center text-xl",
          className
        ),
        role: "progressbar",
        ...props,
        children: /* @__PURE__ */ jsx(SvgSpinner, {})
      }
    );
  }
);
Loading.displayName = "Loading";

const TAB_VARIANT = {
  DEFAULT: "default",
  BORDERED: "bordered",
  BOXED: "boxed",
  HERO: "hero"
};
const Tabs = ({
  children,
  className,
  variant = "default",
  ...props
}) => /* @__PURE__ */ jsx(
  TabsPrimitive.Root,
  {
    ...props,
    className: cn(
      "group/tabs",
      "data-[variant=default]:bg-primary-400 data-[variant=default]:rounded-lg data-[variant=default]:bg-opacity-50",
      className
    ),
    "data-variant": variant,
    children
  }
);
const TabsList = ({ children, className }) => /* @__PURE__ */ jsx(
  TabsPrimitive.List,
  {
    className: cn(
      "flex gap-2 sm:gap-4",
      "group-data-[variant=hero]/tabs:bg-primary-400 group-data-[variant=hero]/tabs:w-fit group-data-[variant=hero]/tabs:gap-4 group-data-[variant=hero]/tabs:rounded-t-lg group-data-[variant=hero]/tabs:lg:gap-2 ",
      className
    ),
    children
  }
);
const TabsTrigger = ({ children, className, ...props }) => /* @__PURE__ */ jsx(
  TabsPrimitive.Trigger,
  {
    ...props,
    className: cn(
      "relative flex flex-col items-center gap-1 rounded-t-lg px-3 py-2 after:absolute after:-bottom-[0.125rem] after:left-0 after:h-[0.1875rem] after:w-full after:rounded-sm sm:flex-row sm:gap-2 sm:px-4 sm:py-3",
      "group-data-[variant=default]/tabs:data-[state=active]:text-primary group-data-[variant=default]/tabs:text-primary-contrast group-data-[variant=default]/tabs:data-[state=active]:bg-neutral-100",
      "group-data-[variant=bordered]/tabs:white group-data-[variant=bordered]/tabs:data-[state=active]:border-primary group-data-[variant=bordered]/tabs:data-[state=active]:text-primary group-data-[variant=bordered]/tabs:data-[state=active]:hover:bg-primary-100/20 group-data-[variant=bordered]/tabs:rounded-full group-data-[variant=bordered]/tabs:border group-data-[variant=bordered]/tabs:border-neutral-700 group-data-[variant=bordered]/tabs:hover:bg-neutral-100",
      "group-data-[variant=boxed]/tabs:white group-data-[variant=boxed]/tabs:data-[state=active]:bg-primary-500 group-data-[variant=boxed]/tabs:data-[state=active]:text-primary-contrast group-data-[variant=boxed]/tabs:rounded-lg",
      "group-data-[variant=hero]/tabs:text-primary-contrast group-data-[variant=hero]/tabs:data-[state=active]:text-primary group-data-[variant=hero]/tabs:rounded-t-lg group-data-[variant=hero]/tabs:data-[state=active]:bg-neutral-100",
      className
    ),
    children
  }
);
const TabsContent = ({
  children,
  value,
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  TabsPrimitive.Content,
  {
    ...props,
    value,
    className: cn(
      "p-5",
      "group-data-[variant=default]/tabs:rounded-b-lg group-data-[variant=default]/tabs:bg-neutral-100",
      "group-data-[variant=hero]/tabs:rounded-b-lg group-data-[variant=hero]/tabs:rounded-tr-lg group-data-[variant=hero]/tabs:bg-neutral-100 group-data-[variant=hero]/tabs:p-5 group-data-[variant=hero]/tabs:text-black",
      className
    ),
    children
  }
);

const Card = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(
        "flex flex-col rounded-lg border bg-white text-black transition-shadow hover:shadow",
        className
      ),
      ...props
    }
  )
);
Card.displayName = "Card";
const CardHeader = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn("flex flex-col p-2 sm:p-3", className),
      ...props
    }
  )
);
CardHeader.displayName = "CardHeader";
const CardTitle = forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx("h3", { ref, className: cn("text-xl font-semibold", className), ...props, children }));
CardTitle.displayName = "CardTitle";
const CardDescription = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("p", { ref, className: cn("text-sm text-black", className), ...props }));
CardDescription.displayName = "CardDescription";
const CardContent = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("p-2 sm:p-3", className), ...props })
);
CardContent.displayName = "CardContent";
const CardFooter = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn("mx-2 flex items-end py-4 sm:mx-3 sm:py-5", className),
      ...props
    }
  )
);
CardFooter.displayName = "CardFooter";

const countryCodes = [
  "AD",
  "AE",
  "AF",
  "AG",
  "AI",
  "AL",
  "AM",
  "AO",
  "AR",
  "AS",
  "AT",
  "AU",
  "AW",
  "AX",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BL",
  "BM",
  "BN",
  "BO",
  "BR",
  "BS",
  "BT",
  "BV",
  "BW",
  "BY",
  "BZ",
  "CA",
  "CC",
  "CD",
  "CF",
  "CG",
  "CH",
  "CI",
  "CK",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CW",
  "CX",
  "CY",
  "CZ",
  "DE",
  "DJ",
  "DK",
  "DM",
  "DO",
  "DZ",
  "EC",
  "EE",
  "EG",
  "ER",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FK",
  "FM",
  "FO",
  "FR",
  "GA",
  "GB",
  "GD",
  "GE",
  "GF",
  "GG",
  "GH",
  "GI",
  "GL",
  "GM",
  "GN",
  "GP",
  "GQ",
  "GR",
  "GS",
  "GT",
  "GU",
  "GW",
  "GY",
  "HK",
  "HM",
  "HN",
  "HR",
  "HT",
  "HU",
  "ID",
  "IE",
  "IL",
  "IM",
  "IN",
  "IO",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JE",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KI",
  "KM",
  "KN",
  "KP",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LA",
  "LB",
  "LC",
  "LGBT",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LY",
  "MA",
  "MC",
  "MD",
  "ME",
  "MG",
  "MH",
  "MK",
  "ML",
  "MM",
  "MN",
  "MO",
  "MP",
  "MQ",
  "MR",
  "MS",
  "MT",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NC",
  "NE",
  "NF",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NR",
  "NU",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PF",
  "PG",
  "PH",
  "PK",
  "PL",
  "PM",
  "PN",
  "PR",
  "PS",
  "PT",
  "PW",
  "PY",
  "QA",
  "RE",
  "RO",
  "RS",
  "RU",
  "RW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SG",
  "SI",
  "SJ",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SR",
  "SS",
  "ST",
  "SV",
  "SX",
  "SY",
  "SZ",
  "TC",
  "TD",
  "TF",
  "TG",
  "TH",
  "TJ",
  "TK",
  "TL",
  "TM",
  "TN",
  "TO",
  "TR",
  "TT",
  "TV",
  "TW",
  "TZ",
  "UA",
  "UG",
  "UM",
  "US",
  "UY",
  "UZ",
  "VA",
  "VC",
  "VE",
  "VG",
  "VI",
  "VN",
  "VU",
  "WF",
  "WS",
  "XK",
  "YE",
  "YT",
  "ZA",
  "ZM",
  "ZW",
  "EU",
  "GB-ENG",
  "GB-NIR",
  "GB-SCT",
  "GB-WLS",
  "GB-ZET",
  "US-CA"
];

const CDN_FILES = "https://cdn.jsdelivr.net/gh/madebybowtie/FlagKit@2.2/Assets/SVG";
const Flag = ({
  country = "US",
  role = "img",
  size = 24,
  alt,
  ...props
}) => {
  if (country) {
    const countryCode = country.toUpperCase();
    if (countryCodes.includes(country) !== void 0) {
      const flagSrc = `${CDN_FILES}/${countryCode}.svg`;
      return /* @__PURE__ */ jsx(
        "img",
        {
          src: flagSrc,
          role,
          alt: alt ?? `${countryCode} Flag`,
          height: size,
          width: size,
          ...props
        }
      );
    }
    return /* @__PURE__ */ jsx("span", { children: countryCode });
  }
  return null;
};

const useTranslation = (namespace, options = {}) => {
  const i18nInstance = typeof window !== "undefined" ? window.i18n : void 0;
  return useTranslation$1(namespace, {
    ...options,
    i18n: i18nInstance
  });
};

const slotCallbackSymbol = Symbol("callback");
const defaultSlot = Symbol("default");
const Provider = ({
  values,
  children
}) => {
  for (let [Context, value] of values) {
    children = /* @__PURE__ */ jsx(Context.Provider, { value, children });
  }
  return children;
};
function useRenderProps(props) {
  let {
    className,
    style,
    children,
    defaultClassName,
    defaultChildren,
    values
  } = props;
  return useMemo(() => {
    let computedClassName;
    let computedStyle;
    let computedChildren;
    computedClassName = typeof className === "function" ? className(values) : className;
    computedStyle = typeof style === "function" ? style(values) : style;
    if (typeof children === "function") {
      computedChildren = children(values);
    } else if (children == null) {
      computedChildren = defaultChildren;
    } else {
      computedChildren = children;
    }
    return {
      className: computedClassName ?? defaultClassName,
      style: computedStyle,
      children: computedChildren,
      "data-rac": ""
    };
  }, [className, style, children, defaultClassName, defaultChildren, values]);
}
function useSlottedContext(context, slot) {
  let ctx = useContext(context);
  if (slot === null) {
    return null;
  }
  if (ctx && typeof ctx === "object" && "slots" in ctx && ctx.slots) {
    let availableSlots = new Intl.ListFormat().format(
      Object.keys(ctx.slots).map((p) => `"${p}"`)
    );
    if (!slot && !ctx.slots[defaultSlot]) {
      throw new Error(
        `A slot prop is required. Valid slot names are ${availableSlots}.`
      );
    }
    let slotKey = slot || defaultSlot;
    if (!ctx.slots[slotKey]) {
      throw new Error(
        `Invalid slot "${slot}". Valid slot names are ${availableSlots}.`
      );
    }
    return ctx.slots[slotKey];
  }
  return ctx;
}
const useContextProps = (props, ref, context) => {
  let ctx = useSlottedContext(context, props.slot) || {};
  let {
    // @ts-ignore - ref issues
    ref: contextRef,
    // @ts-ignore - TS says "Type 'unique symbol' cannot be used as an index type." but not sure why.
    [slotCallbackSymbol]: callback,
    ...contextProps
  } = ctx;
  let mergedRef = useObjectRef(
    useMemo(() => mergeRefs(ref, contextRef), [ref, contextRef])
  );
  let mergedProps = mergeProps(contextProps, props);
  useEffect(() => {
    if (callback) {
      callback(props);
    }
  }, [callback, props]);
  return [mergedProps, mergedRef];
};
function useSlot() {
  let [hasSlot, setHasSlot] = useState(true);
  let hasRun = useRef(false);
  let ref = useCallback((el) => {
    hasRun.current = true;
    setHasSlot(!!el);
  }, []);
  useLayoutEffect(() => {
    if (!hasRun.current) {
      setHasSlot(false);
    }
  }, []);
  return [ref, hasSlot];
}
const useEnterAnimation = (ref, isReady = true) => {
  let [isEntering, setEntering] = useState(true);
  useAnimation(
    ref,
    isEntering && isReady,
    useCallback(() => setEntering(false), [])
  );
  return isEntering && isReady;
};
const useExitAnimation = (ref, isOpen) => {
  let [isExiting, setExiting] = useState(false);
  let [exitState, setExitState] = useState("idle");
  if (!isOpen && ref.current && exitState === "idle") {
    isExiting = true;
    setExiting(true);
    setExitState("exiting");
  }
  if (!ref.current && exitState === "exited") {
    setExitState("idle");
  }
  useAnimation(
    ref,
    isExiting,
    useCallback(() => {
      setExitState("exited");
      setExiting(false);
    }, [])
  );
  return isExiting;
};
const useAnimation = (ref, isActive, onEnd) => {
  const previousAnimation = useRef(null);
  if (isActive && ref.current) {
    previousAnimation.current = window.getComputedStyle(ref.current).animation;
  }
  useLayoutEffect(() => {
    if (isActive && ref.current) {
      let computedStyle = window.getComputedStyle(ref.current);
      if (computedStyle.animationName !== "none" && computedStyle.animation !== previousAnimation.current) {
        let onAnimationEnd = (event) => {
          if (event.target === ref.current) {
            element.removeEventListener("animationend", onAnimationEnd);
            ReactDOM.flushSync(() => {
              onEnd();
            });
          }
        };
        let element = ref.current;
        element.addEventListener("animationend", onAnimationEnd);
        return () => {
          element.removeEventListener("animationend", onAnimationEnd);
        };
      } else {
        onEnd();
      }
    }
    return;
  }, [ref, isActive, onEnd]);
};
if (typeof HTMLTemplateElement !== "undefined") {
  const getFirstChild = Object.getOwnPropertyDescriptor(
    Node.prototype,
    "firstChild"
  ).get;
  Object.defineProperty(HTMLTemplateElement.prototype, "firstChild", {
    configurable: true,
    enumerable: true,
    get: function() {
      return this.dataset.reactAriaHidden ? this.content.firstChild : getFirstChild.call(this);
    }
  });
}
const HiddenContext = createContext(false);
const hiddenFragment = typeof DocumentFragment !== "undefined" ? new DocumentFragment() : null;
const Hidden = (props) => {
  let isHidden = useContext(HiddenContext);
  let isSSR = useIsSSR();
  if (isHidden) {
    return /* @__PURE__ */ jsx(Fragment, { children: props.children });
  }
  let children = /* @__PURE__ */ jsx(HiddenContext.Provider, { value: true, children: props.children });
  return isSSR ? /* @__PURE__ */ jsx("template", { "data-react-aria-hidden": true, children }) : ReactDOM.createPortal(children, hiddenFragment);
};
const createHideableComponent = (fn) => {
  let Wrapper = (props, ref) => {
    let isHidden = useContext(HiddenContext);
    if (isHidden) {
      return null;
    }
    return fn(props, ref);
  };
  Wrapper.displayName = fn.displayName || fn.name;
  return React__default.forwardRef(Wrapper);
};
function removeDataAttributes(props) {
  const prefix = /^(data-.*)$/;
  let filteredProps = {};
  for (const prop in props) {
    if (!prefix.test(prop)) {
      filteredProps[prop] = props[prop];
    }
  }
  return filteredProps;
}

const additionalButtonHTMLAttributes = /* @__PURE__ */ new Set([
  "form",
  "formAction",
  "formEncType",
  "formMethod",
  "formNoValidate",
  "formTarget",
  "name",
  "value"
]);
const ButtonContext = createContext({});
const ComboBoxButton = (props, ref) => {
  [props, ref] = useContextProps(props, ref, ButtonContext);
  const ctx = props;
  const { buttonProps, isPressed } = useButton(props, ref);
  const { focusProps, isFocused, isFocusVisible } = useFocusRing(props);
  const { hoverProps, isHovered } = useHover(props);
  const renderProps = useRenderProps({
    ...props,
    values: {
      isHovered,
      isPressed,
      isFocused,
      isFocusVisible,
      isDisabled: props.isDisabled || false
    },
    defaultClassName: "react-aria-Button"
  });
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      ...filterDOMProps(props, { propNames: additionalButtonHTMLAttributes }),
      ...mergeProps$1(buttonProps, focusProps, hoverProps),
      ...renderProps,
      ref,
      slot: props.slot || void 0,
      "data-disabled": props.isDisabled || void 0,
      "data-pressed": ctx.isPressed || isPressed || void 0,
      "data-hovered": isHovered || void 0,
      "data-focused": isFocused || void 0,
      "data-focus-visible": isFocusVisible || void 0
    }
  );
};
const _ComboBoxButton = /* @__PURE__ */ createHideableComponent(ComboBoxButton);

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
class NodeValue {
  constructor(type, key) {
    __publicField(this, "type");
    __publicField(this, "key");
    __publicField(this, "value", null);
    __publicField(this, "level", 0);
    __publicField(this, "hasChildNodes", false);
    __publicField(this, "rendered", null);
    __publicField(this, "textValue", "");
    __publicField(this, "aria-label");
    __publicField(this, "index", 0);
    __publicField(this, "parentKey", null);
    __publicField(this, "prevKey", null);
    __publicField(this, "nextKey", null);
    __publicField(this, "firstChildKey", null);
    __publicField(this, "lastChildKey", null);
    __publicField(this, "props", {});
    this.type = type;
    this.key = key;
  }
  get childNodes() {
    throw new Error("childNodes is not supported");
  }
  clone() {
    let node = new NodeValue(this.type, this.key);
    node.value = this.value;
    node.level = this.level;
    node.hasChildNodes = this.hasChildNodes;
    node.rendered = this.rendered;
    node.textValue = this.textValue;
    node["aria-label"] = this["aria-label"];
    node.index = this.index;
    node.parentKey = this.parentKey;
    node.prevKey = this.prevKey;
    node.nextKey = this.nextKey;
    node.firstChildKey = this.firstChildKey;
    node.lastChildKey = this.lastChildKey;
    node.props = this.props;
    return node;
  }
}
class BaseNode {
  constructor(ownerDocument) {
    __publicField(this, "_firstChild", null);
    __publicField(this, "_lastChild", null);
    __publicField(this, "_previousSibling", null);
    __publicField(this, "_nextSibling", null);
    __publicField(this, "_parentNode", null);
    __publicField(this, "ownerDocument");
    this.ownerDocument = ownerDocument;
  }
  *[Symbol.iterator]() {
    let node = this.firstChild;
    while (node) {
      yield node;
      node = node.nextSibling;
    }
  }
  get firstChild() {
    return this._firstChild;
  }
  set firstChild(firstChild) {
    this._firstChild = firstChild;
    this.ownerDocument.markDirty(this);
  }
  get lastChild() {
    return this._lastChild;
  }
  set lastChild(lastChild) {
    this._lastChild = lastChild;
    this.ownerDocument.markDirty(this);
  }
  get previousSibling() {
    return this._previousSibling;
  }
  set previousSibling(previousSibling) {
    this._previousSibling = previousSibling;
    this.ownerDocument.markDirty(this);
  }
  get nextSibling() {
    return this._nextSibling;
  }
  set nextSibling(nextSibling) {
    this._nextSibling = nextSibling;
    this.ownerDocument.markDirty(this);
  }
  get parentNode() {
    return this._parentNode;
  }
  set parentNode(parentNode) {
    this._parentNode = parentNode;
    this.ownerDocument.markDirty(this);
  }
  appendChild(child) {
    this.ownerDocument.startTransaction();
    if (child.parentNode) {
      child.parentNode.removeChild(child);
    }
    if (this.firstChild == null) {
      this.firstChild = child;
    }
    if (this.lastChild) {
      this.lastChild.nextSibling = child;
      child.index = this.lastChild.index + 1;
      child.previousSibling = this.lastChild;
    } else {
      child.previousSibling = null;
      child.index = 0;
    }
    child.parentNode = this;
    child.nextSibling = null;
    this.lastChild = child;
    this.ownerDocument.markDirty(this);
    if (child.hasSetProps) {
      this.ownerDocument.addNode(child);
    }
    this.ownerDocument.endTransaction();
    this.ownerDocument.queueUpdate();
  }
  insertBefore(newNode, referenceNode) {
    if (referenceNode == null) {
      return this.appendChild(newNode);
    }
    this.ownerDocument.startTransaction();
    if (newNode.parentNode) {
      newNode.parentNode.removeChild(newNode);
    }
    newNode.nextSibling = referenceNode;
    newNode.previousSibling = referenceNode.previousSibling;
    newNode.index = referenceNode.index;
    if (this.firstChild === referenceNode) {
      this.firstChild = newNode;
    } else if (referenceNode.previousSibling) {
      referenceNode.previousSibling.nextSibling = newNode;
    }
    referenceNode.previousSibling = newNode;
    newNode.parentNode = referenceNode.parentNode;
    let node = referenceNode;
    while (node) {
      node.index++;
      node = node.nextSibling;
    }
    if (newNode.hasSetProps) {
      this.ownerDocument.addNode(newNode);
    }
    this.ownerDocument.endTransaction();
    this.ownerDocument.queueUpdate();
  }
  removeChild(child) {
    if (child.parentNode !== this) {
      return;
    }
    this.ownerDocument.startTransaction();
    let node = child.nextSibling;
    while (node) {
      node.index--;
      node = node.nextSibling;
    }
    if (child.nextSibling) {
      child.nextSibling.previousSibling = child.previousSibling;
    }
    if (child.previousSibling) {
      child.previousSibling.nextSibling = child.nextSibling;
    }
    if (this.firstChild === child) {
      this.firstChild = child.nextSibling;
    }
    if (this.lastChild === child) {
      this.lastChild = child.previousSibling;
    }
    child.parentNode = null;
    child.nextSibling = null;
    child.previousSibling = null;
    child.index = 0;
    this.ownerDocument.removeNode(child);
    this.ownerDocument.endTransaction();
    this.ownerDocument.queueUpdate();
  }
  addEventListener() {
  }
  removeEventListener() {
  }
}
class ElementNode extends BaseNode {
  constructor(type, ownerDocument) {
    super(ownerDocument);
    __publicField(this, "nodeType", 8);
    __publicField(this, "node");
    __publicField(this, "_index", 0);
    __publicField(this, "hasSetProps", false);
    this.node = new NodeValue(type, `react-aria-${++ownerDocument.nodeId}`);
    this.ownerDocument.startTransaction();
  }
  get index() {
    return this._index;
  }
  set index(index) {
    this._index = index;
    this.ownerDocument.markDirty(this);
  }
  get level() {
    if (this.parentNode instanceof ElementNode) {
      return this.parentNode.level + (this.node.type === "item" ? 1 : 0);
    }
    return 0;
  }
  updateNode() {
    let node = this.ownerDocument.getMutableNode(this);
    node.index = this.index;
    node.level = this.level;
    node.parentKey = this.parentNode instanceof ElementNode ? this.parentNode.node.key : null;
    node.prevKey = this.previousSibling?.node.key ?? null;
    node.nextKey = this.nextSibling?.node.key ?? null;
    node.hasChildNodes = !!this.firstChild;
    node.firstChildKey = this.firstChild?.node.key ?? null;
    node.lastChildKey = this.lastChild?.node.key ?? null;
  }
  setProps(obj, ref, rendered) {
    let node = this.ownerDocument.getMutableNode(this);
    let { value, textValue, id, ...props } = obj;
    props.ref = ref;
    node.props = props;
    node.rendered = rendered;
    node.value = value;
    node.textValue = textValue || (typeof rendered === "string" ? rendered : "") || obj["aria-label"] || "";
    if (id != null && id !== node.key) {
      if (this.hasSetProps) {
        throw new Error("Cannot change the id of an item");
      }
      node.key = id;
    }
    if (!this.hasSetProps) {
      this.ownerDocument.addNode(this);
      this.ownerDocument.endTransaction();
      this.hasSetProps = true;
    }
    this.ownerDocument.queueUpdate();
  }
  get style() {
    return {};
  }
  hasAttribute() {
  }
  setAttribute() {
  }
  setAttributeNS() {
  }
  removeAttribute() {
  }
}
class BaseCollection {
  constructor() {
    __publicField(this, "keyMap", /* @__PURE__ */ new Map());
    __publicField(this, "firstKey", null);
    __publicField(this, "lastKey", null);
    __publicField(this, "frozen", false);
  }
  get size() {
    return this.keyMap.size;
  }
  getKeys() {
    return this.keyMap.keys();
  }
  *[Symbol.iterator]() {
    let node = this.firstKey != null ? this.keyMap.get(this.firstKey) : void 0;
    while (node) {
      yield node;
      node = node.nextKey != null ? this.keyMap.get(node.nextKey) : void 0;
    }
  }
  getChildren(key) {
    let keyMap = this.keyMap;
    return {
      *[Symbol.iterator]() {
        let parent = keyMap.get(key);
        let node = parent?.firstChildKey != null ? keyMap.get(parent.firstChildKey) : null;
        while (node) {
          yield node;
          node = node.nextKey != null ? keyMap.get(node.nextKey) : void 0;
        }
      }
    };
  }
  getKeyBefore(key) {
    let node = this.keyMap.get(key);
    if (!node) {
      return null;
    }
    if (node.prevKey != null) {
      node = this.keyMap.get(node.prevKey);
      while (node && node.type !== "item" && node.lastChildKey != null) {
        node = this.keyMap.get(node.lastChildKey);
      }
      return node?.key ?? null;
    }
    return node.parentKey;
  }
  getKeyAfter(key) {
    let node = this.keyMap.get(key);
    if (!node) {
      return null;
    }
    if (node.type !== "item" && node.firstChildKey != null) {
      return node.firstChildKey;
    }
    while (node) {
      if (node.nextKey != null) {
        return node.nextKey;
      }
      if (node.parentKey != null) {
        node = this.keyMap.get(node.parentKey);
      } else {
        return null;
      }
    }
    return null;
  }
  getFirstKey() {
    return this.firstKey;
  }
  getLastKey() {
    let node = this.lastKey != null ? this.keyMap.get(this.lastKey) : null;
    while (node?.lastChildKey != null) {
      node = this.keyMap.get(node.lastChildKey);
    }
    return node?.key ?? null;
  }
  getItem(key) {
    return this.keyMap.get(key) ?? null;
  }
  at() {
    throw new Error("Not implemented");
  }
  clone() {
    let Constructor = this.constructor;
    let collection = new Constructor();
    collection.keyMap = new Map(this.keyMap);
    collection.firstKey = this.firstKey;
    collection.lastKey = this.lastKey;
    return collection;
  }
  addNode(node) {
    if (this.frozen) {
      throw new Error("Cannot add a node to a frozen collection");
    }
    this.keyMap.set(node.key, node);
  }
  removeNode(key) {
    if (this.frozen) {
      throw new Error("Cannot remove a node to a frozen collection");
    }
    this.keyMap.delete(key);
  }
  commit(firstKey, lastKey, isSSR = false) {
    if (this.frozen) {
      throw new Error("Cannot commit a frozen collection");
    }
    this.firstKey = firstKey;
    this.lastKey = lastKey;
    this.frozen = !isSSR;
  }
}
class Document extends BaseNode {
  constructor(collection) {
    super(null);
    __publicField(this, "nodeType", 11);
    // DOCUMENT_FRAGMENT_NODE
    __publicField(this, "ownerDocument", this);
    __publicField(this, "dirtyNodes", /* @__PURE__ */ new Set());
    __publicField(this, "isSSR", false);
    __publicField(this, "nodeId", 0);
    __publicField(this, "nodesByProps", /* @__PURE__ */ new WeakMap());
    __publicField(this, "collection");
    __publicField(this, "collectionMutated");
    __publicField(this, "mutatedNodes", /* @__PURE__ */ new Set());
    __publicField(this, "subscriptions", /* @__PURE__ */ new Set());
    __publicField(this, "transactionCount", 0);
    this.collection = collection;
    this.collectionMutated = true;
  }
  createElement(type) {
    return new ElementNode(type, this);
  }
  getMutableNode(element) {
    let node = element.node;
    if (!this.mutatedNodes.has(element)) {
      node = element.node.clone();
      this.mutatedNodes.add(element);
      element.node = node;
    }
    this.markDirty(element);
    return node;
  }
  getMutableCollection() {
    if (!this.isSSR && !this.collectionMutated) {
      this.collection = this.collection.clone();
      this.collectionMutated = true;
    }
    return this.collection;
  }
  markDirty(node) {
    this.dirtyNodes.add(node);
  }
  startTransaction() {
    this.transactionCount++;
  }
  endTransaction() {
    this.transactionCount--;
  }
  addNode(element) {
    let collection = this.getMutableCollection();
    if (!collection.getItem(element.node.key)) {
      collection.addNode(element.node);
      for (let child of element) {
        this.addNode(child);
      }
    }
    this.markDirty(element);
  }
  removeNode(node) {
    for (let child of node) {
      child.parentNode = null;
      this.removeNode(child);
    }
    let collection = this.getMutableCollection();
    collection.removeNode(node.node.key);
    this.markDirty(node);
  }
  getCollection() {
    if (this.transactionCount > 0) {
      return this.collection;
    }
    this.updateCollection();
    return this.collection;
  }
  updateCollection() {
    for (let element of this.dirtyNodes) {
      if (element instanceof ElementNode && element.parentNode) {
        element.updateNode();
      }
    }
    this.dirtyNodes.clear();
    if (this.mutatedNodes.size > 0) {
      let collection = this.getMutableCollection();
      for (let element of this.mutatedNodes) {
        if (element.parentNode) {
          collection.addNode(element.node);
        }
      }
      collection.commit(
        this.firstChild?.node.key ?? null,
        this.lastChild?.node.key ?? null,
        this.isSSR
      );
      this.mutatedNodes.clear();
    }
    this.collectionMutated = false;
  }
  queueUpdate() {
    if (this.dirtyNodes.size === 0 || this.transactionCount > 0) {
      return;
    }
    for (let fn of this.subscriptions) {
      fn();
    }
  }
  subscribe(fn) {
    this.subscriptions.add(fn);
    return () => this.subscriptions.delete(fn);
  }
  resetAfterSSR() {
    if (this.isSSR) {
      this.isSSR = false;
      this.firstChild = null;
      this.lastChild = null;
      this.nodeId = 0;
    }
  }
}
const useCachedChildren = (props) => {
  let { children, items, idScope, addIdAndValue } = props;
  let cache = useMemo(() => /* @__PURE__ */ new WeakMap(), []);
  return useMemo(() => {
    if (items && typeof children === "function") {
      let result = [];
      for (let item of items) {
        let rendered = cache.get(item);
        if (!rendered) {
          rendered = children(item);
          if (rendered.key === null) {
            let key = rendered.props.id ?? item.key ?? item.id;
            if (key === null) {
              throw new Error("Could not determine key for item");
            }
            if (idScope) {
              key = idScope + ":" + key;
            }
            rendered = cloneElement(
              rendered,
              addIdAndValue ? { key, id: key, value: item } : { key }
            );
          }
          cache.set(item, rendered);
        }
        result.push(rendered);
      }
      return result;
    } else if (typeof children !== "function") {
      return children;
    } else {
      return;
    }
  }, [children, items, cache, idScope, addIdAndValue]);
};
const useCollectionChildren = (props) => {
  return useCachedChildren({ ...props, addIdAndValue: true });
};
const ShallowRenderContext = createContext(false);
const useCollection = (props, initialCollection) => {
  let { collection, document } = useCollectionDocument(initialCollection);
  let portal = useCollectionPortal(props, document);
  return { portal, collection };
};
const useCollectionDocument = (initialCollection) => {
  let document = useMemo(
    () => new Document(initialCollection || new BaseCollection()),
    [initialCollection]
  );
  let subscribe = useCallback(
    (fn) => document.subscribe(fn),
    [document]
  );
  let getSnapshot = useCallback(() => {
    let collection2 = document.getCollection();
    if (document.isSSR) {
      document.resetAfterSSR();
    }
    return collection2;
  }, [document]);
  let getServerSnapshot = useCallback(() => {
    document.isSSR = true;
    return document.getCollection();
  }, [document]);
  let collection = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot
  );
  return { collection, document };
};
const SSRContext = createContext(null);
const CollectionDocumentContext = createContext(null);
const useCollectionPortal = (props, document) => {
  let ctx = useContext(CollectionDocumentContext);
  let doc = document ?? ctx;
  let children = useCollectionChildren(props);
  let wrappedChildren = useMemo(
    () => /* @__PURE__ */ jsx(ShallowRenderContext.Provider, { value: true, children }),
    [children]
  );
  return useIsSSR() ? /* @__PURE__ */ jsx(SSRContext.Provider, { value: doc, children: wrappedChildren }) : createPortal(wrappedChildren, doc);
};
const CollectionPortal = (props) => {
  return /* @__PURE__ */ jsx(Fragment, { children: useCollectionPortal(props) });
};
const useCollectionItemRef = (props, ref, rendered) => {
  return useCallback(
    (element) => {
      element?.setProps(props, ref, rendered);
    },
    [props, ref, rendered]
  );
};
const useSSRCollectionNode = (Type, props, ref, rendered, children) => {
  let itemRef = useCollectionItemRef(props, ref, rendered);
  let parentNode = useContext(SSRContext);
  if (parentNode) {
    let element = parentNode.ownerDocument.nodesByProps.get(props);
    if (!element) {
      element = parentNode.ownerDocument.createElement(Type);
      element.setProps(props, ref, rendered);
      parentNode.appendChild(element);
      parentNode.ownerDocument.updateCollection();
      parentNode.ownerDocument.nodesByProps.set(props, element);
    }
    return children ? /* @__PURE__ */ jsx(SSRContext.Provider, { value: element, children }) : null;
  }
  return /* @__PURE__ */ jsx(Type, { ref: itemRef, children });
};

const ComboBoxContentContext = createContext(null);
const ComboBoxContentStateContext = createContext(
  null
);
const ComboBoxContent = (props, ref) => {
  [props, ref] = useContextProps(props, ref, ComboBoxContentContext);
  const isHidden = useContext(HiddenContext);
  const state = useContext(ComboBoxContentStateContext);
  const document = useContext(CollectionDocumentContext);
  if (document) {
    return /* @__PURE__ */ jsx(CollectionPortal, { ...props });
  }
  if (state) {
    return isHidden ? null : /* @__PURE__ */ jsx(ListBoxInner, { state, props, listBoxRef: ref });
  }
  return /* @__PURE__ */ jsx(StandaloneListBox, { props, listBoxRef: ref });
};
const StandaloneListBox = ({ props, listBoxRef }) => {
  const { portal, collection } = useCollection(props);
  props = { ...props, collection, children: null, items: null };
  const state = useListState(props);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    portal,
    /* @__PURE__ */ jsx(ListBoxInner, { state, props, listBoxRef })
  ] });
};
const _ComboBoxContent = /* @__PURE__ */ forwardRef(
  ComboBoxContent
);
function ListBoxInner({ state, props, listBoxRef }) {
  const {
    layout = "stack",
    orientation = "vertical",
    onLoadMore,
    hasNextPage,
    isLoadingMore,
    onScroll
  } = props;
  const { collection, selectionManager } = state;
  const { direction } = useLocale();
  const { disabledBehavior, disabledKeys } = selectionManager;
  const collator = useCollator({ usage: "search", sensitivity: "base" });
  const keyboardDelegate = useMemo(
    () => props.keyboardDelegate || new ListKeyboardDelegate({
      collection,
      collator,
      ref: listBoxRef,
      disabledKeys: disabledBehavior === "selection" ? /* @__PURE__ */ new Set() : disabledKeys,
      layout,
      orientation,
      direction
    }),
    [
      collection,
      collator,
      listBoxRef,
      disabledBehavior,
      disabledKeys,
      orientation,
      direction,
      props.keyboardDelegate,
      layout
    ]
  );
  const { listBoxProps } = useListBox(
    {
      ...props,
      shouldSelectOnPressUp: props.shouldSelectOnPressUp,
      keyboardDelegate
    },
    state,
    listBoxRef
  );
  const handleScroll = useCallback(
    (event) => {
      onScroll?.(event);
      if (onLoadMore && hasNextPage && !isLoadingMore) {
        const target = event.currentTarget;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;
        if (scrollTop + clientHeight >= scrollHeight - 100) {
          onLoadMore();
        }
      }
    },
    [onScroll, onLoadMore, hasNextPage, isLoadingMore]
  );
  const handleWheel = useCallback((event) => {
    event.stopPropagation();
  }, []);
  const children = useCachedChildren({
    items: collection,
    children: (item) => {
      switch (item.type) {
        case "section": {
          return /* @__PURE__ */ jsx(ListBoxSection, { section: item });
        }
        case "item": {
          return /* @__PURE__ */ jsx(Option, { item });
        }
        default: {
          throw new Error("Unsupported node type in Menu: " + item.type);
        }
      }
    }
  });
  const { focusProps, isFocused, isFocusVisible } = useFocusRing();
  const renderValues = {
    isDropTarget: false,
    isEmpty: state.collection.size === 0,
    isFocused,
    isFocusVisible,
    layout: props.layout || "stack",
    state
  };
  const renderProps = useRenderProps({
    className: props.className,
    style: props.style,
    defaultClassName: "react-aria-ListBox",
    values: renderValues
  });
  let emptyState = null;
  if (state.collection.size === 0 && props.renderEmptyState) {
    emptyState = /* @__PURE__ */ jsx(
      "div",
      {
        role: "option",
        style: { display: "contents" },
        children: props.renderEmptyState(renderValues)
      }
    );
  }
  return /* @__PURE__ */ jsx(FocusScope, { children: /* @__PURE__ */ jsxs(
    "div",
    {
      ...filterDOMProps(props),
      ...mergeProps$1(listBoxProps, focusProps),
      ...renderProps,
      className: cn(
        "z-50 max-h-64 w-[var(--trigger-width)] overflow-y-auto rounded-md bg-white p-2 shadow-md",
        props.className
      ),
      ref: listBoxRef,
      slot: props.slot || void 0,
      onScroll: handleScroll,
      onWheel: handleWheel,
      "data-empty": state.collection.size === 0 || void 0,
      "data-focused": isFocused || void 0,
      "data-focus-visible": isFocusVisible || void 0,
      "data-layout": props.layout || "stack",
      "data-orientation": props.orientation || "vertical",
      children: [
        /* @__PURE__ */ jsxs(
          Provider,
          {
            values: [
              [ComboBoxContentContext, props],
              [ComboBoxContentStateContext, state]
            ],
            children: [
              children,
              isLoadingMore && /* @__PURE__ */ jsx("div", { className: "flex justify-center p-2", children: /* @__PURE__ */ jsx("div", { className: "text-sm text-gray-500", children: "Loading more..." }) })
            ]
          }
        ),
        emptyState
      ]
    }
  ) });
}
function ListBoxSection({
  section,
  className,
  style
}) {
  const state = useContext(ComboBoxContentStateContext);
  const [, heading] = useSlot();
  const { groupProps } = useListBoxSection({
    heading,
    "aria-label": section.props["aria-label"] ?? void 0
  });
  const children = useCachedChildren({
    items: state.collection.getChildren(section.key),
    children: (item) => {
      switch (item.type) {
        case "item": {
          return /* @__PURE__ */ jsx(Option, { item });
        }
        default: {
          throw new Error("Unsupported element type in Section: " + item.type);
        }
      }
    }
  });
  return /* @__PURE__ */ jsx(
    "section",
    {
      ...filterDOMProps(section.props),
      ...groupProps,
      className: className || section.props?.className || "react-aria-Section",
      style: style || section.props?.style,
      ref: section.props.ref,
      children
    }
  );
}
const ComboBoxItem = (props, ref) => {
  return useSSRCollectionNode(
    "item",
    {
      ...props,
      className: ({
        isFocused,
        isSelected
      }) => cn(
        "mb-1 flex cursor-pointer items-center gap-2 rounded-sm p-1 last:mb-0 [&>svg]:text-neutral-700",
        {
          "bg-neutral-100 [&>svg]:text-black": isFocused,
          "bg-primary [&>svg]:text-primary-contrast text-primary-contrast": isSelected
        },
        props.className
      )
    },
    ref,
    props.children
  );
};
const _ComboBoxItem = /* @__PURE__ */ forwardRef(
  ComboBoxItem
);
function Option({ item }) {
  const ref = useObjectRef(item.props.ref);
  const state = useContext(ComboBoxContentStateContext);
  const { optionProps, labelProps, descriptionProps, ...states } = useOption(
    { key: item.key },
    state,
    ref
  );
  const { hoverProps, isHovered } = useHover({
    isDisabled: !states.allowsSelection && !states.hasAction
  });
  const props = item.props;
  const renderProps = useRenderProps({
    ...props,
    id: void 0,
    children: item.rendered,
    defaultClassName: "react-aria-ListBoxItem",
    values: {
      ...states,
      isHovered,
      selectionMode: state.selectionManager.selectionMode,
      selectionBehavior: state.selectionManager.selectionBehavior,
      allowsDragging: false,
      isDragging: false,
      isDropTarget: false
    }
  });
  useEffect(() => {
    if (!item.textValue) {
      console.warn(
        "A `textValue` prop is required for <ListBoxItem> elements with non-plain text children in order to support accessibility features such as type to select."
      );
    }
  }, [item.textValue]);
  const ElementType = props.href ? "a" : "div";
  return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(
    ElementType,
    {
      ...mergeProps$1(optionProps, hoverProps),
      ...renderProps,
      ref,
      "data-selected": states.isSelected || void 0,
      "data-disabled": states.isDisabled || void 0,
      "data-hovered": isHovered || void 0,
      "data-focused": states.isFocused || void 0,
      "data-focus-visible": states.isFocusVisible || void 0,
      "data-pressed": states.isPressed || void 0,
      "data-selection-mode": state.selectionManager.selectionMode === "none" ? void 0 : state.selectionManager.selectionMode,
      children: renderProps.children
    }
  ) });
}

var _path$B;
function _extends$C() { _extends$C = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$C.apply(this, arguments); }
var SvgTimes$3 = function SvgTimes(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$C({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none"
  }, props), _path$B || (_path$B = /*#__PURE__*/React.createElement("path", {
    stroke: "currentColor",
    strokeLinecap: "round",
    d: "M16.365 16.434 5.638 5.707M5.638 16.305 16.365 5.578"
  })));
};

const InputGroupContext = createContext({
  handleInputValueChange: () => {
  },
  handleClearButtonClick: () => {
  }
});
const InputGroupProvider = ({ children }) => {
  const [inputValue, setInputValue] = useState("");
  const context = useMemo(
    () => ({
      inputValue,
      handleInputValueChange: setInputValue,
      handleClearButtonClick: () => {
        setInputValue("");
      }
    }),
    [inputValue, setInputValue]
  );
  return /* @__PURE__ */ jsx(InputGroupContext.Provider, { value: context, children });
};

const InputGroup = forwardRef(
  ({ className, error, ...props }, ref) => {
    return /* @__PURE__ */ jsx(InputGroupProvider, { children: /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "group/input-group flex items-center gap-1 rounded border border-neutral-500 bg-white px-2 [&>svg]:h-5 [&>svg]:w-5",
          "[&>input]:w-full [&>input]:border-none [&>input]:bg-transparent [&>input]:px-0",
          {
            "focus-within:border-primary": !error,
            "border-error": error
          },
          className
        ),
        ...props
      }
    ) });
  }
);
InputGroup.displayName = "InputGroup";
const Input = forwardRef(
  ({
    className,
    asChild,
    error,
    value,
    defaultValue,
    onValueChange,
    onChange,
    ...props
  }, ref) => {
    const { inputValue, handleInputValueChange } = useContext(InputGroupContext);
    const [_value, setValue] = useControllableState({
      value: value ?? inputValue,
      defaultValue: defaultValue ?? "",
      onValueChange: (value2) => {
        handleInputValueChange(value2);
        onValueChange?.(value2);
      }
    });
    useEffect(() => {
      if (value !== void 0 && value !== inputValue) {
        handleInputValueChange(value);
      }
    }, [value, inputValue, handleInputValueChange]);
    const Component = asChild ? Slot : "input";
    return /* @__PURE__ */ jsx(
      Component,
      {
        ref,
        value: _value,
        onChange: (event) => {
          if ("value" in event.target) {
            setValue(event.target.value);
            onChange?.(event);
          }
        },
        className: cn(
          "w-full rounded border border-neutral-500 bg-white p-2 outline-none duration-200 placeholder:text-neutral-800 disabled:opacity-30",
          {
            "focus-visible:border-primary": !error,
            "border-error": error
          },
          className
        ),
        ...props
      }
    );
  }
);
Input.displayName = "Input";
const InputLeftContent = forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(
        "[&>svg]:group-focus-within/input-group:text-primary flex items-center gap-1 [&>svg]:text-xl [&>svg]:text-neutral-500",
        className
      ),
      ...props
    }
  );
});
InputLeftContent.displayName = "InputLeftContent";
const InputClearButton = forwardRef(({ className, type = "button", children, onClick, value, ...props }, ref) => {
  const { handleClearButtonClick, inputValue } = useContext(InputGroupContext);
  return /* @__PURE__ */ jsx(
    "button",
    {
      ref,
      type,
      className: cn(
        "hover:bg-primary-100 pointer-events-none hidden rounded-sm text-xl duration-200",
        {
          "group-hover/input-group:pointer-events-auto group-hover/input-group:block": inputValue || value
        },
        className
      ),
      onClick: (event) => {
        handleClearButtonClick();
        onClick?.(event);
      },
      ...props,
      children: children ?? /* @__PURE__ */ jsx(SvgTimes$3, { className: "text-xl text-black" })
    }
  );
});
InputClearButton.displayName = "InputClearButton";

const ComboBoxInputContext = createContext({});
const ComboBoxInput = ({ size = "lg", ...props }, ref) => {
  [props, ref] = useContextProps(props, ref, ComboBoxInputContext);
  const { hoverProps, isHovered } = useHover({});
  const { isFocused, isFocusVisible, focusProps } = useFocusRing({
    isTextInput: true,
    autoFocus: props.autoFocus
  });
  const isInvalid = !!props["aria-invalid"] && props["aria-invalid"] !== "false";
  const renderProps = useRenderProps({
    ...props,
    values: {
      isHovered,
      isFocused,
      isFocusVisible,
      isDisabled: props.disabled || false,
      isInvalid
    },
    defaultClassName: "react-aria-Input"
  });
  return /* @__PURE__ */ jsx(
    Input,
    {
      ...mergeProps$1(props, focusProps, hoverProps),
      ...renderProps,
      ref,
      className: cn(
        "pl-2 pr-6 pt-2 text-left",
        {
          "h-14": size === "lg",
          "h-[52px]": size === "md"
        },
        props.className
      ),
      "data-focused": isFocused || void 0,
      "data-disabled": props.disabled || void 0,
      "data-hovered": isHovered || void 0,
      "data-focus-visible": isFocusVisible || void 0,
      "data-invalid": isInvalid || void 0
    }
  );
};
const _ComboBoxInput = /* @__PURE__ */ createHideableComponent(ComboBoxInput);

const ComboBoxLabelContext = createContext({});
const ComboBoxLabel = ({ asChild, ...props }, ref) => {
  [props, ref] = useContextProps(props, ref, ComboBoxLabelContext);
  const Component = asChild ? Slot : "label";
  return /* @__PURE__ */ jsx(Component, { className: "react-aria-Label", ...props, ref });
};
const _ComboBoxLabel = /* @__PURE__ */ createHideableComponent(ComboBoxLabel);

const OverlayTriggerStateContext = createContext(null);
const ComboBoxPopoverContext = createContext(null);
const ComboBoxPopover = (props, ref) => {
  [props, ref] = useContextProps(props, ref, ComboBoxPopoverContext);
  const contextState = useContext(OverlayTriggerStateContext);
  const localState = useOverlayTriggerState(props);
  const state = (
    // eslint-disable-next-line eqeqeq
    props.isOpen != null || props.defaultOpen != null || !contextState ? localState : contextState
  );
  const isExiting = useExitAnimation(ref, state.isOpen);
  const isHidden = useContext(HiddenContext);
  if (isHidden) {
    let children = props.children;
    if (typeof children === "function") {
      children = children({
        placement: "bottom",
        isEntering: false,
        isExiting: false
      });
    }
    return /* @__PURE__ */ jsx(Fragment, { children });
  }
  if (state && !state.isOpen && !isExiting) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    PopoverInner,
    {
      ...props,
      triggerRef: props.triggerRef,
      state,
      popoverRef: ref,
      isExiting
    }
  );
};
const _ComboBoxPopover = /* @__PURE__ */ forwardRef(
  ComboBoxPopover
);
const PopoverInner = ({ state, isExiting, ...props }) => {
  const { popoverProps, underlayProps, arrowProps, placement } = usePopover(
    {
      ...props,
      offset: props.offset ?? 8
    },
    state
  );
  const ref = props.popoverRef;
  const isEntering = useEnterAnimation(ref, !!placement);
  const renderProps = useRenderProps({
    ...props,
    defaultClassName: "react-aria-Popover",
    values: {
      placement,
      isEntering,
      isExiting
    }
  });
  const style = { ...renderProps.style, ...popoverProps.style };
  return /* @__PURE__ */ jsxs(Overlay$1, { isExiting, children: [
    !props.isNonModal && /* @__PURE__ */ jsx("div", { ...underlayProps, style: { position: "fixed", inset: 0 } }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        ...mergeProps(filterDOMProps(props), popoverProps),
        ...renderProps,
        ref,
        slot: props.slot || void 0,
        style,
        "data-placement": placement,
        "data-entering": isEntering || void 0,
        "data-exiting": isExiting || void 0,
        children: [
          !props.isNonModal && /* @__PURE__ */ jsx(DismissButton, { onDismiss: state.close }),
          /* @__PURE__ */ jsx(OverlayArrowContext.Provider, { value: { ...arrowProps, placement }, children: renderProps.children }),
          /* @__PURE__ */ jsx(DismissButton, { onDismiss: state.close })
        ]
      }
    )
  ] });
};
const OverlayArrowContext = createContext({
  placement: "bottom"
});

const ComboBoxContext = createContext(null);
const ComboBoxStateContext = createContext(
  null
);
const ComboBox = (props, ref) => {
  [props, ref] = useContextProps(
    {
      ...props,
      selectedKey: props.value,
      onSelectionChange: props.onValueChange,
      onInputChange: props.onInputValueChange
    },
    ref,
    ComboBoxContext
  );
  const { collection, document: document2 } = useCollectionDocument();
  const {
    isDisabled = false,
    isInvalid = false,
    isRequired = false,
    items,
    defaultItems
  } = props;
  let { children } = props;
  children = useMemo(
    () => typeof children === "function" ? children({
      isOpen: false,
      isDisabled,
      isInvalid,
      isRequired
    }) : children,
    [children, isDisabled, isInvalid, isRequired]
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Hidden, { children: /* @__PURE__ */ jsx(
      Provider,
      {
        values: [
          [CollectionDocumentContext, document2],
          [ComboBoxContentContext, { items: items ?? defaultItems }]
        ],
        children
      }
    ) }),
    /* @__PURE__ */ jsx(ComboBoxInner, { props, collection, comboBoxRef: ref })
  ] });
};
const ComboBoxInner = ({
  props,
  collection,
  comboBoxRef: ref
}) => {
  const {
    name,
    allowsCustomValue,
    defaultFilter,
    items,
    isDisabled,
    isInvalid,
    isRequired,
    slot,
    buttonRef
  } = props;
  let { formValue = "key" } = props;
  if (allowsCustomValue) {
    formValue = "text";
  }
  const { contains } = useFilter({ sensitivity: "base" });
  const state = useComboBoxState({
    defaultFilter: defaultFilter || contains,
    ...props,
    items,
    children: void 0,
    collection,
    // HACK: This is a temporary solution to allow async items: https://github.com/adobe/react-spectrum/issues/3392#issuecomment-1726752664
    allowsEmptyCollection: true
  });
  const renderPropsState = useMemo(
    () => ({
      isOpen: state.isOpen,
      isDisabled: isDisabled || false,
      isInvalid: isInvalid || false,
      isRequired: isRequired || false
    }),
    [state.isOpen, isDisabled, isInvalid, isRequired]
  );
  const inputRef = useRef(null);
  const listBoxRef = useRef(null);
  const popoverRef = useRef(null);
  const [labelRef, label] = useSlot();
  const { buttonProps, inputProps, listBoxProps, labelProps } = useComboBox(
    {
      ...removeDataAttributes(props),
      label,
      inputRef,
      buttonRef,
      listBoxRef,
      popoverRef,
      name: formValue === "text" ? name : void 0
    },
    state
  );
  const onResize = useCallback(() => {
    if (inputRef.current) {
      const buttonRect = buttonRef?.current?.getBoundingClientRect();
      const inputRect = inputRef.current.getBoundingClientRect();
      const minX = buttonRect ? Math.min(buttonRect.left, inputRect.left) : inputRect.left;
      const maxX = buttonRect ? Math.max(buttonRect.right, inputRect.right) : inputRect.right;
      document.documentElement.style.setProperty(
        "--trigger-width",
        `${maxX - minX}px`
      );
    }
  }, [buttonRef, inputRef]);
  useResizeObserver({
    ref: inputRef,
    onResize
  });
  const renderProps = useRenderProps({
    ...props,
    values: renderPropsState,
    defaultClassName: "react-aria-ComboBox"
  });
  const DOMProps = filterDOMProps(props);
  delete DOMProps.id;
  return /* @__PURE__ */ jsxs(
    Provider,
    {
      values: [
        [ComboBoxStateContext, state],
        [ComboBoxLabelContext, { ...labelProps, ref: labelRef }],
        [
          ButtonContext,
          { ...buttonProps, ref: buttonRef, isPressed: state.isOpen }
        ],
        [
          ComboBoxInputContext,
          {
            ...inputProps,
            size: "lg",
            value: inputProps.value,
            defaultValue: inputProps.defaultValue,
            ref: inputRef
          }
        ],
        [OverlayTriggerStateContext, state],
        [
          ComboBoxPopoverContext,
          {
            ref: popoverRef,
            triggerRef: inputRef,
            placement: "bottom start",
            isNonModal: true
          }
        ],
        [ComboBoxContentContext, { ...listBoxProps, ref: listBoxRef }],
        [ComboBoxContentStateContext, state]
      ],
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            ...DOMProps,
            ...renderProps,
            ref,
            slot: slot || void 0,
            "data-focused": state.isFocused || void 0,
            "data-open": state.isOpen || void 0,
            "data-disabled": props.isDisabled || void 0,
            "data-invalid": props.isInvalid || void 0,
            "data-required": props.isRequired || void 0
          }
        ),
        name && formValue === "key" && /* @__PURE__ */ jsx("input", { type: "hidden", name, value: state.selectedKey })
      ]
    }
  );
};
const _ComboBox = /* @__PURE__ */ forwardRef(ComboBox);

var _path$A;
function _extends$B() { _extends$B = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$B.apply(this, arguments); }
var SvgArrow$5 = function SvgArrow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$B({
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$A || (_path$A = /*#__PURE__*/React.createElement("path", {
    d: "M6.05 9 11 13.95 15.95 9",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

const FloatingInputGroupContext = createContext({
  hasLeftContent: false,
  handleLeftContentRendered: () => {
  }
});
const FloatingInputGroup = forwardRef(
  ({
    className,
    error,
    focused,
    value,
    success,
    hasLeftContent: hasLeftContentProperty,
    ...props
  }, ref) => {
    const [leftContentRendered, setLeftContentRendered] = useState(
      hasLeftContentProperty ?? false
    );
    const handleLeftContentRendered = useCallback(() => {
      setLeftContentRendered(true);
    }, []);
    const context = useMemo(
      () => ({
        focused,
        hasLeftContent: leftContentRendered,
        handleLeftContentRendered,
        value
      }),
      [focused, handleLeftContentRendered, leftContentRendered, value]
    );
    return /* @__PURE__ */ jsx(FloatingInputGroupContext.Provider, { value: context, children: /* @__PURE__ */ jsx(InputGroupProvider, { children: /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "group/input-group relative flex cursor-pointer items-center gap-1 rounded border border-neutral-500 bg-white px-2 transition-colors duration-200",
          {
            "focus-within:border-primary": !error,
            "border-error": error,
            "border-primary": focused && !error,
            "border-success": success
          },
          className
        ),
        "data-focused": focused,
        ...props
      }
    ) }) });
  }
);
FloatingInputGroup.displayName = "FloatingInputGroup";
const FloatingLabel = forwardRef(({ className, ...props }, ref) => {
  const { hasLeftContent, focused, value } = useContext(
    FloatingInputGroupContext
  );
  const { inputValue } = useContext(InputGroupContext);
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    /* @__PURE__ */ jsx(
      "label",
      {
        ref,
        className: cn(
          "absolute origin-top-left cursor-text text-neutral-800 transition-transform duration-200",
          "group-focus-within/input-group:-translate-y-3 group-focus-within/input-group:translate-x-0 group-focus-within/input-group:scale-75",
          {
            "translate-x-6": hasLeftContent && !(inputValue || focused || value),
            "-translate-y-3 translate-x-0 scale-75": inputValue || focused || value
          },
          className
        ),
        ...props
      }
    )
  );
});
FloatingLabel.displayName = "FloatingLabel";
const FloatingInputLeftContent = forwardRef(({ className, ...props }, ref) => {
  const { handleLeftContentRendered, focused, value } = useContext(
    FloatingInputGroupContext
  );
  const { inputValue } = useContext(InputGroupContext);
  useLayoutEffect$1(() => {
    handleLeftContentRendered();
  }, [handleLeftContentRendered]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(
        "group-focus-within/input-group:text-primary flex items-center gap-1 text-xl text-neutral-500 duration-200 group-focus-within/input-group:translate-y-1.5",
        { "text-primary translate-y-1.5": focused || inputValue || value },
        className
      ),
      ...props
    }
  );
});
FloatingInputLeftContent.displayName = "FloatingInputLeftContent";
const FloatingInput = forwardRef(
  ({ className, size = "lg", ...props }, ref) => /* @__PURE__ */ jsx(
    Input,
    {
      className: cn(
        "w-full border-none bg-transparent pl-0 placeholder:opacity-0 focus-within:placeholder:opacity-100",
        {
          "pt-6": size === "lg",
          "pt-5": size === "md"
        },
        className
      ),
      ref,
      ...props
    }
  )
);
FloatingInput.displayName = Input.displayName;
const FloatingInputRightContent = forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn("flex items-center gap-1 text-xl text-black", className),
      ...props
    }
  );
});
FloatingInputRightContent.displayName = "FloatingInputRightContent";
const FloatingInputArrow = ({
  className,
  ...props
}) => {
  const { focused } = useContext(FloatingInputGroupContext);
  return /* @__PURE__ */ jsx(
    SvgArrow$5,
    {
      ...props,
      className: cn("", { "rotate-180": focused }, className)
    }
  );
};
const FloatingInputClearButton = (props) => {
  const { value } = useContext(FloatingInputGroupContext);
  return /* @__PURE__ */ jsx(
    InputClearButton,
    {
      ...props,
      value,
      tabIndex: props.tabIndex || -1
    }
  );
};

const Command = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    Command$1,
    {
      ref,
      shouldFilter: false,
      className: cn("flex flex-col", className),
      ...props
    }
  )
);
Command.displayName = Command$1.displayName;
const CommandInput = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  Command$1.Input,
  {
    ref,
    className: "w-full rounded border border-neutral-500 bg-white p-2 outline-none duration-200 placeholder:text-neutral-800",
    ...props
  }
));
CommandInput.displayName = Command$1.Input.displayName;
const CommandFloatingInput = forwardRef(({ label, icon, error, focused, value, className, ...props }, ref) => /* @__PURE__ */ jsxs(
  FloatingInputGroup,
  {
    error,
    focused,
    value: value?.toString(),
    className,
    children: [
      icon && /* @__PURE__ */ jsx(FloatingInputLeftContent, { children: icon }),
      /* @__PURE__ */ jsx(FloatingLabel, { children: label }),
      /* @__PURE__ */ jsx(
        Command$1.Input,
        {
          ref,
          value: value?.toString(),
          className: "h-14 w-full border-none bg-transparent pl-0 pt-3 outline-none placeholder:opacity-0 focus-within:placeholder:opacity-100",
          ...props
        }
      )
    ]
  }
));
CommandFloatingInput.displayName = "CommandFloatingInput";
const CommandContent = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    Command$1.List,
    {
      ref,
      className: cn(
        "mt-2 max-h-72 overflow-y-auto overflow-x-hidden rounded bg-white p-2",
        className
      ),
      ...props
    }
  )
);
CommandContent.displayName = Command$1.List.displayName;
const CommandEmpty = forwardRef(
  ({ children, className, ...props }, ref) => {
    const { t } = useTranslation("ui:command");
    return /* @__PURE__ */ jsx(
      Command$1.Empty,
      {
        ref,
        className: cn("py-6 text-center", className),
        ...props,
        children: children ?? t("No results found.")
      }
    );
  }
);
CommandEmpty.displayName = Command$1.Empty.displayName;
const CommandItem = forwardRef(
  ({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(
    Command$1.Item,
    {
      ref,
      className: cn(
        "aria-selected:bg-primary-100 flex cursor-default select-none items-center rounded-sm px-1 py-3 outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      ),
      ...props,
      children
    }
  )
);
CommandItem.displayName = Command$1.Item.displayName;

const Overlay = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      "data-ui-overlay": true,
      className: cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-neutral-900/80",
        className
      ),
      ...props,
      ref
    }
  )
);
Overlay.displayName = "Overlay";

const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetClose = DialogPrimitive.Close;
const SheetPortal = (props) => /* @__PURE__ */ jsx(DialogPrimitive.Portal, { ...props });
SheetPortal.displayName = DialogPrimitive.Portal.displayName;
const SheetOverlay = forwardRef(
  ({ asChild: _, ...props }, ref) => /* @__PURE__ */ jsx(DialogPrimitive.Overlay, { asChild: true, ...props, ref, children: /* @__PURE__ */ jsx(Overlay, {}) })
);
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;
const SHEET_CONTENT_SIDE = {
  TOP: "top",
  BOTTOM: "bottom",
  LEFT: "left",
  RIGHT: "right"
};
const sheetContentStyles = cva("", {
  variants: {
    side: {
      [SHEET_CONTENT_SIDE.TOP]: "inset-x-0 top-0 data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
      [SHEET_CONTENT_SIDE.BOTTOM]: "inset-x-0 bottom-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
      [SHEET_CONTENT_SIDE.LEFT]: "inset-y-0 left-0 h-full w-3/4 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
      [SHEET_CONTENT_SIDE.RIGHT]: "inset-y-0 right-0 h-full w-3/4 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm"
    }
  }
});
const SheetContent = forwardRef(
  ({ side = SHEET_CONTENT_SIDE.RIGHT, className, children, ...props }, ref) => /* @__PURE__ */ jsxs(SheetPortal, { children: [
    /* @__PURE__ */ jsx(SheetOverlay, {}),
    /* @__PURE__ */ jsx(
      DialogPrimitive.Content,
      {
        "data-ui-dialog-content": true,
        ref,
        className: cn(
          "data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 max-h-full overflow-y-auto bg-white p-2 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
          sheetContentStyles({ side }),
          className
        ),
        ...props,
        children
      }
    )
  ] })
);
SheetContent.displayName = DialogPrimitive.Content.displayName;

var _path$z;
function _extends$A() { _extends$A = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$A.apply(this, arguments); }
var SvgX$1 = function SvgX(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$A({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none"
  }, props), _path$z || (_path$z = /*#__PURE__*/React.createElement("path", {
    stroke: "currentColor",
    strokeLinecap: "round",
    d: "M16.365 16.434 5.638 5.707M5.638 16.305 16.365 5.578"
  })));
};

const CommandSheetContext = createContext({
  value: "",
  handleSelect: () => {
  },
  handleTrigger: () => {
  }
});
const CommandSheet = ({
  label,
  value,
  defaultValue,
  open,
  defaultOpen,
  onOpenChange,
  onValueChange,
  ...props
}) => {
  const [_value, setValue] = useControllableState({
    value,
    defaultValue,
    onValueChange
  });
  const [_open, setOpen] = useControllableState({
    value: open,
    defaultValue: defaultOpen,
    onValueChange: onOpenChange
  });
  return /* @__PURE__ */ jsx(
    CommandSheetContext.Provider,
    {
      value: {
        label,
        value: _value,
        handleSelect: (value2) => {
          setValue(value2);
          setOpen(false);
        },
        handleTrigger: () => setOpen(!_open)
      },
      children: /* @__PURE__ */ jsx(Sheet, { open: _open, onOpenChange: setOpen, ...props })
    }
  );
};
CommandSheet.displayName = Sheet.displayName;
const CommandSheetTrigger = forwardRef(
  ({ onClick = () => {
  }, ...props }, ref) => {
    const { handleTrigger } = useContext(CommandSheetContext);
    return /* @__PURE__ */ jsx(
      SheetTrigger,
      {
        ref,
        onClick: (event) => {
          handleTrigger();
          onClick(event);
        },
        ...props
      }
    );
  }
);
CommandSheetTrigger.displayName = SheetTrigger.displayName;
const CommandSheetList = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    CommandContent,
    {
      ref,
      className: cn("max-h-fit flex-1 bg-white", className),
      ...props
    }
  )
);
CommandSheetList.displayName = CommandContent.displayName;
const CommandSheetContent = forwardRef(
  ({
    children,
    className,
    side = SHEET_CONTENT_SIDE.BOTTOM,
    shouldFilter,
    ...props
  }, ref) => {
    const { label } = useContext(CommandSheetContext);
    return /* @__PURE__ */ jsxs(
      SheetContent,
      {
        ref,
        className: cn(
          "flex flex-col-reverse justify-end overflow-y-hidden bg-neutral-100",
          // NOTE: it is reversed because browser automatically focus on the first element, and we want it to focus on the input
          {
            "h-5/6 rounded-b-lg ": side === SHEET_CONTENT_SIDE.TOP,
            "rounded-r-lg": side === SHEET_CONTENT_SIDE.LEFT,
            "h-5/6 rounded-t-lg ": side === SHEET_CONTENT_SIDE.BOTTOM,
            "rounded-l-lg": side === SHEET_CONTENT_SIDE.RIGHT
          },
          className
        ),
        side,
        ...props,
        children: [
          /* @__PURE__ */ jsx(
            Command,
            {
              shouldFilter,
              className: "flex-1 overflow-y-hidden",
              label,
              children
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "mb-2 flex justify-between", children: [
            /* @__PURE__ */ jsx("div", { className: "p-2 text-lg font-bold", children: label }),
            /* @__PURE__ */ jsx(SheetClose, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: BUTTON_VARIANT.GHOST, children: /* @__PURE__ */ jsx(SvgX$1, { height: 24, width: 24 }) }) })
          ] })
        ]
      }
    );
  }
);
CommandSheetContent.displayName = SheetContent.displayName;
const CommandSheetItem = forwardRef(
  ({ children, value, onSelect: _, ...props }, ref) => {
    const { value: selectedValue, handleSelect } = useContext(CommandSheetContext);
    return /* @__PURE__ */ jsx(
      CommandItem,
      {
        ref,
        value,
        className: cn("flex gap-1", {
          "text-primary": value?.toLowerCase().trim() === selectedValue
        }),
        onSelect: handleSelect,
        ...props,
        children
      }
    );
  }
);
CommandSheetItem.displayName = CommandItem.displayName;

const ResponsiveAutocomplete = ({
  id,
  inputValue,
  label,
  items,
  loading,
  icon,
  name,
  error,
  onValueChange,
  onInputValueChange,
  onBlur,
  showMobileIcon = true,
  defaultValue = "",
  shouldFilter = true,
  inputRef,
  size = "lg",
  fullScreen = false,
  className
}) => {
  const { t } = useTranslation("ui:responsive-autocomplete");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [_inputValue, setInputValue] = useState(defaultValue);
  const [previousInputValue, setPreviousInputValue] = useState(inputValue);
  if (inputValue !== previousInputValue) {
    setPreviousInputValue(inputValue);
    setInputValue(inputValue ?? "");
    setValue(inputValue ?? "");
  }
  const _items = shouldFilter ? items.filter(
    (item) => item.title.toLowerCase().includes(_inputValue.toLowerCase().trim())
  ) : items;
  const handleValueChange = (value2) => {
    const inputValue2 = items.find((item) => item.value === value2)?.title;
    inputValue2 && setInputValue(inputValue2);
    setValue(value2);
    onValueChange?.(value2);
  };
  const handleInputValueChange = (value2) => {
    if (value2 === "") {
      setValue("");
      onValueChange?.("");
    }
    setInputValue(value2);
    onInputValueChange?.(value2);
  };
  const handleClearValue = () => {
    setInputValue("");
    onInputValueChange?.("");
    onValueChange?.("");
    setValue("");
  };
  return isMobile ? /* @__PURE__ */ jsxs(
    CommandSheet,
    {
      label,
      open: mobileOpen,
      value,
      onOpenChange: setMobileOpen,
      onValueChange: handleValueChange,
      children: [
        /* @__PURE__ */ jsxs(
          FloatingInputGroup,
          {
            focused: mobileOpen,
            error,
            value,
            hasLeftContent: !!icon,
            children: [
              icon && /* @__PURE__ */ jsx(FloatingInputLeftContent, { children: icon }),
              /* @__PURE__ */ jsx(FloatingLabel, { htmlFor: id, className: "pointer-events-none", children: label }),
              /* @__PURE__ */ jsx(CommandSheetTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
                FloatingInput,
                {
                  ref: inputRef,
                  id,
                  value,
                  size,
                  asChild: true,
                  children: /* @__PURE__ */ jsx(
                    "button",
                    {
                      type: "button",
                      className: cn("truncate text-left", {
                        "h-14": size === "lg",
                        "h-[52px]": size === "md"
                      }),
                      children: items.find(
                        (item) => item.value.toLowerCase() === value.toLowerCase()
                      )?.title ?? ""
                    }
                  )
                }
              ) }),
              /* @__PURE__ */ jsx("input", { type: "hidden", name, value })
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          CommandSheetContent,
          {
            shouldFilter,
            className: cn(fullScreen && "h-full"),
            children: [
              /* @__PURE__ */ jsxs(InputGroup, { children: [
                showMobileIcon && /* @__PURE__ */ jsx(InputLeftContent, { children: icon }),
                /* @__PURE__ */ jsx(
                  CommandInput,
                  {
                    value: _inputValue,
                    onValueChange: handleInputValueChange
                  }
                ),
                loading && /* @__PURE__ */ jsx(Loading, {})
              ] }),
              /* @__PURE__ */ jsxs(CommandSheetList, { children: [
                items.length === 0 && /* @__PURE__ */ jsx(CommandEmpty, { className: loading ? "invisible" : "visible", children: t("No results found.") }),
                items.map((item) => /* @__PURE__ */ jsxs(
                  CommandSheetItem,
                  {
                    className: "flex gap-2",
                    value: item.value,
                    children: [
                      item.icon && /* @__PURE__ */ jsx("span", { className: "text-xl", children: item.icon }),
                      item.subtitle ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
                        /* @__PURE__ */ jsx("span", { className: "font-bold", children: item.title }),
                        /* @__PURE__ */ jsx("span", { className: "text-xs font-normal text-neutral-800", children: item.subtitle })
                      ] }) : /* @__PURE__ */ jsx("span", { children: item.title })
                    ]
                  },
                  item.value
                ))
              ] })
            ]
          }
        )
      ]
    }
  ) : /* @__PURE__ */ jsx(
    FloatingComboBox$1,
    {
      label,
      items: _items,
      value,
      inputValue: _inputValue,
      name,
      loading,
      icon,
      error,
      onValueChange: handleValueChange,
      onInputValueChange: handleInputValueChange,
      onBlur,
      onClear: handleClearValue,
      size,
      className,
      children: (item) => /* @__PURE__ */ jsxs(_ComboBoxItem, { id: item.value, textValue: item.title, children: [
        /* @__PURE__ */ jsx("span", { className: "shrink-0 text-xl", children: item.icon }),
        item.subtitle ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
          /* @__PURE__ */ jsx("span", { className: "font-bold", children: item.title }),
          /* @__PURE__ */ jsx("span", { className: "text-xs font-normal text-neutral-800", children: item.subtitle })
        ] }) : /* @__PURE__ */ jsx("span", { children: item.title })
      ] })
    }
  );
};
const FloatingComboBox$1 = ({
  label,
  description,
  errorMessage,
  children,
  name,
  loading,
  icon,
  onClear,
  error,
  size,
  className,
  inputValue,
  ...props
}) => {
  const { t } = useTranslation("ui:responsive-autocomplete");
  return /* @__PURE__ */ jsxs(_ComboBox, { className, inputValue, ...props, children: [
    /* @__PURE__ */ jsxs(
      FloatingInputGroup,
      {
        error,
        value: inputValue,
        hasLeftContent: !!icon,
        className: "relative w-full px-2",
        children: [
          icon && /* @__PURE__ */ jsx(FloatingInputLeftContent, { className: "absolute left-2 shrink-0", children: icon }),
          /* @__PURE__ */ jsx(_ComboBoxLabel, { asChild: true, children: /* @__PURE__ */ jsx(FloatingLabel, { children: label }) }),
          /* @__PURE__ */ jsx(
            _ComboBoxInput,
            {
              className: cn("pl-0 pt-5", { "pl-7": !!icon }),
              size,
              name,
              asChild: true,
              children: /* @__PURE__ */ jsx(FloatingInput, {})
            }
          ),
          /* @__PURE__ */ jsx(_ComboBoxButton, {}),
          /* @__PURE__ */ jsxs(FloatingInputRightContent, { className: "absolute right-0 pr-2", children: [
            loading && /* @__PURE__ */ jsx(Loading, {}),
            /* @__PURE__ */ jsx(FloatingInputClearButton, { onClick: onClear })
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsx(_ComboBoxPopover, { className: loading || !inputValue ? "hidden" : "", children: /* @__PURE__ */ jsx(
      _ComboBoxContent,
      {
        className: "pointer-events-auto",
        renderEmptyState: () => /* @__PURE__ */ jsx("p", { className: "px-4 py-2 text-gray-700", children: t("No results found.") }),
        children
      }
    ) })
  ] });
};

const useCountries = () => {
  const { t } = useTranslation("ui:country-input");
  return [
    {
      name: t("Afghanistan"),
      callingCode: "+93",
      code: "af"
    },
    {
      name: t("Albania"),
      callingCode: "+355",
      code: "al"
    },
    {
      name: t("Algeria"),
      callingCode: "+213",
      code: "dz"
    },
    {
      name: t("AmericanSamoa"),
      callingCode: "+1 684",
      code: "as"
    },
    {
      name: t("Andorra"),
      callingCode: "+376",
      code: "ad"
    },
    {
      name: t("Angola"),
      callingCode: "+244",
      code: "ao"
    },
    {
      name: t("Anguilla"),
      callingCode: "+1 264",
      code: "ai"
    },
    {
      name: t("Antigua and Barbuda"),
      callingCode: "+1268",
      code: "ag"
    },
    {
      name: t("Argentina"),
      callingCode: "+54",
      code: "ar"
    },
    {
      name: t("Armenia"),
      callingCode: "+374",
      code: "am"
    },
    {
      name: t("Aruba"),
      callingCode: "+297",
      code: "aw"
    },
    {
      name: t("Australia"),
      callingCode: "+61",
      code: "au"
    },
    {
      name: t("Austria"),
      callingCode: "+43",
      code: "at"
    },
    {
      name: t("Azerbaijan"),
      callingCode: "+994",
      code: "az"
    },
    {
      name: t("Bahamas"),
      callingCode: "+1 242",
      code: "bs"
    },
    {
      name: t("Bahrain"),
      callingCode: "+973",
      code: "bh"
    },
    {
      name: t("Bangladesh"),
      callingCode: "+880",
      code: "bd"
    },
    {
      name: t("Barbados"),
      callingCode: "+1 246",
      code: "bb"
    },
    {
      name: t("Belarus"),
      callingCode: "+375",
      code: "by"
    },
    {
      name: t("Belgium"),
      callingCode: "+32",
      code: "be"
    },
    {
      name: t("Belize"),
      callingCode: "+501",
      code: "bz"
    },
    {
      name: t("Benin"),
      callingCode: "+229",
      code: "bj"
    },
    {
      name: t("Bermuda"),
      callingCode: "+1 441",
      code: "bm"
    },
    {
      name: t("Bhutan"),
      callingCode: "+975",
      code: "bt"
    },
    {
      name: t("Bosnia and Herzegovina"),
      callingCode: "+387",
      code: "ba"
    },
    {
      name: t("Botswana"),
      callingCode: "+267",
      code: "bw"
    },
    {
      name: t("Brazil"),
      callingCode: "+55",
      code: "br"
    },
    {
      name: t("British Indian Ocean Territory"),
      callingCode: "+246",
      code: "io"
    },
    {
      name: t("Bulgaria"),
      callingCode: "+359",
      code: "bg"
    },
    {
      name: t("Burkina Faso"),
      callingCode: "+226",
      code: "bf"
    },
    {
      name: t("Burundi"),
      callingCode: "+257",
      code: "bi"
    },
    {
      name: t("Cambodia"),
      callingCode: "+855",
      code: "kh"
    },
    {
      name: t("Cameroon"),
      callingCode: "+237",
      code: "cm"
    },
    {
      name: t("Canada"),
      callingCode: "+1",
      code: "ca"
    },
    {
      name: t("Cape Verde"),
      callingCode: "+238",
      code: "cv"
    },
    {
      name: t("Cayman Islands"),
      callingCode: "+ 345",
      code: "ky"
    },
    {
      name: t("Central African Republic"),
      callingCode: "+236",
      code: "cf"
    },
    {
      name: t("Chad"),
      callingCode: "+235",
      code: "td"
    },
    {
      name: t("Chile"),
      callingCode: "+56",
      code: "cl"
    },
    {
      name: t("China"),
      callingCode: "+86",
      code: "cn"
    },
    {
      name: t("Christmas Island"),
      callingCode: "+61 8 9164",
      code: "cx"
    },
    {
      name: t("Colombia"),
      callingCode: "+57",
      code: "co"
    },
    {
      name: t("Comoros"),
      callingCode: "+269",
      code: "km"
    },
    {
      name: t("Congo"),
      callingCode: "+242",
      code: "cg"
    },
    {
      name: t("Cook Islands"),
      callingCode: "+682",
      code: "ck"
    },
    {
      name: t("Costa Rica"),
      callingCode: "+506",
      code: "cr"
    },
    {
      name: t("Croatia"),
      callingCode: "+385",
      code: "hr"
    },
    {
      name: t("Curacao"),
      callingCode: "+599",
      code: "cw"
    },
    {
      name: t("Cyprus"),
      callingCode: "+357",
      code: "cy"
    },
    {
      name: t("Czech Republic"),
      callingCode: "+420",
      code: "cz"
    },
    {
      name: t("Denmark"),
      callingCode: "+45",
      code: "dk"
    },
    {
      name: t("Djibouti"),
      callingCode: "+253",
      code: "dj"
    },
    {
      name: t("Dominica"),
      callingCode: "+1 767",
      code: "dm"
    },
    {
      name: t("Dominican Republic"),
      callingCode: "+1 849",
      code: "do"
    },
    {
      name: t("Dominican Republic"),
      callingCode: "+1 809",
      code: "do"
    },
    {
      name: t("Dominican Republic"),
      callingCode: "+1 829",
      code: "do"
    },
    {
      name: t("Ecuador"),
      callingCode: "+593",
      code: "ec"
    },
    {
      name: t("Egypt"),
      callingCode: "+20",
      code: "eg"
    },
    {
      name: t("El Salvador"),
      callingCode: "+503",
      code: "sv"
    },
    {
      name: t("Equatorial Guinea"),
      callingCode: "+240",
      code: "gq"
    },
    {
      name: t("Eritrea"),
      callingCode: "+291",
      code: "er"
    },
    {
      name: t("Estonia"),
      callingCode: "+372",
      code: "ee"
    },
    {
      name: t("Ethiopia"),
      callingCode: "+251",
      code: "et"
    },
    {
      name: t("Faroe Islands"),
      callingCode: "+298",
      code: "fo"
    },
    {
      name: t("Fiji"),
      callingCode: "+679",
      code: "fj"
    },
    {
      name: t("Finland"),
      callingCode: "+358",
      code: "fi"
    },
    {
      name: t("France"),
      callingCode: "+33",
      code: "fr"
    },
    {
      name: t("French Guiana"),
      callingCode: "+594",
      code: "gf"
    },
    {
      name: t("French Polynesia"),
      callingCode: "+689",
      code: "pf"
    },
    {
      name: t("Gabon"),
      callingCode: "+241",
      code: "ga"
    },
    {
      name: t("Gambia"),
      callingCode: "+220",
      code: "gm"
    },
    {
      name: t("Georgia"),
      callingCode: "+995",
      code: "ge"
    },
    {
      name: t("Germany"),
      callingCode: "+49",
      code: "de"
    },
    {
      name: t("Ghana"),
      callingCode: "+233",
      code: "gh"
    },
    {
      name: t("Gibraltar"),
      callingCode: "+350",
      code: "gi"
    },
    {
      name: t("Greece"),
      callingCode: "+30",
      code: "gr"
    },
    {
      name: t("Greenland"),
      callingCode: "+299",
      code: "gl"
    },
    {
      name: t("Grenada"),
      callingCode: "+1 473",
      code: "gd"
    },
    {
      name: t("Guadeloupe"),
      callingCode: "+590",
      code: "gp"
    },
    {
      name: t("Guam"),
      callingCode: "+1 671",
      code: "gu"
    },
    {
      name: t("Guatemala"),
      callingCode: "+502",
      code: "gt"
    },
    {
      name: t("Guinea"),
      callingCode: "+224",
      code: "gn"
    },
    {
      name: t("Guinea-Bissau"),
      callingCode: "+245",
      code: "gw"
    },
    {
      name: t("Guyana"),
      callingCode: "+595",
      code: "gy"
    },
    {
      name: t("Haiti"),
      callingCode: "+509",
      code: "ht"
    },
    {
      name: t("Honduras"),
      callingCode: "+504",
      code: "hn"
    },
    {
      name: t("Hungary"),
      callingCode: "+36",
      code: "hu"
    },
    {
      name: t("Iceland"),
      callingCode: "+354",
      code: "is"
    },
    {
      name: t("India"),
      callingCode: "+91",
      code: "in"
    },
    {
      name: t("Indonesia"),
      callingCode: "+62",
      code: "id"
    },
    {
      name: t("Iraq"),
      callingCode: "+964",
      code: "iq"
    },
    {
      name: t("Ireland"),
      callingCode: "+353",
      code: "ie"
    },
    {
      name: t("Israel"),
      callingCode: "+972",
      code: "il"
    },
    {
      name: t("Italy"),
      callingCode: "+39",
      code: "it"
    },
    {
      name: t("Jamaica"),
      callingCode: "+1 876",
      code: "jm"
    },
    {
      name: t("Jamaica"),
      callingCode: "+1 658",
      code: "jm"
    },
    {
      name: t("Japan"),
      callingCode: "+81",
      code: "jp"
    },
    {
      name: t("Jordan"),
      callingCode: "+962",
      code: "jo"
    },
    {
      name: t("Kazakhstan"),
      callingCode: "+7 7",
      code: "kz"
    },
    {
      name: t("Kenya"),
      callingCode: "+254",
      code: "ke"
    },
    {
      name: t("Kiribati"),
      callingCode: "+686",
      code: "ki"
    },
    {
      name: t("Kuwait"),
      callingCode: "+965",
      code: "kw"
    },
    {
      name: t("Kyrgyzstan"),
      callingCode: "+996",
      code: "kg"
    },
    {
      name: t("Latvia"),
      callingCode: "+371",
      code: "lv"
    },
    {
      name: t("Lebanon"),
      callingCode: "+961",
      code: "lb"
    },
    {
      name: t("Lesotho"),
      callingCode: "+266",
      code: "ls"
    },
    {
      name: t("Liberia"),
      callingCode: "+231",
      code: "lr"
    },
    {
      name: t("Liechtenstein"),
      callingCode: "+423",
      code: "li"
    },
    {
      name: t("Lithuania"),
      callingCode: "+370",
      code: "lt"
    },
    {
      name: t("Luxembourg"),
      callingCode: "+352",
      code: "lu"
    },
    {
      name: t("Madagascar"),
      callingCode: "+261",
      code: "mg"
    },
    {
      name: t("Malawi"),
      callingCode: "+265",
      code: "mw"
    },
    {
      name: t("Malaysia"),
      callingCode: "+60",
      code: "my"
    },
    {
      name: t("Maldives"),
      callingCode: "+960",
      code: "mv"
    },
    {
      name: t("Mali"),
      callingCode: "+223",
      code: "ml"
    },
    {
      name: t("Malta"),
      callingCode: "+356",
      code: "mt"
    },
    {
      name: t("Marshall Islands"),
      callingCode: "+692",
      code: "mh"
    },
    {
      name: t("Martinique"),
      callingCode: "+596",
      code: "mq"
    },
    {
      name: t("Mauritania"),
      callingCode: "+222",
      code: "mr"
    },
    {
      name: t("Mauritius"),
      callingCode: "+230",
      code: "mu"
    },
    {
      name: t("Mayotte"),
      callingCode: "+262",
      code: "yt"
    },
    {
      name: t("Mexico"),
      callingCode: "+52",
      code: "mx"
    },
    {
      name: t("Monaco"),
      callingCode: "+377",
      code: "mc"
    },
    {
      name: t("Mongolia"),
      callingCode: "+976",
      code: "mn"
    },
    {
      name: t("Montenegro"),
      callingCode: "+382",
      code: "me"
    },
    {
      name: t("Montserrat"),
      callingCode: "+1664",
      code: "ms"
    },
    {
      name: t("Morocco"),
      callingCode: "+212",
      code: "ma"
    },
    {
      name: t("Myanmar"),
      callingCode: "+95",
      code: "mm"
    },
    {
      name: t("Namibia"),
      callingCode: "+264",
      code: "na"
    },
    {
      name: t("Nauru"),
      callingCode: "+674",
      code: "nr"
    },
    {
      name: t("Nepal"),
      callingCode: "+977",
      code: "np"
    },
    {
      name: t("Netherlands"),
      callingCode: "+31",
      code: "nl"
    },
    {
      name: t("New Caledonia"),
      callingCode: "+687",
      code: "nc"
    },
    {
      name: t("New Zealand"),
      callingCode: "+64",
      code: "nz"
    },
    {
      name: t("Nicaragua"),
      callingCode: "+505",
      code: "ni"
    },
    {
      name: t("Niger"),
      callingCode: "+227",
      code: "ne"
    },
    {
      name: t("Nigeria"),
      callingCode: "+234",
      code: "ng"
    },
    {
      name: t("Niue"),
      callingCode: "+683",
      code: "nu"
    },
    {
      name: t("Norfolk Island"),
      callingCode: "+672",
      code: "nf"
    },
    {
      name: t("Northern Mariana Islands"),
      callingCode: "+1 670",
      code: "mp"
    },
    {
      name: t("Norway"),
      callingCode: "+47",
      code: "no"
    },
    {
      name: t("Oman"),
      callingCode: "+968",
      code: "om"
    },
    {
      name: t("Pakistan"),
      callingCode: "+92",
      code: "pk"
    },
    {
      name: t("Palau"),
      callingCode: "+680",
      code: "pw"
    },
    {
      name: t("Panama"),
      callingCode: "+507",
      code: "pa"
    },
    {
      name: t("Papua New Guinea"),
      callingCode: "+675",
      code: "pg"
    },
    {
      name: t("Paraguay"),
      callingCode: "+595",
      code: "py"
    },
    {
      name: t("Peru"),
      callingCode: "+51",
      code: "pe"
    },
    {
      name: t("Philippines"),
      callingCode: "+63",
      code: "ph"
    },
    {
      name: t("Poland"),
      callingCode: "+48",
      code: "pl"
    },
    {
      name: t("Portugal"),
      callingCode: "+351",
      code: "pt"
    },
    {
      name: t("Puerto Rico"),
      callingCode: "+1 939",
      code: "pr"
    },
    {
      name: t("Puerto Rico"),
      callingCode: "+1 787",
      code: "pr"
    },
    {
      name: t("Qatar"),
      callingCode: "+974",
      code: "qa"
    },
    {
      name: t("Romania"),
      callingCode: "+40",
      code: "ro"
    },
    {
      name: t("Rwanda"),
      callingCode: "+250",
      code: "rw"
    },
    {
      name: t("Samoa"),
      callingCode: "+685",
      code: "ws"
    },
    {
      name: t("San Marino"),
      callingCode: "+378",
      code: "sm"
    },
    {
      name: t("Saudi Arabia"),
      callingCode: "+966",
      code: "sa"
    },
    {
      name: t("Senegal"),
      callingCode: "+221",
      code: "sn"
    },
    {
      name: t("Serbia"),
      callingCode: "+381",
      code: "rs"
    },
    {
      name: t("Seychelles"),
      callingCode: "+248",
      code: "sc"
    },
    {
      name: t("Sierra Leone"),
      callingCode: "+232",
      code: "sl"
    },
    {
      name: t("Singapore"),
      callingCode: "+65",
      code: "sg"
    },
    {
      name: t("Slovakia"),
      callingCode: "+421",
      code: "sk"
    },
    {
      name: t("Slovenia"),
      callingCode: "+386",
      code: "si"
    },
    {
      name: t("Solomon Islands"),
      callingCode: "+677",
      code: "sb"
    },
    {
      name: t("South Africa"),
      callingCode: "+27",
      code: "za"
    },
    {
      name: t("South Georgia and the South Sandwich Islands"),
      callingCode: "+500",
      code: "gs"
    },
    {
      name: t("Spain"),
      callingCode: "+34",
      code: "es"
    },
    {
      name: t("Sri Lanka"),
      callingCode: "+94",
      code: "lk"
    },
    {
      name: t("Suriname"),
      callingCode: "+597",
      code: "sr"
    },
    {
      name: t("Swaziland"),
      callingCode: "+268",
      code: "sz"
    },
    {
      name: t("Sweden"),
      callingCode: "+46",
      code: "se"
    },
    {
      name: t("Switzerland"),
      callingCode: "+41",
      code: "ch"
    },
    {
      name: t("Tajikistan"),
      callingCode: "+992",
      code: "tj"
    },
    {
      name: t("Thailand"),
      callingCode: "+66",
      code: "th"
    },
    {
      name: t("Togo"),
      callingCode: "+228",
      code: "tg"
    },
    {
      name: t("Tokelau"),
      callingCode: "+690",
      code: "tk"
    },
    {
      name: t("Tonga"),
      callingCode: "+676",
      code: "to"
    },
    {
      name: t("Trinidad and Tobago"),
      callingCode: "+1 868",
      code: "tt"
    },
    {
      name: t("Tunisia"),
      callingCode: "+216",
      code: "tn"
    },
    {
      name: t("Turkey"),
      callingCode: "+90",
      code: "tr"
    },
    {
      name: t("Turkmenistan"),
      callingCode: "+993",
      code: "tm"
    },
    {
      name: t("Turks and Caicos Islands"),
      callingCode: "+1 649",
      code: "tc"
    },
    {
      name: t("Tuvalu"),
      callingCode: "+688",
      code: "tv"
    },
    {
      name: t("Uganda"),
      callingCode: "+256",
      code: "ug"
    },
    {
      name: t("Ukraine"),
      callingCode: "+380",
      code: "ua"
    },
    {
      name: t("United Arab Emirates"),
      callingCode: "+971",
      code: "ae"
    },
    {
      name: t("United Kingdom"),
      callingCode: "+44",
      code: "gb"
    },
    {
      name: t("United States"),
      callingCode: "+1",
      code: "us"
    },
    {
      name: t("Uruguay"),
      callingCode: "+598",
      code: "uy"
    },
    {
      name: t("Uzbekistan"),
      callingCode: "+998",
      code: "uz"
    },
    {
      name: t("Vanuatu"),
      callingCode: "+678",
      code: "vu"
    },
    {
      name: t("Wallis and Futuna"),
      callingCode: "+681",
      code: "wf"
    },
    {
      name: t("Yemen"),
      callingCode: "+967",
      code: "ye"
    },
    {
      name: t("Zambia"),
      callingCode: "+260",
      code: "zm"
    },
    {
      name: t("Zimbabwe"),
      callingCode: "+263",
      code: "zw"
    },
    {
      name: t("\xC5land Islands"),
      callingCode: "+358",
      code: "ax"
    },
    {
      name: t("Bolivia, Plurinational State of"),
      callingCode: "+591",
      code: "bo"
    },
    {
      name: t("Brunei Darussalam"),
      callingCode: "+673",
      code: "bn"
    },
    {
      name: t("Cocos (Keeling) Islands"),
      callingCode: "+61 8 9162",
      code: "cc"
    },
    {
      name: t("Congo, The Democratic Republic of the"),
      callingCode: "+243",
      code: "cd"
    },
    {
      name: t("Cote d'Ivoire"),
      callingCode: "+225",
      code: "ci"
    },
    {
      name: t("Falkland Islands (Malvinas)"),
      callingCode: "+500",
      code: "fk"
    },
    {
      name: t("Guernsey"),
      callingCode: "+44",
      code: "gg"
    },
    {
      name: t("Holy See (Vatican City State)"),
      callingCode: "+379",
      code: "va"
    },
    {
      name: t("Hong Kong"),
      callingCode: "+852",
      code: "hk"
    },
    {
      name: t("Isle of Man"),
      callingCode: "+44",
      code: "im"
    },
    {
      name: t("Jersey"),
      callingCode: "+44",
      code: "je"
    },
    {
      name: t("Korea, Republic of"),
      callingCode: "+82",
      code: "kr"
    },
    {
      name: t("Lao People's Democratic Republic"),
      callingCode: "+856",
      code: "la"
    },
    {
      name: t("Libyan Arab Jamahiriya"),
      callingCode: "+218",
      code: "ly"
    },
    {
      name: t("Macao"),
      callingCode: "+853",
      code: "mo"
    },
    {
      name: t("Macedonia, The Former Yugoslav Republic of"),
      callingCode: "+389",
      code: "mk"
    },
    {
      name: t("Micronesia, Federated States of"),
      callingCode: "+691",
      code: "fm"
    },
    {
      name: t("Moldova, Republic of"),
      callingCode: "+373",
      code: "md"
    },
    {
      name: t("Mozambique"),
      callingCode: "+258",
      code: "mz"
    },
    {
      name: t("Palestinian Territory, Occupied"),
      callingCode: "+970",
      code: "ps"
    },
    {
      name: t("Pitcairn"),
      callingCode: "+872",
      code: "pn"
    },
    {
      name: t("R\xE9union"),
      callingCode: "+262",
      code: "re"
    },
    {
      name: t("Russia"),
      callingCode: "+7",
      code: "ru"
    },
    {
      name: t("Saint Barth\xE9lemy"),
      callingCode: "+590",
      code: "bl"
    },
    {
      name: t("Saint Helena, Ascension and Tristan Da Cunha"),
      callingCode: "+290",
      code: "sh"
    },
    {
      name: t("Saint Kitts and Nevis"),
      callingCode: "+1 869",
      code: "kn"
    },
    {
      name: t("Saint Lucia"),
      callingCode: "+1 758",
      code: "lc"
    },
    {
      name: t("Saint Martin"),
      callingCode: "+590",
      code: "mf"
    },
    {
      name: t("Saint Pierre and Miquelon"),
      callingCode: "+508",
      code: "pm"
    },
    {
      name: t("Saint Vincent and the Grenadines"),
      callingCode: "+1 784",
      code: "vc"
    },
    {
      name: t("Sao Tome and Principe"),
      callingCode: "+239",
      code: "st"
    },
    {
      name: t("Somalia"),
      callingCode: "+252",
      code: "so"
    },
    {
      name: t("Svalbard and Jan Mayen"),
      callingCode: "+47",
      code: "sj"
    },
    {
      name: t("Taiwan, Province of China"),
      callingCode: "+886",
      code: "tw"
    },
    {
      name: t("Tanzania, United Republic of"),
      callingCode: "+255",
      code: "tz"
    },
    {
      name: t("Timor-Leste"),
      callingCode: "+670",
      code: "tl"
    },
    {
      name: t("Venezuela, Bolivarian Republic of"),
      callingCode: "+58",
      code: "ve"
    },
    {
      name: t("Viet Nam"),
      callingCode: "+84",
      code: "vn"
    },
    {
      name: t("Virgin Islands, British"),
      callingCode: "+1 284",
      code: "vg"
    },
    {
      name: t("Virgin Islands, U.S."),
      callingCode: "+1 340",
      code: "vi"
    }
  ];
};
const useCountriesAllowedOnStripe = () => {
  const { t } = useTranslation("ui:country-input");
  return [
    {
      name: t("Albania"),
      callingCode: "+355",
      code: "al"
    },
    {
      name: t("Algeria"),
      callingCode: "+213",
      code: "dz"
    },
    {
      name: t("Angola"),
      callingCode: "+244",
      code: "ao"
    },
    {
      name: t("Antigua and Barbuda"),
      callingCode: "+1268",
      code: "ag"
    },
    {
      name: t("Argentina"),
      callingCode: "+54",
      code: "ar"
    },
    {
      name: t("Armenia"),
      callingCode: "+374",
      code: "am"
    },
    {
      name: t("Australia"),
      callingCode: "+61",
      code: "au"
    },
    {
      name: t("Austria"),
      callingCode: "+43",
      code: "at"
    },
    {
      name: t("Azerbaijan"),
      callingCode: "+994",
      code: "az"
    },
    {
      name: t("Bahamas"),
      callingCode: "+1 242",
      code: "bs"
    },
    {
      name: t("Bahrain"),
      callingCode: "+973",
      code: "bh"
    },
    {
      name: t("Bangladesh"),
      callingCode: "+880",
      code: "bd"
    },
    {
      name: t("Belgium"),
      callingCode: "+32",
      code: "be"
    },
    {
      name: t("Benin"),
      callingCode: "+229",
      code: "bj"
    },
    {
      name: t("Bhutan"),
      callingCode: "+975",
      code: "bt"
    },
    {
      name: t("Bolivia, Plurinational State of"),
      callingCode: "+591",
      code: "bo"
    },
    {
      name: t("Bosnia and Herzegovina"),
      callingCode: "+387",
      code: "ba"
    },
    {
      name: t("Botswana"),
      callingCode: "+267",
      code: "bw"
    },
    {
      name: t("Brunei Darussalam"),
      callingCode: "+673",
      code: "bn"
    },
    {
      name: t("Bulgaria"),
      callingCode: "+359",
      code: "bg"
    },
    {
      name: t("Cambodia"),
      callingCode: "+855",
      code: "kh"
    },
    {
      name: t("Canada"),
      callingCode: "+1",
      code: "ca"
    },
    {
      name: t("Chile"),
      callingCode: "+56",
      code: "cl"
    },
    {
      name: t("Colombia"),
      callingCode: "+57",
      code: "co"
    },
    {
      name: t("Costa Rica"),
      callingCode: "+506",
      code: "cr"
    },
    {
      name: t("Cote d'Ivoire"),
      callingCode: "+225",
      code: "ci"
    },
    {
      name: t("Croatia"),
      callingCode: "+385",
      code: "hr"
    },
    {
      name: t("Cyprus"),
      callingCode: "+357",
      code: "cy"
    },
    {
      name: t("Czech Republic"),
      callingCode: "+420",
      code: "cz"
    },
    {
      name: t("Denmark"),
      callingCode: "+45",
      code: "dk"
    },
    {
      name: t("Dominican Republic"),
      callingCode: "+1 849",
      code: "do"
    },
    {
      name: t("Ecuador"),
      callingCode: "+593",
      code: "ec"
    },
    {
      name: t("Egypt"),
      callingCode: "+20",
      code: "eg"
    },
    {
      name: t("El Salvador"),
      callingCode: "+503",
      code: "sv"
    },
    {
      name: t("Estonia"),
      callingCode: "+372",
      code: "ee"
    },
    {
      name: t("Ethiopia"),
      callingCode: "+251",
      code: "et"
    },
    {
      name: t("Finland"),
      callingCode: "+358",
      code: "fi"
    },
    {
      name: t("France"),
      callingCode: "+33",
      code: "fr"
    },
    {
      name: t("Gabon"),
      callingCode: "+241",
      code: "ga"
    },
    {
      name: t("Gambia"),
      callingCode: "+220",
      code: "gm"
    },
    {
      name: t("Germany"),
      callingCode: "+49",
      code: "de"
    },
    {
      name: t("Ghana"),
      callingCode: "+233",
      code: "gh"
    },
    {
      name: t("Greece"),
      callingCode: "+30",
      code: "gr"
    },
    {
      name: t("Guatemala"),
      callingCode: "+502",
      code: "gt"
    },
    {
      name: t("Guyana"),
      callingCode: "+595",
      code: "gy"
    },
    {
      name: t("Hong Kong"),
      callingCode: "+852",
      code: "hk"
    },
    {
      name: t("Hungary"),
      callingCode: "+36",
      code: "hu"
    },
    {
      name: t("Iceland"),
      callingCode: "+354",
      code: "is"
    },
    {
      name: t("India"),
      callingCode: "+91",
      code: "in"
    },
    {
      name: t("Indonesia"),
      callingCode: "+62",
      code: "id"
    },
    {
      name: t("Ireland"),
      callingCode: "+353",
      code: "ie"
    },
    {
      name: t("Israel"),
      callingCode: "+972",
      code: "il"
    },
    {
      name: t("Italy"),
      callingCode: "+39",
      code: "it"
    },
    {
      name: t("Jamaica"),
      callingCode: "+1 876",
      code: "jm"
    },
    {
      name: t("Jordan"),
      callingCode: "+962",
      code: "jo"
    },
    {
      name: t("Kazakhstan"),
      callingCode: "+7 7",
      code: "kz"
    },
    {
      name: t("Kenya"),
      callingCode: "+254",
      code: "ke"
    },
    {
      name: t("Kuwait"),
      callingCode: "+965",
      code: "kw"
    },
    {
      name: t("Lao People's Democratic Republic"),
      callingCode: "+856",
      code: "la"
    },
    {
      name: t("Latvia"),
      callingCode: "+371",
      code: "lv"
    },
    {
      name: t("Liechtenstein"),
      callingCode: "+423",
      code: "li"
    },
    {
      name: t("Lithuania"),
      callingCode: "+370",
      code: "lt"
    },
    {
      name: t("Luxembourg"),
      callingCode: "+352",
      code: "lu"
    },
    {
      name: t("Macao"),
      callingCode: "+853",
      code: "mo"
    },
    {
      name: t("Madagascar"),
      callingCode: "+261",
      code: "mg"
    },
    {
      name: t("Malaysia"),
      callingCode: "+60",
      code: "my"
    },
    {
      name: t("Malta"),
      callingCode: "+356",
      code: "mt"
    },
    {
      name: t("Mauritius"),
      callingCode: "+230",
      code: "mu"
    },
    {
      name: t("Mexico"),
      callingCode: "+52",
      code: "mx"
    },
    {
      name: t("Moldova, Republic of"),
      callingCode: "+373",
      code: "md"
    },
    {
      name: t("Monaco"),
      callingCode: "+377",
      code: "mc"
    },
    {
      name: t("Mongolia"),
      callingCode: "+976",
      code: "mn"
    },
    {
      name: t("Morocco"),
      callingCode: "+212",
      code: "ma"
    },
    {
      name: t("Mozambique"),
      callingCode: "+258",
      code: "mz"
    },
    {
      name: t("Namibia"),
      callingCode: "+264",
      code: "na"
    },
    {
      name: t("Netherlands"),
      callingCode: "+31",
      code: "nl"
    },
    {
      name: t("New Zealand"),
      callingCode: "+64",
      code: "nz"
    },
    {
      name: t("Niger"),
      callingCode: "+227",
      code: "ne"
    },
    {
      name: t("Nigeria"),
      callingCode: "+234",
      code: "ng"
    },
    {
      name: t("Macedonia, The Former Yugoslav Republic of"),
      callingCode: "+389",
      code: "mk"
    },
    {
      name: t("Norway"),
      callingCode: "+47",
      code: "no"
    },
    {
      name: t("Oman"),
      callingCode: "+968",
      code: "om"
    },
    {
      name: t("Pakistan"),
      callingCode: "+92",
      code: "pk"
    },
    {
      name: t("Panama"),
      callingCode: "+507",
      code: "pa"
    },
    {
      name: t("Paraguay"),
      callingCode: "+595",
      code: "py"
    },
    {
      name: t("Peru"),
      callingCode: "+51",
      code: "pe"
    },
    {
      name: t("Philippines"),
      callingCode: "+63",
      code: "ph"
    },
    {
      name: t("Poland"),
      callingCode: "+48",
      code: "pl"
    },
    {
      name: t("Portugal"),
      callingCode: "+351",
      code: "pt"
    },
    {
      name: t("Qatar"),
      callingCode: "+974",
      code: "qa"
    },
    {
      name: t("Romania"),
      callingCode: "+40",
      code: "ro"
    },
    {
      name: t("Rwanda"),
      callingCode: "+250",
      code: "rw"
    },
    {
      name: t("San Marino"),
      callingCode: "+378",
      code: "sm"
    },
    {
      name: t("Saudi Arabia"),
      callingCode: "+966",
      code: "sa"
    },
    {
      name: t("Senegal"),
      callingCode: "+221",
      code: "sn"
    },
    {
      name: t("Serbia"),
      callingCode: "+381",
      code: "rs"
    },
    {
      name: t("Singapore"),
      callingCode: "+65",
      code: "sg"
    },
    {
      name: t("Slovakia"),
      callingCode: "+421",
      code: "sk"
    },
    {
      name: t("Slovenia"),
      callingCode: "+386",
      code: "si"
    },
    {
      name: t("South Africa"),
      callingCode: "+27",
      code: "za"
    },
    {
      name: t("Korea, Republic of"),
      callingCode: "+82",
      code: "kr"
    },
    {
      name: t("Spain"),
      callingCode: "+34",
      code: "es"
    },
    {
      name: t("Sri Lanka"),
      callingCode: "+94",
      code: "lk"
    },
    {
      name: t("Saint Lucia"),
      callingCode: "+1 758",
      code: "lc"
    },
    {
      name: t("Sweden"),
      callingCode: "+46",
      code: "se"
    },
    {
      name: t("Switzerland"),
      callingCode: "+41",
      code: "ch"
    },
    {
      name: t("Taiwan, Province of China"),
      callingCode: "+886",
      code: "tw"
    },
    {
      name: t("Tanzania, United Republic of"),
      callingCode: "+255",
      code: "tz"
    },
    {
      name: t("Thailand"),
      callingCode: "+66",
      code: "th"
    },
    {
      name: t("Trinidad and Tobago"),
      callingCode: "+1 868",
      code: "tt"
    },
    {
      name: t("Tunisia"),
      callingCode: "+216",
      code: "tn"
    },
    {
      name: t("Turkey"),
      callingCode: "+90",
      code: "tr"
    },
    {
      name: t("United Arab Emirates"),
      callingCode: "+971",
      code: "ae"
    },
    {
      name: t("United Kingdom"),
      callingCode: "+44",
      code: "gb"
    },
    {
      name: t("United States"),
      callingCode: "+1",
      code: "us"
    },
    {
      name: t("Uruguay"),
      callingCode: "+598",
      code: "uy"
    },
    {
      name: t("Uzbekistan"),
      callingCode: "+998",
      code: "uz"
    }
  ];
};
const callingCodeToCountryCode = {
  "+93": "af",
  "+355": "al",
  "+213": "dz",
  "+1684": "as",
  "+376": "ad",
  "+244": "ao",
  "+1264": "ai",
  "+1268": "ag",
  "+54": "ar",
  "+374": "am",
  "+297": "aw",
  "+61": "au",
  "+43": "at",
  "+994": "az",
  "+1242": "bs",
  "+973": "bh",
  "+880": "bd",
  "+1246": "bb",
  "+375": "by",
  "+32": "be",
  "+501": "bz",
  "+229": "bj",
  "+1441": "bm",
  "+975": "bt",
  "+387": "ba",
  "+267": "bw",
  "+55": "br",
  "+246": "io",
  "+359": "bg",
  "+226": "bf",
  "+257": "bi",
  "+855": "kh",
  "+237": "cm",
  "+1": "us",
  "+238": "cv",
  "+345": "ky",
  "+236": "cf",
  "+235": "td",
  "+56": "cl",
  "+86": "cn",
  "+6189164": "cx",
  "+57": "co",
  "+269": "km",
  "+242": "cg",
  "+682": "ck",
  "+506": "cr",
  "+385": "hr",
  "+599": "cw",
  "+357": "cy",
  "+420": "cz",
  "+45": "dk",
  "+253": "dj",
  "+1767": "dm",
  "+1849": "do",
  "+1809": "do",
  "+1829": "do",
  "+593": "ec",
  "+20": "eg",
  "+503": "sv",
  "+240": "gq",
  "+291": "er",
  "+372": "ee",
  "+251": "et",
  "+298": "fo",
  "+679": "fj",
  "+358": "fi",
  "+33": "fr",
  "+594": "gf",
  "+689": "pf",
  "+241": "ga",
  "+220": "gm",
  "+995": "ge",
  "+49": "de",
  "+233": "gh",
  "+350": "gi",
  "+30": "gr",
  "+299": "gl",
  "+1473": "gd",
  "+590": "gp",
  "+1671": "gu",
  "+502": "gt",
  "+224": "gn",
  "+245": "gw",
  "+592": "gy",
  "+509": "ht",
  "+504": "hn",
  "+36": "hu",
  "+354": "is",
  "+91": "in",
  "+62": "id",
  "+964": "iq",
  "+353": "ie",
  "+972": "il",
  "+39": "it",
  "+1876": "jm",
  "+1658": "jm",
  "+81": "jp",
  "+962": "jo",
  "+77": "kz",
  "+254": "ke",
  "+686": "ki",
  "+965": "kw",
  "+996": "kg",
  "+371": "lv",
  "+961": "lb",
  "+266": "ls",
  "+231": "lr",
  "+423": "li",
  "+370": "lt",
  "+352": "lu",
  "+261": "mg",
  "+265": "mw",
  "+60": "my",
  "+960": "mv",
  "+223": "ml",
  "+356": "mt",
  "+692": "mh",
  "+596": "mq",
  "+222": "mr",
  "+230": "mu",
  "+262": "yt",
  "+52": "mx",
  "+377": "mc",
  "+976": "mn",
  "+382": "me",
  "+1664": "ms",
  "+212": "ma",
  "+95": "mm",
  "+264": "na",
  "+674": "nr",
  "+977": "np",
  "+31": "nl",
  "+687": "nc",
  "+64": "nz",
  "+505": "ni",
  "+227": "ne",
  "+234": "ng",
  "+683": "nu",
  "+672": "nf",
  "+1670": "mp",
  "+47": "no",
  "+968": "om",
  "+92": "pk",
  "+680": "pw",
  "+507": "pa",
  "+675": "pg",
  "+595": "py",
  "+51": "pe",
  "+63": "ph",
  "+48": "pl",
  "+351": "pt",
  "+1939": "pr",
  "+1787": "pr",
  "+974": "qa",
  "+40": "ro",
  "+250": "rw",
  "+685": "ws",
  "+378": "sm",
  "+966": "sa",
  "+221": "sn",
  "+381": "rs",
  "+248": "sc",
  "+232": "sl",
  "+65": "sg",
  "+421": "sk",
  "+386": "si",
  "+677": "sb",
  "+27": "za",
  "+500": "gs",
  "+34": "es",
  "+94": "lk",
  "+597": "sr",
  "+268": "sz",
  "+46": "se",
  "+41": "ch",
  "+992": "tj",
  "+66": "th",
  "+228": "tg",
  "+690": "tk",
  "+676": "to",
  "+1868": "tt",
  "+216": "tn",
  "+90": "tr",
  "+993": "tm",
  "+1649": "tc",
  "+688": "tv",
  "+256": "ug",
  "+380": "ua",
  "+971": "ae",
  "+44": "gb",
  "+598": "uy",
  "+998": "uz",
  "+678": "vu",
  "+681": "wf",
  "+967": "ye",
  "+260": "zm",
  "+263": "zw",
  "+591": "bo",
  "+673": "bn",
  "+6189162": "cc",
  "+243": "cd",
  "+225": "ci",
  "+379": "va",
  "+852": "hk",
  "+82": "kr",
  "+856": "la",
  "+218": "ly",
  "+853": "mo",
  "+389": "mk",
  "+691": "fm",
  "+373": "md",
  "+258": "mz",
  "+970": "ps",
  "+872": "pn",
  "+7": "ru",
  "+290": "sh",
  "+1869": "kn",
  "+1758": "lc",
  "+508": "pm",
  "+1784": "vc",
  "+239": "st",
  "+252": "so",
  "+886": "tw",
  "+255": "tz",
  "+670": "tl",
  "+58": "ve",
  "+84": "vn",
  "+1284": "vg",
  "+1340": "vi"
};

const getDefaultValue = (country) => {
  if (!country) {
    return "";
  }
  return `${country.name} (${country.callingCode})`;
};
const CountryInput = ({
  onValueChange,
  value,
  ...props
}) => {
  const countries = useCountries();
  const handleValueChange = (displayText) => {
    if (displayText.length === 0) {
      onValueChange?.({ callingCode: "", code: "", name: "" });
      return;
    }
    const countryData = countries.find(
      ({ name, callingCode }) => `${name?.toLowerCase()} (${callingCode?.toLowerCase()})` === displayText.toLowerCase()
    );
    if (!countryData) {
      console.error(
        `Country not found for displayText: ${displayText}. This should not happen.`
      );
      return;
    }
    onValueChange?.(countryData);
  };
  const items = countries.map(({ callingCode, code, name }) => {
    const label = `${name} (${callingCode})`;
    return {
      value: label,
      title: label,
      icon: /* @__PURE__ */ jsx(Flag, { className: "w-5", country: code.toUpperCase() })
    };
  });
  const selectedCountry = countries.find(
    (country) => country.code === value?.code?.toLowerCase()
  );
  const icon = selectedCountry ? /* @__PURE__ */ jsx(Flag, { className: "mr-2 w-5", country: selectedCountry.code.toUpperCase() }) : /* @__PURE__ */ jsx("span", { className: "w-5 text-center", children: "+" });
  return /* @__PURE__ */ jsx(
    ResponsiveAutocomplete,
    {
      ...props,
      items,
      showMobileIcon: false,
      onValueChange: handleValueChange,
      defaultValue: getDefaultValue(selectedCountry),
      inputValue: value?.name ? `${value?.name} (${value?.callingCode})` : "",
      icon,
      fullScreen: true
    }
  );
};

var _path$y;
function _extends$z() { _extends$z = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$z.apply(this, arguments); }
var SvgCheck$1 = function SvgCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$z({
    width: "1em",
    height: "1em",
    viewBox: "0 0 14 10",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$y || (_path$y = /*#__PURE__*/React.createElement("path", {
    d: "m1.672 5 3.603 3.603 7.052-7.052",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

const Checkbox = forwardRef(({ className, onValueChange = () => {
}, value, ...props }, ref) => /* @__PURE__ */ jsx(
  CheckboxPrimitive.Root,
  {
    ...props,
    ref,
    className: cn("peer/checkbox group/checkbox", className),
    checked: value,
    onCheckedChange: onValueChange
  }
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;
const CheckboxIndicatorWrapper = forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    ...props,
    className: cn(
      "h-5 w-5 shrink-0 rounded-[0.250rem] border border-neutral-500 bg-white outline-none disabled:cursor-not-allowed disabled:opacity-50",
      "group-data-[state=checked]/checkbox:border-primary group-data-[state=checked]/checkbox:bg-primary group-data-[state=checked]/checkbox:text-primary-contrast",
      className
    ),
    children: /* @__PURE__ */ jsx(
      CheckboxPrimitive.Indicator,
      {
        ...props,
        className: cn(
          "flex h-full w-full items-center justify-center text-current",
          className
        ),
        children: /* @__PURE__ */ jsx(SvgCheck$1, { className: "text-sm" })
      }
    )
  }
));
CheckboxIndicatorWrapper.displayName = "CheckboxIndicatorWrapper";

const CheckboxCard = forwardRef(({ className, onValueChange = () => {
}, value, children, ...props }, ref) => /* @__PURE__ */ jsx(
  CheckboxPrimitive.Root,
  {
    ...props,
    ref,
    asChild: true,
    checked: value,
    onCheckedChange: onValueChange,
    children: /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          "group/checkbox data-[state=checked]:border-primary flex cursor-pointer gap-4 rounded-md border border-neutral-500 bg-white p-2 outline-none transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50 sm:gap-3",
          className
        ),
        children
      }
    )
  }
));
CheckboxCard.displayName = CheckboxPrimitive.Root.displayName;
const CheckboxCardTitle = forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx("h2", { ref, ...props, className: cn("text-base font-semibold", className), children }));
CheckboxCardTitle.displayName = "CheckboxCardTitle";
const CheckboxCardDescription = forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx("p", { ref, ...props, className: cn("text-xs text-neutral-800", className), children }));
CheckboxCardDescription.displayName = "CheckboxCardDescription";
const CheckboxCardButton = forwardRef(
  ({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(
    Button,
    {
      ref,
      ...props,
      className: cn(
        "hover:bg-primary group-data-[state=checked]/checkbox:bg-success-300 px-3 text-base",
        className
      ),
      children
    }
  )
);
CheckboxCardButton.displayName = "CheckboxCardDescription";

var _path$x;
function _extends$y() { _extends$y = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$y.apply(this, arguments); }
var SvgChevronLeft = function SvgChevronLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$y({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none"
  }, props), _path$x || (_path$x = /*#__PURE__*/React.createElement("path", {
    d: "M13 6.05 8.05 11 13 15.95",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

var _path$w;
function _extends$x() { _extends$x = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$x.apply(this, arguments); }
var SvgChevronRight = function SvgChevronRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$x({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none"
  }, props), _path$w || (_path$w = /*#__PURE__*/React.createElement("path", {
    d: "M9 15.95 13.95 11 9 6.05",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  value,
  onValueChange,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    DayPicker,
    {
      selected: value,
      onSelect: onValueChange,
      showOutsideDays,
      className: cn("p-2", className),
      classNames: {
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: "inline-flex hover:bg-neutral-200 items-center justify-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 h-7 w-7 bg-transparent !p-0 hover:bg-neutral-100",
        nav_button_previous: "absolute left-1 p-0",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-neutral-400 w-8 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-primary rounded-full",
        day: "inline-flex hover:bg-neutral-200 items-center justify-center rounded-full text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 h-8 w-8 p-0 font-normal rounded-full aria-selected:opacity-100",
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_today: "bg-neutral-200 text-black aria-selected:bg-primary aria-selected:text-primary-contrast",
        day_selected: "bg-primary rounded-full text-primary-contrast hover:bg-primary hover:text-primary-contrast focus:bg-primary focus:text-primary-contrast",
        day_outside: "text-neutral-400 aria-selected:text-primary-contrast",
        day_disabled: "text-neutral-400 opacity-50",
        day_range_middle: "aria-selected:bg-primary aria-selected:text-primary-contrast",
        day_hidden: "invisible",
        ...classNames
      },
      components: {
        IconLeft: (props2) => /* @__PURE__ */ jsx(SvgChevronLeft, { ...props2, className: "text-xl" }),
        IconRight: (props2) => /* @__PURE__ */ jsx(SvgChevronRight, { ...props2, className: "text-xl" })
      },
      ...props
    }
  );
};
Calendar.displayName = "Calendar";

const Popover = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverClose = PopoverPrimitive.Close;
const PopoverContent = forwardRef(
  ({ className, align = "center", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx(
    PopoverPrimitive.Content,
    {
      ref,
      align,
      sideOffset,
      className: cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-80 rounded bg-white p-2 shadow-md outline-none",
        className
      ),
      ...props
    }
  ) })
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

var _g;
function _extends$w() { _extends$w = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$w.apply(this, arguments); }
var SvgCalendar = function SvgCalendar(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$w({
    width: "1em",
    height: "1em",
    viewBox: "0 0 16 18",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g || (_g = /*#__PURE__*/React.createElement("g", {
    stroke: "currentColor",
    strokeMiterlimit: 10
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12.98 3.61H3.02A2.48 2.48 0 0 0 .54 6.09v8.78a2.48 2.48 0 0 0 2.48 2.48h9.96a2.48 2.48 0 0 0 2.48-2.48V6.09a2.48 2.48 0 0 0-2.48-2.48Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M.79 7.52H15.2M3.29 10.4h2M7.29 10.4h2M11.29 10.4h2M3.29 14.06h2M7.29 14.06h2M11.29 14.06h2M4.29 5.1V1M11.71 5.1V1",
    strokeLinecap: "round"
  }))));
};

const DatePicker = forwardRef(
  ({
    id,
    value,
    label,
    open,
    name,
    error,
    placeholder,
    fromDate,
    toDate,
    onValueChange,
    onOpenChange,
    ...props
  }, ref) => {
    const { t } = useTranslation("ui:date-picker");
    const triggerText = value ? format(value, "MMM d, y") : "";
    const inputValue = value?.toISOString() ?? "";
    return isMobile ? /* @__PURE__ */ jsxs(FloatingInputGroup, { error, children: [
      /* @__PURE__ */ jsx(FloatingInputLeftContent, { children: /* @__PURE__ */ jsx(SvgCalendar, {}) }),
      /* @__PURE__ */ jsx(FloatingLabel, { htmlFor: id, children: label }),
      /* @__PURE__ */ jsx(
        FloatingInput,
        {
          name,
          id,
          placeholder,
          value: inputValue,
          type: "date",
          className: "h-14 opacity-0",
          min: fromDate?.toISOString().split("T")[0],
          max: toDate?.toISOString().split("T")[0],
          onChange: (event) => {
            const { value: value2 } = event.target;
            if (!value2) {
              return;
            }
            onValueChange?.(value2 ? parseISO(value2) : void 0);
          }
        }
      ),
      triggerText && /* @__PURE__ */ jsx("p", { className: "pointer-events-none absolute left-8 top-6", children: triggerText })
    ] }) : /* @__PURE__ */ jsxs(Popover, { open, onOpenChange, children: [
      /* @__PURE__ */ jsxs(FloatingInputGroup, { focused: open, error, children: [
        /* @__PURE__ */ jsx(FloatingInputLeftContent, { children: /* @__PURE__ */ jsx(SvgCalendar, { className: cn({ "text-primary": open }) }) }),
        /* @__PURE__ */ jsx(FloatingLabel, { className: "pointer-events-none", htmlFor: id, children: label }),
        /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(FloatingInput, { name, value: inputValue, asChild: true, children: /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "h-14 overflow-hidden whitespace-nowrap text-left",
            children: triggerText
          }
        ) }) }),
        /* @__PURE__ */ jsx(
          "input",
          {
            ref,
            type: "hidden",
            id,
            name,
            value: inputValue
          }
        )
      ] }),
      /* @__PURE__ */ jsxs(PopoverContent, { className: "w-auto p-2", children: [
        /* @__PURE__ */ jsx(
          Calendar,
          {
            ...props,
            mode: "single",
            fromDate,
            toDate,
            value,
            initialFocus: true,
            onValueChange
          }
        ),
        /* @__PURE__ */ jsx(PopoverClose, { asChild: true, children: /* @__PURE__ */ jsx(Button, { className: "w-full", children: t("Done") }) })
      ] })
    ] });
  }
);
DatePicker.displayName = "DatePicker";

var _path$v;
function _extends$v() { _extends$v = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$v.apply(this, arguments); }
var SvgArrow$4 = function SvgArrow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$v({
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$v || (_path$v = /*#__PURE__*/React.createElement("path", {
    d: "M15.95 13 11 8.05 6.05 13",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

var _path$u, _path2$1;
function _extends$u() { _extends$u = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$u.apply(this, arguments); }
var SvgTime = function SvgTime(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$u({
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$u || (_path$u = /*#__PURE__*/React.createElement("path", {
    d: "M11 20a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z",
    stroke: "currentColor",
    strokeMiterlimit: 10
  })), _path2$1 || (_path2$1 = /*#__PURE__*/React.createElement("path", {
    d: "M11 4v1M11 17v1M4 11h1M17 11h1M6 6l1 1M6 16l1-1M15 7l1-1M11 7v4.253L16 16",
    stroke: "currentColor",
    strokeMiterlimit: 10,
    strokeLinecap: "round"
  })));
};

const TimePicker = ({
  open,
  value,
  id,
  label,
  placeholder,
  name,
  error,
  popoverContentClassName,
  minuteStep = 10,
  onValueChange,
  onDone,
  onOpenChange
}) => {
  const { t } = useTranslation("ui:time-picker");
  return isMobile ? /* @__PURE__ */ jsxs(FloatingInputGroup, { error, children: [
    /* @__PURE__ */ jsx(FloatingInputLeftContent, { children: /* @__PURE__ */ jsx(SvgTime, {}) }),
    /* @__PURE__ */ jsx(FloatingLabel, { htmlFor: id, children: label }),
    /* @__PURE__ */ jsx(
      FloatingInput,
      {
        id,
        name,
        placeholder,
        value: convertValueObjectToString(value),
        type: "time",
        className: "h-14 opacity-0",
        onChange: (event) => {
          const { value: value2 } = event.target;
          if (!value2) {
            return;
          }
          const [hour, minute] = value2.split(":");
          onValueChange?.(
            new Time(
              hour ? Number.parseInt(hour) : 0,
              minute ? Number.parseInt(minute) : 0
            )
          );
        }
      }
    ),
    value && /* @__PURE__ */ jsx("p", { className: "pointer-events-none absolute left-8 top-6", children: getTriggerText(value) })
  ] }) : /* @__PURE__ */ jsxs(Popover, { open, onOpenChange, children: [
    /* @__PURE__ */ jsxs(FloatingInputGroup, { focused: open, error, children: [
      /* @__PURE__ */ jsx(FloatingInputLeftContent, { children: /* @__PURE__ */ jsx(SvgTime, { className: cn({ "text-primary": open }) }) }),
      /* @__PURE__ */ jsx(FloatingLabel, { className: "pointer-events-none", htmlFor: id, children: label }),
      /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(FloatingInput, { name, value: getTriggerText(value), asChild: true, children: /* @__PURE__ */ jsx("button", { type: "button", className: "h-14 text-left", children: getTriggerText(value) }) }) }),
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "hidden",
          id,
          name,
          value: getTriggerText(value)
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(
      PopoverContent,
      {
        className: cn("w-72 px-2 pb-2 pt-4", popoverContentClassName),
        children: [
          /* @__PURE__ */ jsx(
            TimeField,
            {
              steps: { minute: minuteStep },
              value,
              onValueChange,
              "aria-label": "Time-picker"
            }
          ),
          /* @__PURE__ */ jsx(PopoverClose, { asChild: true, children: /* @__PURE__ */ jsx(Button, { className: "w-full", onClick: onDone, children: t("Done") }) })
        ]
      }
    )
  ] });
};
TimePicker.displayName = "TimePicker";
const convertValueObjectToString = (value) => {
  if (!value) {
    return "";
  }
  const { hour, minute } = value;
  const formattedHour = hour < 10 ? `0${hour}` : hour ?? "--";
  const formattedMinute = minute < 10 ? `0${minute}` : minute ?? "--";
  return `${formattedHour}:${formattedMinute}`;
};
const getTriggerText = (value) => {
  if (!value) {
    return "";
  }
  const { hour, minute } = value;
  const formattedHour = hour === 0 || hour === 12 ? 12 : hour % 12;
  const formattedMinute = minute < 10 ? `0${minute}` : minute;
  const period = hour >= 12 ? "PM" : "AM";
  return `${formattedHour}:${formattedMinute} ${period}`;
};
const TimeField = (props) => {
  const state = useTimeFieldState({
    ...props,
    onChange: props.onValueChange,
    value: props.value ?? null,
    locale: "en-us"
  });
  const ref = useRef(null);
  const { steps } = props;
  const hasSteps = steps && Object.keys(steps).length > 0;
  const stateIncrementOriginal = state.increment;
  const stateDecrementOriginal = state.decrement;
  const stepValue = (operation) => (type) => {
    if (type === "dayPeriod") {
      if (operation === "increment") {
        stateIncrementOriginal(type);
      }
      if (operation === "decrement") {
        stateDecrementOriginal(type);
      }
      return;
    }
    const step = steps?.[type] ?? 1;
    const multiplier = operation === "increment" ? 1 : -1;
    if (state.value) {
      const dateValue = state.value.cycle(type, multiplier * step, {
        round: true
      });
      state.setValue(dateValue);
    }
    if (!state.value) {
      const segment = state.segments.find((s) => s.type === type);
      let segmentValue = 0;
      if (!segment?.isPlaceholder || segment?.value !== 0) {
        const time = new Time().set({
          [type]: segment?.value
        }).cycle(type, multiplier * step, {
          round: true
        });
        segmentValue = time[type];
      }
      state.setSegment(type, segmentValue);
    }
  };
  if (hasSteps) {
    state.increment = stepValue("increment");
    state.decrement = stepValue("decrement");
  }
  const { fieldProps } = useTimeField(
    {
      ...props,
      onBlur: (event) => {
        const { onBlur } = props;
        onBlur && onBlur(event);
        if (hasSteps && state.value) {
          let steppedValue = state.value.copy();
          Object.entries(steps).forEach(([type, step]) => {
            if (state.value[type] % step !== 0) {
              steppedValue = steppedValue.cycle(type, step, {
                round: true
              });
            }
          });
          if (steppedValue.compare(state.value) !== 0) {
            state.setValue(steppedValue);
          }
        }
      }
    },
    state,
    ref
  );
  return /* @__PURE__ */ jsx(
    "div",
    {
      ...fieldProps,
      ref,
      className: "mb-4 flex items-center justify-center gap-1",
      children: state.segments.map(
        (segment) => segment.type !== "literal" && /* @__PURE__ */ jsx(DateSegment, { segment, state }, segment.type)
      )
    }
  );
};
const DateSegment = ({ segment, state }) => {
  const ref = useRef(null);
  const { segmentProps } = useDateSegment(segment, state, ref);
  const handleUp = () => {
    state.increment(segment.type);
  };
  const handleDown = () => {
    state.decrement(segment.type);
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ...segmentProps,
      className: "group/segment flex flex-col items-center outline-none",
      ref,
      children: [
        /* @__PURE__ */ jsx(
          IconButton,
          {
            tabIndex: -1,
            variant: BUTTON_VARIANT.GHOST,
            size: BUTTON_SIZE.LG,
            onClick: handleUp,
            "aria-hidden": true,
            onFocus: () => ref.current && ref.current.focus(),
            children: /* @__PURE__ */ jsx(SvgArrow$4, {})
          }
        ),
        /* @__PURE__ */ jsx("div", { className: "group-focus-within/segment:border-primary my-3 flex h-14 w-14 items-center justify-center rounded-md border border-neutral-200 p-1 px-2 outline-none", children: /* @__PURE__ */ jsx("span", { className: "w-full rounded-md p-1 text-center", children: segment.text }) }),
        /* @__PURE__ */ jsx(
          IconButton,
          {
            onClick: handleDown,
            variant: BUTTON_VARIANT.GHOST,
            size: BUTTON_SIZE.LG,
            tabIndex: -1,
            "aria-hidden": true,
            onFocus: () => ref.current && ref.current.focus(),
            children: /* @__PURE__ */ jsx(SvgArrow$4, { className: "rotate-180" })
          }
        )
      ]
    }
  );
};

const TooltipTrigger = TooltipPrimitive.Trigger;
const Tooltip = ({ children, ...props }) => /* @__PURE__ */ jsx(TooltipPrimitive.Provider, { children: /* @__PURE__ */ jsx(TooltipPrimitive.Root, { ...props, children }) });
const TooltipContent = forwardRef(
  ({ className, side = "top", sideOffset = 4, children, ...props }, ref) => /* @__PURE__ */ jsx(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsxs(
    TooltipPrimitive.Content,
    {
      ref,
      side,
      sideOffset,
      className: cn(
        "data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        "bg-primary-500 max-w-[180px] rounded px-2 py-1 text-center text-sm text-white shadow-md",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx(TooltipPrimitive.Arrow, { className: "fill-primary-500" }),
        children
      ]
    }
  ) })
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

var _path$t;
function _extends$t() { _extends$t = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$t.apply(this, arguments); }
var SvgCalendarCheckCircle = function SvgCalendarCheckCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$t({
    width: "1em",
    height: "1em",
    viewBox: "0 0 40 40",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$t || (_path$t = /*#__PURE__*/React.createElement("path", {
    d: "m30.208 26.119-3.18 3.179-1.82-1.82M13.333 4.582V7.5m0 0H9.167a2.5 2.5 0 0 0-2.5 2.5v5m6.666-7.5h13.334m0-2.917V7.5m0 0h4.166a2.5 2.5 0 0 1 2.5 2.5v5H6.667M15 34.167H9.167a2.5 2.5 0 0 1-2.5-2.5V15m28.75 12.708a7.708 7.708 0 1 1-15.417 0 7.708 7.708 0 0 1 15.417 0Z",
    stroke: "currentColor",
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

const DateTimePicker = forwardRef(
  ({
    id,
    label,
    name,
    value,
    onValueChange,
    error,
    open,
    onOpenChange,
    fromDate,
    toDate,
    className
  }, ref) => {
    const { t } = useTranslation("ui:date-time-picker");
    const inputValue = value?.toISOString() ?? "";
    const triggerText = value ? format(value, "MMM do, hh:mm") : "";
    const handleDateSelect = (selectedDate) => {
      if (selectedDate) {
        selectedDate.setHours(value?.getHours() ?? 12);
        selectedDate.setMinutes(value?.getMinutes() ?? 0);
        onValueChange?.(selectedDate);
      }
    };
    return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange, children: [
      /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
        FloatingInputGroup,
        {
          focused: open,
          value: inputValue,
          error,
          className: cn("cursor-default overflow-clip", className),
          hasLeftContent: true,
          children: [
            /* @__PURE__ */ jsx(FloatingInputLeftContent, { children: /* @__PURE__ */ jsx(SvgCalendarCheckCircle, { className: cn({ "text-primary": open }) }) }),
            /* @__PURE__ */ jsx(FloatingLabel, { className: "pointer-events-none", htmlFor: id, children: label }),
            /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Tooltip, { children: [
              /* @__PURE__ */ jsx(TooltipTrigger, { className: "overflow-hidden", asChild: true, children: /* @__PURE__ */ jsx(FloatingInput, { name, value: inputValue, asChild: true, children: /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: "h-14 overflow-hidden whitespace-nowrap text-left",
                  children: triggerText
                }
              ) }) }),
              /* @__PURE__ */ jsx(TooltipContent, { children: triggerText })
            ] }) }),
            /* @__PURE__ */ jsx(
              "input",
              {
                ref,
                type: "hidden",
                id,
                name,
                value: inputValue
              }
            )
          ]
        }
      ) }),
      /* @__PURE__ */ jsxs(PopoverContent, { className: "w-auto p-5", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex gap-6", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center gap-5", children: [
            /* @__PURE__ */ jsx("span", { className: "font-bold", children: "Date" }),
            /* @__PURE__ */ jsx(
              Calendar,
              {
                mode: "single",
                selected: value,
                onSelect: handleDateSelect,
                initialFocus: true,
                fromDate,
                toDate
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center gap-5", children: [
            /* @__PURE__ */ jsx("span", { className: "font-bold", children: "Time" }),
            /* @__PURE__ */ jsx(
              TimeField,
              {
                "aria-label": "Time-picker",
                value: value ? new Time(value.getHours(), value.getMinutes()) : new Time(12, 0),
                onValueChange: (timeValue) => {
                  const newDate = value ? new Date(value) : /* @__PURE__ */ new Date();
                  newDate.setHours(timeValue.hour);
                  newDate.setMinutes(timeValue.minute);
                  onValueChange?.(newDate);
                }
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsx(PopoverClose, { asChild: true, children: /* @__PURE__ */ jsx("div", { className: "p-2", children: /* @__PURE__ */ jsx(Button, { className: "mt-2 w-full", type: "button", children: t("Done") }) }) })
      ] })
    ] });
  }
);
DateTimePicker.displayName = "DateTimePicker";

var _path$s;
function _extends$s() { _extends$s = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$s.apply(this, arguments); }
var SvgTimes$2 = function SvgTimes(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$s({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none"
  }, props), _path$s || (_path$s = /*#__PURE__*/React.createElement("path", {
    stroke: "currentColor",
    strokeLinecap: "round",
    d: "M16.365 16.434 5.638 5.707M5.638 16.305 16.365 5.578"
  })));
};

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogOverlay = forwardRef(({ asChild: _, ...props }, ref) => /* @__PURE__ */ jsx(DialogPrimitive.Overlay, { ref, asChild: true, ...props, children: /* @__PURE__ */ jsx(Overlay, {}) }));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
const DialogPortal = ({
  children,
  className,
  ...props
}) => /* @__PURE__ */ jsxs(DialogPrimitive.Portal, { ...props, children: [
  /* @__PURE__ */ jsx(DialogOverlay, {}),
  /* @__PURE__ */ jsx(
    "div",
    {
      "data-ui-dialog-portal": true,
      className: cn(
        "fixed inset-0 z-50 flex items-center justify-center p-2",
        className
      ),
      children
    }
  )
] });
DialogPortal.displayName = DialogPrimitive.Portal.displayName;
const DialogContent = forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Content,
  {
    ref,
    className: cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 relative flex max-h-[90%] w-full max-w-lg flex-col rounded-lg bg-white shadow-lg duration-200",
      className
    ),
    ...props,
    children
  }
));
DialogContent.displayName = DialogPrimitive.Content.displayName;
const DialogClose = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Close,
  {
    ref,
    ...props,
    className: cn("absolute -left-6 top-0 hidden md:block", className),
    asChild: true,
    children: /* @__PURE__ */ jsx(IconButton, { size: BUTTON_SIZE.SM, className: "rounded-lg", children: /* @__PURE__ */ jsx(SvgTimes$2, {}) })
  }
));
DialogClose.displayName = DialogPrimitive.Close.displayName;
const DialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col space-y-1.5 p-3 text-center sm:text-left",
      className
    ),
    ...props
  }
);
DialogHeader.displayName = "DialogHeader";
const DialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col-reverse p-3 sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";
const DialogTitle = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Title,
  {
    ref,
    className: cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
const DialogDescription = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("p-3 text-sm", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
const DialogScrollableContent = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx("div", { className: cn("overflow-auto p-3", className), ...props });

var _path$r;
function _extends$r() { _extends$r = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$r.apply(this, arguments); }
var SvgArrow$3 = function SvgArrow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$r({
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$r || (_path$r = /*#__PURE__*/React.createElement("path", {
    d: "M6.05 9 11 13.95 15.95 9",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

const Collapsible = CollapsiblePrimitive.Root;
const CollapsibleTrigger = forwardRef(
  ({
    className,
    children,
    arrowPosition = "left",
    arrowDirection = "down",
    ...props
  }, ref) => /* @__PURE__ */ jsxs(
    CollapsiblePrimitive.Trigger,
    {
      ref,
      className: cn("group/trigger flex items-center gap-1", className),
      ...props,
      children: [
        arrowPosition === "left" && /* @__PURE__ */ jsx(
          SvgArrow$3,
          {
            className: `text-2xl transition-transform duration-200 ${arrowDirection === "down" ? "group-data-[state=closed]/trigger:rotate-0 group-data-[state=open]/trigger:rotate-180" : "group-data-[state=closed]/trigger:rotate-180 group-data-[state=open]/trigger:rotate-0"}`
          }
        ),
        children,
        arrowPosition === "right" && /* @__PURE__ */ jsx(
          SvgArrow$3,
          {
            className: `text-2xl transition-transform duration-200 ${arrowDirection === "down" ? "group-data-[state=closed]/trigger:rotate-0 group-data-[state=open]/trigger:rotate-180" : "group-data-[state=closed]/trigger:rotate-180 group-data-[state=open]/trigger:rotate-0"}`
          }
        )
      ]
    }
  )
);
CollapsibleTrigger.displayName = CollapsiblePrimitive.Trigger.displayName;
const CollapsibleContent = CollapsiblePrimitive.Content;

var _rect, _path$q;
function _extends$q() { _extends$q = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$q.apply(this, arguments); }
var SvgFilter = function SvgFilter(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$q({
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _rect || (_rect = /*#__PURE__*/React.createElement("rect", {
    x: 2.5,
    y: 2.5,
    width: 17,
    height: 17,
    rx: 1.5,
    stroke: "currentColor"
  })), _path$q || (_path$q = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M8.5 6a.5.5 0 0 0-.5.5V7H5.5a.5.5 0 0 0 0 1H8v.5a.5.5 0 0 0 1 0V8h7.5a.5.5 0 0 0 0-1H9v-.5a.5.5 0 0 0-.5-.5Zm-3 4.5a.5.5 0 0 0 0 1H14v.5a.5.5 0 0 0 1 0v-.5h1.5a.5.5 0 0 0 0-1H15V10a.5.5 0 0 0-1 0v.5H5.5Zm-.5 4a.5.5 0 0 1 .5-.5H10v-.5a.5.5 0 0 1 1 0v.5h5.5a.5.5 0 0 1 0 1H11v.5a.5.5 0 0 1-1 0V15H5.5a.5.5 0 0 1-.5-.5Z",
    fill: "currentColor"
  })));
};

const Filter = ({
  options = [],
  groups = [],
  value,
  title,
  onValueChange = () => {
  },
  onClear = () => {
  }
}) => {
  const { t } = useTranslation("ui:filter");
  const [isCheckboxSelected, setIsCheckboxSelected] = useState(false);
  const groupedOptions = options.reduce(
    (acc, option) => {
      const group = acc.find((group2) => group2.group.value === option.group);
      if (group) {
        group.options.push(option);
      } else {
        const group2 = groups.find((group3) => group3.value === option.group);
        if (!group2) {
          console.error(`Group ${option.group.toString()} not found.`);
          return acc;
        }
        acc.push({
          group: group2,
          options: [option]
        });
      }
      return acc;
    },
    []
  );
  const renderFilters = () => /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-end justify-between py-2", children: [
      /* @__PURE__ */ jsx("h3", { className: "px-2 text-xl font-semibold", children: title || t("Filters") }),
      isCheckboxSelected && /* @__PURE__ */ jsx(
        Button,
        {
          onClick: () => {
            setIsCheckboxSelected(false);
            onClear();
          },
          variant: BUTTON_VARIANT.GHOST,
          color: BUTTON_COLOR.PRIMARY,
          size: BUTTON_SIZE.SM,
          children: t("Clear All")
        }
      )
    ] }),
    groupedOptions.map((group) => /* @__PURE__ */ jsxs(
      Collapsible,
      {
        defaultOpen: true,
        className: "mb-3",
        children: [
          /* @__PURE__ */ jsx(CollapsibleTrigger, { className: "mb-2 w-full border-b border-b-neutral-500 pb-1.5 text-sm text-neutral-800 sm:text-xs", children: group.group.label }),
          /* @__PURE__ */ jsx(CollapsibleContent, { children: group.options.map((option) => /* @__PURE__ */ jsx("div", { className: "mb-2 pr-2 sm:mb-0", children: /* @__PURE__ */ jsxs(
            "label",
            {
              htmlFor: option.value,
              className: "flex flex-1 cursor-pointer items-center gap-2 p-1 text-base font-medium leading-none",
              children: [
                /* @__PURE__ */ jsx(
                  Checkbox,
                  {
                    onValueChange: (_value) => {
                      setIsCheckboxSelected(
                        typeof _value === "boolean" ? _value : false
                      );
                      onValueChange(
                        _value ? {
                          ...value,
                          [option.group]: [
                            ...value[option.group],
                            option.value
                          ]
                        } : {
                          ...value,
                          [option.group]: value[option.group]?.filter(
                            (v) => v !== option.value
                          )
                        }
                      );
                    },
                    value: value[option.group]?.includes(option.value),
                    id: option.value,
                    children: /* @__PURE__ */ jsx(CheckboxIndicatorWrapper, {})
                  }
                ),
                /* @__PURE__ */ jsx("span", { className: "text-sm", children: option.label }),
                typeof option.quantity === "number" && /* @__PURE__ */ jsx(Badge, { className: "peer-data-[state=checked]/checkbox:bg-primary ml-auto min-w-[1.375rem] justify-center bg-neutral-700 px-0.5 py-0.5", children: option.quantity })
              ]
            }
          ) }, option.value)) })
        ]
      },
      group.group.value
    ))
  ] });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "hidden md:block", children: renderFilters() }),
    /* @__PURE__ */ jsx("div", { className: "grid md:hidden", children: /* @__PURE__ */ jsxs(Sheet, { children: [
      /* @__PURE__ */ jsx(SheetTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { color: BUTTON_COLOR.WHITE, children: [
        /* @__PURE__ */ jsx(SvgFilter, { className: "text-primary text-xl" }),
        title || t("Filters")
      ] }) }),
      /* @__PURE__ */ jsxs(
        SheetContent,
        {
          side: SHEET_CONTENT_SIDE.BOTTOM,
          className: "max-h-full overflow-auto rounded-md bg-neutral-100 pb-16",
          children: [
            renderFilters(),
            /* @__PURE__ */ jsx("div", { className: "fixed bottom-0 left-0 right-0 bg-neutral-100 px-2 py-4", children: /* @__PURE__ */ jsx(SheetClose, { asChild: true, children: /* @__PURE__ */ jsx(Button, { className: "w-full py-3 text-base leading-6", children: t("Show results") }) }) })
          ]
        }
      )
    ] }) })
  ] });
};

var _path$p;
function _extends$p() { _extends$p = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$p.apply(this, arguments); }
var SvgArrow$2 = function SvgArrow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$p({
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$p || (_path$p = /*#__PURE__*/React.createElement("path", {
    d: "M6.05 9 11 13.95 15.95 9",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

const SelectGroup = SelectPrimitive.Group;
const Select = SelectPrimitive.Root;
Select.displayName = SelectPrimitive.Root.displayName;
const SelectTrigger = forwardRef(
  ({ className, children, asChild, ...props }, ref) => /* @__PURE__ */ jsx(
    SelectPrimitive.Trigger,
    {
      ...props,
      ref,
      className: cn(
        "",
        {
          "border-input group/trigger flex w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:opacity-50": !asChild
        },
        className
      ),
      asChild,
      children
    }
  )
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
const SelectValue = forwardRef(({ className, customRender, placeholder, ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.Value, { ref, ...props, placeholder }));
SelectValue.displayName = SelectPrimitive.Value.displayName;
const SelectContent = forwardRef(
  ({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs(
    SelectPrimitive.Content,
    {
      ref,
      className: cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 min-w-[8rem] overflow-hidden rounded-md bg-white text-black shadow-md",
        position === "popper" && "data-[side=bottom]:translate-y-2 data-[side=left]:-translate-x-2 data-[side=right]:translate-x-2 data-[side=top]:-translate-y-2",
        className
      ),
      position,
      ...props,
      children: [
        /* @__PURE__ */ jsx(SelectPrimitive.ScrollUpButton, { className: "flex justify-center py-1", children: /* @__PURE__ */ jsx(SvgArrow$2, { className: "h-6 w-6 rotate-180" }) }),
        /* @__PURE__ */ jsx(
          SelectPrimitive.Viewport,
          {
            className: cn(
              "p-2",
              position === "popper" && "w-full min-w-[var(--radix-select-trigger-width)]"
            ),
            children
          }
        ),
        /* @__PURE__ */ jsx(SelectPrimitive.ScrollDownButton, { className: "flex justify-center py-1", children: /* @__PURE__ */ jsx(SvgArrow$2, { className: "h-6 w-6" }) })
      ]
    }
  ) })
);
SelectContent.displayName = SelectPrimitive.Content.displayName;
const SelectLabel = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    SelectPrimitive.Label,
    {
      ref,
      className: cn("px-2 py-1.5 text-sm font-semibold", className),
      ...props
    }
  )
);
SelectLabel.displayName = SelectPrimitive.Label.displayName;
const SelectIcon = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    SelectPrimitive.Icon,
    {
      ref,
      ...props,
      className: cn("group-data-[state=open]/trigger:rotate-180", className)
    }
  )
);
SelectIcon.displayName = SelectPrimitive.Icon.displayName;
const SelectItem = forwardRef(({ className, children, icon, ...props }, ref) => /* @__PURE__ */ jsxs(
  SelectPrimitive.Item,
  {
    ref,
    className: cn(
      "data-[state=checked]:text-primary relative mb-1 flex w-full cursor-pointer select-none items-center gap-1 rounded-sm p-1 text-base outline-none focus:bg-neutral-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      icon && /* @__PURE__ */ jsx("span", { className: "mr-1 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, { children: icon }) }),
      /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children })
    ]
  }
));
SelectItem.displayName = SelectPrimitive.Item.displayName;
const SelectSeparator = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    SelectPrimitive.Separator,
    {
      ref,
      className: cn("-mx-1 my-1 h-px bg-neutral-300", className),
      ...props
    }
  )
);
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

const screens = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px"
};
const mediaQueries = {
  sm: `(min-width: ${screens.sm})`,
  md: `(min-width: ${screens.md})`,
  lg: `(min-width: ${screens.lg})`,
  xl: `(min-width: ${screens.xl})`
};
const getCssVariable = (cssVariableName) => getComputedStyle(document.documentElement).getPropertyValue(cssVariableName);
const getHslColor = (cssVariableName) => `hsl(${getCssVariable(cssVariableName)})`;
const getTheme = () => ({
  palette: {
    white: getHslColor("--mz-palette-white"),
    black: getHslColor("--mz-palette-black"),
    primary: {
      100: getHslColor("--mz-palette-primary-100"),
      200: getHslColor("--mz-palette-primary-200"),
      300: getHslColor("--mz-palette-primary-300"),
      400: getHslColor("--mz-palette-primary-400"),
      500: getHslColor("--mz-palette-primary-500"),
      contrast: getHslColor("--mz-palette-primary-contrast")
    },
    secondary: {
      100: getHslColor("--mz-palette-secondary-100"),
      200: getHslColor("--mz-palette-secondary-200"),
      300: getHslColor("--mz-palette-secondary-300"),
      400: getHslColor("--mz-palette-secondary-400"),
      500: getHslColor("--mz-palette-secondary-500"),
      contrast: getHslColor("--mz-palette-secondary-contrast")
    },
    neutral: {
      100: getHslColor("--mz-palette-neutral-100"),
      200: getHslColor("--mz-palette-neutral-200"),
      300: getHslColor("--mz-palette-neutral-300"),
      400: getHslColor("--mz-palette-neutral-400"),
      500: getHslColor("--mz-palette-neutral-500"),
      600: getHslColor("--mz-palette-neutral-600"),
      700: getHslColor("--mz-palette-neutral-700"),
      800: getHslColor("--mz-palette-neutral-800"),
      900: getHslColor("--mz-palette-neutral-900")
    },
    success: {
      100: getHslColor("--mz-palette-success-100"),
      200: getHslColor("--mz-palette-success-200"),
      300: getHslColor("--mz-palette-success-300"),
      400: getHslColor("--mz-palette-success-400"),
      500: getHslColor("--mz-palette-success-500")
    },
    error: {
      100: getHslColor("--mz-palette-error-100"),
      200: getHslColor("--mz-palette-error-200"),
      300: getHslColor("--mz-palette-error-300"),
      400: getHslColor("--mz-palette-error-400"),
      500: getHslColor("--mz-palette-error-500")
    },
    warning: {
      100: getHslColor("--mz-palette-warning-100"),
      200: getHslColor("--mz-palette-warning-200"),
      300: getHslColor("--mz-palette-warning-300"),
      400: getHslColor("--mz-palette-warning-400"),
      500: getHslColor("--mz-palette-warning-500")
    },
    marketingLilac: {
      100: getHslColor("--mz-palette-marketing-lilac-100"),
      200: getHslColor("--mz-palette-marketing-lilac-200"),
      300: getHslColor("--mz-palette-marketing-lilac-300"),
      400: getHslColor("--mz-palette-marketing-lilac-400"),
      500: getHslColor("--mz-palette-marketing-lilac-500")
    }
  },
  fontFamily: {
    sans: getCssVariable("--mz-font-family-sans")
  },
  header: {
    logoHeight: getCssVariable("--mz-header-logo-height"),
    height: getCssVariable("--mz-header-height") || void 0,
    bg: getHslColor("--mz-header-bg"),
    border: getHslColor("--mz-header-border"),
    createAccountBtn: {
      bg: getHslColor("--mz-header-create-account-btn-bg"),
      text: getHslColor("--mz-header-create-account-btn-text"),
      hover: {
        bg: getHslColor("--mz-header-create-account-btn-hover-bg"),
        text: getHslColor("--mz-header-create-account-btn-hover-text")
      }
    },
    signInBtn: {
      bg: getHslColor("--mz-header-sign-in-btn-bg"),
      text: getHslColor("--mz-header-sign-in-btn-text"),
      hover: {
        bg: getHslColor("--mz-header-sign-in-btn-hover-bg"),
        text: getHslColor("--mz-header-sign-in-btn-hover-text")
      }
    },
    generalBtn: {
      bg: getHslColor("--mz-header-general-btn-bg"),
      text: getHslColor("--mz-header-general-btn-text"),
      hover: {
        bg: getHslColor("--mz-header-general-btn-hover-bg"),
        text: getHslColor("--mz-header-general-btn-hover-text")
      }
    }
  }
});
const setTheme = (theme) => {
  const root = document.documentElement;
  const cssVariables = toThemeVars(theme);
  Object.entries(cssVariables).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
};

const reForwardRef = (fromRef, toRef) => {
  if (!toRef) {
    return;
  }
  if (typeof toRef === "function") {
    toRef(fromRef);
  } else {
    toRef.current = fromRef;
  }
};

const Container = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      ...props,
      className: cn("mx-auto max-w-5xl px-2 md:px-4", className)
    }
  )
);
Container.displayName = "Container";

var _path$o;
function _extends$o() { _extends$o = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$o.apply(this, arguments); }
var SvgAlignRight = function SvgAlignRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$o({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none"
  }, props), _path$o || (_path$o = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M4 5a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1Zm4 6a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Zm-3 5a1 1 0 1 0 0 2h12a1 1 0 1 0 0-2H5Z",
    fill: "currentColor"
  })));
};

const HeaderButton = forwardRef(
  ({ asChild, className, ...props }, ref) => {
    const Component = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Component,
      {
        ref,
        type: "button",
        ...props,
        className: cn(
          "bg-header-general-btn-bg text-header-general-btn-text hover:bg-header-general-btn-hover-bg hover:text-header-general-btn-hover-text flex h-9 items-center gap-1 rounded px-4 py-2 text-center leading-5 duration-200",
          className
        )
      }
    );
  }
);
HeaderButton.displayName = "HeaderButton";

const Header = forwardRef(
  ({ className, children, ...props }, ref) => {
    const handleRef = (_ref) => {
      if (!_ref) return;
      const headerHeight = _ref.offsetHeight;
      document.documentElement.style.setProperty(
        "--mz-header-height",
        `${headerHeight}px`
      );
      reForwardRef(_ref, ref);
    };
    return /* @__PURE__ */ jsx(
      "header",
      {
        ref: handleRef,
        ...props,
        className: cn(
          "border-b-header-border bg-header-bg fixed left-0 right-0 top-0 z-30 h-[var(--mz-header-height)] w-[calc(100%-var(--removed-body-scroll-bar-size,0px))] border-b",
          className
        ),
        children: /* @__PURE__ */ jsx(Container, { className: "flex h-full max-w-none justify-between py-2", children })
      }
    );
  }
);
Header.displayName = "Header";
const HeaderLogo = forwardRef(
  ({
    className,
    children,
    href = "/",
    ...props
  }, ref) => /* @__PURE__ */ jsx(
    "a",
    {
      ...props,
      ref,
      className: cn(
        "flex items-center rounded-sm duration-200 [&>*]:h-[var(--mz-header-logo-height)]",
        className
      ),
      href,
      children
    }
  )
);
HeaderLogo.displayName = "HeaderLogo";
const HeaderContent = forwardRef(
  ({ className, children, open, onOpenChange, breakpoint = "lg", ...props }, ref) => {
    const greaterThanSmall = useMediaQuery(mediaQueries.lg);
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          ref: greaterThanSmall ? ref : void 0,
          ...props,
          className: cn(`hidden gap-1 ${breakpoint}:flex`, className),
          children
        }
      ),
      /* @__PURE__ */ jsx(
        "div",
        {
          ref: greaterThanSmall ? void 0 : ref,
          ...props,
          className: `block ${breakpoint}:hidden`,
          children: /* @__PURE__ */ jsxs(Sheet, { open, onOpenChange, children: [
            /* @__PURE__ */ jsx(SheetTrigger, { asChild: true, children: /* @__PURE__ */ jsx(HeaderButton, { "aria-label": "Menu", className: "group", children: /* @__PURE__ */ jsx(SvgAlignRight, { className: "hover:text-header-general-btn-hover-text text-xl" }) }) }),
            /* @__PURE__ */ jsx(SheetContent, { side: "bottom", className: "z-50 rounded-t-lg", children })
          ] })
        }
      )
    ] });
  }
);
HeaderContent.displayName = "HeaderContent";

const HeaderCreateAccountButton = forwardRef(
  ({ asChild, className, ...props }, ref) => {
    const Component = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Component,
      {
        ref,
        type: "button",
        ...props,
        className: cn(
          "bg-header-create-account-btn-bg text-header-create-account-btn-text hover:bg-header-create-account-btn-hover-bg hover:text-header-create-account-btn-hover-text flex h-9 items-center gap-1 rounded px-4 py-2 text-center leading-5 duration-200",
          className
        )
      }
    );
  }
);
HeaderCreateAccountButton.displayName = "HeaderCreateAccountButton";

const HeaderSignInButton = forwardRef(
  ({ asChild, className, ...props }, ref) => {
    const Component = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Component,
      {
        ref,
        type: "button",
        ...props,
        className: cn(
          "bg-header-sign-in-btn-bg text-header-sign-in-btn-text hover:bg-header-sign-in-btn-hover-bg/20 hover:text-header-sign-in-btn-hover-text flex h-9 items-center gap-1 rounded px-4 py-2 text-center leading-5 duration-200",
          className
        )
      }
    );
  }
);
HeaderSignInButton.displayName = "HeaderSignInButton";

var _path$n;
function _extends$n() { _extends$n = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$n.apply(this, arguments); }
var SvgAngleDown = function SvgAngleDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$n({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none"
  }, props), _path$n || (_path$n = /*#__PURE__*/React.createElement("path", {
    d: "M6.05 9 11 13.95 15.95 9",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

var _path$m;
function _extends$m() { _extends$m = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$m.apply(this, arguments); }
var SvgCheck = function SvgCheck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$m({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none"
  }, props), _path$m || (_path$m = /*#__PURE__*/React.createElement("path", {
    d: "m4.218 11.246 4.255 4.255L16.8 7.174",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

var _circle$3, _path$l;
function _extends$l() { _extends$l = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$l.apply(this, arguments); }
var SvgSearch = function SvgSearch(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$l({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none"
  }, props), _circle$3 || (_circle$3 = /*#__PURE__*/React.createElement("circle", {
    cx: 9.995,
    cy: 9.926,
    r: 6.501,
    stroke: "currentColor"
  })), _path$l || (_path$l = /*#__PURE__*/React.createElement("path", {
    stroke: "currentColor",
    strokeLinecap: "round",
    d: "m19.293 19.308-4.45-4.45"
  })));
};

const HeaderCombobox = ({
  label,
  value,
  items,
  triggerText = "",
  open,
  loading = false,
  shouldFilter = false,
  noResultsText,
  onValueChange,
  onOpenChange
}) => {
  const { t } = useTranslation("ui:header-combobox");
  const greaterThanLarge = useMediaQuery(mediaQueries.lg);
  const [_commandSheetOpen, setCommandSheetOpen] = useControllableState({
    value: open,
    defaultValue: false,
    onValueChange: onOpenChange
  });
  const [_popoverOpen, setPopoverOpen] = useControllableState({
    value: open,
    defaultValue: false,
    onValueChange: onOpenChange
  });
  const commandSheetOpen = !greaterThanLarge ? _commandSheetOpen : false;
  const popoverOpen = greaterThanLarge ? _popoverOpen : false;
  const _triggerText = triggerText.length > 0 ? triggerText : items.find((item) => item.value === value)?.label;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "w-full lg:hidden", children: /* @__PURE__ */ jsxs(
      CommandSheet,
      {
        label,
        open: commandSheetOpen,
        value,
        onOpenChange: setCommandSheetOpen,
        onValueChange,
        children: [
          /* @__PURE__ */ jsx(CommandSheetTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(HeaderButton, { className: "w-full [&>svg]:data-[state=open]:rotate-180", children: [
            _triggerText,
            /* @__PURE__ */ jsx(SvgAngleDown, { className: "text-header-general-btn-text ml-auto -rotate-90 text-xl duration-200" })
          ] }) }),
          /* @__PURE__ */ jsxs(CommandSheetContent, { children: [
            /* @__PURE__ */ jsxs(InputGroup, { children: [
              /* @__PURE__ */ jsx(InputLeftContent, { children: /* @__PURE__ */ jsx(SvgSearch, {}) }),
              /* @__PURE__ */ jsx(CommandInput, {}),
              loading && /* @__PURE__ */ jsx(Loading, {})
            ] }),
            /* @__PURE__ */ jsxs(CommandSheetList, { children: [
              /* @__PURE__ */ jsx(CommandEmpty, { children: noResultsText || t("No results found.") }),
              items.map((item) => /* @__PURE__ */ jsxs(CommandSheetItem, { value: item.value, children: [
                /* @__PURE__ */ jsx(
                  SvgCheck,
                  {
                    className: cn("stroke-primary text-xl opacity-0", {
                      "opacity-100": item.value.toLowerCase().trim() === value?.toLowerCase().trim()
                    })
                  }
                ),
                item.label
              ] }, item.value))
            ] })
          ] })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx("div", { className: "hidden lg:block", children: /* @__PURE__ */ jsxs(Popover, { open: popoverOpen, onOpenChange: setPopoverOpen, children: [
      /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(HeaderButton, { className: "[&>svg]:data-[state=open]:rotate-180", children: [
        _triggerText,
        /* @__PURE__ */ jsx(SvgAngleDown, { className: "text-xl duration-200" })
      ] }) }),
      /* @__PURE__ */ jsx(PopoverContent, { sideOffset: 12, children: /* @__PURE__ */ jsxs(Command, { shouldFilter, children: [
        /* @__PURE__ */ jsxs(InputGroup, { children: [
          /* @__PURE__ */ jsx(InputLeftContent, { children: /* @__PURE__ */ jsx(SvgSearch, {}) }),
          /* @__PURE__ */ jsx(CommandInput, { className: "h-10" }),
          loading && /* @__PURE__ */ jsx(Loading, {})
        ] }),
        /* @__PURE__ */ jsxs(CommandContent, { className: "-mr-2 p-0", children: [
          /* @__PURE__ */ jsx(CommandEmpty, { children: noResultsText || t("No results found.") }),
          items.map((item) => /* @__PURE__ */ jsxs(
            CommandItem,
            {
              className: cn("flex gap-1 aria-selected:bg-neutral-100", {
                "text-primary": item.value.toLowerCase().trim() === value
              }),
              onSelect: () => {
                setPopoverOpen(false);
                onValueChange?.(item.value);
              },
              children: [
                /* @__PURE__ */ jsx(
                  SvgCheck,
                  {
                    className: cn("text-primary text-xl opacity-0", {
                      "opacity-100": item.value.toLowerCase().trim() === value
                    })
                  }
                ),
                item.label
              ]
            },
            item.value
          ))
        ] })
      ] }) })
    ] }) })
  ] });
};

var _circle$2, _path$k, _circle2$2;
function _extends$k() { _extends$k = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$k.apply(this, arguments); }
var SvgAvatarSignedOut = function SvgAvatarSignedOut(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$k({
    width: "1em",
    height: "1em",
    viewBox: "0 0 34 34",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _circle$2 || (_circle$2 = /*#__PURE__*/React.createElement("circle", {
    cx: 17,
    cy: 17,
    r: 15,
    fill: "currentColor"
  })), _path$k || (_path$k = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M8.9 25.847v-.001a8.102 8.102 0 0 1 8.1-8.104c4.474 0 8.1 3.628 8.1 8.104v.008A11.95 11.95 0 0 1 17.004 29c-3.124 0-5.97-1.195-8.104-3.153Z",
    fill: "#fff"
  })), _circle2$2 || (_circle2$2 = /*#__PURE__*/React.createElement("circle", {
    cx: 17,
    cy: 13.699,
    r: 4.5,
    fill: "#fff"
  })));
};

var _path$j;
function _extends$j() { _extends$j = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$j.apply(this, arguments); }
var SvgChevron = function SvgChevron(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$j({
    width: "1em",
    height: "1em",
    viewBox: "0 0 6 4",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$j || (_path$j = /*#__PURE__*/React.createElement("path", {
    d: "M.828 1.334 3 3.506 5.17 1.334",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

const HeaderAvatar = forwardRef(
  ({ label, showCaret, isOpen }, ref) => {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: "bg-primary relative flex h-8 w-8 items-center justify-center rounded-full text-lg font-extrabold text-white",
        children: [
          label ?? /* @__PURE__ */ jsx(SvgAvatarSignedOut, { className: "text-primary text-3xl" }),
          showCaret && /* @__PURE__ */ jsx("div", { className: "absolute left-2/3 top-2/3 flex h-3 w-3 items-center justify-center rounded-full border-[1px] border-white bg-neutral-400", children: /* @__PURE__ */ jsx(
            SvgChevron,
            {
              className: cn(
                "text-[6px] text-neutral-800 transition-transform duration-300 ease-in-out",
                {
                  "rotate-180": isOpen
                }
              )
            }
          ) })
        ]
      }
    );
  }
);
HeaderAvatar.displayName = "HeaderAvatar";

const HeaderUserAvatar = forwardRef(
  ({
    email,
    name,
    role,
    isOpen,
    displayField = "email"
  }, ref) => {
    return /* @__PURE__ */ jsxs("div", { ref, className: "hidden gap-2 sm:flex", children: [
      /* @__PURE__ */ jsxs("div", { className: "self-center text-right", children: [
        displayField === "email" ? email : name,
        role && /* @__PURE__ */ jsx("div", { className: "text-xs text-gray-500", children: role })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "relative", children: /* @__PURE__ */ jsx(HeaderAvatar, { label: name[0], showCaret: true, isOpen }) })
    ] });
  }
);
HeaderUserAvatar.displayName = "HeaderUserAvatar";

const PhoneInput = forwardRef(
  ({ callingCode, id, label, onValueChange, error, ...props }, ref) => /* @__PURE__ */ jsxs(FloatingInputGroup, { error, children: [
    callingCode && /* @__PURE__ */ jsx(
      FloatingInputLeftContent,
      {
        className: cn(
          "!translate-y-2 whitespace-nowrap pt-0 text-base text-neutral-800 "
        ),
        children: /* @__PURE__ */ jsxs("span", { children: [
          "(",
          callingCode,
          ")"
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      FloatingLabel,
      {
        className: cn({
          "-translate-y-3 translate-x-0 scale-75": !!callingCode,
          "translate-x-0": !callingCode
        }),
        htmlFor: id,
        children: label
      }
    ),
    /* @__PURE__ */ jsx(
      FloatingInput,
      {
        onValueChange,
        type: "tel",
        className: "pl-0",
        ref,
        ...props,
        id
      }
    ),
    /* @__PURE__ */ jsx(
      FloatingInputClearButton,
      {
        onClick: () => onValueChange && onValueChange("")
      }
    )
  ] })
);
PhoneInput.displayName = "PhoneInput";

var _path$i;
function _extends$i() { _extends$i = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$i.apply(this, arguments); }
var SvgArrow$1 = function SvgArrow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$i({
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$i || (_path$i = /*#__PURE__*/React.createElement("path", {
    d: "M6.05 9 11 13.95 15.95 9",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

var _path$h;
function _extends$h() { _extends$h = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$h.apply(this, arguments); }
var SvgX = function SvgX(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$h({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none"
  }, props), _path$h || (_path$h = /*#__PURE__*/React.createElement("path", {
    stroke: "currentColor",
    strokeLinecap: "round",
    d: "M16.365 16.434 5.638 5.707M5.638 16.305 16.365 5.578"
  })));
};

const MultiSelectAutocomplete = ({
  inputValue,
  label,
  items,
  loading,
  icon,
  name,
  error,
  onValuesChange,
  onInputValueChange,
  onBlur,
  defaultValues = [],
  shouldFilter = true,
  size = "lg",
  maxValues,
  onLoadMore,
  hasNextPage,
  isLoadingMore,
  onOpen
}) => {
  const [values, setValues] = useState(defaultValues);
  const [_inputValue, setInputValue] = useState("");
  const [previousInputValue, setPreviousInputValue] = useState(inputValue);
  const [selectedItemsMap, setSelectedItemsMap] = useState({});
  if (inputValue !== previousInputValue) {
    setPreviousInputValue(inputValue);
    setInputValue(inputValue ?? "");
  }
  useEffect(() => {
    setSelectedItemsMap((prevMap) => {
      const newMap = { ...prevMap };
      for (const item of items) {
        if (values.includes(item.value)) {
          newMap[item.value] = item;
        }
      }
      return newMap;
    });
  }, [items, values]);
  const selectedItems = values.map(
    (value) => selectedItemsMap[value] || { value, label: value }
  );
  const itemsList = shouldFilter ? items.filter(
    (item) => item.label.toLowerCase().includes(_inputValue.toLowerCase().trim())
  ) : items;
  const handleValueChange = (value) => {
    if (!value || value.trim() === "") {
      return;
    }
    if (maxValues && values.length >= maxValues) {
      return;
    }
    if (!values.includes(value)) {
      const newValues = [...values, value];
      setValues(newValues);
      onValuesChange?.(newValues);
      const selectedItem = items.find((item) => item.value === value);
      if (selectedItem) {
        setSelectedItemsMap((prev) => ({
          ...prev,
          [value]: selectedItem
        }));
      }
    }
    setInputValue("");
    onInputValueChange?.("");
  };
  const handleRemoveValue = (valueToRemove) => {
    const newValues = values.filter((val) => val !== valueToRemove);
    setValues(newValues);
    onValuesChange?.(newValues);
  };
  const handleInputValueChange = (value) => {
    setInputValue(value);
    onInputValueChange?.(value);
  };
  const handleClearValues = () => {
    setInputValue("");
    onInputValueChange?.("");
    setValues([]);
    onValuesChange?.([]);
    setSelectedItemsMap({});
  };
  return /* @__PURE__ */ jsx(
    MultiComboBox,
    {
      label,
      items: itemsList,
      values,
      selectedItems,
      inputValue: _inputValue,
      name,
      loading,
      icon,
      error,
      onValueChange: handleValueChange,
      onRemoveValue: handleRemoveValue,
      onInputValueChange: handleInputValueChange,
      onBlur,
      onClear: handleClearValues,
      size,
      onLoadMore,
      hasNextPage,
      isLoadingMore,
      onOpen
    }
  );
};
const MultiComboBox = ({
  label,
  description,
  errorMessage,
  name,
  loading,
  icon,
  onClear,
  error,
  size,
  values,
  selectedItems,
  onValueChange,
  onRemoveValue,
  items,
  onLoadMore,
  hasNextPage,
  isLoadingMore,
  onOpen,
  ...props
}) => {
  const { t } = useTranslation("ui:responsive-autocomplete");
  const [value, setValue] = useState("");
  const handleValueChange = (newValue) => {
    setValue(newValue ?? "");
    onValueChange(newValue);
  };
  const renderSelectedItems = () => {
    return selectedItems.map((item) => /* @__PURE__ */ jsxs(
      Badge,
      {
        variant: "outline",
        className: "flex items-center gap-1 rounded-[4px] border-neutral-500 px-2 py-1 text-xs text-black",
        children: [
          item.icon && /* @__PURE__ */ jsx("span", { className: "text-sm", children: item.icon }),
          /* @__PURE__ */ jsx("span", { children: item.label }),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: (event) => {
                event.preventDefault();
                event.stopPropagation();
                setValue("");
                onRemoveValue(item.value);
              },
              className: "flex items-center justify-center",
              children: /* @__PURE__ */ jsx(SvgX, { className: "h-4 w-4" })
            }
          )
        ]
      },
      item.value
    ));
  };
  return /* @__PURE__ */ jsxs(
    _ComboBox,
    {
      ...props,
      value,
      onValueChange: handleValueChange,
      onOpenChange: (isOpen) => {
        if (isOpen && onOpen) {
          onOpen();
        }
      },
      className: "flex flex-col gap-2",
      children: [
        /* @__PURE__ */ jsxs("div", { className: "relative", children: [
          /* @__PURE__ */ jsx(_ComboBoxLabel, { children: label }),
          /* @__PURE__ */ jsxs("div", { className: "relative", children: [
            /* @__PURE__ */ jsx(_ComboBoxInput, { size, name }),
            /* @__PURE__ */ jsx(_ComboBoxButton, { className: "group absolute right-3 top-1/2 -translate-y-1/2", children: loading ? /* @__PURE__ */ jsx(Loading, {}) : /* @__PURE__ */ jsx(
              SvgArrow$1,
              {
                className: `text-xl group-data-[pressed=true]:rotate-180`
              }
            ) })
          ] }),
          /* @__PURE__ */ jsx(_ComboBoxPopover, { children: /* @__PURE__ */ jsx(
            _ComboBoxContent,
            {
              renderEmptyState: () => /* @__PURE__ */ jsx("p", { className: "px-4 py-2 text-gray-700", children: t("No results found.") }),
              onLoadMore,
              hasNextPage,
              isLoadingMore,
              children: items.map((item) => /* @__PURE__ */ jsxs(
                _ComboBoxItem,
                {
                  id: item.value,
                  textValue: item.label,
                  className: "flex items-center gap-2 data-[selected]:bg-transparent data-[selected]:text-black data-[selected]:hover:bg-neutral-100",
                  children: [
                    /* @__PURE__ */ jsx("div", { className: "flex items-center", children: /* @__PURE__ */ jsx(Checkbox, { value: values.includes(item.value), children: /* @__PURE__ */ jsx(CheckboxIndicatorWrapper, {}) }) }),
                    item.icon && /* @__PURE__ */ jsx("span", { className: "shrink-0 text-xl", children: item.icon }),
                    /* @__PURE__ */ jsx("span", { children: item.label })
                  ]
                },
                item.value
              ))
            }
          ) })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-2", children: values.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          renderSelectedItems(),
          /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              onClick: (event) => {
                event.preventDefault();
                event.stopPropagation();
                setValue("");
                onClear?.();
              },
              className: "ml-1 flex items-center text-xs text-blue-500",
              children: [
                /* @__PURE__ */ jsx("span", { className: "mr-2 text-gray-400", children: /* @__PURE__ */ jsx(SvgX, { className: "h-4 w-4" }) }),
                t("Clear all")
              ]
            }
          )
        ] }) })
      ]
    }
  );
};

var _path$g;
function _extends$g() { _extends$g = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$g.apply(this, arguments); }
var SvgArrow = function SvgArrow(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$g({
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$g || (_path$g = /*#__PURE__*/React.createElement("path", {
    d: "M6.05 9 11 13.95 15.95 9",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

const ResponsiveCombobox = ({
  id,
  inputValue,
  label,
  items,
  loading,
  icon,
  name,
  error,
  onValueChange,
  onInputValueChange,
  onBlur,
  showMobileIcon = true,
  defaultValue = "",
  shouldFilter = true,
  inputRef,
  onOpenChange,
  open,
  fallbackLabel,
  className
}) => {
  const { t } = useTranslation("ui:responsive-combo-box");
  const greaterThenSmall = useMediaQuery(mediaQueries.sm);
  const [value, setValue] = useState(defaultValue);
  const [_inputValue, setInputValue] = useState(defaultValue);
  const [_mobileOpen, setMobileOpen] = useControllableState({
    value: open,
    defaultValue: false,
    onValueChange: onOpenChange
  });
  const [_desktopOpen, setDesktopOpen] = useControllableState({
    value: open,
    defaultValue: false,
    onValueChange: onOpenChange
  });
  const [previousInputValue, setPreviousInputValue] = useState(inputValue);
  if (inputValue !== previousInputValue) {
    setPreviousInputValue(inputValue);
    setInputValue(inputValue ?? "");
  }
  const _items = shouldFilter ? items.filter(
    (item) => item.label.toLowerCase().includes(_inputValue.toLowerCase().trim())
  ) : items;
  const handleValueChange = (value2) => {
    const inputValue2 = items.find(
      (item) => item.value.toLowerCase() === value2.toLowerCase()
    )?.label;
    inputValue2 && setInputValue(inputValue2);
    setValue(value2);
    onValueChange?.(value2);
  };
  const handleInputValueChange = (value2) => {
    if (value2 === "") {
      setValue("");
      onValueChange?.("");
    }
    setInputValue(value2);
    onInputValueChange?.(value2);
  };
  const handleClearValue = () => {
    setInputValue("");
    onInputValueChange?.("");
    onValueChange?.("");
    setValue("");
  };
  const mobileOpen = !greaterThenSmall ? _mobileOpen : false;
  const desktopOpen = greaterThenSmall ? _desktopOpen : false;
  return isMobile ? /* @__PURE__ */ jsxs(
    CommandSheet,
    {
      label,
      open: mobileOpen,
      value,
      onOpenChange: setMobileOpen,
      onValueChange: handleValueChange,
      children: [
        /* @__PURE__ */ jsxs(
          FloatingInputGroup,
          {
            focused: mobileOpen,
            error,
            value,
            hasLeftContent: !!icon,
            children: [
              icon && /* @__PURE__ */ jsx(FloatingInputLeftContent, { children: icon }),
              /* @__PURE__ */ jsx(FloatingLabel, { htmlFor: id, className: "pointer-events-none", children: label }),
              /* @__PURE__ */ jsx(CommandSheetTrigger, { asChild: true, children: /* @__PURE__ */ jsx(FloatingInput, { ref: inputRef, id, value, asChild: true, children: /* @__PURE__ */ jsx("button", { type: "button", className: "h-14 truncate text-left", children: items.find(
                (item) => item.label.toLowerCase() === value.toLowerCase()
              )?.label ?? (value || "") }) }) }),
              /* @__PURE__ */ jsx("input", { type: "hidden", name, value })
            ]
          }
        ),
        /* @__PURE__ */ jsxs(CommandSheetContent, { shouldFilter, children: [
          /* @__PURE__ */ jsxs(InputGroup, { children: [
            showMobileIcon && /* @__PURE__ */ jsx(InputLeftContent, { children: icon }),
            /* @__PURE__ */ jsx(
              CommandInput,
              {
                value: _inputValue,
                onValueChange: handleInputValueChange
              }
            ),
            loading && /* @__PURE__ */ jsx(Loading, {})
          ] }),
          /* @__PURE__ */ jsxs(CommandSheetList, { children: [
            items.length === 0 && /* @__PURE__ */ jsx(CommandEmpty, { className: loading ? "invisible" : "visible", children: fallbackLabel ?? t("No results found.") }),
            items.map((item) => /* @__PURE__ */ jsxs(
              CommandSheetItem,
              {
                className: "flex gap-2",
                value: item.value,
                children: [
                  item.icon && /* @__PURE__ */ jsx("span", { className: "text-xl", children: item.icon }),
                  item.label
                ]
              },
              item.value
            ))
          ] })
        ] })
      ]
    }
  ) : /* @__PURE__ */ jsx(
    FloatingComboBox,
    {
      label,
      items: _items,
      value,
      inputValue: _inputValue,
      name,
      loading,
      icon,
      error,
      onValueChange: handleValueChange,
      onInputValueChange: handleInputValueChange,
      onBlur,
      onClear: handleClearValue,
      open: desktopOpen,
      onOpenChange: setDesktopOpen,
      fallbackLabel,
      className,
      children: (item) => /* @__PURE__ */ jsxs(_ComboBoxItem, { id: item.value, textValue: item.label, children: [
        /* @__PURE__ */ jsx("span", { className: "shrink-0 text-xl", children: item.icon }),
        item.label
      ] })
    }
  );
};
const FloatingComboBox = ({
  label,
  description,
  errorMessage,
  children,
  name,
  loading,
  icon,
  onClear,
  error,
  fallbackLabel,
  inputValue,
  className,
  ...props
}) => {
  const { t } = useTranslation("ui:responsive-combo-box");
  return /* @__PURE__ */ jsxs(
    _ComboBox,
    {
      className,
      defaultSelectedKey: inputValue,
      ...props,
      menuTrigger: "focus",
      children: [
        /* @__PURE__ */ jsxs(
          FloatingInputGroup,
          {
            error,
            value: inputValue,
            hasLeftContent: !!icon,
            className: "relative w-full px-2",
            children: [
              icon && /* @__PURE__ */ jsx(FloatingInputLeftContent, { className: "absolute left-2 shrink-0", children: icon }),
              /* @__PURE__ */ jsx(_ComboBoxLabel, { asChild: true, children: /* @__PURE__ */ jsx(FloatingLabel, { children: label }) }),
              /* @__PURE__ */ jsx(
                _ComboBoxInput,
                {
                  className: cn("pl-0 pt-6", { "pl-7": !!icon }),
                  name,
                  asChild: true,
                  children: /* @__PURE__ */ jsx(FloatingInput, {})
                }
              ),
              /* @__PURE__ */ jsxs(FloatingInputRightContent, { className: "absolute right-0 flex gap-1 pr-2", children: [
                loading && /* @__PURE__ */ jsx(Loading, {}),
                /* @__PURE__ */ jsx(FloatingInputClearButton, { onClick: onClear }),
                /* @__PURE__ */ jsx(_ComboBoxButton, { className: "group", children: /* @__PURE__ */ jsx(SvgArrow, { className: "text-xl group-data-[pressed=true]:rotate-180" }) })
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsx(_ComboBoxPopover, { className: loading ? "hidden" : "", children: /* @__PURE__ */ jsx(
          _ComboBoxContent,
          {
            className: "pointer-events-auto",
            renderEmptyState: () => /* @__PURE__ */ jsx("p", { className: "px-4 py-2 text-gray-700", children: fallbackLabel ?? t("No results found.") }),
            children
          }
        ) })
      ]
    }
  );
};

const linkVariants = cva("", {
  variants: {
    color: {
      primary: "text-primary hover:text-primary-400",
      secondary: "text-secondary hover:text-secondary-400",
      neutral: "text-neutral-800 hover:text-neutral-900",
      error: "text-error hover:text-error-400",
      warning: "text-warning hover:text-warning-400",
      success: "text-success hover:text-success-400"
    }
  },
  defaultVariants: {
    color: "primary"
  }
});
const Link = forwardRef(
  ({ children, className, color, target, rel, ...others }, ref) => {
    const getRelation = () => {
      if (rel) {
        return rel;
      }
      if (target === "_blank") {
        return "noreferrer";
      }
      return void 0;
    };
    return /* @__PURE__ */ jsx(
      "a",
      {
        ref,
        className: cn(
          "cursor-pointer duration-200 hover:underline",
          linkVariants({ color }),
          className
        ),
        target,
        rel: getRelation(),
        ...others,
        children
      }
    );
  }
);
Link.displayName = "Link";

var _path$f;
function _extends$f() { _extends$f = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$f.apply(this, arguments); }
var SvgFacebook = function SvgFacebook(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$f({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 5 10",
    fill: "none"
  }, props), _path$f || (_path$f = /*#__PURE__*/React.createElement("path", {
    d: "m4.672 5.26.26-1.693H3.31V2.47c0-.463.227-.915.953-.915H5V.115S4.331 0 3.692 0C2.357 0 1.484.81 1.484 2.277v1.29H0V5.26h1.484v4.09a5.9 5.9 0 0 0 1.827 0V5.26h1.361Z",
    fill: "currentColor"
  })));
};

var _path$e, _path2, _path3;
function _extends$e() { _extends$e = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$e.apply(this, arguments); }
var SvgInstagram = function SvgInstagram(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$e({
    width: "1em",
    height: "1em",
    viewBox: "0 0 10 10",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$e || (_path$e = /*#__PURE__*/React.createElement("path", {
    d: "M5.003.848c1.337 0 1.495.006 2.02.028.49.02.753.098.929.162.232.085.4.188.574.351.176.166.284.323.374.542.068.165.15.415.172.874.023.497.029.646.029 1.903 0 1.258-.006 1.408-.03 1.903-.02.46-.103.708-.171.874-.09.219-.2.377-.374.54a1.551 1.551 0 0 1-.574.352c-.176.065-.442.142-.928.162-.528.022-.686.028-2.021.028-1.337 0-1.495-.006-2.02-.028-.49-.02-.753-.097-.93-.162a1.56 1.56 0 0 1-.574-.351 1.444 1.444 0 0 1-.373-.541c-.068-.166-.15-.416-.172-.874-.023-.497-.03-.646-.03-1.903 0-1.26.007-1.408.03-1.903.022-.46.104-.709.172-.874.09-.22.2-.378.373-.542.176-.165.342-.266.575-.351.176-.064.442-.142.928-.162.526-.022.684-.028 2.02-.028Zm0-.848C3.645 0 3.475.006 2.94.028 2.41.05 2.044.13 1.728.247c-.33.121-.61.281-.888.544a2.3 2.3 0 0 0-.578.834c-.123.3-.21.642-.233 1.143C.006 3.272 0 3.432 0 4.71c0 1.28.006 1.44.03 1.942.023.5.109.845.232 1.143.129.31.299.574.578.835.278.262.557.424.886.543.318.116.682.197 1.213.22.534.021.704.027 2.062.027 1.358 0 1.528-.006 2.062-.028.531-.022.897-.103 1.213-.219.329-.12.608-.281.886-.543.277-.261.45-.524.576-.833.123-.3.21-.643.233-1.143.023-.503.029-.663.029-1.942s-.006-1.439-.03-1.941c-.023-.501-.109-.845-.232-1.143A2.196 2.196 0 0 0 9.166.79 2.466 2.466 0 0 0 8.28.248C7.962.133 7.598.052 7.067.03 6.53.006 6.36 0 5.003 0Z",
    fill: "currentColor"
  })), _path2 || (_path2 = /*#__PURE__*/React.createElement("path", {
    d: "M5 1.883c-1.656 0-3 1.266-3 2.826s1.344 2.826 3 2.826S8 6.27 8 4.71 6.656 1.883 5 1.883Zm0 4.66c-1.075 0-1.946-.822-1.946-1.834S3.925 2.876 5 2.876s1.946.82 1.946 1.833c0 1.012-.871 1.833-1.946 1.833Z",
    fill: "currentColor"
  })), _path3 || (_path3 = /*#__PURE__*/React.createElement("path", {
    d: "M8 2.354c0 .26-.225.47-.5.47-.277 0-.5-.21-.5-.47 0-.261.225-.471.5-.471s.5.212.5.47Z",
    fill: "currentColor"
  })));
};

var _path$d;
function _extends$d() { _extends$d = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$d.apply(this, arguments); }
var SvgLinkedin = function SvgLinkedin(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$d({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 10 10",
    fill: "none"
  }, props), _path$d || (_path$d = /*#__PURE__*/React.createElement("path", {
    d: "M9.262 0H.738a.762.762 0 0 0-.522.204A.676.676 0 0 0 0 .695v8.03c0 .185.078.362.216.492.139.13.326.204.522.204h8.524a.762.762 0 0 0 .522-.204.676.676 0 0 0 .216-.492V.695a.676.676 0 0 0-.216-.491A.762.762 0 0 0 9.262 0ZM2.98 8.025H1.477V3.526h1.504v4.5Zm-.753-5.122a.901.901 0 0 1-.479-.14.825.825 0 0 1-.315-.366.77.77 0 0 1-.047-.47.8.8 0 0 1 .237-.414.881.881 0 0 1 .442-.221.913.913 0 0 1 .499.047.854.854 0 0 1 .386.3.78.78 0 0 1 .082.765.8.8 0 0 1-.188.266.857.857 0 0 1-.284.176.897.897 0 0 1-.333.057Zm6.294 5.126H7.02V5.571c0-.725-.327-.948-.749-.948-.446 0-.883.316-.883.967v2.44H3.883v-4.5H5.33v.623h.02c.145-.277.653-.75 1.429-.75.839 0 1.745.47 1.745 1.843V8.03Z",
    fill: "currentColor"
  })));
};

const Footer = (props) => {
  const { t } = useTranslation("ui:footer");
  const defaultProps = {
    logo: {
      src: "https://static.mozio.com/assets/logo.svg",
      alt: "Mozio logo",
      href: "/"
    },
    socials: [
      {
        href: "https://www.linkedin.com/company/mozio/",
        icon: /* @__PURE__ */ jsx(SvgLinkedin, {}),
        title: "LinkedIn"
      },
      {
        href: "https://www.facebook.com/letsmozio/",
        icon: /* @__PURE__ */ jsx(SvgFacebook, {}),
        title: "Facebook"
      },
      {
        href: "https://www.instagram.com/letsmozio/",
        icon: /* @__PURE__ */ jsx(SvgInstagram, {}),
        title: "Instagram"
      }
    ],
    copyRight: t(
      "{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.",
      { year: (/* @__PURE__ */ new Date()).getFullYear() }
    )
  };
  const {
    className,
    navLinks,
    legalLinks,
    socials = defaultProps.socials,
    logo = defaultProps.logo,
    copyRight = defaultProps.copyRight,
    showSocials = true,
    children
  } = props;
  const renderLogoAndSocials = () => /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-center gap-2 md:justify-start", children: [
    /* @__PURE__ */ jsx("a", { className: "hover:bg-primary-100 duration-200", href: logo.href, children: /* @__PURE__ */ jsx("img", { className: "h-6", src: logo.src, alt: logo.alt }) }),
    showSocials && socials.map((social) => /* @__PURE__ */ jsx(
      "a",
      {
        className: "grid h-5 w-5 place-items-center rounded-full bg-neutral-800 text-xs text-white duration-200 hover:bg-neutral-900",
        href: social.href,
        target: "_blank",
        rel: "noreferrer",
        "aria-label": social.title,
        children: social.icon
      },
      social.href
    ))
  ] });
  const renderNavLinks = () => /* @__PURE__ */ jsx("div", { className: "flex flex-wrap justify-center gap-x-7 gap-y-4 md:justify-end", children: navLinks.map((link) => /* @__PURE__ */ jsx(
    Link,
    {
      color: "primary",
      className: "text-primary-400",
      href: link.href,
      children: link.content
    },
    link.href
  )) });
  const renderLegalLinks = () => /* @__PURE__ */ jsx("div", { className: "flex justify-center gap-x-4 text-xs", children: legalLinks.map(({ content, href }) => /* @__PURE__ */ jsx(
    Link,
    {
      color: "neutral",
      className: "text-primary-400",
      href,
      target: "_blank",
      children: content
    },
    href
  )) });
  const renderCopyRight = () => /* @__PURE__ */ jsx("div", { className: "text-xs text-neutral-900", children: copyRight });
  return /* @__PURE__ */ jsxs(
    "footer",
    {
      className: cn(
        "mx-auto mt-auto w-full py-14 text-sm text-gray-500",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs(Container, { className: "grid max-w-full gap-4 text-center md:hidden", children: [
          renderLogoAndSocials(),
          renderNavLinks(),
          renderLegalLinks(),
          renderCopyRight(),
          children
        ] }),
        /* @__PURE__ */ jsxs(Container, { className: "hidden max-w-full gap-4 md:grid", children: [
          /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-[auto_1fr] gap-8", children: [
            /* @__PURE__ */ jsx("div", { className: "grid gap-4", children: renderLogoAndSocials() }),
            /* @__PURE__ */ jsx("div", { children: renderNavLinks() })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-end justify-between", children: [
            renderCopyRight(),
            renderLegalLinks(),
            children
          ] })
        ] })
      ]
    }
  );
};

const ensureKeyEqualValue = (langs) => {
  Object.keys(langs).forEach((k) => langs[k].value = k);
  return langs;
};
const LANGUAGES = ensureKeyEqualValue({
  "en-us": {
    name: "English",
    englishName: "English",
    value: "en-us",
    intl: "en",
    code: "us",
    zendeskLocale: "en-us"
  },
  "de-de": {
    name: "Deutsch",
    englishName: "German",
    value: "de-de",
    intl: "de",
    moment: "de",
    code: "de",
    zendeskLocale: "de"
  },
  "ja-jp": {
    name: "\u65E5\u672C\u8A9E",
    englishName: "Japanese",
    value: "ja-jp",
    intl: "ja",
    code: "jp",
    zendeskLocale: "ja"
  },
  nl: {
    name: "Dutch",
    englishName: "Dutch",
    value: "nl",
    intl: "nl",
    moment: "nl",
    code: "nl",
    zendeskLocale: "nl"
  },
  "pt-br": {
    name: "Portugu\xEAs",
    englishName: "Portuguese",
    value: "pt-br",
    intl: "pt",
    moment: "pt",
    code: "br",
    zendeskLocale: "pt-br"
  },
  es: {
    name: "Espa\xF1ol",
    englishName: "Spanish",
    value: "es",
    intl: "es",
    moment: "es",
    code: "es",
    zendeskLocale: "es"
  },
  fr: {
    name: "Fran\xE7ais",
    englishName: "French",
    value: "fr",
    intl: "fr",
    moment: "fr",
    code: "fr",
    zendeskLocale: "fr"
  },
  "id-id": {
    name: "Bahasa Indonesia",
    englishName: "Indonesian",
    value: "id-id",
    intl: "en",
    code: "id",
    zendeskLocale: "en-us"
  },
  "it-it": {
    name: "Italiano",
    englishName: "Italian",
    value: "it-it",
    intl: "it",
    moment: "it",
    code: "it",
    zendeskLocale: "it"
  },
  pl: {
    name: "Polski",
    englishName: "Polish",
    value: "pl",
    intl: "pl",
    moment: "pl",
    code: "pl",
    zendeskLocale: "pl"
  },
  sv: {
    name: "Svenska",
    englishName: "Swedish",
    value: "sv",
    intl: "sv",
    moment: "sv",
    code: "sv",
    zendeskLocale: "sv"
  },
  "ru-ru": {
    name: "\u0420\u0443\u0441\u0441\u043A\u0438\u0439",
    englishName: "Russian",
    value: "ru-ru",
    intl: "ru",
    moment: "ru",
    code: "ru",
    zendeskLocale: "ru"
  },
  "vi-vn": {
    name: "ti\u1EBFng Vi\u1EC7t",
    englishName: "Vietnamese",
    value: "vi-vn",
    intl: "vi",
    code: "vn",
    zendeskLocale: "vi"
  },
  "zh-cn": {
    name: "\u7B80\u4F53\u4E2D\u6587",
    englishName: "Simplified Chinese",
    value: "zh-cn",
    intl: "zh",
    code: "cn",
    zendeskLocale: "zh-cn"
  },
  "zh-tw": {
    name: "\u6B63\u9AD4\u4E2D\u6587",
    englishName: "Traditional Chinese",
    value: "zh-tw",
    intl: "zh",
    code: "tw",
    zendeskLocale: "zh-tw"
  },
  "ko-kr": {
    name: "\uD55C\uAD6D\uC778",
    englishName: "Korean",
    value: "ko-kr",
    intl: "ko",
    code: "kr",
    zendeskLocale: "ko"
  },
  "th-th": {
    name: "\u0E44\u0E17\u0E22",
    englishName: "Thai",
    value: "th-th",
    intl: "en",
    code: "th",
    zendeskLocale: "en-us"
  }
});

const LocaleInput = ({ onValueChange, value, ...props }) => {
  const { i18n } = useTranslation$1();
  const getLabel = (language) => {
    return i18n.language === "en-us" ? `${language.name}${language.englishName === "English" ? "" : ` (${language.englishName})`}` : language.name;
  };
  const languages = Object.values(LANGUAGES).map((l) => ({
    ...l,
    label: getLabel(l),
    icon: /* @__PURE__ */ jsx(Flag, { className: "w-5", country: l.code.toUpperCase() })
  }));
  const findLanguage = (value2) => {
    return languages.find(
      (locale) => locale.value === value2 || locale.name.toLowerCase() === value2
    );
  };
  const selectedLocale = findLanguage(value);
  const handleValueChange = (displayText) => {
    if (displayText.length === 0) {
      onValueChange?.(null);
      return;
    }
    const localeData = findLanguage(displayText);
    if (!localeData) {
      console.error(
        `Locale not found for displayText: ${displayText}. This should not happen.`
      );
      return;
    }
    onValueChange?.(localeData.value);
  };
  return /* @__PURE__ */ jsx(
    ResponsiveCombobox,
    {
      ...props,
      items: languages,
      showMobileIcon: false,
      defaultValue: selectedLocale?.name,
      onValueChange: handleValueChange,
      icon: selectedLocale ? /* @__PURE__ */ jsx(Flag, { className: "w-5", country: selectedLocale.code.toUpperCase() }) : (
        // TODO: Check with designer an icon when no value is selected
        /* @__PURE__ */ jsx("span", { className: "w-5 text-center", children: "+" })
      )
    }
  );
};

const RadioButtonGroup = forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Root,
    {
      className: cn("flex flex-nowrap gap-2", "sm:gap-0", className),
      ...props,
      ref
    }
  );
});
RadioButtonGroup.displayName = RadioGroupPrimitive.Root.displayName;
const RadioButtonGroupItem = forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Item,
    {
      ref,
      className: cn(
        "group/item flex w-full flex-nowrap gap-x-2 whitespace-nowrap rounded-md border border-neutral-200 bg-white px-1.5 py-1 text-neutral-800 duration-200",
        "data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-contrast",
        "sm:rounded-none sm:px-5 sm:first-of-type:rounded-bl-md sm:first-of-type:rounded-tl-md sm:last-of-type:rounded-br-md sm:last-of-type:rounded-tr-md ",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props
    }
  );
});
RadioButtonGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

const RadioGroup = React.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Root,
    {
      className: cn("grid gap-2", className),
      ...props,
      ref
    }
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;
const RadioGroupItem = React.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Item,
    {
      ref,
      className: cn(
        "text-primary ring-offset-background focus-visible:ring-ring data-[state=checked]:border-primary data-[state=checked]:bg-primary flex aspect-square h-4 w-4 min-w-[1rem] items-center justify-center rounded-full border border-neutral-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(RadioGroupPrimitive.Indicator, { className: "data-[state=checked]:bg-primary-contrast flex h-1.5 w-1.5 items-center justify-center rounded-full" })
    }
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

const ScrollArea = forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  ScrollAreaPrimitive.Root,
  {
    ref,
    className: cn("relative overflow-hidden", className),
    ...props,
    children: [
      /* @__PURE__ */ jsx(ScrollAreaPrimitive.Viewport, { className: "h-full w-full rounded-[inherit]", children }),
      /* @__PURE__ */ jsx(ScrollBar, {}),
      /* @__PURE__ */ jsx(ScrollAreaPrimitive.Corner, {})
    ]
  }
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;
const ScrollBar = forwardRef(({ className, orientation = "horizontal", ...props }, ref) => /* @__PURE__ */ jsx(
  ScrollAreaPrimitive.ScrollAreaScrollbar,
  {
    ref,
    orientation,
    className: cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" && "h-2.5 border-t border-t-transparent p-[1px]",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(ScrollAreaPrimitive.ScrollAreaThumb, { className: "bg-border relative flex-1 rounded-full" })
  }
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

var _path$c;
function _extends$c() { _extends$c = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$c.apply(this, arguments); }
var SvgAlert$1 = function SvgAlert(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$c({
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$c || (_path$c = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M9 3a6 6 0 0 0-6 6v6a6 6 0 0 0 6 6h6a6 6 0 0 0 6-6V9a6 6 0 0 0-6-6H9Zm2 5a1 1 0 1 1 2 0v5a1 1 0 1 1-2 0V8Zm0 8a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z",
    fill: "currentColor"
  })));
};

var _circle$1, _path$b, _circle2$1;
function _extends$b() { _extends$b = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b.apply(this, arguments); }
var SvgInfo$1 = function SvgInfo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b({
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _circle$1 || (_circle$1 = /*#__PURE__*/React.createElement("circle", {
    cx: 12,
    cy: 12,
    r: 9,
    stroke: "currentColor",
    strokeMiterlimit: 10,
    strokeLinejoin: "round"
  })), _path$b || (_path$b = /*#__PURE__*/React.createElement("path", {
    d: "M11 11h1v6",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })), _circle2$1 || (_circle2$1 = /*#__PURE__*/React.createElement("circle", {
    cx: 12,
    cy: 8,
    r: 1,
    fill: "currentColor"
  })));
};

var _path$a;
function _extends$a() { _extends$a = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a.apply(this, arguments); }
var SvgSuccess$1 = function SvgSuccess(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a({
    width: "1em",
    height: "1em",
    viewBox: "0 0 18 18",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$a || (_path$a = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M9 18A9 9 0 1 0 9 0a9 9 0 0 0 0 18ZM5.327 8.92a1 1 0 1 0-1.414 1.415l2.962 2.962a1 1 0 0 0 1.415 0L14.087 7.5a1 1 0 0 0-1.414-1.415l-5.09 5.09-2.256-2.254Z",
    fill: "currentColor"
  })));
};

var _path$9;
function _extends$9() { _extends$9 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9.apply(this, arguments); }
var SvgTimes$1 = function SvgTimes(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none"
  }, props), _path$9 || (_path$9 = /*#__PURE__*/React.createElement("path", {
    stroke: "currentColor",
    strokeLinecap: "round",
    d: "M16.365 16.434 5.638 5.707M5.638 16.305 16.365 5.578"
  })));
};

const ICONS$1 = {
  primary: /* @__PURE__ */ jsx(SvgInfo$1, { className: "text-primary" }),
  secondary: /* @__PURE__ */ jsx(SvgInfo$1, { className: "text-secondary-400" }),
  neutral: /* @__PURE__ */ jsx(SvgInfo$1, { className: "text-neutral-700" }),
  warning: /* @__PURE__ */ jsx(SvgInfo$1, { className: "text-warning-700" }),
  error: /* @__PURE__ */ jsx(SvgAlert$1, { className: "text-error-300" }),
  success: /* @__PURE__ */ jsx(SvgSuccess$1, { className: "text-success-300" })
};
const alertVariants = cva(
  "inline-flex w-max rounded-md border px-2 py-2 text-xs font-normal items-start gap-1",
  {
    variants: {
      variant: {
        primary: "border-transparent bg-primary-100 text-primary-400",
        secondary: "border-transparent bg-secondary-100 text-secondary-400",
        error: "border-transparent bg-error-100 text-error-400 ",
        success: "border-transparent bg-success-100 text-success-400",
        warning: "border-transparent bg-warning-100 text-warning-400",
        neutral: "border-transparent bg-neutral-100 text-neutral-900"
      }
    },
    defaultVariants: {
      variant: "primary"
    }
  }
);
const Alert = forwardRef(
  ({ className, variant, onClose, ...props }, ref) => /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      className: cn(alertVariants({ variant }), className),
      ...props,
      children: [
        /* @__PURE__ */ jsx("div", { className: "text-lg", children: ICONS$1[variant || "primary"] }),
        props.children,
        onClose && /* @__PURE__ */ jsx("div", { className: "ml-auto cursor-pointer text-lg", children: /* @__PURE__ */ jsx(SvgTimes$1, { onClick: onClose }) })
      ]
    }
  )
);
Alert.displayName = "Alert";

const NavigationMenu = React.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  NavigationMenuPrimitive.Root,
  {
    ref,
    className: cn(
      "relative z-10 flex flex-1 items-center justify-center [&>div]:w-full",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx(NavigationMenuViewport, {})
    ]
  }
));
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;
const NavigationMenuList = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  NavigationMenuPrimitive.List,
  {
    ref,
    className: cn(
      "group flex flex-1 list-none flex-col items-start justify-center space-x-0",
      className
    ),
    ...props
  }
));
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;
const NavigationMenuItem = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  NavigationMenuPrimitive.Item,
  {
    ref,
    className: cn(
      "group flex w-full flex-1 items-center justify-start gap-2",
      className
    ),
    ...props
  }
));
NavigationMenuItem.displayName = NavigationMenuPrimitive.Item.displayName;
const NavigationMenuLink = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  NavigationMenuPrimitive.Link,
  {
    ref,
    className: cn(
      "data-[active]:bg-primary-100 data-[active]:text-primary-400 data-[active]:font-bold",
      "hover:text-primary hover:no-underline",
      "group flex w-full flex-1 items-center justify-start gap-1 rounded p-2 text-neutral-800",
      className
    ),
    ...props
  }
));
NavigationMenuLink.displayName = NavigationMenuPrimitive.Link.displayName;
const NavigationMenuViewport = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { className: cn("absolute left-0 top-full flex justify-center"), children: /* @__PURE__ */ jsx(
  NavigationMenuPrimitive.Viewport,
  {
    className: cn(
      "origin-top-center bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 relative mt-1.5 h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden rounded-md border text-left shadow-lg md:w-[var(--radix-navigation-menu-viewport-width)]",
      className
    ),
    ref,
    ...props
  }
) }));
NavigationMenuViewport.displayName = NavigationMenuPrimitive.Viewport.displayName;
const NavigationMenuIndicator = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  NavigationMenuPrimitive.Indicator,
  {
    ref,
    className: cn(
      "data-[state=visible]:animate-in data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:fade-in top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx("div", { className: "bg-border relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm shadow-md" })
  }
));
NavigationMenuIndicator.displayName = NavigationMenuPrimitive.Indicator.displayName;

const Switch = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SwitchPrimitives.Root,
  {
    className: cn(
      "focus-visible:ring-ring focus-visible:ring-offset-background data-[state=checked]:bg-primary peer inline-flex h-5 w-8 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=unchecked]:bg-neutral-500",
      className
    ),
    ...props,
    ref,
    children: /* @__PURE__ */ jsx(
      SwitchPrimitives.Thumb,
      {
        className: cn(
          "bg-background bg-primary-contrast pointer-events-none block h-4 w-4 rounded-full shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-3 data-[state=unchecked]:translate-x-0"
        )
      }
    )
  }
));
Switch.displayName = SwitchPrimitives.Root.displayName;

var _path$8;
function _extends$8() { _extends$8 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8.apply(this, arguments); }
var SvgAlert = function SvgAlert(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8({
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$8 || (_path$8 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M9 3a6 6 0 0 0-6 6v6a6 6 0 0 0 6 6h6a6 6 0 0 0 6-6V9a6 6 0 0 0-6-6H9Zm2 5a1 1 0 1 1 2 0v5a1 1 0 1 1-2 0V8Zm0 8a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z",
    fill: "currentColor"
  })));
};

var _circle, _path$7, _circle2;
function _extends$7() { _extends$7 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7.apply(this, arguments); }
var SvgInfo = function SvgInfo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7({
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _circle || (_circle = /*#__PURE__*/React.createElement("circle", {
    cx: 12,
    cy: 12,
    r: 9,
    stroke: "currentColor",
    strokeMiterlimit: 10,
    strokeLinejoin: "round"
  })), _path$7 || (_path$7 = /*#__PURE__*/React.createElement("path", {
    d: "M11 11h1v6",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })), _circle2 || (_circle2 = /*#__PURE__*/React.createElement("circle", {
    cx: 12,
    cy: 8,
    r: 1,
    fill: "currentColor"
  })));
};

var _path$6;
function _extends$6() { _extends$6 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6.apply(this, arguments); }
var SvgSuccess = function SvgSuccess(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6({
    width: "1em",
    height: "1em",
    viewBox: "0 0 18 18",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$6 || (_path$6 = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M9 18A9 9 0 1 0 9 0a9 9 0 0 0 0 18ZM5.327 8.92a1 1 0 1 0-1.414 1.415l2.962 2.962a1 1 0 0 0 1.415 0L14.087 7.5a1 1 0 0 0-1.414-1.415l-5.09 5.09-2.256-2.254Z",
    fill: "currentColor"
  })));
};

var _path$5;
function _extends$5() { _extends$5 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5.apply(this, arguments); }
var SvgTimes = function SvgTimes(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5({
    xmlns: "http://www.w3.org/2000/svg",
    width: "1em",
    height: "1em",
    viewBox: "0 0 22 22",
    fill: "none"
  }, props), _path$5 || (_path$5 = /*#__PURE__*/React.createElement("path", {
    stroke: "currentColor",
    strokeLinecap: "round",
    d: "M16.365 16.434 5.638 5.707M5.638 16.305 16.365 5.578"
  })));
};

const ToastProvider = ToastPrimitives.Provider;
const ToastViewport = React.forwardRef(({ className, position = "bottom-right", ...props }, ref) => {
  const positionClasses = {
    "top-left": "top-0 left-0 justify-start",
    "top-center": "top-0 left-1/2 transform -translate-x-1/2 justify-center",
    "top-right": "top-0 right-0 justify-end",
    "bottom-left": "bottom-[50px] left-0 -translate-y-1/2 justify-start",
    "bottom-center": "bottom-[50px] left-1/2 transform -translate-x-1/2 -translate-y-1/2 justify-center",
    "bottom-right": "bottom-[50px] right-0 -translate-y-1/2  justify-end"
  };
  return /* @__PURE__ */ jsx(
    ToastPrimitives.Viewport,
    {
      ref,
      className: cn(
        "fixed z-[100] flex max-h-screen w-full max-w-sm p-4 lg:max-w-none",
        positionClasses[position],
        // Apply the selected position classes
        className
      ),
      ...props
    }
  );
});
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;
const toastVariants = cva(
  "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=open]:fade-in-80 group pointer-events-auto relative flex w-full items-center justify-between overflow-hidden rounded-md p-2 transition-all data-[state=closed]:slide-out-to-top-8 data-[state=open]:slide-in-from-top-8 top-[calc(var(--mz-header-height)+1rem)] z-50 m-auto w-auto duration-300 md:max-w-lg lg:max-w-none",
  {
    variants: {
      variant: {
        primary: "bg-primary-100 text-primary-400",
        secondary: "bg-secondary-100 text-secondary-400",
        error: "bg-error-100 text-error-400 ",
        success: "bg-success-100 text-success-500",
        warning: "bg-warning-100 text-warning-400",
        neutral: "bg-neutral-100 text-neutral-800"
      }
    },
    defaultVariants: {
      variant: "primary"
    }
  }
);
const Toast = React.forwardRef(({ className, variant, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    ToastPrimitives.Root,
    {
      ref,
      className: cn(toastVariants({ variant }), className),
      ...props
    }
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;
const ToastTitle = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Title,
  {
    ref,
    className: cn("text-sm", className),
    ...props
  }
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;
const ToastDescription = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  ToastPrimitives.Description,
  {
    ref,
    className: cn("text-xs opacity-90", className),
    ...props
  }
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1e6;
let count = 0;
const genId = () => {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
};
const toastTimeouts = /* @__PURE__ */ new Map();
const addToRemoveQueue = (toastId) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId
    });
  }, TOAST_REMOVE_DELAY);
  toastTimeouts.set(toastId, timeout);
};
const reducer = (state, action) => {
  switch (action.type) {
    case "ADD_TOAST": {
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT)
      };
    }
    case "UPDATE_TOAST": {
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === action.toast.id ? { ...t, ...action.toast } : t
        )
      };
    }
    case "DISMISS_TOAST": {
      const { toastId } = action;
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast2) => {
          addToRemoveQueue(toast2.id);
        });
      }
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === toastId || toastId === void 0 ? {
            ...t,
            open: false
          } : t
        )
      };
    }
    case "REMOVE_TOAST": {
      if (action.toastId === void 0) {
        return {
          ...state,
          toasts: []
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId)
      };
    }
  }
};
const listeners = [];
let memoryState = { toasts: [] };
const dispatch = (action) => {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
};
const toast = ({ ...props }) => {
  const id = genId();
  const update = (props2) => dispatch({
    type: "UPDATE_TOAST",
    toast: { ...props2, id }
  });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });
  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      }
    }
  });
  return {
    id,
    dismiss,
    update
  };
};
const useToast = () => {
  const [state, setState] = React.useState(memoryState);
  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);
  return {
    ...state,
    toast,
    dismiss: (toastId) => dispatch({ type: "DISMISS_TOAST", toastId })
  };
};

const ICONS = {
  primary: /* @__PURE__ */ jsx(SvgInfo, { className: "text-primary" }),
  secondary: /* @__PURE__ */ jsx(SvgInfo, { className: "text-secondary" }),
  neutral: /* @__PURE__ */ jsx(SvgInfo, { className: "text-neutral-700" }),
  warning: /* @__PURE__ */ jsx(SvgInfo, { className: "text-warning-700" }),
  error: /* @__PURE__ */ jsx(SvgAlert, { className: "text-error-300" }),
  success: /* @__PURE__ */ jsx(SvgSuccess, { className: "text-success-400" })
};
const Toaster = ({ duration = 5e3 }) => {
  const { toasts } = useToast();
  const position = toasts[0]?.position;
  const showDismiss = toasts[0]?.showDismiss || true;
  return /* @__PURE__ */ jsxs(ToastProvider, { duration, swipeDirection: "right", children: [
    toasts.map(({ id, title, description, ...props }) => /* @__PURE__ */ jsxs(
      Toast,
      {
        ...props,
        className: "flex items-start justify-between gap-4",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "grid gap-1", children: [
            title && /* @__PURE__ */ jsxs(ToastTitle, { className: "flex items-center gap-1", children: [
              /* @__PURE__ */ jsx("div", { className: "text-lg", children: ICONS[props.variant || "primary"] }),
              title
            ] }),
            description && /* @__PURE__ */ jsx(ToastDescription, { children: description })
          ] }),
          showDismiss && /* @__PURE__ */ jsx(ToastAction, { altText: "Dismiss", className: "ml-auto", children: /* @__PURE__ */ jsx(SvgTimes, {}) })
        ]
      },
      id
    )),
    /* @__PURE__ */ jsx(ToastViewport, { position })
  ] });
};

const Timeline = ({ steps }) => {
  return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
    /* @__PURE__ */ jsx("div", { className: "z-1 absolute top-6 h-[2px] w-full bg-neutral-500" }),
    /* @__PURE__ */ jsx("div", { className: "relative z-10 flex", children: steps.map((step) => /* @__PURE__ */ jsxs(
      "div",
      {
        className: "flex w-full flex-col items-center text-center",
        children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              className: cn$1(
                "flex h-12 w-12 items-center justify-center rounded-full text-3xl text-white",
                step.bgClass
              ),
              children: step.icon
            }
          ),
          /* @__PURE__ */ jsx("h2", { className: "mt-2 text-lg font-bold text-gray-900", children: step.time }),
          /* @__PURE__ */ jsx("p", { className: "text-sm text-gray-600", children: step.label })
        ]
      },
      step.time
    )) })
  ] });
};
Timeline.displayName = "Timeline";

var _path$4;
function _extends$4() { _extends$4 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }
var SvgArrowLeft = function SvgArrowLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 6 10",
    width: "1em",
    height: "1em"
  }, props), _path$4 || (_path$4 = /*#__PURE__*/React.createElement("path", {
    d: "m1.711 5 3.395 3.394c.115.116.174.26.177.435a.589.589 0 0 1-.177.443.598.598 0 0 1-.44.181.598.598 0 0 1-.439-.18L.482 5.526A.69.69 0 0 1 .27 5a.69.69 0 0 1 .213-.527L4.227.728A.604.604 0 0 1 4.662.55a.589.589 0 0 1 .444.178c.12.12.18.267.18.439s-.06.318-.18.439L1.71 5Z",
    fill: "#637280",
    xmlns: "http://www.w3.org/2000/svg"
  })));
};

var _path$3;
function _extends$3() { _extends$3 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }
var SvgArrowRight = function SvgArrowRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3({
    width: "1em",
    height: "1em",
    viewBox: "0 0 6 10",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$3 || (_path$3 = /*#__PURE__*/React.createElement("path", {
    d: "M4.289 5 .894 1.606a.604.604 0 0 1-.177-.435.589.589 0 0 1 .177-.443.598.598 0 0 1 .44-.181c.172 0 .318.06.439.18l3.745 3.746A.69.69 0 0 1 5.73 5a.69.69 0 0 1-.213.527L1.773 9.272a.604.604 0 0 1-.435.178.589.589 0 0 1-.444-.178.598.598 0 0 1-.18-.439c0-.172.06-.318.18-.439L4.29 5Z",
    fill: "#637280"
  })));
};

const DOTS = "...";
const range = (start, end) => {
  return Array.from({ length: end - start + 1 }, (_, idx) => idx + start);
};
const usePagination = ({
  totalCount,
  pageSize,
  siblingCount = 1,
  currentPage
}) => {
  return useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize);
    const totalPageNumbers = siblingCount + 5;
    if (totalPageNumbers >= totalPageCount) return range(1, totalPageCount);
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPageCount
    );
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;
    if (!shouldShowLeftDots && shouldShowRightDots) {
      return [...range(1, 3 + 2 * siblingCount), DOTS, totalPageCount];
    }
    if (shouldShowLeftDots && !shouldShowRightDots) {
      return [
        1,
        DOTS,
        ...range(totalPageCount - (3 + 2 * siblingCount) + 1, totalPageCount)
      ];
    }
    return [
      1,
      DOTS,
      ...range(leftSiblingIndex, rightSiblingIndex),
      DOTS,
      totalPageCount
    ];
  }, [totalCount, pageSize, siblingCount, currentPage]);
};

const Pagination = ({
  totalCount,
  pageSize,
  siblingCount = 1,
  currentPage,
  onPageChange,
  activeColor = "bg-neutral-100 text-black font-semibold",
  inactiveColor = "text-gray-600",
  buttonColor = "text-gray-500",
  disabledColor = "opacity-50"
}) => {
  const paginationRange = usePagination({
    totalCount,
    pageSize,
    siblingCount,
    currentPage
  });
  if (!paginationRange || paginationRange.length < 2) return null;
  const onNext = () => onPageChange(currentPage + 1);
  const onPrevious = () => onPageChange(currentPage - 1);
  return /* @__PURE__ */ jsxs("ul", { className: "flex items-center space-x-2", children: [
    /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: onPrevious,
        disabled: currentPage === 1,
        className: cn(
          "px-3 py-2",
          buttonColor,
          currentPage === 1 && disabledColor
        ),
        children: /* @__PURE__ */ jsx(SvgArrowLeft, {})
      }
    ) }),
    paginationRange.map((page, index) => (
      // eslint-disable-next-line react/no-array-index-key
      /* @__PURE__ */ jsx("li", { children: page === "..." ? /* @__PURE__ */ jsx("span", { className: cn("px-3 py-2", inactiveColor), children: "..." }) : /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => onPageChange(Number(page)),
          className: cn(
            "rounded-lg px-4 py-2 text-sm",
            currentPage === page ? activeColor : inactiveColor
          ),
          children: page
        }
      ) }, index)
    )),
    /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: onNext,
        disabled: currentPage === paginationRange[paginationRange.length - 1],
        className: cn(
          "px-3 py-2",
          buttonColor,
          currentPage === paginationRange[paginationRange.length - 1] && disabledColor
        ),
        children: /* @__PURE__ */ jsx(SvgArrowRight, {})
      }
    ) })
  ] });
};
Pagination.displayName = "Pagination";

const Skeleton = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("animate-pulse rounded-md bg-neutral-200", className),
      ...props
    }
  );
};

const Slider = ({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  displayMin,
  displayMax,
  ...props
}) => {
  const _values = React.useMemo(
    () => Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max],
    [value, defaultValue, min, max]
  );
  if (min === max && displayMin && displayMax) {
    const range = displayMax - displayMin;
    const clampedValue = Math.min(Math.max(min, displayMin), displayMax);
    const leftPercent = (clampedValue - displayMin) / range * 100;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "pointer-events-none relative flex h-1 w-full items-center opacity-50",
          className
        ),
        children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "h-[1px] bg-black",
              style: {
                width: `${leftPercent}%`
              }
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "bg-primary-300 size-3 rounded-full" }),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "h-[1px] bg-black",
              style: {
                width: `${100 - leftPercent}%`
              }
            }
          )
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs(
    SliderPrimitive.Root,
    {
      "data-slot": "slider",
      defaultValue,
      value,
      min,
      max,
      className: cn(
        "data-vertical:min-h-40 data-disabled:opacity-50 data-vertical:h-full data-vertical:w-auto data-vertical:flex-col relative flex w-full touch-none select-none items-center",
        props.disabled && "opacity-50",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx(
          SliderPrimitive.Track,
          {
            "data-slot": "slider-track",
            className: "data-horizontal:h-1 data-vertical:w-1 data-horizontal:w-full data-vertical:h-full relative h-[1px] grow rounded-full bg-black",
            children: /* @__PURE__ */ jsx(
              SliderPrimitive.Range,
              {
                "data-slot": "slider-range",
                className: "bg-primary-300 data-horizontal:h-full data-vertical:w-full absolute top-[-1px] h-1 select-none"
              }
            )
          }
        ),
        Array.from({ length: _values.length }, (_, index) => /* @__PURE__ */ jsx(
          SliderPrimitive.Thumb,
          {
            "data-slot": "slider-thumb",
            className: "bg-primary-300 relative block size-3 shrink-0 select-none rounded-full transition-[color,box-shadow] after:absolute after:-inset-2"
          },
          index
        ))
      ]
    }
  );
};

var _path$2;
function _extends$2() { _extends$2 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }
var SvgMinus = function SvgMinus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2 || (_path$2 = /*#__PURE__*/React.createElement("path", {
    d: "M6 12h12",
    stroke: "#637280",
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

var _path$1;
function _extends$1() { _extends$1 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }
var SvgPickupPassengers = function SvgPickupPassengers(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1 || (_path$1 = /*#__PURE__*/React.createElement("path", {
    d: "M4.433 17.439a4.5 4.5 0 0 1 4.5-4.5h6.135a4.5 4.5 0 0 1 4.5 4.5v1.311a1.5 1.5 0 0 1-1.5 1.5H5.933a1.5 1.5 0 0 1-1.5-1.5v-1.311ZM15.595 6.345a3.595 3.595 0 1 1-7.19 0 3.595 3.595 0 0 1 7.19 0Z",
    stroke: "#637280",
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

var _path;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
var SvgPlus = function SvgPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path || (_path = /*#__PURE__*/React.createElement("path", {
    d: "M6 12h12m-6-6v12",
    stroke: "#637280",
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

const PassengerInput = ({
  id,
  name,
  value,
  onValueChange,
  defaultValue,
  error,
  min = 1,
  max = 99,
  className
}) => {
  const { t } = useTranslation("ui:passenger-input");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const controlledValue = (value ?? defaultValue)?.toString();
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref: containerRef,
      className: "relative",
      onBlur: (event) => {
        if (!containerRef.current?.contains(event.relatedTarget)) {
          setOpen(false);
        }
      },
      children: /* @__PURE__ */ jsxs(Command, { children: [
        /* @__PURE__ */ jsx(
          CommandFloatingInput,
          {
            label: t("Passengers"),
            value: controlledValue,
            icon: /* @__PURE__ */ jsx(SvgPickupPassengers, {}),
            onFocus: () => setOpen(true),
            focused: open,
            readOnly: true,
            className: `cursor-pointer ${className}`,
            error
          }
        ),
        open && /* @__PURE__ */ jsx(CommandContent, { className: "absolute top-full z-10 w-[340px] p-3", children: /* @__PURE__ */ jsx(
          CommandItem,
          {
            onSelect: () => void 0,
            className: "p-0 hover:bg-white aria-selected:bg-white",
            children: /* @__PURE__ */ jsxs("div", { className: "flex w-full items-center justify-between gap-4", children: [
              /* @__PURE__ */ jsx("span", { children: t("Number of passengers") }),
              /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-center gap-2 rounded border border-neutral-500 bg-white text-neutral-800", children: [
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    className: "flex h-12 w-10 items-center justify-center font-semibold hover:bg-neutral-100",
                    onClick: () => onValueChange(
                      String(Math.max(min, Number(controlledValue) - 1))
                    ),
                    children: /* @__PURE__ */ jsx(SvgMinus, {})
                  }
                ),
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    id: `passenger-${id}`,
                    name,
                    min,
                    max,
                    value: controlledValue,
                    onChange: (event) => onValueChange(event.target.value),
                    className: "h-12 w-6 text-center outline-none"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    className: "flex h-12 w-10 items-center justify-center font-semibold hover:bg-neutral-100",
                    onClick: () => onValueChange(
                      String(Math.min(max, Number(controlledValue) + 1))
                    ),
                    children: /* @__PURE__ */ jsx(SvgPlus, {})
                  }
                )
              ] })
            ] })
          }
        ) })
      ] })
    }
  );
};

var Done$R = "完畢";
var t0 = {
	Done: Done$R
};

var t1 = {
	"No results found.": "未找到結果。"
};

var t2 = {
	"Clear all": "全部清除",
	"No results found.": "未找到結果。"
};

var Passengers$h = "乘客";
var t3 = {
	"Number of passengers": "乘客人數",
	Passengers: Passengers$h
};

var t4 = {
	"No results found.": "未找到結果。"
};

var Blog$h = "部落格";
var Careers$h = "職涯發展";
var Contact$h = "接觸";
var Coverage$h = "覆蓋範圍";
var Guides$h = "指南";
var Imprint$h = "版本說明";
var Parking$h = "停車處";
var eSIM$h = "eSIM";
var t5 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. 商標和品牌均為其各自所有者所有。",
	Blog: Blog$h,
	Careers: Careers$h,
	Contact: Contact$h,
	Coverage: Coverage$h,
	Guides: Guides$h,
	Imprint: Imprint$h,
	Parking: Parking$h,
	"Privacy Policy": "隱私權政策",
	"Terms Of Services": "服務條款",
	eSIM: eSIM$h,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Mozio Inc. 的商標和品牌均為其各自所有者的財產。"
};

var Filters$h = "過濾器";
var t6 = {
	"Clear All": "清除全部",
	Filters: Filters$h,
	"Show results": "顯示結果"
};

var Done$Q = "完成";
var Hours$h = "小時";
var Minutes$h = "分鐘";
var t7 = {
	"AM/PM": "上午/下午",
	Done: Done$Q,
	Hours: Hours$h,
	Minutes: Minutes$h
};

var Done$P = "完畢";
var t8 = {
	Done: Done$P
};

var Afghanistan$h = "阿富汗";
var Albania$h = "阿爾巴尼亞";
var Algeria$h = "阿爾及利亞";
var AmericanSamoa$h = "美屬薩摩亞";
var Andorra$h = "安道爾";
var Angola$h = "安哥拉";
var Anguilla$h = "安圭拉";
var Argentina$h = "阿根廷";
var Armenia$h = "亞美尼亞";
var Aruba$h = "阿魯巴";
var Australia$h = "澳洲";
var Austria$h = "奧地利";
var Azerbaijan$h = "亞塞拜然";
var Bahamas$h = "巴哈馬";
var Bahrain$h = "巴林";
var Bangladesh$h = "孟加拉";
var Barbados$h = "巴貝多";
var Belarus$h = "白俄羅斯";
var Belgium$h = "比利時";
var Belize$h = "貝里斯";
var Benin$h = "貝南";
var Bermuda$h = "百慕達";
var Bhutan$h = "不丹";
var Botswana$h = "波札那";
var Brazil$h = "巴西";
var Bulgaria$h = "保加利亞";
var Burundi$h = "蒲隆地";
var Cambodia$h = "柬埔寨";
var Cameroon$h = "喀麥隆";
var Canada$h = "加拿大";
var Chad$h = "查德";
var Chile$h = "智利";
var China$h = "中國";
var Colombia$h = "哥倫比亞";
var Comoros$h = "葛摩";
var Congo$h = "剛果";
var Croatia$h = "克羅埃西亞";
var Curacao$h = "庫拉索";
var Cyprus$h = "賽普勒斯";
var Denmark$h = "丹麥";
var Djibouti$h = "吉布地";
var Dominica$h = "多明尼加";
var Ecuador$h = "厄瓜多";
var Egypt$h = "埃及";
var Eritrea$h = "厄利垂亞";
var Estonia$h = "愛沙尼亞";
var Ethiopia$h = "衣索比亞";
var Fiji$h = "斐濟";
var Finland$h = "芬蘭";
var France$h = "法國";
var Gabon$h = "加彭";
var Gambia$h = "甘比亞";
var Georgia$h = "喬治亞州";
var Germany$h = "德國";
var Ghana$h = "迦納";
var Gibraltar$h = "直布羅陀";
var Greece$h = "希臘";
var Greenland$h = "格陵蘭";
var Grenada$h = "格瑞那達";
var Guadeloupe$h = "瓜德羅普島";
var Guam$h = "關島";
var Guatemala$h = "瓜地馬拉";
var Guernsey$h = "根西島";
var Guinea$h = "幾內亞";
var Guyana$h = "蓋亞那";
var Haiti$h = "海地";
var Honduras$h = "宏都拉斯";
var Hungary$h = "匈牙利";
var Iceland$h = "冰島";
var India$h = "印度";
var Indonesia$h = "印尼";
var Iraq$h = "伊拉克";
var Ireland$h = "愛爾蘭";
var Israel$h = "以色列";
var Italy$h = "義大利";
var Jamaica$h = "牙買加";
var Japan$h = "日本";
var Jersey$h = "球衣";
var Jordan$h = "約旦";
var Kazakhstan$h = "哈薩克";
var Kenya$h = "肯亞";
var Kiribati$h = "吉里巴斯";
var Kuwait$h = "科威特";
var Kyrgyzstan$h = "吉爾吉斯斯坦";
var Latvia$h = "拉脫維亞";
var Lebanon$h = "黎巴嫩";
var Lesotho$h = "賴索托";
var Liberia$h = "賴比瑞亞";
var Liechtenstein$h = "列支敦斯登";
var Lithuania$h = "立陶宛";
var Luxembourg$h = "盧森堡";
var Macao$h = "澳門";
var Madagascar$h = "馬達加斯加";
var Malawi$h = "馬拉威";
var Malaysia$h = "馬來西亞";
var Maldives$h = "馬爾地夫";
var Mali$h = "馬裡";
var Malta$h = "馬爾他";
var Martinique$h = "馬提尼克島";
var Mauritania$h = "茅利塔尼亞";
var Mauritius$h = "模里西斯";
var Mayotte$h = "馬約特島";
var Mexico$h = "墨西哥";
var Monaco$h = "摩納哥";
var Mongolia$h = "蒙古";
var Montenegro$h = "蒙特內哥羅";
var Montserrat$h = "蒙特塞拉特";
var Morocco$h = "摩洛哥";
var Mozambique$h = "莫三比克";
var Myanmar$h = "緬甸";
var Namibia$h = "納米比亞";
var Nauru$h = "諾魯";
var Nepal$h = "尼泊爾";
var Netherlands$h = "荷蘭";
var Nicaragua$h = "尼加拉瓜";
var Niger$h = "尼日";
var Nigeria$h = "奈及利亞";
var Niue$h = "紐埃";
var Norway$h = "挪威";
var Oman$h = "阿曼";
var Pakistan$h = "巴基斯坦";
var Palau$h = "帛琉";
var Panama$h = "巴拿馬";
var Paraguay$h = "巴拉圭";
var Peru$h = "秘魯";
var Philippines$h = "菲律賓";
var Pitcairn$h = "皮特凱恩";
var Poland$h = "波蘭";
var Portugal$h = "葡萄牙";
var Qatar$h = "卡達";
var Romania$h = "羅馬尼亞";
var Russia$h = "俄羅斯";
var Rwanda$h = "盧安達";
var Samoa$h = "薩摩亞";
var Senegal$h = "塞內加爾";
var Serbia$h = "塞爾維亞";
var Seychelles$h = "塞席爾";
var Singapore$h = "新加坡";
var Slovakia$h = "斯洛伐克";
var Slovenia$h = "斯洛維尼亞";
var Somalia$h = "索馬利亞";
var Spain$h = "西班牙";
var Suriname$h = "蘇利南";
var Swaziland$h = "史瓦濟蘭";
var Sweden$h = "瑞典";
var Switzerland$h = "瑞士";
var Tajikistan$h = "塔吉克";
var Thailand$h = "泰國";
var Togo$h = "多哥";
var Tokelau$h = "托克勞";
var Tonga$h = "東加";
var Tunisia$h = "突尼西亞";
var Turkey$h = "火雞";
var Turkmenistan$h = "土庫曼";
var Tuvalu$h = "吐瓦魯";
var Uganda$h = "烏幹達";
var Ukraine$h = "烏克蘭";
var Uruguay$h = "烏拉圭";
var Uzbekistan$h = "烏茲別克";
var Vanuatu$h = "萬那杜";
var Yemen$h = "葉門";
var Zambia$h = "尚比亞";
var Zimbabwe$h = "辛巴威";
var t9 = {
	Afghanistan: Afghanistan$h,
	Albania: Albania$h,
	Algeria: Algeria$h,
	AmericanSamoa: AmericanSamoa$h,
	Andorra: Andorra$h,
	Angola: Angola$h,
	Anguilla: Anguilla$h,
	"Antigua and Barbuda": "安地卡及巴布達",
	Argentina: Argentina$h,
	Armenia: Armenia$h,
	Aruba: Aruba$h,
	Australia: Australia$h,
	Austria: Austria$h,
	Azerbaijan: Azerbaijan$h,
	Bahamas: Bahamas$h,
	Bahrain: Bahrain$h,
	Bangladesh: Bangladesh$h,
	Barbados: Barbados$h,
	Belarus: Belarus$h,
	Belgium: Belgium$h,
	Belize: Belize$h,
	Benin: Benin$h,
	Bermuda: Bermuda$h,
	Bhutan: Bhutan$h,
	"Bolivia, Plurinational State of": "玻利維亞多民族國",
	"Bosnia and Herzegovina": "波士尼亞與赫塞哥維納",
	Botswana: Botswana$h,
	Brazil: Brazil$h,
	"British Indian Ocean Territory": "英屬印度洋領地",
	"Brunei Darussalam": "汶萊達魯薩蘭國",
	Bulgaria: Bulgaria$h,
	"Burkina Faso": "布吉納法索",
	Burundi: Burundi$h,
	Cambodia: Cambodia$h,
	Cameroon: Cameroon$h,
	Canada: Canada$h,
	"Cape Verde": "維德角",
	"Cayman Islands": "開曼群島",
	"Central African Republic": "中非共和國",
	Chad: Chad$h,
	Chile: Chile$h,
	China: China$h,
	"Christmas Island": "聖誕島",
	"Cocos (Keeling) Islands": "科科斯（基林）群島",
	Colombia: Colombia$h,
	Comoros: Comoros$h,
	Congo: Congo$h,
	"Congo, The Democratic Republic of the": "剛果民主共和國",
	"Cook Islands": "庫克群島",
	"Costa Rica": "哥斯大黎加",
	"Cote d'Ivoire": "科特迪瓦",
	Croatia: Croatia$h,
	Curacao: Curacao$h,
	Cyprus: Cyprus$h,
	"Czech Republic": "捷克共和國",
	Denmark: Denmark$h,
	Djibouti: Djibouti$h,
	Dominica: Dominica$h,
	"Dominican Republic": "多明尼加共和國",
	Ecuador: Ecuador$h,
	Egypt: Egypt$h,
	"El Salvador": "薩爾瓦多",
	"Equatorial Guinea": "赤道幾內亞",
	Eritrea: Eritrea$h,
	Estonia: Estonia$h,
	Ethiopia: Ethiopia$h,
	"Falkland Islands (Malvinas)": "福克蘭群島（馬爾維納斯群島）",
	"Faroe Islands": "法羅群島",
	Fiji: Fiji$h,
	Finland: Finland$h,
	France: France$h,
	"French Guiana": "法屬圭亞那",
	"French Polynesia": "法屬玻里尼西亞",
	Gabon: Gabon$h,
	Gambia: Gambia$h,
	Georgia: Georgia$h,
	Germany: Germany$h,
	Ghana: Ghana$h,
	Gibraltar: Gibraltar$h,
	Greece: Greece$h,
	Greenland: Greenland$h,
	Grenada: Grenada$h,
	Guadeloupe: Guadeloupe$h,
	Guam: Guam$h,
	Guatemala: Guatemala$h,
	Guernsey: Guernsey$h,
	Guinea: Guinea$h,
	"Guinea-Bissau": "幾內亞比紹",
	Guyana: Guyana$h,
	Haiti: Haiti$h,
	"Holy See (Vatican City State)": "梵蒂岡城國",
	Honduras: Honduras$h,
	"Hong Kong": "香港",
	Hungary: Hungary$h,
	Iceland: Iceland$h,
	India: India$h,
	Indonesia: Indonesia$h,
	Iraq: Iraq$h,
	Ireland: Ireland$h,
	"Isle of Man": "馬恩島",
	Israel: Israel$h,
	Italy: Italy$h,
	Jamaica: Jamaica$h,
	Japan: Japan$h,
	Jersey: Jersey$h,
	Jordan: Jordan$h,
	Kazakhstan: Kazakhstan$h,
	Kenya: Kenya$h,
	Kiribati: Kiribati$h,
	"Korea, Republic of": "韓國",
	Kuwait: Kuwait$h,
	Kyrgyzstan: Kyrgyzstan$h,
	"Lao People's Democratic Republic": "寮國人民民主共和國",
	Latvia: Latvia$h,
	Lebanon: Lebanon$h,
	Lesotho: Lesotho$h,
	Liberia: Liberia$h,
	"Libyan Arab Jamahiriya": "阿拉伯利比亞民眾國",
	Liechtenstein: Liechtenstein$h,
	Lithuania: Lithuania$h,
	Luxembourg: Luxembourg$h,
	Macao: Macao$h,
	"Macedonia, The Former Yugoslav Republic of": "馬其頓（前南斯拉夫共和國）",
	Madagascar: Madagascar$h,
	Malawi: Malawi$h,
	Malaysia: Malaysia$h,
	Maldives: Maldives$h,
	Mali: Mali$h,
	Malta: Malta$h,
	"Marshall Islands": "馬紹爾群島",
	Martinique: Martinique$h,
	Mauritania: Mauritania$h,
	Mauritius: Mauritius$h,
	Mayotte: Mayotte$h,
	Mexico: Mexico$h,
	"Micronesia, Federated States of": "密克羅尼西亞聯邦",
	"Moldova, Republic of": "摩爾多瓦共和國",
	Monaco: Monaco$h,
	Mongolia: Mongolia$h,
	Montenegro: Montenegro$h,
	Montserrat: Montserrat$h,
	Morocco: Morocco$h,
	Mozambique: Mozambique$h,
	Myanmar: Myanmar$h,
	Namibia: Namibia$h,
	Nauru: Nauru$h,
	Nepal: Nepal$h,
	Netherlands: Netherlands$h,
	"New Caledonia": "新喀裡多尼亞",
	"New Zealand": "紐西蘭",
	Nicaragua: Nicaragua$h,
	Niger: Niger$h,
	Nigeria: Nigeria$h,
	Niue: Niue$h,
	"Norfolk Island": "諾福克島",
	"Northern Mariana Islands": "北馬裡亞納群島",
	Norway: Norway$h,
	Oman: Oman$h,
	Pakistan: Pakistan$h,
	Palau: Palau$h,
	"Palestinian Territory, Occupied": "巴勒斯坦被佔領土",
	Panama: Panama$h,
	"Papua New Guinea": "巴布亞紐幾內亞",
	Paraguay: Paraguay$h,
	Peru: Peru$h,
	Philippines: Philippines$h,
	Pitcairn: Pitcairn$h,
	Poland: Poland$h,
	Portugal: Portugal$h,
	"Puerto Rico": "波多黎各",
	Qatar: Qatar$h,
	Romania: Romania$h,
	Russia: Russia$h,
	Rwanda: Rwanda$h,
	"Réunion": "團圓",
	"Saint Barthélemy": "聖巴泰勒米",
	"Saint Helena, Ascension and Tristan Da Cunha": "聖赫勒拿島、阿森松島和特里斯坦-達庫尼亞島",
	"Saint Kitts and Nevis": "聖克里斯多福及尼維斯",
	"Saint Lucia": "聖露西亞",
	"Saint Martin": "聖馬丁",
	"Saint Pierre and Miquelon": "聖皮埃爾和密克隆群島",
	"Saint Vincent and the Grenadines": "聖文森及格瑞那丁",
	Samoa: Samoa$h,
	"San Marino": "聖馬利諾",
	"Sao Tome and Principe": "聖多美和普林西比",
	"Saudi Arabia": "沙烏地阿拉伯",
	Senegal: Senegal$h,
	Serbia: Serbia$h,
	Seychelles: Seychelles$h,
	"Sierra Leone": "獅子山",
	Singapore: Singapore$h,
	Slovakia: Slovakia$h,
	Slovenia: Slovenia$h,
	"Solomon Islands": "索羅門群島",
	Somalia: Somalia$h,
	"South Africa": "南非",
	"South Georgia and the South Sandwich Islands": "南喬治亞島和南桑威奇群島",
	Spain: Spain$h,
	"Sri Lanka": "斯里蘭卡",
	Suriname: Suriname$h,
	"Svalbard and Jan Mayen": "斯瓦爾巴群島和揚馬延島",
	Swaziland: Swaziland$h,
	Sweden: Sweden$h,
	Switzerland: Switzerland$h,
	"Taiwan, Province of China": "中國台灣省",
	Tajikistan: Tajikistan$h,
	"Tanzania, United Republic of": "坦尚尼亞聯合共和國",
	Thailand: Thailand$h,
	"Timor-Leste": "東帝汶",
	Togo: Togo$h,
	Tokelau: Tokelau$h,
	Tonga: Tonga$h,
	"Trinidad and Tobago": "千里達及托巴哥",
	Tunisia: Tunisia$h,
	Turkey: Turkey$h,
	Turkmenistan: Turkmenistan$h,
	"Turks and Caicos Islands": "特克斯和凱科斯群島",
	Tuvalu: Tuvalu$h,
	Uganda: Uganda$h,
	Ukraine: Ukraine$h,
	"United Arab Emirates": "阿拉伯聯合大公國",
	"United Kingdom": "英國",
	"United States": "美國",
	Uruguay: Uruguay$h,
	Uzbekistan: Uzbekistan$h,
	Vanuatu: Vanuatu$h,
	"Venezuela, Bolivarian Republic of": "委內瑞拉玻利瓦爾共和國",
	"Viet Nam": "越南",
	"Virgin Islands, British": "英屬維京群島",
	"Virgin Islands, U.S.": "美屬維京群島",
	"Wallis and Futuna": "瓦利斯和富圖納群島",
	Yemen: Yemen$h,
	Zambia: Zambia$h,
	Zimbabwe: Zimbabwe$h,
	"Åland Islands": "奧蘭群島"
};

var t10 = {
	"No results found.": "未找到結果。"
};

var Done$O = "完毕";
var t11 = {
	Done: Done$O
};

var t12 = {
	"No results found.": "未找到结果。"
};

var t13 = {
	"Clear all": "全部清除",
	"No results found.": "未找到结果。"
};

var Passengers$g = "乘客";
var t14 = {
	"Number of passengers": "乘客人数",
	Passengers: Passengers$g
};

var t15 = {
	"No results found.": "未找到结果。"
};

var Blog$g = "博客";
var Careers$g = "职业发展";
var Contact$g = "接触";
var Coverage$g = "覆盖范围";
var Guides$g = "指南";
var Imprint$g = "版本说明";
var Parking$g = "停車處";
var eSIM$g = "eSIM";
var t16 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. 商标和品牌均归其各自所有者所有。",
	Blog: Blog$g,
	Careers: Careers$g,
	Contact: Contact$g,
	Coverage: Coverage$g,
	Guides: Guides$g,
	Imprint: Imprint$g,
	Parking: Parking$g,
	"Privacy Policy": "隐私政策",
	"Terms Of Services": "服务条款",
	eSIM: eSIM$g,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Mozio Inc. 的商标和品牌均为其各自所有者的财产。"
};

var Filters$g = "过滤器";
var t17 = {
	"Clear All": "清除全部",
	Filters: Filters$g,
	"Show results": "显示结果"
};

var Done$N = "完成";
var Hours$g = "小时";
var Minutes$g = "分钟";
var t18 = {
	"AM/PM": "上午/下午",
	Done: Done$N,
	Hours: Hours$g,
	Minutes: Minutes$g
};

var Done$M = "完毕";
var t19 = {
	Done: Done$M
};

var Afghanistan$g = "阿富汗";
var Albania$g = "阿尔巴尼亚";
var Algeria$g = "阿尔及利亚";
var AmericanSamoa$g = "美属萨摩亚";
var Andorra$g = "安道尔";
var Angola$g = "安哥拉";
var Anguilla$g = "安圭拉";
var Argentina$g = "阿根廷";
var Armenia$g = "亚美尼亚";
var Aruba$g = "阿鲁巴";
var Australia$g = "澳大利亚";
var Austria$g = "奥地利";
var Azerbaijan$g = "阿塞拜疆";
var Bahamas$g = "巴哈马";
var Bahrain$g = "巴林";
var Bangladesh$g = "孟加拉国";
var Barbados$g = "巴巴多斯";
var Belarus$g = "白俄罗斯";
var Belgium$g = "比利时";
var Belize$g = "伯利兹";
var Benin$g = "贝宁";
var Bermuda$g = "百慕大";
var Bhutan$g = "不丹";
var Botswana$g = "博茨瓦纳";
var Brazil$g = "巴西";
var Bulgaria$g = "保加利亚";
var Burundi$g = "布隆迪";
var Cambodia$g = "柬埔寨";
var Cameroon$g = "喀麦隆";
var Canada$g = "加拿大";
var Chad$g = "乍得";
var Chile$g = "智利";
var China$g = "中国";
var Colombia$g = "哥伦比亚";
var Comoros$g = "科摩罗";
var Congo$g = "刚果";
var Croatia$g = "克罗地亚";
var Curacao$g = "库拉索";
var Cyprus$g = "塞浦路斯";
var Denmark$g = "丹麦";
var Djibouti$g = "吉布提";
var Dominica$g = "多米尼加";
var Ecuador$g = "厄瓜多尔";
var Egypt$g = "埃及";
var Eritrea$g = "厄立特里亚";
var Estonia$g = "爱沙尼亚";
var Ethiopia$g = "埃塞俄比亚";
var Fiji$g = "斐济";
var Finland$g = "芬兰";
var France$g = "法国";
var Gabon$g = "加蓬";
var Gambia$g = "冈比亚";
var Georgia$g = "乔治亚州";
var Germany$g = "德国";
var Ghana$g = "加纳";
var Gibraltar$g = "直布罗陀";
var Greece$g = "希腊";
var Greenland$g = "格陵兰";
var Grenada$g = "格林纳达";
var Guadeloupe$g = "瓜德罗普岛";
var Guam$g = "关岛";
var Guatemala$g = "危地马拉";
var Guernsey$g = "根西岛";
var Guinea$g = "几内亚";
var Guyana$g = "圭亚那";
var Haiti$g = "海地";
var Honduras$g = "洪都拉斯";
var Hungary$g = "匈牙利";
var Iceland$g = "冰岛";
var India$g = "印度";
var Indonesia$g = "印度尼西亚";
var Iraq$g = "伊拉克";
var Ireland$g = "爱尔兰";
var Israel$g = "以色列";
var Italy$g = "意大利";
var Jamaica$g = "牙买加";
var Japan$g = "日本";
var Jersey$g = "球衣";
var Jordan$g = "约旦";
var Kazakhstan$g = "哈萨克斯坦";
var Kenya$g = "肯尼亚";
var Kiribati$g = "基里巴斯";
var Kuwait$g = "科威特";
var Kyrgyzstan$g = "吉尔吉斯斯坦";
var Latvia$g = "拉脱维亚";
var Lebanon$g = "黎巴嫩";
var Lesotho$g = "莱索托";
var Liberia$g = "利比里亚";
var Liechtenstein$g = "列支敦士登";
var Lithuania$g = "立陶宛";
var Luxembourg$g = "卢森堡";
var Macao$g = "澳门";
var Madagascar$g = "马达加斯加";
var Malawi$g = "马拉维";
var Malaysia$g = "马来西亚";
var Maldives$g = "马尔代夫";
var Mali$g = "马里";
var Malta$g = "马耳他";
var Martinique$g = "马提尼克岛";
var Mauritania$g = "毛里塔尼亚";
var Mauritius$g = "毛里求斯";
var Mayotte$g = "马约特岛";
var Mexico$g = "墨西哥";
var Monaco$g = "摩纳哥";
var Mongolia$g = "蒙古";
var Montenegro$g = "黑山";
var Montserrat$g = "蒙特塞拉特";
var Morocco$g = "摩洛哥";
var Mozambique$g = "莫桑比克";
var Myanmar$g = "缅甸";
var Namibia$g = "纳米比亚";
var Nauru$g = "瑙鲁";
var Nepal$g = "尼泊尔";
var Netherlands$g = "荷兰";
var Nicaragua$g = "尼加拉瓜";
var Niger$g = "尼日尔";
var Nigeria$g = "尼日利亚";
var Niue$g = "纽埃";
var Norway$g = "挪威";
var Oman$g = "阿曼";
var Pakistan$g = "巴基斯坦";
var Palau$g = "帕劳";
var Panama$g = "巴拿马";
var Paraguay$g = "巴拉圭";
var Peru$g = "秘鲁";
var Philippines$g = "菲律宾";
var Pitcairn$g = "皮特凯恩";
var Poland$g = "波兰";
var Portugal$g = "葡萄牙";
var Qatar$g = "卡塔尔";
var Romania$g = "罗马尼亚";
var Russia$g = "俄罗斯";
var Rwanda$g = "卢旺达";
var Samoa$g = "萨摩亚";
var Senegal$g = "塞内加尔";
var Serbia$g = "塞尔维亚";
var Seychelles$g = "塞舌尔";
var Singapore$g = "新加坡";
var Slovakia$g = "斯洛伐克";
var Slovenia$g = "斯洛文尼亚";
var Somalia$g = "索马里";
var Spain$g = "西班牙";
var Suriname$g = "苏里南";
var Swaziland$g = "斯威士兰";
var Sweden$g = "瑞典";
var Switzerland$g = "瑞士";
var Tajikistan$g = "塔吉克斯坦";
var Thailand$g = "泰国";
var Togo$g = "多哥";
var Tokelau$g = "托克劳";
var Tonga$g = "汤加";
var Tunisia$g = "突尼斯";
var Turkey$g = "火鸡";
var Turkmenistan$g = "土库曼斯坦";
var Tuvalu$g = "图瓦卢";
var Uganda$g = "乌干达";
var Ukraine$g = "乌克兰";
var Uruguay$g = "乌拉圭";
var Uzbekistan$g = "乌兹别克斯坦";
var Vanuatu$g = "瓦努阿图";
var Yemen$g = "也门";
var Zambia$g = "赞比亚";
var Zimbabwe$g = "津巴布韦";
var t20 = {
	Afghanistan: Afghanistan$g,
	Albania: Albania$g,
	Algeria: Algeria$g,
	AmericanSamoa: AmericanSamoa$g,
	Andorra: Andorra$g,
	Angola: Angola$g,
	Anguilla: Anguilla$g,
	"Antigua and Barbuda": "安提瓜和巴布达",
	Argentina: Argentina$g,
	Armenia: Armenia$g,
	Aruba: Aruba$g,
	Australia: Australia$g,
	Austria: Austria$g,
	Azerbaijan: Azerbaijan$g,
	Bahamas: Bahamas$g,
	Bahrain: Bahrain$g,
	Bangladesh: Bangladesh$g,
	Barbados: Barbados$g,
	Belarus: Belarus$g,
	Belgium: Belgium$g,
	Belize: Belize$g,
	Benin: Benin$g,
	Bermuda: Bermuda$g,
	Bhutan: Bhutan$g,
	"Bolivia, Plurinational State of": "玻利维亚多民族国",
	"Bosnia and Herzegovina": "波斯尼亚和黑塞哥维那",
	Botswana: Botswana$g,
	Brazil: Brazil$g,
	"British Indian Ocean Territory": "英属印度洋领地",
	"Brunei Darussalam": "文莱达鲁萨兰国",
	Bulgaria: Bulgaria$g,
	"Burkina Faso": "布基纳法索",
	Burundi: Burundi$g,
	Cambodia: Cambodia$g,
	Cameroon: Cameroon$g,
	Canada: Canada$g,
	"Cape Verde": "佛得角",
	"Cayman Islands": "开曼群岛",
	"Central African Republic": "中非共和国",
	Chad: Chad$g,
	Chile: Chile$g,
	China: China$g,
	"Christmas Island": "圣诞岛",
	"Cocos (Keeling) Islands": "科科斯（基林）群岛",
	Colombia: Colombia$g,
	Comoros: Comoros$g,
	Congo: Congo$g,
	"Congo, The Democratic Republic of the": "刚果民主共和国",
	"Cook Islands": "库克群岛",
	"Costa Rica": "哥斯达黎加",
	"Cote d'Ivoire": "科特迪瓦",
	Croatia: Croatia$g,
	Curacao: Curacao$g,
	Cyprus: Cyprus$g,
	"Czech Republic": "捷克共和国",
	Denmark: Denmark$g,
	Djibouti: Djibouti$g,
	Dominica: Dominica$g,
	"Dominican Republic": "多明尼加共和国",
	Ecuador: Ecuador$g,
	Egypt: Egypt$g,
	"El Salvador": "萨尔瓦多",
	"Equatorial Guinea": "赤道几内亚",
	Eritrea: Eritrea$g,
	Estonia: Estonia$g,
	Ethiopia: Ethiopia$g,
	"Falkland Islands (Malvinas)": "福克兰群岛（马尔维纳斯群岛）",
	"Faroe Islands": "法罗群岛",
	Fiji: Fiji$g,
	Finland: Finland$g,
	France: France$g,
	"French Guiana": "法属圭亚那",
	"French Polynesia": "法属波利尼西亚",
	Gabon: Gabon$g,
	Gambia: Gambia$g,
	Georgia: Georgia$g,
	Germany: Germany$g,
	Ghana: Ghana$g,
	Gibraltar: Gibraltar$g,
	Greece: Greece$g,
	Greenland: Greenland$g,
	Grenada: Grenada$g,
	Guadeloupe: Guadeloupe$g,
	Guam: Guam$g,
	Guatemala: Guatemala$g,
	Guernsey: Guernsey$g,
	Guinea: Guinea$g,
	"Guinea-Bissau": "几内亚比绍",
	Guyana: Guyana$g,
	Haiti: Haiti$g,
	"Holy See (Vatican City State)": "梵蒂冈城国",
	Honduras: Honduras$g,
	"Hong Kong": "香港",
	Hungary: Hungary$g,
	Iceland: Iceland$g,
	India: India$g,
	Indonesia: Indonesia$g,
	Iraq: Iraq$g,
	Ireland: Ireland$g,
	"Isle of Man": "马恩岛",
	Israel: Israel$g,
	Italy: Italy$g,
	Jamaica: Jamaica$g,
	Japan: Japan$g,
	Jersey: Jersey$g,
	Jordan: Jordan$g,
	Kazakhstan: Kazakhstan$g,
	Kenya: Kenya$g,
	Kiribati: Kiribati$g,
	"Korea, Republic of": "韩国",
	Kuwait: Kuwait$g,
	Kyrgyzstan: Kyrgyzstan$g,
	"Lao People's Democratic Republic": "老挝人民民主共和国",
	Latvia: Latvia$g,
	Lebanon: Lebanon$g,
	Lesotho: Lesotho$g,
	Liberia: Liberia$g,
	"Libyan Arab Jamahiriya": "阿拉伯利比亚民众国",
	Liechtenstein: Liechtenstein$g,
	Lithuania: Lithuania$g,
	Luxembourg: Luxembourg$g,
	Macao: Macao$g,
	"Macedonia, The Former Yugoslav Republic of": "马其顿（前南斯拉夫共和国）",
	Madagascar: Madagascar$g,
	Malawi: Malawi$g,
	Malaysia: Malaysia$g,
	Maldives: Maldives$g,
	Mali: Mali$g,
	Malta: Malta$g,
	"Marshall Islands": "马绍尔群岛",
	Martinique: Martinique$g,
	Mauritania: Mauritania$g,
	Mauritius: Mauritius$g,
	Mayotte: Mayotte$g,
	Mexico: Mexico$g,
	"Micronesia, Federated States of": "密克罗尼西亚联邦",
	"Moldova, Republic of": "摩尔多瓦共和国",
	Monaco: Monaco$g,
	Mongolia: Mongolia$g,
	Montenegro: Montenegro$g,
	Montserrat: Montserrat$g,
	Morocco: Morocco$g,
	Mozambique: Mozambique$g,
	Myanmar: Myanmar$g,
	Namibia: Namibia$g,
	Nauru: Nauru$g,
	Nepal: Nepal$g,
	Netherlands: Netherlands$g,
	"New Caledonia": "新喀里多尼亚",
	"New Zealand": "新西兰",
	Nicaragua: Nicaragua$g,
	Niger: Niger$g,
	Nigeria: Nigeria$g,
	Niue: Niue$g,
	"Norfolk Island": "诺福克岛",
	"Northern Mariana Islands": "北马里亚纳群岛",
	Norway: Norway$g,
	Oman: Oman$g,
	Pakistan: Pakistan$g,
	Palau: Palau$g,
	"Palestinian Territory, Occupied": "巴勒斯坦被占领土",
	Panama: Panama$g,
	"Papua New Guinea": "巴布亚新几内亚",
	Paraguay: Paraguay$g,
	Peru: Peru$g,
	Philippines: Philippines$g,
	Pitcairn: Pitcairn$g,
	Poland: Poland$g,
	Portugal: Portugal$g,
	"Puerto Rico": "波多黎各",
	Qatar: Qatar$g,
	Romania: Romania$g,
	Russia: Russia$g,
	Rwanda: Rwanda$g,
	"Réunion": "团圆",
	"Saint Barthélemy": "圣巴泰勒米",
	"Saint Helena, Ascension and Tristan Da Cunha": "圣赫勒拿岛、阿森松岛和特里斯坦-达库尼亚岛",
	"Saint Kitts and Nevis": "圣基茨和尼维斯",
	"Saint Lucia": "圣卢西亚",
	"Saint Martin": "圣马丁",
	"Saint Pierre and Miquelon": "圣皮埃尔和密克隆群岛",
	"Saint Vincent and the Grenadines": "圣文森特和格林纳丁斯",
	Samoa: Samoa$g,
	"San Marino": "圣马力诺",
	"Sao Tome and Principe": "圣多美和普林西比",
	"Saudi Arabia": "沙特阿拉伯",
	Senegal: Senegal$g,
	Serbia: Serbia$g,
	Seychelles: Seychelles$g,
	"Sierra Leone": "塞拉利昂",
	Singapore: Singapore$g,
	Slovakia: Slovakia$g,
	Slovenia: Slovenia$g,
	"Solomon Islands": "所罗门群岛",
	Somalia: Somalia$g,
	"South Africa": "南非",
	"South Georgia and the South Sandwich Islands": "南乔治亚岛和南桑威奇群岛",
	Spain: Spain$g,
	"Sri Lanka": "斯里兰卡",
	Suriname: Suriname$g,
	"Svalbard and Jan Mayen": "斯瓦尔巴群岛和扬马延岛",
	Swaziland: Swaziland$g,
	Sweden: Sweden$g,
	Switzerland: Switzerland$g,
	"Taiwan, Province of China": "中国台湾省",
	Tajikistan: Tajikistan$g,
	"Tanzania, United Republic of": "坦桑尼亚联合共和国",
	Thailand: Thailand$g,
	"Timor-Leste": "东帝汶",
	Togo: Togo$g,
	Tokelau: Tokelau$g,
	Tonga: Tonga$g,
	"Trinidad and Tobago": "特立尼达和多巴哥",
	Tunisia: Tunisia$g,
	Turkey: Turkey$g,
	Turkmenistan: Turkmenistan$g,
	"Turks and Caicos Islands": "特克斯和凯科斯群岛",
	Tuvalu: Tuvalu$g,
	Uganda: Uganda$g,
	Ukraine: Ukraine$g,
	"United Arab Emirates": "阿拉伯联合酋长国",
	"United Kingdom": "英国",
	"United States": "美国",
	Uruguay: Uruguay$g,
	Uzbekistan: Uzbekistan$g,
	Vanuatu: Vanuatu$g,
	"Venezuela, Bolivarian Republic of": "委内瑞拉玻利瓦尔共和国",
	"Viet Nam": "越南",
	"Virgin Islands, British": "英属维尔京群岛",
	"Virgin Islands, U.S.": "美属维尔京群岛",
	"Wallis and Futuna": "瓦利斯和富图纳群岛",
	Yemen: Yemen$g,
	Zambia: Zambia$g,
	Zimbabwe: Zimbabwe$g,
	"Åland Islands": "奥兰群岛"
};

var t21 = {
	"No results found.": "未找到结果。"
};

var Done$L = "Xong";
var t22 = {
	Done: Done$L
};

var t23 = {
	"No results found.": "Không tìm thấy kết quả nào."
};

var t24 = {
	"Clear all": "Xóa tất cả",
	"No results found.": "Không tìm thấy kết quả nào."
};

var Passengers$f = "Hành khách";
var t25 = {
	"Number of passengers": "Số lượng hành khách",
	Passengers: Passengers$f
};

var t26 = {
	"No results found.": "Không tìm thấy kết quả nào."
};

var Blog$f = "Blog";
var Careers$f = "Sự nghiệp";
var Contact$f = "Liên hệ";
var Coverage$f = "Phạm vi phủ sóng";
var Guides$f = "Hướng dẫn";
var Imprint$f = "Dấu ấn";
var Parking$f = "Bãi đậu xe";
var eSIM$f = "eSIM";
var t27 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. Nhãn hiệu và thương hiệu là tài sản của chủ sở hữu tương ứng.",
	Blog: Blog$f,
	Careers: Careers$f,
	Contact: Contact$f,
	Coverage: Coverage$f,
	Guides: Guides$f,
	Imprint: Imprint$f,
	Parking: Parking$f,
	"Privacy Policy": "Chính sách bảo mật",
	"Terms Of Services": "Điều khoản dịch vụ",
	eSIM: eSIM$f,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Các nhãn hiệu và thương hiệu của Mozio Inc. là tài sản của các chủ sở hữu tương ứng."
};

var Filters$f = "Bộ lọc";
var t28 = {
	"Clear All": "Xóa tất cả",
	Filters: Filters$f,
	"Show results": "Hiển thị kết quả"
};

var Done$K = "Xong";
var Hours$f = "Giờ";
var Minutes$f = "Phút";
var t29 = {
	"AM/PM": "AM/PM",
	Done: Done$K,
	Hours: Hours$f,
	Minutes: Minutes$f
};

var Done$J = "Xong";
var t30 = {
	Done: Done$J
};

var Afghanistan$f = "Afghanistan";
var Albania$f = "Albania";
var Algeria$f = "Algeria";
var AmericanSamoa$f = "Samoa thuộc Mỹ";
var Andorra$f = "Andorra";
var Angola$f = "Angola";
var Anguilla$f = "Anguilla";
var Argentina$f = "Argentina";
var Armenia$f = "Armenia";
var Aruba$f = "Aruba";
var Australia$f = "Úc";
var Austria$f = "Áo";
var Azerbaijan$f = "Azerbaijan";
var Bahamas$f = "Bahamas";
var Bahrain$f = "Bahrain";
var Bangladesh$f = "Bangladesh";
var Barbados$f = "Barbados";
var Belarus$f = "Belarus";
var Belgium$f = "Bỉ";
var Belize$f = "Belize";
var Benin$f = "Bénin";
var Bermuda$f = "Bermuda";
var Bhutan$f = "Bhutan";
var Botswana$f = "Botswana";
var Brazil$f = "Brazil";
var Bulgaria$f = "Bulgaria";
var Burundi$f = "Burundi";
var Cambodia$f = "Campuchia";
var Cameroon$f = "Cameroon";
var Canada$f = "Canada";
var Chad$f = "Chad";
var Chile$f = "Chile";
var China$f = "Trung Quốc";
var Colombia$f = "Colombia";
var Comoros$f = "Comoros";
var Congo$f = "Congo";
var Croatia$f = "Croatia";
var Curacao$f = "Curacao";
var Cyprus$f = "Síp";
var Denmark$f = "Đan Mạch";
var Djibouti$f = "Djibouti";
var Dominica$f = "Dominica";
var Ecuador$f = "Ecuador";
var Egypt$f = "Ai Cập";
var Eritrea$f = "Eritrea";
var Estonia$f = "Estonia";
var Ethiopia$f = "Ethiopia";
var Fiji$f = "Fiji";
var Finland$f = "Phần Lan";
var France$f = "Pháp";
var Gabon$f = "Gabon";
var Gambia$f = "Gambia";
var Georgia$f = "Georgia";
var Germany$f = "Đức";
var Ghana$f = "Ghana";
var Gibraltar$f = "Gibraltar";
var Greece$f = "Hy Lạp";
var Greenland$f = "Greenland";
var Grenada$f = "Grenada";
var Guadeloupe$f = "Guadeloupe";
var Guam$f = "Guam";
var Guatemala$f = "Guatemala";
var Guernsey$f = "Guernsey";
var Guinea$f = "Guinea";
var Guyana$f = "Guyana";
var Haiti$f = "Haiti";
var Honduras$f = "Honduras";
var Hungary$f = "Hungary";
var Iceland$f = "Iceland";
var India$f = "Ấn Độ";
var Indonesia$f = "Indonesia";
var Iraq$f = "Iraq";
var Ireland$f = "Ai-len";
var Israel$f = "Israel";
var Italy$f = "Ý";
var Jamaica$f = "Jamaica";
var Japan$f = "Nhật Bản";
var Jersey$f = "Jersey";
var Jordan$f = "Jordan";
var Kazakhstan$f = "Kazakhstan";
var Kenya$f = "Kenya";
var Kiribati$f = "Kiribati";
var Kuwait$f = "Cô-oét";
var Kyrgyzstan$f = "Kyrgyzstan";
var Latvia$f = "Latvia";
var Lebanon$f = "Li-băng";
var Lesotho$f = "Lesotho";
var Liberia$f = "Liberia";
var Liechtenstein$f = "Liechtenstein";
var Lithuania$f = "Litva";
var Luxembourg$f = "Luxembourg";
var Macao$f = "Ma Cao";
var Madagascar$f = "Madagascar";
var Malawi$f = "Malawi";
var Malaysia$f = "Malaysia";
var Maldives$f = "Maldives";
var Mali$f = "Mali";
var Malta$f = "Malta";
var Martinique$f = "Martinique";
var Mauritania$f = "Mauritania";
var Mauritius$f = "Mauritius";
var Mayotte$f = "Mayotte";
var Mexico$f = "Mexico";
var Monaco$f = "Monaco";
var Mongolia$f = "Mông Cổ";
var Montenegro$f = "Montenegro";
var Montserrat$f = "Montserrat";
var Morocco$f = "Ma-rốc";
var Mozambique$f = "Mozambique";
var Myanmar$f = "Myanmar";
var Namibia$f = "Namibia";
var Nauru$f = "Nauru";
var Nepal$f = "Nepal";
var Netherlands$f = "Hà Lan";
var Nicaragua$f = "Nicaragua";
var Niger$f = "Niger";
var Nigeria$f = "Nigeria";
var Niue$f = "Niue";
var Norway$f = "Na Uy";
var Oman$f = "Ô-man";
var Pakistan$f = "Pakistan";
var Palau$f = "Palau";
var Panama$f = "Panama";
var Paraguay$f = "Paraguay";
var Peru$f = "Peru";
var Philippines$f = "Philippines";
var Pitcairn$f = "Pitcairn";
var Poland$f = "Ba Lan";
var Portugal$f = "Bồ Đào Nha";
var Qatar$f = "Qatar";
var Romania$f = "Rumani";
var Russia$f = "Nga";
var Rwanda$f = "Rwanda";
var Samoa$f = "Samoa";
var Senegal$f = "Senegal";
var Serbia$f = "Serbia";
var Seychelles$f = "Seychelles";
var Singapore$f = "Singapore";
var Slovakia$f = "Slovakia";
var Slovenia$f = "Slovenia";
var Somalia$f = "Somalia";
var Spain$f = "Tây ban nha";
var Suriname$f = "Suriname";
var Swaziland$f = "Swaziland";
var Sweden$f = "Thụy Điển";
var Switzerland$f = "Thụy Sĩ";
var Tajikistan$f = "Tajikistan";
var Thailand$f = "Thái Lan";
var Togo$f = "Togo";
var Tokelau$f = "Tokelau";
var Tonga$f = "Tonga";
var Tunisia$f = "Tunisia";
var Turkey$f = "Thổ Nhĩ Kỳ";
var Turkmenistan$f = "Turkmenistan";
var Tuvalu$f = "Tuvalu";
var Uganda$f = "Uganda";
var Ukraine$f = "Ukraina";
var Uruguay$f = "Uruguay";
var Uzbekistan$f = "Uzbekistan";
var Vanuatu$f = "Vanuatu";
var Yemen$f = "Yemen";
var Zambia$f = "Zambia";
var Zimbabwe$f = "Zimbabwe";
var t31 = {
	Afghanistan: Afghanistan$f,
	Albania: Albania$f,
	Algeria: Algeria$f,
	AmericanSamoa: AmericanSamoa$f,
	Andorra: Andorra$f,
	Angola: Angola$f,
	Anguilla: Anguilla$f,
	"Antigua and Barbuda": "Antigua và Barbuda",
	Argentina: Argentina$f,
	Armenia: Armenia$f,
	Aruba: Aruba$f,
	Australia: Australia$f,
	Austria: Austria$f,
	Azerbaijan: Azerbaijan$f,
	Bahamas: Bahamas$f,
	Bahrain: Bahrain$f,
	Bangladesh: Bangladesh$f,
	Barbados: Barbados$f,
	Belarus: Belarus$f,
	Belgium: Belgium$f,
	Belize: Belize$f,
	Benin: Benin$f,
	Bermuda: Bermuda$f,
	Bhutan: Bhutan$f,
	"Bolivia, Plurinational State of": "Bolivia, Nhà nước đa dân tộc",
	"Bosnia and Herzegovina": "Bosnia và Herzegovina",
	Botswana: Botswana$f,
	Brazil: Brazil$f,
	"British Indian Ocean Territory": "Lãnh thổ Ấn Độ Dương thuộc Anh",
	"Brunei Darussalam": "Brunei Darussalam",
	Bulgaria: Bulgaria$f,
	"Burkina Faso": "Burkina Faso",
	Burundi: Burundi$f,
	Cambodia: Cambodia$f,
	Cameroon: Cameroon$f,
	Canada: Canada$f,
	"Cape Verde": "Cape Verde",
	"Cayman Islands": "Quần đảo Cayman",
	"Central African Republic": "Cộng hòa Trung Phi",
	Chad: Chad$f,
	Chile: Chile$f,
	China: China$f,
	"Christmas Island": "Đảo Giáng Sinh",
	"Cocos (Keeling) Islands": "Quần đảo Cocos (Keeling)",
	Colombia: Colombia$f,
	Comoros: Comoros$f,
	Congo: Congo$f,
	"Congo, The Democratic Republic of the": "Congo, Cộng hòa Dân chủ",
	"Cook Islands": "Quần đảo Cook",
	"Costa Rica": "Costa Rica",
	"Cote d'Ivoire": "Bờ Biển Ngà",
	Croatia: Croatia$f,
	Curacao: Curacao$f,
	Cyprus: Cyprus$f,
	"Czech Republic": "Cộng hòa Séc",
	Denmark: Denmark$f,
	Djibouti: Djibouti$f,
	Dominica: Dominica$f,
	"Dominican Republic": "Cộng hòa Dominica",
	Ecuador: Ecuador$f,
	Egypt: Egypt$f,
	"El Salvador": "El Salvador",
	"Equatorial Guinea": "Guinea Xích Đạo",
	Eritrea: Eritrea$f,
	Estonia: Estonia$f,
	Ethiopia: Ethiopia$f,
	"Falkland Islands (Malvinas)": "Quần đảo Falkland (Malvinas)",
	"Faroe Islands": "Quần đảo Faroe",
	Fiji: Fiji$f,
	Finland: Finland$f,
	France: France$f,
	"French Guiana": "Guiana thuộc Pháp",
	"French Polynesia": "Polynésie thuộc Pháp",
	Gabon: Gabon$f,
	Gambia: Gambia$f,
	Georgia: Georgia$f,
	Germany: Germany$f,
	Ghana: Ghana$f,
	Gibraltar: Gibraltar$f,
	Greece: Greece$f,
	Greenland: Greenland$f,
	Grenada: Grenada$f,
	Guadeloupe: Guadeloupe$f,
	Guam: Guam$f,
	Guatemala: Guatemala$f,
	Guernsey: Guernsey$f,
	Guinea: Guinea$f,
	"Guinea-Bissau": "Guinea-Bissau",
	Guyana: Guyana$f,
	Haiti: Haiti$f,
	"Holy See (Vatican City State)": "Tòa thánh (Thành phố Vatican)",
	Honduras: Honduras$f,
	"Hong Kong": "Hồng Kông",
	Hungary: Hungary$f,
	Iceland: Iceland$f,
	India: India$f,
	Indonesia: Indonesia$f,
	Iraq: Iraq$f,
	Ireland: Ireland$f,
	"Isle of Man": "Đảo Man",
	Israel: Israel$f,
	Italy: Italy$f,
	Jamaica: Jamaica$f,
	Japan: Japan$f,
	Jersey: Jersey$f,
	Jordan: Jordan$f,
	Kazakhstan: Kazakhstan$f,
	Kenya: Kenya$f,
	Kiribati: Kiribati$f,
	"Korea, Republic of": "Hàn Quốc, Cộng hòa",
	Kuwait: Kuwait$f,
	Kyrgyzstan: Kyrgyzstan$f,
	"Lao People's Democratic Republic": "Cộng hòa Dân chủ Nhân dân Lào",
	Latvia: Latvia$f,
	Lebanon: Lebanon$f,
	Lesotho: Lesotho$f,
	Liberia: Liberia$f,
	"Libyan Arab Jamahiriya": "Cộng hòa Ả Rập Libya",
	Liechtenstein: Liechtenstein$f,
	Lithuania: Lithuania$f,
	Luxembourg: Luxembourg$f,
	Macao: Macao$f,
	"Macedonia, The Former Yugoslav Republic of": "Macedonia, Cộng hòa Nam Tư cũ",
	Madagascar: Madagascar$f,
	Malawi: Malawi$f,
	Malaysia: Malaysia$f,
	Maldives: Maldives$f,
	Mali: Mali$f,
	Malta: Malta$f,
	"Marshall Islands": "Quần đảo Marshall",
	Martinique: Martinique$f,
	Mauritania: Mauritania$f,
	Mauritius: Mauritius$f,
	Mayotte: Mayotte$f,
	Mexico: Mexico$f,
	"Micronesia, Federated States of": "Micronesia, Liên bang các quốc gia",
	"Moldova, Republic of": "Moldova, Cộng hòa",
	Monaco: Monaco$f,
	Mongolia: Mongolia$f,
	Montenegro: Montenegro$f,
	Montserrat: Montserrat$f,
	Morocco: Morocco$f,
	Mozambique: Mozambique$f,
	Myanmar: Myanmar$f,
	Namibia: Namibia$f,
	Nauru: Nauru$f,
	Nepal: Nepal$f,
	Netherlands: Netherlands$f,
	"New Caledonia": "New Caledonia",
	"New Zealand": "New Zealand",
	Nicaragua: Nicaragua$f,
	Niger: Niger$f,
	Nigeria: Nigeria$f,
	Niue: Niue$f,
	"Norfolk Island": "Đảo Norfolk",
	"Northern Mariana Islands": "Quần đảo Bắc Mariana",
	Norway: Norway$f,
	Oman: Oman$f,
	Pakistan: Pakistan$f,
	Palau: Palau$f,
	"Palestinian Territory, Occupied": "Lãnh thổ Palestine, bị chiếm đóng",
	Panama: Panama$f,
	"Papua New Guinea": "Papua New Guinea",
	Paraguay: Paraguay$f,
	Peru: Peru$f,
	Philippines: Philippines$f,
	Pitcairn: Pitcairn$f,
	Poland: Poland$f,
	Portugal: Portugal$f,
	"Puerto Rico": "Puerto Rico",
	Qatar: Qatar$f,
	Romania: Romania$f,
	Russia: Russia$f,
	Rwanda: Rwanda$f,
	"Réunion": "Đoàn tụ",
	"Saint Barthélemy": "Thánh Barthélemy",
	"Saint Helena, Ascension and Tristan Da Cunha": "Saint Helena, Ascension và Tristan Da Cunha",
	"Saint Kitts and Nevis": "Saint Kitts và Nevis",
	"Saint Lucia": "Thánh Lucia",
	"Saint Martin": "Thánh Martin",
	"Saint Pierre and Miquelon": "Saint Pierre và Miquelon",
	"Saint Vincent and the Grenadines": "Saint Vincent và Grenadines",
	Samoa: Samoa$f,
	"San Marino": "San Marino",
	"Sao Tome and Principe": "Sao Tome và Principe",
	"Saudi Arabia": "Ả Rập Xê Út",
	Senegal: Senegal$f,
	Serbia: Serbia$f,
	Seychelles: Seychelles$f,
	"Sierra Leone": "Sierra Leone",
	Singapore: Singapore$f,
	Slovakia: Slovakia$f,
	Slovenia: Slovenia$f,
	"Solomon Islands": "Quần đảo Solomon",
	Somalia: Somalia$f,
	"South Africa": "Nam Phi",
	"South Georgia and the South Sandwich Islands": "Nam Georgia và Quần đảo Nam Sandwich",
	Spain: Spain$f,
	"Sri Lanka": "Sri Lanka",
	Suriname: Suriname$f,
	"Svalbard and Jan Mayen": "Svalbard và Jan Mayen",
	Swaziland: Swaziland$f,
	Sweden: Sweden$f,
	Switzerland: Switzerland$f,
	"Taiwan, Province of China": "Đài Loan, Tỉnh của Trung Quốc",
	Tajikistan: Tajikistan$f,
	"Tanzania, United Republic of": "Tanzania, Cộng hòa Thống nhất",
	Thailand: Thailand$f,
	"Timor-Leste": "Đông Timor",
	Togo: Togo$f,
	Tokelau: Tokelau$f,
	Tonga: Tonga$f,
	"Trinidad and Tobago": "Trinidad và Tobago",
	Tunisia: Tunisia$f,
	Turkey: Turkey$f,
	Turkmenistan: Turkmenistan$f,
	"Turks and Caicos Islands": "Quần đảo Turks và Caicos",
	Tuvalu: Tuvalu$f,
	Uganda: Uganda$f,
	Ukraine: Ukraine$f,
	"United Arab Emirates": "Các Tiểu vương quốc Ả Rập Thống nhất",
	"United Kingdom": "Vương quốc Anh",
	"United States": "Hoa Kỳ",
	Uruguay: Uruguay$f,
	Uzbekistan: Uzbekistan$f,
	Vanuatu: Vanuatu$f,
	"Venezuela, Bolivarian Republic of": "Venezuela, Cộng hòa Bolivar",
	"Viet Nam": "Việt Nam",
	"Virgin Islands, British": "Quần đảo Virgin, Anh",
	"Virgin Islands, U.S.": "Quần đảo Virgin, Hoa Kỳ",
	"Wallis and Futuna": "Wallis và Futuna",
	Yemen: Yemen$f,
	Zambia: Zambia$f,
	Zimbabwe: Zimbabwe$f,
	"Åland Islands": "Quần đảo Åland"
};

var t32 = {
	"No results found.": "Không tìm thấy kết quả nào."
};

var Done$I = "เสร็จแล้ว";
var t33 = {
	Done: Done$I
};

var t34 = {
	"No results found.": "ไม่พบผลลัพธ์"
};

var t35 = {
	"Clear all": "ล้างทั้งหมด",
	"No results found.": "ไม่พบผลลัพธ์"
};

var Passengers$e = "ผู้โดยสาร";
var t36 = {
	"Number of passengers": "จำนวนผู้โดยสาร",
	Passengers: Passengers$e
};

var t37 = {
	"No results found.": "ไม่พบผลลัพธ์"
};

var Blog$e = "บล็อก";
var Careers$e = "อาชีพ";
var Contact$e = "ติดต่อ";
var Coverage$e = "ความคุ้มครอง";
var Guides$e = "คำแนะนำ";
var Imprint$e = "สำนักพิมพ์";
var Parking$e = "ที่จอดรถ";
var eSIM$e = "อีซิม";
var t38 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. เครื่องหมายการค้าและแบรนด์เป็นทรัพย์สินของเจ้าของที่เกี่ยวข้อง",
	Blog: Blog$e,
	Careers: Careers$e,
	Contact: Contact$e,
	Coverage: Coverage$e,
	Guides: Guides$e,
	Imprint: Imprint$e,
	Parking: Parking$e,
	"Privacy Policy": "นโยบายความเป็นส่วนตัว",
	"Terms Of Services": "เงื่อนไขการให้บริการ",
	eSIM: eSIM$e,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} เครื่องหมายการค้าและตราสินค้าของ Mozio Inc. เป็นทรัพย์สินของเจ้าของที่เกี่ยวข้อง"
};

var Filters$e = "ตัวกรอง";
var t39 = {
	"Clear All": "ล้างทั้งหมด",
	Filters: Filters$e,
	"Show results": "แสดงผลลัพธ์"
};

var Done$H = "เสร็จสิ้น";
var Hours$e = "ชั่วโมง";
var Minutes$e = "นาที";
var t40 = {
	"AM/PM": "AM/PM",
	Done: Done$H,
	Hours: Hours$e,
	Minutes: Minutes$e
};

var Done$G = "เสร็จแล้ว";
var t41 = {
	Done: Done$G
};

var Afghanistan$e = "อัฟกานิสถาน";
var Albania$e = "แอลเบเนีย";
var Algeria$e = "แอลจีเรีย";
var AmericanSamoa$e = "อเมริกันซามัว";
var Andorra$e = "อันดอร์รา";
var Angola$e = "แองโกลา";
var Anguilla$e = "แองกวิลลา";
var Argentina$e = "อาร์เจนตินา";
var Armenia$e = "อาร์เมเนีย";
var Aruba$e = "อารูบา";
var Australia$e = "ออสเตรเลีย";
var Austria$e = "ออสเตรีย";
var Azerbaijan$e = "อาเซอร์ไบจาน";
var Bahamas$e = "บาฮามาส";
var Bahrain$e = "บาห์เรน";
var Bangladesh$e = "บังคลาเทศ";
var Barbados$e = "บาร์เบโดส";
var Belarus$e = "เบลารุส";
var Belgium$e = "เบลเยียม";
var Belize$e = "เบลีซ";
var Benin$e = "เบนิน";
var Bermuda$e = "เบอร์มิวดา";
var Bhutan$e = "ภูฏาน";
var Botswana$e = "บอตสวานา";
var Brazil$e = "บราซิล";
var Bulgaria$e = "บัลแกเรีย";
var Burundi$e = "บุรุนดี";
var Cambodia$e = "กัมพูชา";
var Cameroon$e = "แคเมอรูน";
var Canada$e = "แคนาดา";
var Chad$e = "แชด";
var Chile$e = "ชิลี";
var China$e = "จีน";
var Colombia$e = "โคลอมเบีย";
var Comoros$e = "คอโมโรส";
var Congo$e = "คองโก";
var Croatia$e = "โครเอเชีย";
var Curacao$e = "คูราเซา";
var Cyprus$e = "ไซปรัส";
var Denmark$e = "เดนมาร์ก";
var Djibouti$e = "จิบูตี";
var Dominica$e = "โดมินิกา";
var Ecuador$e = "เอกวาดอร์";
var Egypt$e = "อียิปต์";
var Eritrea$e = "เอริเทรีย";
var Estonia$e = "เอสโตเนีย";
var Ethiopia$e = "เอธิโอเปีย";
var Fiji$e = "ฟิจิ";
var Finland$e = "ฟินแลนด์";
var France$e = "ฝรั่งเศส";
var Gabon$e = "กาบอง";
var Gambia$e = "แกมเบีย";
var Georgia$e = "จอร์เจีย";
var Germany$e = "เยอรมนี";
var Ghana$e = "กานา";
var Gibraltar$e = "ยิบรอลตาร์";
var Greece$e = "กรีซ";
var Greenland$e = "กรีนแลนด์";
var Grenada$e = "เกรเนดา";
var Guadeloupe$e = "กัวเดอลูป";
var Guam$e = "กวม";
var Guatemala$e = "กัวเตมาลา";
var Guernsey$e = "เกิร์นซีย์";
var Guinea$e = "กินี";
var Guyana$e = "กายอานา";
var Haiti$e = "เฮติ";
var Honduras$e = "ฮอนดูรัส";
var Hungary$e = "ฮังการี";
var Iceland$e = "ไอซ์แลนด์";
var India$e = "อินเดีย";
var Indonesia$e = "อินโดนีเซีย";
var Iraq$e = "อิรัก";
var Ireland$e = "ไอร์แลนด์";
var Israel$e = "อิสราเอล";
var Italy$e = "อิตาลี";
var Jamaica$e = "จาเมกา";
var Japan$e = "ประเทศญี่ปุ่น";
var Jersey$e = "เสื้อเจอร์ซีย์";
var Jordan$e = "จอร์แดน";
var Kazakhstan$e = "คาซัคสถาน";
var Kenya$e = "เคนยา";
var Kiribati$e = "คิริบาติ";
var Kuwait$e = "คูเวต";
var Kyrgyzstan$e = "คีร์กีซสถาน";
var Latvia$e = "ลัตเวีย";
var Lebanon$e = "เลบานอน";
var Lesotho$e = "เลโซโท";
var Liberia$e = "ไลบีเรีย";
var Liechtenstein$e = "ลิกเตนสไตน์";
var Lithuania$e = "ลิทัวเนีย";
var Luxembourg$e = "ลักเซมเบิร์ก";
var Macao$e = "มาเก๊า";
var Madagascar$e = "มาดากัสการ์";
var Malawi$e = "มาลาวี";
var Malaysia$e = "มาเลเซีย";
var Maldives$e = "มัลดีฟส์";
var Mali$e = "มาลี";
var Malta$e = "มอลตา";
var Martinique$e = "มาร์ตินีก";
var Mauritania$e = "ประเทศมอริเตเนีย";
var Mauritius$e = "มอริเชียส";
var Mayotte$e = "มายอต";
var Mexico$e = "เม็กซิโก";
var Monaco$e = "โมนาโก";
var Mongolia$e = "มองโกเลีย";
var Montenegro$e = "มอนเตเนโกร";
var Montserrat$e = "มอนต์เซอร์รัต";
var Morocco$e = "โมร็อกโก";
var Mozambique$e = "โมซัมบิก";
var Myanmar$e = "พม่า";
var Namibia$e = "นามิเบีย";
var Nauru$e = "นาอูรู";
var Nepal$e = "เนปาล";
var Netherlands$e = "เนเธอร์แลนด์";
var Nicaragua$e = "นิการากัว";
var Niger$e = "ไนเจอร์";
var Nigeria$e = "ไนจีเรีย";
var Niue$e = "นีอูเอ";
var Norway$e = "นอร์เวย์";
var Oman$e = "โอมาน";
var Pakistan$e = "ปากีสถาน";
var Palau$e = "ปาเลา";
var Panama$e = "ปานามา";
var Paraguay$e = "ปารากวัย";
var Peru$e = "เปรู";
var Philippines$e = "ฟิลิปปินส์";
var Pitcairn$e = "พิตแคร์น";
var Poland$e = "โปแลนด์";
var Portugal$e = "โปรตุเกส";
var Qatar$e = "กาตาร์";
var Romania$e = "โรมาเนีย";
var Russia$e = "รัสเซีย";
var Rwanda$e = "รวันดา";
var Samoa$e = "ซามัว";
var Senegal$e = "เซเนกัล";
var Serbia$e = "เซอร์เบีย";
var Seychelles$e = "เซเชลส์";
var Singapore$e = "สิงคโปร์";
var Slovakia$e = "สโลวาเกีย";
var Slovenia$e = "สโลวีเนีย";
var Somalia$e = "โซมาเลีย";
var Spain$e = "สเปน";
var Suriname$e = "ซูรินาม";
var Swaziland$e = "สวาซิแลนด์";
var Sweden$e = "สวีเดน";
var Switzerland$e = "สวิตเซอร์แลนด์";
var Tajikistan$e = "ทาจิกิสถาน";
var Thailand$e = "ประเทศไทย";
var Togo$e = "โตโก";
var Tokelau$e = "โตเกเลา";
var Tonga$e = "ตองกา";
var Tunisia$e = "ตูนิเซีย";
var Turkey$e = "ไก่งวง";
var Turkmenistan$e = "เติร์กเมนิสถาน";
var Tuvalu$e = "ตูวาลู";
var Uganda$e = "ยูกันดา";
var Ukraine$e = "ยูเครน";
var Uruguay$e = "อุรุกวัย";
var Uzbekistan$e = "อุซเบกิสถาน";
var Vanuatu$e = "วานูอาตู";
var Yemen$e = "เยเมน";
var Zambia$e = "แซมเบีย";
var Zimbabwe$e = "ซิมบับเว";
var t42 = {
	Afghanistan: Afghanistan$e,
	Albania: Albania$e,
	Algeria: Algeria$e,
	AmericanSamoa: AmericanSamoa$e,
	Andorra: Andorra$e,
	Angola: Angola$e,
	Anguilla: Anguilla$e,
	"Antigua and Barbuda": "แอนติกาและบาร์บูดา",
	Argentina: Argentina$e,
	Armenia: Armenia$e,
	Aruba: Aruba$e,
	Australia: Australia$e,
	Austria: Austria$e,
	Azerbaijan: Azerbaijan$e,
	Bahamas: Bahamas$e,
	Bahrain: Bahrain$e,
	Bangladesh: Bangladesh$e,
	Barbados: Barbados$e,
	Belarus: Belarus$e,
	Belgium: Belgium$e,
	Belize: Belize$e,
	Benin: Benin$e,
	Bermuda: Bermuda$e,
	Bhutan: Bhutan$e,
	"Bolivia, Plurinational State of": "โบลิเวีย รัฐพหุนิยมแห่ง",
	"Bosnia and Herzegovina": "บอสเนียและเฮอร์เซโกวีนา",
	Botswana: Botswana$e,
	Brazil: Brazil$e,
	"British Indian Ocean Territory": "ดินแดนมหาสมุทรอินเดียของอังกฤษ",
	"Brunei Darussalam": "บรูไนดารุสซาลาม",
	Bulgaria: Bulgaria$e,
	"Burkina Faso": "บูร์กินาฟาโซ",
	Burundi: Burundi$e,
	Cambodia: Cambodia$e,
	Cameroon: Cameroon$e,
	Canada: Canada$e,
	"Cape Verde": "ประเทศกาบูเวร์ดี",
	"Cayman Islands": "หมู่เกาะเคย์แมน",
	"Central African Republic": "สาธารณรัฐแอฟริกากลาง",
	Chad: Chad$e,
	Chile: Chile$e,
	China: China$e,
	"Christmas Island": "เกาะคริสต์มาส",
	"Cocos (Keeling) Islands": "หมู่เกาะโคโคส (คีลิง)",
	Colombia: Colombia$e,
	Comoros: Comoros$e,
	Congo: Congo$e,
	"Congo, The Democratic Republic of the": "คองโก สาธารณรัฐประชาธิปไตย",
	"Cook Islands": "หมู่เกาะคุก",
	"Costa Rica": "คอสตาริกา",
	"Cote d'Ivoire": "โกตดิวัวร์",
	Croatia: Croatia$e,
	Curacao: Curacao$e,
	Cyprus: Cyprus$e,
	"Czech Republic": "สาธารณรัฐเช็ก",
	Denmark: Denmark$e,
	Djibouti: Djibouti$e,
	Dominica: Dominica$e,
	"Dominican Republic": "สาธารณรัฐโดมินิกัน",
	Ecuador: Ecuador$e,
	Egypt: Egypt$e,
	"El Salvador": "เอลซัลวาดอร์",
	"Equatorial Guinea": "อิเควทอเรียลกินี",
	Eritrea: Eritrea$e,
	Estonia: Estonia$e,
	Ethiopia: Ethiopia$e,
	"Falkland Islands (Malvinas)": "หมู่เกาะฟอล์กแลนด์ (มัลวินาส)",
	"Faroe Islands": "หมู่เกาะแฟโร",
	Fiji: Fiji$e,
	Finland: Finland$e,
	France: France$e,
	"French Guiana": "เฟรนช์เกียนา",
	"French Polynesia": "เฟรนช์โปลินีเซีย",
	Gabon: Gabon$e,
	Gambia: Gambia$e,
	Georgia: Georgia$e,
	Germany: Germany$e,
	Ghana: Ghana$e,
	Gibraltar: Gibraltar$e,
	Greece: Greece$e,
	Greenland: Greenland$e,
	Grenada: Grenada$e,
	Guadeloupe: Guadeloupe$e,
	Guam: Guam$e,
	Guatemala: Guatemala$e,
	Guernsey: Guernsey$e,
	Guinea: Guinea$e,
	"Guinea-Bissau": "กินี-บิสเซา",
	Guyana: Guyana$e,
	Haiti: Haiti$e,
	"Holy See (Vatican City State)": "นครรัฐวาติกัน",
	Honduras: Honduras$e,
	"Hong Kong": "ฮ่องกง",
	Hungary: Hungary$e,
	Iceland: Iceland$e,
	India: India$e,
	Indonesia: Indonesia$e,
	Iraq: Iraq$e,
	Ireland: Ireland$e,
	"Isle of Man": "เกาะแมน",
	Israel: Israel$e,
	Italy: Italy$e,
	Jamaica: Jamaica$e,
	Japan: Japan$e,
	Jersey: Jersey$e,
	Jordan: Jordan$e,
	Kazakhstan: Kazakhstan$e,
	Kenya: Kenya$e,
	Kiribati: Kiribati$e,
	"Korea, Republic of": "สาธารณรัฐเกาหลี",
	Kuwait: Kuwait$e,
	Kyrgyzstan: Kyrgyzstan$e,
	"Lao People's Democratic Republic": "สาธารณรัฐประชาธิปไตยประชาชนลาว",
	Latvia: Latvia$e,
	Lebanon: Lebanon$e,
	Lesotho: Lesotho$e,
	Liberia: Liberia$e,
	"Libyan Arab Jamahiriya": "จามาฮิริยาอาหรับลิเบีย",
	Liechtenstein: Liechtenstein$e,
	Lithuania: Lithuania$e,
	Luxembourg: Luxembourg$e,
	Macao: Macao$e,
	"Macedonia, The Former Yugoslav Republic of": "มาซิโดเนีย อดีตสาธารณรัฐยูโกสลาเวีย",
	Madagascar: Madagascar$e,
	Malawi: Malawi$e,
	Malaysia: Malaysia$e,
	Maldives: Maldives$e,
	Mali: Mali$e,
	Malta: Malta$e,
	"Marshall Islands": "หมู่เกาะมาร์แชลล์",
	Martinique: Martinique$e,
	Mauritania: Mauritania$e,
	Mauritius: Mauritius$e,
	Mayotte: Mayotte$e,
	Mexico: Mexico$e,
	"Micronesia, Federated States of": "ไมโครนีเซีย สหพันธรัฐแห่ง",
	"Moldova, Republic of": "มอลโดวา สาธารณรัฐ",
	Monaco: Monaco$e,
	Mongolia: Mongolia$e,
	Montenegro: Montenegro$e,
	Montserrat: Montserrat$e,
	Morocco: Morocco$e,
	Mozambique: Mozambique$e,
	Myanmar: Myanmar$e,
	Namibia: Namibia$e,
	Nauru: Nauru$e,
	Nepal: Nepal$e,
	Netherlands: Netherlands$e,
	"New Caledonia": "นิวแคลิโดเนีย",
	"New Zealand": "นิวซีแลนด์",
	Nicaragua: Nicaragua$e,
	Niger: Niger$e,
	Nigeria: Nigeria$e,
	Niue: Niue$e,
	"Norfolk Island": "เกาะนอร์ฟอล์ก",
	"Northern Mariana Islands": "หมู่เกาะนอร์เทิร์นมาเรียนา",
	Norway: Norway$e,
	Oman: Oman$e,
	Pakistan: Pakistan$e,
	Palau: Palau$e,
	"Palestinian Territory, Occupied": "ดินแดนปาเลสไตน์ที่ถูกยึดครอง",
	Panama: Panama$e,
	"Papua New Guinea": "ปาปัวนิวกินี",
	Paraguay: Paraguay$e,
	Peru: Peru$e,
	Philippines: Philippines$e,
	Pitcairn: Pitcairn$e,
	Poland: Poland$e,
	Portugal: Portugal$e,
	"Puerto Rico": "เปอร์โตริโก",
	Qatar: Qatar$e,
	Romania: Romania$e,
	Russia: Russia$e,
	Rwanda: Rwanda$e,
	"Réunion": "เรอูนียง",
	"Saint Barthélemy": "เซนต์บาร์เธเลมี",
	"Saint Helena, Ascension and Tristan Da Cunha": "เซนต์เฮเลนา แอสเซนชัน และทริสตัน ดา คูนญา",
	"Saint Kitts and Nevis": "เซนต์คิตส์และเนวิส",
	"Saint Lucia": "เซนต์ลูเซีย",
	"Saint Martin": "เซนต์มาร์ติน",
	"Saint Pierre and Miquelon": "แซงต์ปีแยร์และมีเกอลง",
	"Saint Vincent and the Grenadines": "เซนต์วินเซนต์และเกรนาดีนส์",
	Samoa: Samoa$e,
	"San Marino": "ซานมารีโน",
	"Sao Tome and Principe": "เซาตูเมและปรินซิปี",
	"Saudi Arabia": "ซาอุดีอาระเบีย",
	Senegal: Senegal$e,
	Serbia: Serbia$e,
	Seychelles: Seychelles$e,
	"Sierra Leone": "เซียร์ราลีโอน",
	Singapore: Singapore$e,
	Slovakia: Slovakia$e,
	Slovenia: Slovenia$e,
	"Solomon Islands": "หมู่เกาะโซโลมอน",
	Somalia: Somalia$e,
	"South Africa": "แอฟริกาใต้",
	"South Georgia and the South Sandwich Islands": "เกาะเซาท์จอร์เจียและหมู่เกาะเซาท์แซนด์วิช",
	Spain: Spain$e,
	"Sri Lanka": "ศรีลังกา",
	Suriname: Suriname$e,
	"Svalbard and Jan Mayen": "สฟาลบาร์ดและยานไมเอน",
	Swaziland: Swaziland$e,
	Sweden: Sweden$e,
	Switzerland: Switzerland$e,
	"Taiwan, Province of China": "ไต้หวัน มณฑลของจีน",
	Tajikistan: Tajikistan$e,
	"Tanzania, United Republic of": "แทนซาเนีย, สหสาธารณรัฐ",
	Thailand: Thailand$e,
	"Timor-Leste": "ติมอร์-เลสเต",
	Togo: Togo$e,
	Tokelau: Tokelau$e,
	Tonga: Tonga$e,
	"Trinidad and Tobago": "ตรินิแดดและโตเบโก",
	Tunisia: Tunisia$e,
	Turkey: Turkey$e,
	Turkmenistan: Turkmenistan$e,
	"Turks and Caicos Islands": "หมู่เกาะเติกส์และเคคอส",
	Tuvalu: Tuvalu$e,
	Uganda: Uganda$e,
	Ukraine: Ukraine$e,
	"United Arab Emirates": "สหรัฐอาหรับเอมิเรตส์",
	"United Kingdom": "สหราชอาณาจักร",
	"United States": "สหรัฐอเมริกา",
	Uruguay: Uruguay$e,
	Uzbekistan: Uzbekistan$e,
	Vanuatu: Vanuatu$e,
	"Venezuela, Bolivarian Republic of": "เวเนซุเอลา สาธารณรัฐโบลิวาร์",
	"Viet Nam": "เวียดนาม",
	"Virgin Islands, British": "หมู่เกาะเวอร์จิน, บริติช",
	"Virgin Islands, U.S.": "หมู่เกาะเวอร์จิน สหรัฐอเมริกา",
	"Wallis and Futuna": "วาลลิสและฟูตูนา",
	Yemen: Yemen$e,
	Zambia: Zambia$e,
	Zimbabwe: Zimbabwe$e,
	"Åland Islands": "หมู่เกาะโอลันด์"
};

var t43 = {
	"No results found.": "ไม่พบผลลัพธ์"
};

var Done$F = "Gjort";
var t44 = {
	Done: Done$F
};

var t45 = {
	"No results found.": "Inga resultat hittades."
};

var t46 = {
	"Clear all": "Rensa alla",
	"No results found.": "Inga resultat hittades."
};

var Passengers$d = "Passagerare";
var t47 = {
	"Number of passengers": "Antal passagerare",
	Passengers: Passengers$d
};

var t48 = {
	"No results found.": "Inga resultat hittades."
};

var Blog$d = "Blogg";
var Careers$d = "Karriärer";
var Contact$d = "Kontakta";
var Coverage$d = "Rapportering";
var Guides$d = "Guider";
var Imprint$d = "Avtryck";
var Parking$d = "Parkering";
var eSIM$d = "eSIM-kort";
var t49 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. Varumärken och varumärken tillhör sina respektive ägare.",
	Blog: Blog$d,
	Careers: Careers$d,
	Contact: Contact$d,
	Coverage: Coverage$d,
	Guides: Guides$d,
	Imprint: Imprint$d,
	Parking: Parking$d,
	"Privacy Policy": "Integritetspolicy",
	"Terms Of Services": "Användarvillkor",
	eSIM: eSIM$d,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Mozio Inc. Varumärken och varumärken tillhör sina respektive ägare."
};

var Filters$d = "Filter";
var t50 = {
	"Clear All": "Rensa alla",
	Filters: Filters$d,
	"Show results": "Visa resultat"
};

var Done$E = "Klar";
var Hours$d = "Timmar";
var Minutes$d = "Minuter";
var t51 = {
	"AM/PM": "AM/PM",
	Done: Done$E,
	Hours: Hours$d,
	Minutes: Minutes$d
};

var Done$D = "Gjort";
var t52 = {
	Done: Done$D
};

var Afghanistan$d = "Afghanistan";
var Albania$d = "Albanien";
var Algeria$d = "Algeriet";
var AmericanSamoa$d = "Amerikanska Samoa";
var Andorra$d = "Andorra";
var Angola$d = "Angola";
var Anguilla$d = "Anguilla";
var Argentina$d = "Argentina";
var Armenia$d = "Armenien";
var Aruba$d = "Aruba";
var Australia$d = "Australien";
var Austria$d = "Österrike";
var Azerbaijan$d = "Azerbajdzjan";
var Bahamas$d = "Bahamas";
var Bahrain$d = "Bahrain";
var Bangladesh$d = "Bangladesh";
var Barbados$d = "Barbados";
var Belarus$d = "Belarus";
var Belgium$d = "Belgien";
var Belize$d = "Belize";
var Benin$d = "Benin";
var Bermuda$d = "Bermuda";
var Bhutan$d = "Bhutan";
var Botswana$d = "Botswana";
var Brazil$d = "Brasilien";
var Bulgaria$d = "Bulgarien";
var Burundi$d = "Burundi";
var Cambodia$d = "Kambodja";
var Cameroon$d = "Kamerun";
var Canada$d = "Kanada";
var Chad$d = "Tchad";
var Chile$d = "Chile";
var China$d = "Kina";
var Colombia$d = "Colombia";
var Comoros$d = "Komorerna";
var Congo$d = "Kongo";
var Croatia$d = "Kroatien";
var Curacao$d = "Curaçao";
var Cyprus$d = "Cypern";
var Denmark$d = "Danmark";
var Djibouti$d = "Djibouti";
var Dominica$d = "Dominica";
var Ecuador$d = "Ecuador";
var Egypt$d = "Egypten";
var Eritrea$d = "Eritrea";
var Estonia$d = "Estland";
var Ethiopia$d = "Etiopien";
var Fiji$d = "Fiji";
var Finland$d = "Finland";
var France$d = "Frankrike";
var Gabon$d = "Gabon";
var Gambia$d = "Gambia";
var Georgia$d = "Georgien";
var Germany$d = "Tyskland";
var Ghana$d = "Ghana";
var Gibraltar$d = "Gibraltar";
var Greece$d = "Grekland";
var Greenland$d = "Grönland";
var Grenada$d = "Grenada";
var Guadeloupe$d = "Guadeloupe";
var Guam$d = "Guam";
var Guatemala$d = "Guatemala";
var Guernsey$d = "Guernsey";
var Guinea$d = "Guinea";
var Guyana$d = "Guyana";
var Haiti$d = "Haiti";
var Honduras$d = "Honduras";
var Hungary$d = "Ungern";
var Iceland$d = "Island";
var India$d = "Indien";
var Indonesia$d = "Indonesien";
var Iraq$d = "Irak";
var Ireland$d = "Irland";
var Israel$d = "Israel";
var Italy$d = "Italien";
var Jamaica$d = "Jamaica";
var Japan$d = "Japan";
var Jersey$d = "Jersey";
var Jordan$d = "Jordanien";
var Kazakhstan$d = "Kazakstan";
var Kenya$d = "Kenya";
var Kiribati$d = "Kiribati";
var Kuwait$d = "Kuwait";
var Kyrgyzstan$d = "Kirgizistan";
var Latvia$d = "Lettland";
var Lebanon$d = "Libanon";
var Lesotho$d = "Lesotho";
var Liberia$d = "Liberia";
var Liechtenstein$d = "Liechtenstein";
var Lithuania$d = "Litauen";
var Luxembourg$d = "Luxemburg";
var Macao$d = "Macao";
var Madagascar$d = "Madagaskar";
var Malawi$d = "Malawi";
var Malaysia$d = "Malaysia";
var Maldives$d = "Maldiverna";
var Mali$d = "Mali";
var Malta$d = "Malta";
var Martinique$d = "Martinique";
var Mauritania$d = "Mauretanien";
var Mauritius$d = "Mauritius";
var Mayotte$d = "Mayotte";
var Mexico$d = "Mexiko";
var Monaco$d = "Monaco";
var Mongolia$d = "mongoliet";
var Montenegro$d = "Montenegro";
var Montserrat$d = "Montserrat";
var Morocco$d = "Marocko";
var Mozambique$d = "Moçambique";
var Myanmar$d = "Myanmar";
var Namibia$d = "Namibia";
var Nauru$d = "Nauru";
var Nepal$d = "Nepal";
var Netherlands$d = "Nederländerna";
var Nicaragua$d = "Nicaragua";
var Niger$d = "Niger";
var Nigeria$d = "Nigeria";
var Niue$d = "Niue";
var Norway$d = "Norge";
var Oman$d = "oman";
var Pakistan$d = "Pakistan";
var Palau$d = "Palau";
var Panama$d = "Panama";
var Paraguay$d = "Paraguay";
var Peru$d = "Peru";
var Philippines$d = "Filippinerna";
var Pitcairn$d = "Pitcairnöarna";
var Poland$d = "Polen";
var Portugal$d = "Portugal";
var Qatar$d = "Qatar";
var Romania$d = "Rumänien";
var Russia$d = "Ryssland";
var Rwanda$d = "Rwanda";
var Samoa$d = "Samoa";
var Senegal$d = "Senegal";
var Serbia$d = "Serbien";
var Seychelles$d = "Seychellerna";
var Singapore$d = "Singapore";
var Slovakia$d = "Slovakien";
var Slovenia$d = "Slovenien";
var Somalia$d = "Somalia";
var Spain$d = "Spanien";
var Suriname$d = "Surinam";
var Swaziland$d = "Swaziland";
var Sweden$d = "Sverige";
var Switzerland$d = "Schweiz";
var Tajikistan$d = "Tadzjikistan";
var Thailand$d = "Thailand";
var Togo$d = "Togo";
var Tokelau$d = "Tokelauöarna";
var Tonga$d = "Tonga";
var Tunisia$d = "Tunisien";
var Turkey$d = "Turkiet";
var Turkmenistan$d = "Turkmenistan";
var Tuvalu$d = "Tuvalu";
var Uganda$d = "Uganda";
var Ukraine$d = "Ukraina";
var Uruguay$d = "Uruguay";
var Uzbekistan$d = "Uzbekistan";
var Vanuatu$d = "Vanuatu";
var Yemen$d = "Jemen";
var Zambia$d = "Zambia";
var Zimbabwe$d = "Zimbabwe";
var t53 = {
	Afghanistan: Afghanistan$d,
	Albania: Albania$d,
	Algeria: Algeria$d,
	AmericanSamoa: AmericanSamoa$d,
	Andorra: Andorra$d,
	Angola: Angola$d,
	Anguilla: Anguilla$d,
	"Antigua and Barbuda": "Antigua och Barbuda",
	Argentina: Argentina$d,
	Armenia: Armenia$d,
	Aruba: Aruba$d,
	Australia: Australia$d,
	Austria: Austria$d,
	Azerbaijan: Azerbaijan$d,
	Bahamas: Bahamas$d,
	Bahrain: Bahrain$d,
	Bangladesh: Bangladesh$d,
	Barbados: Barbados$d,
	Belarus: Belarus$d,
	Belgium: Belgium$d,
	Belize: Belize$d,
	Benin: Benin$d,
	Bermuda: Bermuda$d,
	Bhutan: Bhutan$d,
	"Bolivia, Plurinational State of": "Bolivia, plurinationell stat",
	"Bosnia and Herzegovina": "Bosnien och Hercegovina",
	Botswana: Botswana$d,
	Brazil: Brazil$d,
	"British Indian Ocean Territory": "Brittiska territoriet i Indiska oceanen",
	"Brunei Darussalam": "Brunei Darussalam",
	Bulgaria: Bulgaria$d,
	"Burkina Faso": "Burkina Faso",
	Burundi: Burundi$d,
	Cambodia: Cambodia$d,
	Cameroon: Cameroon$d,
	Canada: Canada$d,
	"Cape Verde": "Kap Verde",
	"Cayman Islands": "Caymanöarna",
	"Central African Republic": "Centralafrikanska republiken",
	Chad: Chad$d,
	Chile: Chile$d,
	China: China$d,
	"Christmas Island": "Julön",
	"Cocos (Keeling) Islands": "Kokosöarna (Keelingöarna)",
	Colombia: Colombia$d,
	Comoros: Comoros$d,
	Congo: Congo$d,
	"Congo, The Democratic Republic of the": "Kongo, Demokratiska republiken",
	"Cook Islands": "Cooköarna",
	"Costa Rica": "Costa Rica",
	"Cote d'Ivoire": "Elfenbenskusten",
	Croatia: Croatia$d,
	Curacao: Curacao$d,
	Cyprus: Cyprus$d,
	"Czech Republic": "Tjeckien",
	Denmark: Denmark$d,
	Djibouti: Djibouti$d,
	Dominica: Dominica$d,
	"Dominican Republic": "Dominikanska republiken",
	Ecuador: Ecuador$d,
	Egypt: Egypt$d,
	"El Salvador": "El Salvador",
	"Equatorial Guinea": "Ekvatorialguinea",
	Eritrea: Eritrea$d,
	Estonia: Estonia$d,
	Ethiopia: Ethiopia$d,
	"Falkland Islands (Malvinas)": "Falklandsöarna (Malvinas)",
	"Faroe Islands": "Färöarna",
	Fiji: Fiji$d,
	Finland: Finland$d,
	France: France$d,
	"French Guiana": "Franska Guyana",
	"French Polynesia": "Franska Polynesien",
	Gabon: Gabon$d,
	Gambia: Gambia$d,
	Georgia: Georgia$d,
	Germany: Germany$d,
	Ghana: Ghana$d,
	Gibraltar: Gibraltar$d,
	Greece: Greece$d,
	Greenland: Greenland$d,
	Grenada: Grenada$d,
	Guadeloupe: Guadeloupe$d,
	Guam: Guam$d,
	Guatemala: Guatemala$d,
	Guernsey: Guernsey$d,
	Guinea: Guinea$d,
	"Guinea-Bissau": "Guinea-Bissau",
	Guyana: Guyana$d,
	Haiti: Haiti$d,
	"Holy See (Vatican City State)": "Heliga stolen (Vatikanstaten)",
	Honduras: Honduras$d,
	"Hong Kong": "Hongkong",
	Hungary: Hungary$d,
	Iceland: Iceland$d,
	India: India$d,
	Indonesia: Indonesia$d,
	Iraq: Iraq$d,
	Ireland: Ireland$d,
	"Isle of Man": "Isle of Man",
	Israel: Israel$d,
	Italy: Italy$d,
	Jamaica: Jamaica$d,
	Japan: Japan$d,
	Jersey: Jersey$d,
	Jordan: Jordan$d,
	Kazakhstan: Kazakhstan$d,
	Kenya: Kenya$d,
	Kiribati: Kiribati$d,
	"Korea, Republic of": "Korea, Republiken",
	Kuwait: Kuwait$d,
	Kyrgyzstan: Kyrgyzstan$d,
	"Lao People's Democratic Republic": "Laos folkdemokratiska republik",
	Latvia: Latvia$d,
	Lebanon: Lebanon$d,
	Lesotho: Lesotho$d,
	Liberia: Liberia$d,
	"Libyan Arab Jamahiriya": "Libyska arabiska Jamahiriya",
	Liechtenstein: Liechtenstein$d,
	Lithuania: Lithuania$d,
	Luxembourg: Luxembourg$d,
	Macao: Macao$d,
	"Macedonia, The Former Yugoslav Republic of": "Makedonien, f.d. jugoslaviska republiken",
	Madagascar: Madagascar$d,
	Malawi: Malawi$d,
	Malaysia: Malaysia$d,
	Maldives: Maldives$d,
	Mali: Mali$d,
	Malta: Malta$d,
	"Marshall Islands": "Marshallöarna",
	Martinique: Martinique$d,
	Mauritania: Mauritania$d,
	Mauritius: Mauritius$d,
	Mayotte: Mayotte$d,
	Mexico: Mexico$d,
	"Micronesia, Federated States of": "Mikronesien, Federerade staterna",
	"Moldova, Republic of": "Moldavien, Republiken",
	Monaco: Monaco$d,
	Mongolia: Mongolia$d,
	Montenegro: Montenegro$d,
	Montserrat: Montserrat$d,
	Morocco: Morocco$d,
	Mozambique: Mozambique$d,
	Myanmar: Myanmar$d,
	Namibia: Namibia$d,
	Nauru: Nauru$d,
	Nepal: Nepal$d,
	Netherlands: Netherlands$d,
	"New Caledonia": "Nya Kaledonien",
	"New Zealand": "Nya Zeeland",
	Nicaragua: Nicaragua$d,
	Niger: Niger$d,
	Nigeria: Nigeria$d,
	Niue: Niue$d,
	"Norfolk Island": "Norfolkön",
	"Northern Mariana Islands": "Nordmarianerna",
	Norway: Norway$d,
	Oman: Oman$d,
	Pakistan: Pakistan$d,
	Palau: Palau$d,
	"Palestinian Territory, Occupied": "Palestinskt territorium, ockuperat",
	Panama: Panama$d,
	"Papua New Guinea": "Papua Nya Guinea",
	Paraguay: Paraguay$d,
	Peru: Peru$d,
	Philippines: Philippines$d,
	Pitcairn: Pitcairn$d,
	Poland: Poland$d,
	Portugal: Portugal$d,
	"Puerto Rico": "Puerto Rico",
	Qatar: Qatar$d,
	Romania: Romania$d,
	Russia: Russia$d,
	Rwanda: Rwanda$d,
	"Réunion": "Återförening",
	"Saint Barthélemy": "Saint Barthélemy",
	"Saint Helena, Ascension and Tristan Da Cunha": "Sankta Helena, Ascension och Tristan da Cunha",
	"Saint Kitts and Nevis": "Saint Kitts och Nevis",
	"Saint Lucia": "Saint Lucia",
	"Saint Martin": "Sankt Martin",
	"Saint Pierre and Miquelon": "Saint Pierre och Miquelon",
	"Saint Vincent and the Grenadines": "Saint Vincent och Grenadinerna",
	Samoa: Samoa$d,
	"San Marino": "San Marino",
	"Sao Tome and Principe": "São Tomé och Principe",
	"Saudi Arabia": "saudi-arabien",
	Senegal: Senegal$d,
	Serbia: Serbia$d,
	Seychelles: Seychelles$d,
	"Sierra Leone": "Sierra Leone",
	Singapore: Singapore$d,
	Slovakia: Slovakia$d,
	Slovenia: Slovenia$d,
	"Solomon Islands": "Salomonöarna",
	Somalia: Somalia$d,
	"South Africa": "Sydafrika",
	"South Georgia and the South Sandwich Islands": "Sydgeorgien och Sydsandwichöarna",
	Spain: Spain$d,
	"Sri Lanka": "Sri Lanka",
	Suriname: Suriname$d,
	"Svalbard and Jan Mayen": "Svalbard och Jan Mayen",
	Swaziland: Swaziland$d,
	Sweden: Sweden$d,
	Switzerland: Switzerland$d,
	"Taiwan, Province of China": "Taiwan, Kinas provins",
	Tajikistan: Tajikistan$d,
	"Tanzania, United Republic of": "Tanzania, Förenade republiken",
	Thailand: Thailand$d,
	"Timor-Leste": "Östtimor",
	Togo: Togo$d,
	Tokelau: Tokelau$d,
	Tonga: Tonga$d,
	"Trinidad and Tobago": "Trinidad och Tobago",
	Tunisia: Tunisia$d,
	Turkey: Turkey$d,
	Turkmenistan: Turkmenistan$d,
	"Turks and Caicos Islands": "Turks- och Caicosöarna",
	Tuvalu: Tuvalu$d,
	Uganda: Uganda$d,
	Ukraine: Ukraine$d,
	"United Arab Emirates": "Förenade Arabemiraten",
	"United Kingdom": "Storbritannien",
	"United States": "Förenta staterna",
	Uruguay: Uruguay$d,
	Uzbekistan: Uzbekistan$d,
	Vanuatu: Vanuatu$d,
	"Venezuela, Bolivarian Republic of": "Venezuela, Bolivianska republiken",
	"Viet Nam": "Vietnam",
	"Virgin Islands, British": "Brittiska Jungfruöarna",
	"Virgin Islands, U.S.": "Jungfruöarna, USA",
	"Wallis and Futuna": "Wallis- och Futunaöarna",
	Yemen: Yemen$d,
	Zambia: Zambia$d,
	Zimbabwe: Zimbabwe$d,
	"Åland Islands": "Ålandsöarna"
};

var t54 = {
	"No results found.": "Inga resultat hittades."
};

var Done$C = "Сделанный";
var t55 = {
	Done: Done$C
};

var t56 = {
	"No results found.": "Результаты не найдены."
};

var t57 = {
	"Clear all": "Очистить все",
	"No results found.": "Результаты не найдены."
};

var Passengers$c = "Пассажиры";
var t58 = {
	"Number of passengers": "Количество пассажиров",
	Passengers: Passengers$c
};

var t59 = {
	"No results found.": "Результаты не найдены."
};

var Blog$c = "Блог";
var Careers$c = "Карьера";
var Contact$c = "Контакт";
var Coverage$c = "Покрытие";
var Guides$c = "Гиды";
var Imprint$c = "Отпечаток";
var Parking$c = "Стоянка";
var eSIM$c = "eSIM";
var t60 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. Торговые марки и бренды являются собственностью их соответствующих владельцев.",
	Blog: Blog$c,
	Careers: Careers$c,
	Contact: Contact$c,
	Coverage: Coverage$c,
	Guides: Guides$c,
	Imprint: Imprint$c,
	Parking: Parking$c,
	"Privacy Policy": "политика конфиденциальности",
	"Terms Of Services": "Условия предоставления услуг",
	eSIM: eSIM$c,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Торговые марки и бренды компании Mozio Inc. являются собственностью их соответствующих владельцев."
};

var Filters$c = "Фильтры";
var t61 = {
	"Clear All": "Очистить все",
	Filters: Filters$c,
	"Show results": "Показать результаты"
};

var Done$B = "Готово";
var Hours$c = "Часы";
var Minutes$c = "Минуты";
var t62 = {
	"AM/PM": "AM/PM",
	Done: Done$B,
	Hours: Hours$c,
	Minutes: Minutes$c
};

var Done$A = "Сделанный";
var t63 = {
	Done: Done$A
};

var Afghanistan$c = "Афганистан";
var Albania$c = "Албания";
var Algeria$c = "Алжир";
var AmericanSamoa$c = "АмериканскоеСамоа";
var Andorra$c = "Андорра";
var Angola$c = "Ангола";
var Anguilla$c = "Ангилья";
var Argentina$c = "Аргентина";
var Armenia$c = "Армения";
var Aruba$c = "Аруба";
var Australia$c = "Австралия";
var Austria$c = "Австрия";
var Azerbaijan$c = "Азербайджан";
var Bahamas$c = "Багамские острова";
var Bahrain$c = "Бахрейн";
var Bangladesh$c = "Бангладеш";
var Barbados$c = "Барбадос";
var Belarus$c = "Беларусь";
var Belgium$c = "Бельгия";
var Belize$c = "Белиз";
var Benin$c = "Бенин";
var Bermuda$c = "Бермудские острова";
var Bhutan$c = "Бутан";
var Botswana$c = "Ботсвана";
var Brazil$c = "Бразилия";
var Bulgaria$c = "Болгария";
var Burundi$c = "Бурунди";
var Cambodia$c = "Камбоджа";
var Cameroon$c = "Камерун";
var Canada$c = "Канада";
var Chad$c = "Чад";
var Chile$c = "Чили";
var China$c = "Китай";
var Colombia$c = "Колумбия";
var Comoros$c = "Коморские острова";
var Congo$c = "Конго";
var Croatia$c = "Хорватия";
var Curacao$c = "Кюрасао";
var Cyprus$c = "Кипр";
var Denmark$c = "Дания";
var Djibouti$c = "Джибути";
var Dominica$c = "Доминика";
var Ecuador$c = "Эквадор";
var Egypt$c = "Египет";
var Eritrea$c = "Эритрея";
var Estonia$c = "Эстония";
var Ethiopia$c = "Эфиопия";
var Fiji$c = "Фиджи";
var Finland$c = "Финляндия";
var France$c = "Франция";
var Gabon$c = "Габон";
var Gambia$c = "Гамбия";
var Georgia$c = "Джорджия";
var Germany$c = "Германия";
var Ghana$c = "Гана";
var Gibraltar$c = "Гибралтар";
var Greece$c = "Греция";
var Greenland$c = "Гренландия";
var Grenada$c = "Гренада";
var Guadeloupe$c = "Гваделупа";
var Guam$c = "Гуам";
var Guatemala$c = "Гватемала";
var Guernsey$c = "Гернси";
var Guinea$c = "Гвинея";
var Guyana$c = "Гайана";
var Haiti$c = "Гаити";
var Honduras$c = "Гондурас";
var Hungary$c = "Венгрия";
var Iceland$c = "Исландия";
var India$c = "Индия";
var Indonesia$c = "Индонезия";
var Iraq$c = "Ирак";
var Ireland$c = "Ирландия";
var Israel$c = "Израиль";
var Italy$c = "Италия";
var Jamaica$c = "Ямайка";
var Japan$c = "Япония";
var Jersey$c = "Джерси";
var Jordan$c = "Иордания";
var Kazakhstan$c = "Казахстан";
var Kenya$c = "Кения";
var Kiribati$c = "Кирибати";
var Kuwait$c = "Кувейт";
var Kyrgyzstan$c = "Кыргызстан";
var Latvia$c = "Латвия";
var Lebanon$c = "Ливан";
var Lesotho$c = "Лесото";
var Liberia$c = "Либерия";
var Liechtenstein$c = "Лихтенштейн";
var Lithuania$c = "Литва";
var Luxembourg$c = "Люксембург";
var Macao$c = "Макао";
var Madagascar$c = "Мадагаскар";
var Malawi$c = "Малави";
var Malaysia$c = "Малайзия";
var Maldives$c = "Мальдивы";
var Mali$c = "Мали";
var Malta$c = "Мальта";
var Martinique$c = "Мартиника";
var Mauritania$c = "Мавритания";
var Mauritius$c = "Маврикий";
var Mayotte$c = "Майотта";
var Mexico$c = "Мексика";
var Monaco$c = "Монако";
var Mongolia$c = "Монголия";
var Montenegro$c = "Черногория";
var Montserrat$c = "Монтсеррат";
var Morocco$c = "Марокко";
var Mozambique$c = "Мозамбик";
var Myanmar$c = "Мьянма";
var Namibia$c = "Намибия";
var Nauru$c = "Науру";
var Nepal$c = "Непал";
var Netherlands$c = "Нидерланды";
var Nicaragua$c = "Никарагуа";
var Niger$c = "Нигер";
var Nigeria$c = "Нигерия";
var Niue$c = "Ниуэ";
var Norway$c = "Норвегия";
var Oman$c = "Оман";
var Pakistan$c = "Пакистан";
var Palau$c = "Палау";
var Panama$c = "Панама";
var Paraguay$c = "Парагвай";
var Peru$c = "Перу";
var Philippines$c = "Филиппины";
var Pitcairn$c = "Питкэрн";
var Poland$c = "Польша";
var Portugal$c = "Португалия";
var Qatar$c = "Катар";
var Romania$c = "Румыния";
var Russia$c = "Россия";
var Rwanda$c = "Руанда";
var Samoa$c = "Самоа";
var Senegal$c = "Сенегал";
var Serbia$c = "Сербия";
var Seychelles$c = "Сейшельские острова";
var Singapore$c = "Сингапур";
var Slovakia$c = "Словакия";
var Slovenia$c = "Словения";
var Somalia$c = "Сомали";
var Spain$c = "Испания";
var Suriname$c = "Суринам";
var Swaziland$c = "Свазиленд";
var Sweden$c = "Швеция";
var Switzerland$c = "Швейцария";
var Tajikistan$c = "Таджикистан";
var Thailand$c = "Таиланд";
var Togo$c = "Того";
var Tokelau$c = "Токелау";
var Tonga$c = "Тонга";
var Tunisia$c = "Тунис";
var Turkey$c = "Турция";
var Turkmenistan$c = "Туркменистан";
var Tuvalu$c = "Тувалу";
var Uganda$c = "Уганда";
var Ukraine$c = "Украина";
var Uruguay$c = "Уругвай";
var Uzbekistan$c = "Узбекистан";
var Vanuatu$c = "Вануату";
var Yemen$c = "Йемен";
var Zambia$c = "Замбия";
var Zimbabwe$c = "Зимбабве";
var t64 = {
	Afghanistan: Afghanistan$c,
	Albania: Albania$c,
	Algeria: Algeria$c,
	AmericanSamoa: AmericanSamoa$c,
	Andorra: Andorra$c,
	Angola: Angola$c,
	Anguilla: Anguilla$c,
	"Antigua and Barbuda": "Антигуа и Барбуда",
	Argentina: Argentina$c,
	Armenia: Armenia$c,
	Aruba: Aruba$c,
	Australia: Australia$c,
	Austria: Austria$c,
	Azerbaijan: Azerbaijan$c,
	Bahamas: Bahamas$c,
	Bahrain: Bahrain$c,
	Bangladesh: Bangladesh$c,
	Barbados: Barbados$c,
	Belarus: Belarus$c,
	Belgium: Belgium$c,
	Belize: Belize$c,
	Benin: Benin$c,
	Bermuda: Bermuda$c,
	Bhutan: Bhutan$c,
	"Bolivia, Plurinational State of": "Боливия, Многонациональное Государство",
	"Bosnia and Herzegovina": "Босния и Герцеговина",
	Botswana: Botswana$c,
	Brazil: Brazil$c,
	"British Indian Ocean Territory": "Британская территория в Индийском океане",
	"Brunei Darussalam": "Бруней-Даруссалам",
	Bulgaria: Bulgaria$c,
	"Burkina Faso": "Буркина-Фасо",
	Burundi: Burundi$c,
	Cambodia: Cambodia$c,
	Cameroon: Cameroon$c,
	Canada: Canada$c,
	"Cape Verde": "Кабо-Верде",
	"Cayman Islands": "Каймановы острова",
	"Central African Republic": "Центральноафриканская Республика",
	Chad: Chad$c,
	Chile: Chile$c,
	China: China$c,
	"Christmas Island": "Остров Рождества",
	"Cocos (Keeling) Islands": "Кокосовые (Килинг) острова",
	Colombia: Colombia$c,
	Comoros: Comoros$c,
	Congo: Congo$c,
	"Congo, The Democratic Republic of the": "Конго, Демократическая Республика",
	"Cook Islands": "Острова Кука",
	"Costa Rica": "Коста-Рика",
	"Cote d'Ivoire": "Берег Слоновой Кости",
	Croatia: Croatia$c,
	Curacao: Curacao$c,
	Cyprus: Cyprus$c,
	"Czech Republic": "Чешская Республика",
	Denmark: Denmark$c,
	Djibouti: Djibouti$c,
	Dominica: Dominica$c,
	"Dominican Republic": "Доминиканская Республика",
	Ecuador: Ecuador$c,
	Egypt: Egypt$c,
	"El Salvador": "Сальвадор",
	"Equatorial Guinea": "Экваториальная Гвинея",
	Eritrea: Eritrea$c,
	Estonia: Estonia$c,
	Ethiopia: Ethiopia$c,
	"Falkland Islands (Malvinas)": "Фолклендские (Мальвинские) острова",
	"Faroe Islands": "Фарерские острова",
	Fiji: Fiji$c,
	Finland: Finland$c,
	France: France$c,
	"French Guiana": "Французская Гвиана",
	"French Polynesia": "Французская Полинезия",
	Gabon: Gabon$c,
	Gambia: Gambia$c,
	Georgia: Georgia$c,
	Germany: Germany$c,
	Ghana: Ghana$c,
	Gibraltar: Gibraltar$c,
	Greece: Greece$c,
	Greenland: Greenland$c,
	Grenada: Grenada$c,
	Guadeloupe: Guadeloupe$c,
	Guam: Guam$c,
	Guatemala: Guatemala$c,
	Guernsey: Guernsey$c,
	Guinea: Guinea$c,
	"Guinea-Bissau": "Гвинея-Бисау",
	Guyana: Guyana$c,
	Haiti: Haiti$c,
	"Holy See (Vatican City State)": "Святой Престол (город-государство Ватикан)",
	Honduras: Honduras$c,
	"Hong Kong": "Гонконг",
	Hungary: Hungary$c,
	Iceland: Iceland$c,
	India: India$c,
	Indonesia: Indonesia$c,
	Iraq: Iraq$c,
	Ireland: Ireland$c,
	"Isle of Man": "Остров Мэн",
	Israel: Israel$c,
	Italy: Italy$c,
	Jamaica: Jamaica$c,
	Japan: Japan$c,
	Jersey: Jersey$c,
	Jordan: Jordan$c,
	Kazakhstan: Kazakhstan$c,
	Kenya: Kenya$c,
	Kiribati: Kiribati$c,
	"Korea, Republic of": "Корея, Республика",
	Kuwait: Kuwait$c,
	Kyrgyzstan: Kyrgyzstan$c,
	"Lao People's Democratic Republic": "Лаосская Народно-Демократическая Республика",
	Latvia: Latvia$c,
	Lebanon: Lebanon$c,
	Lesotho: Lesotho$c,
	Liberia: Liberia$c,
	"Libyan Arab Jamahiriya": "Ливийская Арабская Джамахирия",
	Liechtenstein: Liechtenstein$c,
	Lithuania: Lithuania$c,
	Luxembourg: Luxembourg$c,
	Macao: Macao$c,
	"Macedonia, The Former Yugoslav Republic of": "Македония, бывшая югославская республика",
	Madagascar: Madagascar$c,
	Malawi: Malawi$c,
	Malaysia: Malaysia$c,
	Maldives: Maldives$c,
	Mali: Mali$c,
	Malta: Malta$c,
	"Marshall Islands": "Маршалловы Острова",
	Martinique: Martinique$c,
	Mauritania: Mauritania$c,
	Mauritius: Mauritius$c,
	Mayotte: Mayotte$c,
	Mexico: Mexico$c,
	"Micronesia, Federated States of": "Микронезия, Федеративные Штаты",
	"Moldova, Republic of": "Молдова, Республика",
	Monaco: Monaco$c,
	Mongolia: Mongolia$c,
	Montenegro: Montenegro$c,
	Montserrat: Montserrat$c,
	Morocco: Morocco$c,
	Mozambique: Mozambique$c,
	Myanmar: Myanmar$c,
	Namibia: Namibia$c,
	Nauru: Nauru$c,
	Nepal: Nepal$c,
	Netherlands: Netherlands$c,
	"New Caledonia": "Новая Каледония",
	"New Zealand": "Новая Зеландия",
	Nicaragua: Nicaragua$c,
	Niger: Niger$c,
	Nigeria: Nigeria$c,
	Niue: Niue$c,
	"Norfolk Island": "остров Норфолк",
	"Northern Mariana Islands": "Северные Марианские острова",
	Norway: Norway$c,
	Oman: Oman$c,
	Pakistan: Pakistan$c,
	Palau: Palau$c,
	"Palestinian Territory, Occupied": "Палестинская территория, оккупированная",
	Panama: Panama$c,
	"Papua New Guinea": "Папуа-Новая Гвинея",
	Paraguay: Paraguay$c,
	Peru: Peru$c,
	Philippines: Philippines$c,
	Pitcairn: Pitcairn$c,
	Poland: Poland$c,
	Portugal: Portugal$c,
	"Puerto Rico": "Пуэрто-Рико",
	Qatar: Qatar$c,
	Romania: Romania$c,
	Russia: Russia$c,
	Rwanda: Rwanda$c,
	"Réunion": "Реюньон",
	"Saint Barthélemy": "Сен-Бартелеми",
	"Saint Helena, Ascension and Tristan Da Cunha": "Острова Святой Елены, Вознесения и Тристан-да-Кунья",
	"Saint Kitts and Nevis": "Сент-Китс и Невис",
	"Saint Lucia": "Сент-Люсия",
	"Saint Martin": "Сен-Мартен",
	"Saint Pierre and Miquelon": "Сен-Пьер и Микелон",
	"Saint Vincent and the Grenadines": "Сент-Винсент и Гренадины",
	Samoa: Samoa$c,
	"San Marino": "Сан-Марино",
	"Sao Tome and Principe": "Сан-Томе и Принсипи",
	"Saudi Arabia": "Саудовская Аравия",
	Senegal: Senegal$c,
	Serbia: Serbia$c,
	Seychelles: Seychelles$c,
	"Sierra Leone": "Сьерра-Леоне",
	Singapore: Singapore$c,
	Slovakia: Slovakia$c,
	Slovenia: Slovenia$c,
	"Solomon Islands": "Соломоновы Острова",
	Somalia: Somalia$c,
	"South Africa": "ЮАР",
	"South Georgia and the South Sandwich Islands": "Южная Георгия и Южные Сандвичевы острова",
	Spain: Spain$c,
	"Sri Lanka": "Шри-Ланка",
	Suriname: Suriname$c,
	"Svalbard and Jan Mayen": "Шпицберген и Ян-Майен",
	Swaziland: Swaziland$c,
	Sweden: Sweden$c,
	Switzerland: Switzerland$c,
	"Taiwan, Province of China": "Тайвань, провинция Китая",
	Tajikistan: Tajikistan$c,
	"Tanzania, United Republic of": "Танзания, Объединенная Республика",
	Thailand: Thailand$c,
	"Timor-Leste": "Тимор-Лешти",
	Togo: Togo$c,
	Tokelau: Tokelau$c,
	Tonga: Tonga$c,
	"Trinidad and Tobago": "Тринидад и Тобаго",
	Tunisia: Tunisia$c,
	Turkey: Turkey$c,
	Turkmenistan: Turkmenistan$c,
	"Turks and Caicos Islands": "Острова Теркс и Кайкос",
	Tuvalu: Tuvalu$c,
	Uganda: Uganda$c,
	Ukraine: Ukraine$c,
	"United Arab Emirates": "Объединенные Арабские Эмираты",
	"United Kingdom": "Великобритания",
	"United States": "Соединенные Штаты",
	Uruguay: Uruguay$c,
	Uzbekistan: Uzbekistan$c,
	Vanuatu: Vanuatu$c,
	"Venezuela, Bolivarian Republic of": "Венесуэла, Боливарианская Республика",
	"Viet Nam": "Вьетнам",
	"Virgin Islands, British": "Виргинские острова, Британские",
	"Virgin Islands, U.S.": "Виргинские острова, США",
	"Wallis and Futuna": "Уоллис и Футуна",
	Yemen: Yemen$c,
	Zambia: Zambia$c,
	Zimbabwe: Zimbabwe$c,
	"Åland Islands": "Аландские острова"
};

var t65 = {
	"No results found.": "Результаты не найдены."
};

var Done$z = "Feito";
var t66 = {
	Done: Done$z
};

var t67 = {
	"No results found.": "Nenhum resultado encontrado."
};

var t68 = {
	"Clear all": "Limpar tudo",
	"No results found.": "Nenhum resultado encontrado."
};

var Passengers$b = "Passageiros";
var t69 = {
	"Number of passengers": "Número de passageiros",
	Passengers: Passengers$b
};

var t70 = {
	"No results found.": "Nenhum resultado encontrado."
};

var Blog$b = "Blogue";
var Careers$b = "Carreiras";
var Contact$b = "Contato";
var Coverage$b = "Cobertura";
var Guides$b = "Guias";
var Imprint$b = "Imprimir";
var Parking$b = "Estacionamento";
var eSIM$b = "eSIM";
var t71 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. Marcas registradas e marcas são propriedade de seus respectivos proprietários.",
	Blog: Blog$b,
	Careers: Careers$b,
	Contact: Contact$b,
	Coverage: Coverage$b,
	Guides: Guides$b,
	Imprint: Imprint$b,
	Parking: Parking$b,
	"Privacy Policy": "política de Privacidade",
	"Terms Of Services": "Termos de Serviço",
	eSIM: eSIM$b,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} As marcas comerciais e marcas da Mozio Inc. são propriedade de seus respectivos proprietários."
};

var Filters$b = "Filtros";
var t72 = {
	"Clear All": "Limpar tudo",
	Filters: Filters$b,
	"Show results": "Mostrar resultados"
};

var Done$y = "Pronto";
var Hours$b = "Horas";
var Minutes$b = "Minutos";
var t73 = {
	"AM/PM": "AM/PM",
	Done: Done$y,
	Hours: Hours$b,
	Minutes: Minutes$b
};

var Done$x = "Feito";
var t74 = {
	Done: Done$x
};

var Afghanistan$b = "Afeganistão";
var Albania$b = "Albânia";
var Algeria$b = "Argélia";
var AmericanSamoa$b = "Samoa Americana";
var Andorra$b = "Andorra";
var Angola$b = "Angola";
var Anguilla$b = "Anguila";
var Argentina$b = "Argentina";
var Armenia$b = "Armênia";
var Aruba$b = "Aruba";
var Australia$b = "Austrália";
var Austria$b = "Áustria";
var Azerbaijan$b = "Azerbaijão";
var Bahamas$b = "Bahamas";
var Bahrain$b = "Bahrein";
var Bangladesh$b = "Bangladesh";
var Barbados$b = "Barbados";
var Belarus$b = "Bielorrússia";
var Belgium$b = "Bélgica";
var Belize$b = "Belize";
var Benin$b = "Benim";
var Bermuda$b = "Bermudas";
var Bhutan$b = "Butão";
var Botswana$b = "Botsuana";
var Brazil$b = "Brasil";
var Bulgaria$b = "Bulgária";
var Burundi$b = "Burundi";
var Cambodia$b = "Camboja";
var Cameroon$b = "Camarões";
var Canada$b = "Canadá";
var Chad$b = "Chade";
var Chile$b = "Chile";
var China$b = "China";
var Colombia$b = "Colômbia";
var Comoros$b = "Comores";
var Congo$b = "Congo";
var Croatia$b = "Croácia";
var Curacao$b = "Curaçao";
var Cyprus$b = "Chipre";
var Denmark$b = "Dinamarca";
var Djibouti$b = "Djibuti";
var Dominica$b = "Dominica";
var Ecuador$b = "Equador";
var Egypt$b = "Egito";
var Eritrea$b = "Eritreia";
var Estonia$b = "Estônia";
var Ethiopia$b = "Etiópia";
var Fiji$b = "Fiji";
var Finland$b = "Finlândia";
var France$b = "França";
var Gabon$b = "Gabão";
var Gambia$b = "Gâmbia";
var Georgia$b = "Geórgia";
var Germany$b = "Alemanha";
var Ghana$b = "Gana";
var Gibraltar$b = "Gibraltar";
var Greece$b = "Grécia";
var Greenland$b = "Groenlândia";
var Grenada$b = "Granada";
var Guadeloupe$b = "Guadalupe";
var Guam$b = "Guam";
var Guatemala$b = "Guatemala";
var Guernsey$b = "Guernsey";
var Guinea$b = "Guiné";
var Guyana$b = "Guiana";
var Haiti$b = "Haiti";
var Honduras$b = "Honduras";
var Hungary$b = "Hungria";
var Iceland$b = "Islândia";
var India$b = "Índia";
var Indonesia$b = "Indonésia";
var Iraq$b = "Iraque";
var Ireland$b = "Irlanda";
var Israel$b = "Israel";
var Italy$b = "Itália";
var Jamaica$b = "Jamaica";
var Japan$b = "Japão";
var Jersey$b = "Jersey";
var Jordan$b = "Jordânia";
var Kazakhstan$b = "Cazaquistão";
var Kenya$b = "Quênia";
var Kiribati$b = "Kiribati";
var Kuwait$b = "Kuwait";
var Kyrgyzstan$b = "Quirguistão";
var Latvia$b = "Letônia";
var Lebanon$b = "Líbano";
var Lesotho$b = "Lesoto";
var Liberia$b = "Libéria";
var Liechtenstein$b = "Liechtenstein";
var Lithuania$b = "Lituânia";
var Luxembourg$b = "Luxemburgo";
var Macao$b = "Macau";
var Madagascar$b = "Madagáscar";
var Malawi$b = "Malawi";
var Malaysia$b = "Malásia";
var Maldives$b = "Maldivas";
var Mali$b = "Mali";
var Malta$b = "Malta";
var Martinique$b = "Martinica";
var Mauritania$b = "Mauritânia";
var Mauritius$b = "Maurício";
var Mayotte$b = "Maiote";
var Mexico$b = "México";
var Monaco$b = "Mônaco";
var Mongolia$b = "Mongólia";
var Montenegro$b = "Montenegro";
var Montserrat$b = "Montserrat";
var Morocco$b = "Marrocos";
var Mozambique$b = "Moçambique";
var Myanmar$b = "Mianmar";
var Namibia$b = "Namíbia";
var Nauru$b = "Nauru";
var Nepal$b = "Nepal";
var Netherlands$b = "Holanda";
var Nicaragua$b = "Nicarágua";
var Niger$b = "Níger";
var Nigeria$b = "Nigéria";
var Niue$b = "Niue";
var Norway$b = "Noruega";
var Oman$b = "Omã";
var Pakistan$b = "Paquistão";
var Palau$b = "Palau";
var Panama$b = "Panamá";
var Paraguay$b = "Paraguai";
var Peru$b = "Peru";
var Philippines$b = "Filipinas";
var Pitcairn$b = "Pitcairn";
var Poland$b = "Polônia";
var Portugal$b = "Portugal";
var Qatar$b = "Catar";
var Romania$b = "Romênia";
var Russia$b = "Rússia";
var Rwanda$b = "Ruanda";
var Samoa$b = "Samoa";
var Senegal$b = "Senegal";
var Serbia$b = "Sérvia";
var Seychelles$b = "Seicheles";
var Singapore$b = "Cingapura";
var Slovakia$b = "Eslováquia";
var Slovenia$b = "Eslovênia";
var Somalia$b = "Somália";
var Spain$b = "Espanha";
var Suriname$b = "Suriname";
var Swaziland$b = "Suazilândia";
var Sweden$b = "Suécia";
var Switzerland$b = "Suíça";
var Tajikistan$b = "Tajiquistão";
var Thailand$b = "Tailândia";
var Togo$b = "Ir";
var Tokelau$b = "Toquelau";
var Tonga$b = "Tonga";
var Tunisia$b = "Tunísia";
var Turkey$b = "Peru";
var Turkmenistan$b = "Turcomenistão";
var Tuvalu$b = "Tuvalu";
var Uganda$b = "Uganda";
var Ukraine$b = "Ucrânia";
var Uruguay$b = "Uruguai";
var Uzbekistan$b = "Uzbequistão";
var Vanuatu$b = "Vanuatu";
var Yemen$b = "Iémen";
var Zambia$b = "Zâmbia";
var Zimbabwe$b = "Zimbábue";
var t75 = {
	Afghanistan: Afghanistan$b,
	Albania: Albania$b,
	Algeria: Algeria$b,
	AmericanSamoa: AmericanSamoa$b,
	Andorra: Andorra$b,
	Angola: Angola$b,
	Anguilla: Anguilla$b,
	"Antigua and Barbuda": "Antígua e Barbuda",
	Argentina: Argentina$b,
	Armenia: Armenia$b,
	Aruba: Aruba$b,
	Australia: Australia$b,
	Austria: Austria$b,
	Azerbaijan: Azerbaijan$b,
	Bahamas: Bahamas$b,
	Bahrain: Bahrain$b,
	Bangladesh: Bangladesh$b,
	Barbados: Barbados$b,
	Belarus: Belarus$b,
	Belgium: Belgium$b,
	Belize: Belize$b,
	Benin: Benin$b,
	Bermuda: Bermuda$b,
	Bhutan: Bhutan$b,
	"Bolivia, Plurinational State of": "Bolívia, Estado Plurinacional da",
	"Bosnia and Herzegovina": "Bósnia e Herzegovina",
	Botswana: Botswana$b,
	Brazil: Brazil$b,
	"British Indian Ocean Territory": "Território Britânico do Oceano Índico",
	"Brunei Darussalam": "Brunei Darussalam",
	Bulgaria: Bulgaria$b,
	"Burkina Faso": "Burkina Faso",
	Burundi: Burundi$b,
	Cambodia: Cambodia$b,
	Cameroon: Cameroon$b,
	Canada: Canada$b,
	"Cape Verde": "Cabo Verde",
	"Cayman Islands": "Ilhas Cayman",
	"Central African Republic": "República Centro-Africana",
	Chad: Chad$b,
	Chile: Chile$b,
	China: China$b,
	"Christmas Island": "Ilha Christmas",
	"Cocos (Keeling) Islands": "Ilhas Cocos (Keeling)",
	Colombia: Colombia$b,
	Comoros: Comoros$b,
	Congo: Congo$b,
	"Congo, The Democratic Republic of the": "Congo, República Democrática do",
	"Cook Islands": "Ilhas Cook",
	"Costa Rica": "Costa Rica",
	"Cote d'Ivoire": "Costa do Marfim",
	Croatia: Croatia$b,
	Curacao: Curacao$b,
	Cyprus: Cyprus$b,
	"Czech Republic": "República Tcheca",
	Denmark: Denmark$b,
	Djibouti: Djibouti$b,
	Dominica: Dominica$b,
	"Dominican Republic": "República Dominicana",
	Ecuador: Ecuador$b,
	Egypt: Egypt$b,
	"El Salvador": "El Salvador",
	"Equatorial Guinea": "Guiné Equatorial",
	Eritrea: Eritrea$b,
	Estonia: Estonia$b,
	Ethiopia: Ethiopia$b,
	"Falkland Islands (Malvinas)": "Ilhas Malvinas",
	"Faroe Islands": "Ilhas Faroé",
	Fiji: Fiji$b,
	Finland: Finland$b,
	France: France$b,
	"French Guiana": "Guiana Francesa",
	"French Polynesia": "Polinésia Francesa",
	Gabon: Gabon$b,
	Gambia: Gambia$b,
	Georgia: Georgia$b,
	Germany: Germany$b,
	Ghana: Ghana$b,
	Gibraltar: Gibraltar$b,
	Greece: Greece$b,
	Greenland: Greenland$b,
	Grenada: Grenada$b,
	Guadeloupe: Guadeloupe$b,
	Guam: Guam$b,
	Guatemala: Guatemala$b,
	Guernsey: Guernsey$b,
	Guinea: Guinea$b,
	"Guinea-Bissau": "Guiné-Bissau",
	Guyana: Guyana$b,
	Haiti: Haiti$b,
	"Holy See (Vatican City State)": "Santa Sé (Estado da Cidade do Vaticano)",
	Honduras: Honduras$b,
	"Hong Kong": "Hong Kong",
	Hungary: Hungary$b,
	Iceland: Iceland$b,
	India: India$b,
	Indonesia: Indonesia$b,
	Iraq: Iraq$b,
	Ireland: Ireland$b,
	"Isle of Man": "Ilha de Man",
	Israel: Israel$b,
	Italy: Italy$b,
	Jamaica: Jamaica$b,
	Japan: Japan$b,
	Jersey: Jersey$b,
	Jordan: Jordan$b,
	Kazakhstan: Kazakhstan$b,
	Kenya: Kenya$b,
	Kiribati: Kiribati$b,
	"Korea, Republic of": "Coreia, República da",
	Kuwait: Kuwait$b,
	Kyrgyzstan: Kyrgyzstan$b,
	"Lao People's Democratic Republic": "República Democrática Popular do Laos",
	Latvia: Latvia$b,
	Lebanon: Lebanon$b,
	Lesotho: Lesotho$b,
	Liberia: Liberia$b,
	"Libyan Arab Jamahiriya": "Jamahiriya Árabe Líbia",
	Liechtenstein: Liechtenstein$b,
	Lithuania: Lithuania$b,
	Luxembourg: Luxembourg$b,
	Macao: Macao$b,
	"Macedonia, The Former Yugoslav Republic of": "Macedônia, Antiga República Iugoslava da",
	Madagascar: Madagascar$b,
	Malawi: Malawi$b,
	Malaysia: Malaysia$b,
	Maldives: Maldives$b,
	Mali: Mali$b,
	Malta: Malta$b,
	"Marshall Islands": "Ilhas Marshall",
	Martinique: Martinique$b,
	Mauritania: Mauritania$b,
	Mauritius: Mauritius$b,
	Mayotte: Mayotte$b,
	Mexico: Mexico$b,
	"Micronesia, Federated States of": "Micronésia, Estados Federados da",
	"Moldova, Republic of": "Moldávia, República da",
	Monaco: Monaco$b,
	Mongolia: Mongolia$b,
	Montenegro: Montenegro$b,
	Montserrat: Montserrat$b,
	Morocco: Morocco$b,
	Mozambique: Mozambique$b,
	Myanmar: Myanmar$b,
	Namibia: Namibia$b,
	Nauru: Nauru$b,
	Nepal: Nepal$b,
	Netherlands: Netherlands$b,
	"New Caledonia": "Nova Caledônia",
	"New Zealand": "Nova Zelândia",
	Nicaragua: Nicaragua$b,
	Niger: Niger$b,
	Nigeria: Nigeria$b,
	Niue: Niue$b,
	"Norfolk Island": "Ilha Norfolk",
	"Northern Mariana Islands": "Ilhas Marianas do Norte",
	Norway: Norway$b,
	Oman: Oman$b,
	Pakistan: Pakistan$b,
	Palau: Palau$b,
	"Palestinian Territory, Occupied": "Território Palestino Ocupado",
	Panama: Panama$b,
	"Papua New Guinea": "Papua Nova Guiné",
	Paraguay: Paraguay$b,
	Peru: Peru$b,
	Philippines: Philippines$b,
	Pitcairn: Pitcairn$b,
	Poland: Poland$b,
	Portugal: Portugal$b,
	"Puerto Rico": "Porto Rico",
	Qatar: Qatar$b,
	Romania: Romania$b,
	Russia: Russia$b,
	Rwanda: Rwanda$b,
	"Réunion": "Reunião",
	"Saint Barthélemy": "São Bartolomeu",
	"Saint Helena, Ascension and Tristan Da Cunha": "Santa Helena, Ascensão e Tristão da Cunha",
	"Saint Kitts and Nevis": "São Cristóvão e Nevis",
	"Saint Lucia": "Santa Lúcia",
	"Saint Martin": "São Martinho",
	"Saint Pierre and Miquelon": "São Pedro e Miquelon",
	"Saint Vincent and the Grenadines": "São Vicente e Granadinas",
	Samoa: Samoa$b,
	"San Marino": "São Marino",
	"Sao Tome and Principe": "São Tomé e Príncipe",
	"Saudi Arabia": "Arábia Saudita",
	Senegal: Senegal$b,
	Serbia: Serbia$b,
	Seychelles: Seychelles$b,
	"Sierra Leone": "Serra Leoa",
	Singapore: Singapore$b,
	Slovakia: Slovakia$b,
	Slovenia: Slovenia$b,
	"Solomon Islands": "Ilhas Salomão",
	Somalia: Somalia$b,
	"South Africa": "África do Sul",
	"South Georgia and the South Sandwich Islands": "Ilhas Geórgia do Sul e Sandwich do Sul",
	Spain: Spain$b,
	"Sri Lanka": "Sri Lanka",
	Suriname: Suriname$b,
	"Svalbard and Jan Mayen": "Svalbard e Jan Mayen",
	Swaziland: Swaziland$b,
	Sweden: Sweden$b,
	Switzerland: Switzerland$b,
	"Taiwan, Province of China": "Taiwan, Província da China",
	Tajikistan: Tajikistan$b,
	"Tanzania, United Republic of": "Tanzânia, República Unida da",
	Thailand: Thailand$b,
	"Timor-Leste": "Timor-Leste",
	Togo: Togo$b,
	Tokelau: Tokelau$b,
	Tonga: Tonga$b,
	"Trinidad and Tobago": "Trinidad e Tobago",
	Tunisia: Tunisia$b,
	Turkey: Turkey$b,
	Turkmenistan: Turkmenistan$b,
	"Turks and Caicos Islands": "Ilhas Turcas e Caicos",
	Tuvalu: Tuvalu$b,
	Uganda: Uganda$b,
	Ukraine: Ukraine$b,
	"United Arab Emirates": "Emirados Árabes Unidos",
	"United Kingdom": "Reino Unido",
	"United States": "Estados Unidos",
	Uruguay: Uruguay$b,
	Uzbekistan: Uzbekistan$b,
	Vanuatu: Vanuatu$b,
	"Venezuela, Bolivarian Republic of": "Venezuela, República Bolivariana da",
	"Viet Nam": "Vietnã",
	"Virgin Islands, British": "Ilhas Virgens Britânicas",
	"Virgin Islands, U.S.": "Ilhas Virgens, EUA",
	"Wallis and Futuna": "Wallis e Futuna",
	Yemen: Yemen$b,
	Zambia: Zambia$b,
	Zimbabwe: Zimbabwe$b,
	"Åland Islands": "Ilhas Åland"
};

var t76 = {
	"No results found.": "Nenhum resultado encontrado."
};

var Done$w = "Zrobione";
var t77 = {
	Done: Done$w
};

var t78 = {
	"No results found.": "Nie znaleziono wyników."
};

var t79 = {
	"Clear all": "Wyczyść wszystko",
	"No results found.": "Nie znaleziono wyników."
};

var Passengers$a = "Pasażerowie";
var t80 = {
	"Number of passengers": "Liczba pasażerów",
	Passengers: Passengers$a
};

var t81 = {
	"No results found.": "Nie znaleziono wyników."
};

var Blog$a = "Blog";
var Careers$a = "Kariera";
var Contact$a = "Kontakt";
var Coverage$a = "Zasięg";
var Guides$a = "Przewodniki";
var Imprint$a = "Odcisk";
var Parking$a = "Parking";
var eSIM$a = "eSIM";
var t82 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. Znaki towarowe i marki są własnością ich odpowiednich właścicieli.",
	Blog: Blog$a,
	Careers: Careers$a,
	Contact: Contact$a,
	Coverage: Coverage$a,
	Guides: Guides$a,
	Imprint: Imprint$a,
	Parking: Parking$a,
	"Privacy Policy": "Polityka prywatności",
	"Terms Of Services": "Warunki korzystania z usług",
	eSIM: eSIM$a,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Mozio Inc. Znaki towarowe i marki są własnością ich odpowiednich właścicieli."
};

var Filters$a = "Filtry";
var t83 = {
	"Clear All": "Wyczyść wszystko",
	Filters: Filters$a,
	"Show results": "Pokaż wyniki"
};

var Done$v = "Gotowe";
var Hours$a = "Godziny";
var Minutes$a = "Minuty";
var t84 = {
	"AM/PM": "AM/PM",
	Done: Done$v,
	Hours: Hours$a,
	Minutes: Minutes$a
};

var Done$u = "Zrobione";
var t85 = {
	Done: Done$u
};

var Afghanistan$a = "Afganistan";
var Albania$a = "Albania";
var Algeria$a = "Algieria";
var AmericanSamoa$a = "Samoa Amerykańskie";
var Andorra$a = "Andora";
var Angola$a = "Angola";
var Anguilla$a = "Anguilla";
var Argentina$a = "Argentyna";
var Armenia$a = "Armenia";
var Aruba$a = "Aruba";
var Australia$a = "Australia";
var Austria$a = "Austria";
var Azerbaijan$a = "Azerbejdżan";
var Bahamas$a = "Bahamy";
var Bahrain$a = "Bahrajn";
var Bangladesh$a = "Bangladesz";
var Barbados$a = "Barbados";
var Belarus$a = "Białoruś";
var Belgium$a = "Belgia";
var Belize$a = "Belize";
var Benin$a = "Benin";
var Bermuda$a = "Bermudy";
var Bhutan$a = "Bhutan";
var Botswana$a = "Botswana";
var Brazil$a = "Brazylia";
var Bulgaria$a = "Bułgaria";
var Burundi$a = "Burundi";
var Cambodia$a = "Kambodża";
var Cameroon$a = "Kamerun";
var Canada$a = "Kanada";
var Chad$a = "Czad";
var Chile$a = "Chile";
var China$a = "Chiny";
var Colombia$a = "Kolumbia";
var Comoros$a = "Komory";
var Congo$a = "Kongo";
var Croatia$a = "Chorwacja";
var Curacao$a = "Curacao";
var Cyprus$a = "Cypr";
var Denmark$a = "Dania";
var Djibouti$a = "Dżibuti";
var Dominica$a = "Dominika";
var Ecuador$a = "Ekwador";
var Egypt$a = "Egipt";
var Eritrea$a = "Erytrea";
var Estonia$a = "Estonia";
var Ethiopia$a = "Etiopia";
var Fiji$a = "Fidżi";
var Finland$a = "Finlandia";
var France$a = "Francja";
var Gabon$a = "Gabon";
var Gambia$a = "Gambia";
var Georgia$a = "Gruzja";
var Germany$a = "Niemcy";
var Ghana$a = "Ghana";
var Gibraltar$a = "Gibraltar";
var Greece$a = "Grecja";
var Greenland$a = "Grenlandia";
var Grenada$a = "Grenada";
var Guadeloupe$a = "Gwadelupa";
var Guam$a = "Guam";
var Guatemala$a = "Gwatemala";
var Guernsey$a = "Guernsey";
var Guinea$a = "Gwinea";
var Guyana$a = "Gujana";
var Haiti$a = "Haiti";
var Honduras$a = "Honduras";
var Hungary$a = "Węgry";
var Iceland$a = "Islandia";
var India$a = "Indie";
var Indonesia$a = "Indonezja";
var Iraq$a = "Irak";
var Ireland$a = "Irlandia";
var Israel$a = "Izrael";
var Italy$a = "Włochy";
var Jamaica$a = "Jamajka";
var Japan$a = "Japonia";
var Jersey$a = "Golf";
var Jordan$a = "Jordania";
var Kazakhstan$a = "Kazachstan";
var Kenya$a = "Kenia";
var Kiribati$a = "Kiribati";
var Kuwait$a = "Kuwejt";
var Kyrgyzstan$a = "Kirgistan";
var Latvia$a = "Łotwa";
var Lebanon$a = "Liban";
var Lesotho$a = "Lesotho";
var Liberia$a = "Liberia";
var Liechtenstein$a = "Liechtenstein";
var Lithuania$a = "Litwa";
var Luxembourg$a = "Luksemburg";
var Macao$a = "Makao";
var Madagascar$a = "Madagaskar";
var Malawi$a = "Malawi";
var Malaysia$a = "Malezja";
var Maldives$a = "Malediwy";
var Mali$a = "Mali";
var Malta$a = "Malta";
var Martinique$a = "Martynika";
var Mauritania$a = "Mauretania";
var Mauritius$a = "Mauritius";
var Mayotte$a = "Majotta";
var Mexico$a = "Meksyk";
var Monaco$a = "Monako";
var Mongolia$a = "Mongolia";
var Montenegro$a = "Czarnogóra";
var Montserrat$a = "Montserrat";
var Morocco$a = "Maroko";
var Mozambique$a = "Mozambik";
var Myanmar$a = "Myanmar";
var Namibia$a = "Namibia";
var Nauru$a = "Nauru";
var Nepal$a = "Nepal";
var Netherlands$a = "Niderlandy";
var Nicaragua$a = "Nikaragua";
var Niger$a = "Niger";
var Nigeria$a = "Nigeria";
var Niue$a = "Niue";
var Norway$a = "Norwegia";
var Oman$a = "Oman";
var Pakistan$a = "Pakistan";
var Palau$a = "Palau";
var Panama$a = "Panama";
var Paraguay$a = "Paragwaj";
var Peru$a = "Peru";
var Philippines$a = "Filipiny";
var Pitcairn$a = "Pitcairn";
var Poland$a = "Polska";
var Portugal$a = "Portugalia";
var Qatar$a = "Katar";
var Romania$a = "Rumunia";
var Russia$a = "Rosja";
var Rwanda$a = "Rwanda";
var Samoa$a = "Samoa";
var Senegal$a = "Senegal";
var Serbia$a = "Serbia";
var Seychelles$a = "Seszele";
var Singapore$a = "Singapur";
var Slovakia$a = "Słowacja";
var Slovenia$a = "Słowenia";
var Somalia$a = "Somali";
var Spain$a = "Hiszpania";
var Suriname$a = "Surinam";
var Swaziland$a = "Suazi";
var Sweden$a = "Szwecja";
var Switzerland$a = "Szwajcaria";
var Tajikistan$a = "Tadżykistan";
var Thailand$a = "Tajlandia";
var Togo$a = "Togo";
var Tokelau$a = "Tokelau";
var Tonga$a = "Tonga";
var Tunisia$a = "Tunezja";
var Turkey$a = "Indyk";
var Turkmenistan$a = "Turkmenia";
var Tuvalu$a = "Tuvalu";
var Uganda$a = "Uganda";
var Ukraine$a = "Ukraina";
var Uruguay$a = "Urugwaj";
var Uzbekistan$a = "Uzbekistan";
var Vanuatu$a = "Vanuatu";
var Yemen$a = "Jemen";
var Zambia$a = "Zambia";
var Zimbabwe$a = "Zimbabwe";
var t86 = {
	Afghanistan: Afghanistan$a,
	Albania: Albania$a,
	Algeria: Algeria$a,
	AmericanSamoa: AmericanSamoa$a,
	Andorra: Andorra$a,
	Angola: Angola$a,
	Anguilla: Anguilla$a,
	"Antigua and Barbuda": "Antigua i Barbuda",
	Argentina: Argentina$a,
	Armenia: Armenia$a,
	Aruba: Aruba$a,
	Australia: Australia$a,
	Austria: Austria$a,
	Azerbaijan: Azerbaijan$a,
	Bahamas: Bahamas$a,
	Bahrain: Bahrain$a,
	Bangladesh: Bangladesh$a,
	Barbados: Barbados$a,
	Belarus: Belarus$a,
	Belgium: Belgium$a,
	Belize: Belize$a,
	Benin: Benin$a,
	Bermuda: Bermuda$a,
	Bhutan: Bhutan$a,
	"Bolivia, Plurinational State of": "Boliwia, Wielonarodowe Państwo",
	"Bosnia and Herzegovina": "Bośnia i Hercegowina",
	Botswana: Botswana$a,
	Brazil: Brazil$a,
	"British Indian Ocean Territory": "Brytyjskie Terytorium Oceanu Indyjskiego",
	"Brunei Darussalam": "Brunei Darussalam",
	Bulgaria: Bulgaria$a,
	"Burkina Faso": "Burkina Faso",
	Burundi: Burundi$a,
	Cambodia: Cambodia$a,
	Cameroon: Cameroon$a,
	Canada: Canada$a,
	"Cape Verde": "Republika Zielonego Przylądka",
	"Cayman Islands": "Wyspy Kajmany",
	"Central African Republic": "Republika Środkowoafrykańska",
	Chad: Chad$a,
	Chile: Chile$a,
	China: China$a,
	"Christmas Island": "Wyspa Bożego Narodzenia",
	"Cocos (Keeling) Islands": "Wyspy Kokosowe (Keelinga)",
	Colombia: Colombia$a,
	Comoros: Comoros$a,
	Congo: Congo$a,
	"Congo, The Democratic Republic of the": "Kongo, Demokratyczna Republika",
	"Cook Islands": "Wyspy Cooka",
	"Costa Rica": "Kostaryka",
	"Cote d'Ivoire": "Wybrzeże Kości Słoniowej",
	Croatia: Croatia$a,
	Curacao: Curacao$a,
	Cyprus: Cyprus$a,
	"Czech Republic": "Czechy",
	Denmark: Denmark$a,
	Djibouti: Djibouti$a,
	Dominica: Dominica$a,
	"Dominican Republic": "Republika Dominikańska",
	Ecuador: Ecuador$a,
	Egypt: Egypt$a,
	"El Salvador": "Salwador",
	"Equatorial Guinea": "Gwinea Równikowa",
	Eritrea: Eritrea$a,
	Estonia: Estonia$a,
	Ethiopia: Ethiopia$a,
	"Falkland Islands (Malvinas)": "Falklandy (Malwiny)",
	"Faroe Islands": "Wyspy Owcze",
	Fiji: Fiji$a,
	Finland: Finland$a,
	France: France$a,
	"French Guiana": "Gujana Francuska",
	"French Polynesia": "Polinezja Francuska",
	Gabon: Gabon$a,
	Gambia: Gambia$a,
	Georgia: Georgia$a,
	Germany: Germany$a,
	Ghana: Ghana$a,
	Gibraltar: Gibraltar$a,
	Greece: Greece$a,
	Greenland: Greenland$a,
	Grenada: Grenada$a,
	Guadeloupe: Guadeloupe$a,
	Guam: Guam$a,
	Guatemala: Guatemala$a,
	Guernsey: Guernsey$a,
	Guinea: Guinea$a,
	"Guinea-Bissau": "Gwinea Bissau",
	Guyana: Guyana$a,
	Haiti: Haiti$a,
	"Holy See (Vatican City State)": "Stolica Apostolska (Państwo Watykańskie)",
	Honduras: Honduras$a,
	"Hong Kong": "Hongkong",
	Hungary: Hungary$a,
	Iceland: Iceland$a,
	India: India$a,
	Indonesia: Indonesia$a,
	Iraq: Iraq$a,
	Ireland: Ireland$a,
	"Isle of Man": "Wyspa Man",
	Israel: Israel$a,
	Italy: Italy$a,
	Jamaica: Jamaica$a,
	Japan: Japan$a,
	Jersey: Jersey$a,
	Jordan: Jordan$a,
	Kazakhstan: Kazakhstan$a,
	Kenya: Kenya$a,
	Kiribati: Kiribati$a,
	"Korea, Republic of": "Korea, Republika",
	Kuwait: Kuwait$a,
	Kyrgyzstan: Kyrgyzstan$a,
	"Lao People's Democratic Republic": "Laotańska Republika Ludowo-Demokratyczna",
	Latvia: Latvia$a,
	Lebanon: Lebanon$a,
	Lesotho: Lesotho$a,
	Liberia: Liberia$a,
	"Libyan Arab Jamahiriya": "Libijska Arabska Dżamahirija",
	Liechtenstein: Liechtenstein$a,
	Lithuania: Lithuania$a,
	Luxembourg: Luxembourg$a,
	Macao: Macao$a,
	"Macedonia, The Former Yugoslav Republic of": "Macedonia, Była Jugosłowiańska Republika",
	Madagascar: Madagascar$a,
	Malawi: Malawi$a,
	Malaysia: Malaysia$a,
	Maldives: Maldives$a,
	Mali: Mali$a,
	Malta: Malta$a,
	"Marshall Islands": "Wyspy Marshalla",
	Martinique: Martinique$a,
	Mauritania: Mauritania$a,
	Mauritius: Mauritius$a,
	Mayotte: Mayotte$a,
	Mexico: Mexico$a,
	"Micronesia, Federated States of": "Mikronezja, Sfederowane Stany",
	"Moldova, Republic of": "Mołdawia, Republika",
	Monaco: Monaco$a,
	Mongolia: Mongolia$a,
	Montenegro: Montenegro$a,
	Montserrat: Montserrat$a,
	Morocco: Morocco$a,
	Mozambique: Mozambique$a,
	Myanmar: Myanmar$a,
	Namibia: Namibia$a,
	Nauru: Nauru$a,
	Nepal: Nepal$a,
	Netherlands: Netherlands$a,
	"New Caledonia": "Nowa Kaledonia",
	"New Zealand": "Nowa Zelandia",
	Nicaragua: Nicaragua$a,
	Niger: Niger$a,
	Nigeria: Nigeria$a,
	Niue: Niue$a,
	"Norfolk Island": "Wyspa Norfolk",
	"Northern Mariana Islands": "Mariany Północne",
	Norway: Norway$a,
	Oman: Oman$a,
	Pakistan: Pakistan$a,
	Palau: Palau$a,
	"Palestinian Territory, Occupied": "Terytorium Palestyńskie, Okupowane",
	Panama: Panama$a,
	"Papua New Guinea": "Papua-Nowa Gwinea",
	Paraguay: Paraguay$a,
	Peru: Peru$a,
	Philippines: Philippines$a,
	Pitcairn: Pitcairn$a,
	Poland: Poland$a,
	Portugal: Portugal$a,
	"Puerto Rico": "Portoryko",
	Qatar: Qatar$a,
	Romania: Romania$a,
	Russia: Russia$a,
	Rwanda: Rwanda$a,
	"Réunion": "Zjazd",
	"Saint Barthélemy": "Święty Bartłomiej",
	"Saint Helena, Ascension and Tristan Da Cunha": "Święta Helena, Wniebowstąpienie i Tristan da Cunha",
	"Saint Kitts and Nevis": "Saint Kitts i Nevis",
	"Saint Lucia": "Święta Łucja",
	"Saint Martin": "Święty Marcin",
	"Saint Pierre and Miquelon": "Saint-Pierre i Miquelon",
	"Saint Vincent and the Grenadines": "Saint Vincent i Grenadyny",
	Samoa: Samoa$a,
	"San Marino": "San Marino",
	"Sao Tome and Principe": "Wyspy Świętego Tomasza i Książęca",
	"Saudi Arabia": "Arabia Saudyjska",
	Senegal: Senegal$a,
	Serbia: Serbia$a,
	Seychelles: Seychelles$a,
	"Sierra Leone": "Sierra Leone",
	Singapore: Singapore$a,
	Slovakia: Slovakia$a,
	Slovenia: Slovenia$a,
	"Solomon Islands": "Wyspy Salomona",
	Somalia: Somalia$a,
	"South Africa": "Republika Południowej Afryki",
	"South Georgia and the South Sandwich Islands": "Georgia Południowa i Wyspy Sandwich Południowy",
	Spain: Spain$a,
	"Sri Lanka": "Sri Lanka",
	Suriname: Suriname$a,
	"Svalbard and Jan Mayen": "Svalbard i Jan Mayen",
	Swaziland: Swaziland$a,
	Sweden: Sweden$a,
	Switzerland: Switzerland$a,
	"Taiwan, Province of China": "Tajwan, prowincja Chin",
	Tajikistan: Tajikistan$a,
	"Tanzania, United Republic of": "Tanzania, Zjednoczona Republika",
	Thailand: Thailand$a,
	"Timor-Leste": "Timor Wschodni",
	Togo: Togo$a,
	Tokelau: Tokelau$a,
	Tonga: Tonga$a,
	"Trinidad and Tobago": "Trynidad i Tobago",
	Tunisia: Tunisia$a,
	Turkey: Turkey$a,
	Turkmenistan: Turkmenistan$a,
	"Turks and Caicos Islands": "Wyspy Turks i Caicos",
	Tuvalu: Tuvalu$a,
	Uganda: Uganda$a,
	Ukraine: Ukraine$a,
	"United Arab Emirates": "Emiraty Arabskie",
	"United Kingdom": "Zjednoczone Królestwo",
	"United States": "Stany Zjednoczone",
	Uruguay: Uruguay$a,
	Uzbekistan: Uzbekistan$a,
	Vanuatu: Vanuatu$a,
	"Venezuela, Bolivarian Republic of": "Wenezuela, Republika Boliwariańska",
	"Viet Nam": "Wietnam",
	"Virgin Islands, British": "Brytyjskie Wyspy Dziewicze",
	"Virgin Islands, U.S.": "Wyspy Dziewicze, USA",
	"Wallis and Futuna": "Wallis i Futuna",
	Yemen: Yemen$a,
	Zambia: Zambia$a,
	Zimbabwe: Zimbabwe$a,
	"Åland Islands": "Wyspy Alandzkie"
};

var t87 = {
	"No results found.": "Nie znaleziono wyników."
};

var Done$t = "Klaar";
var t88 = {
	Done: Done$t
};

var t89 = {
	"No results found.": "Geen resultaten gevonden."
};

var t90 = {
	"Clear all": "Alles wissen",
	"No results found.": "Geen resultaten gevonden."
};

var Passengers$9 = "Passagiers";
var t91 = {
	"Number of passengers": "Aantal passagiers",
	Passengers: Passengers$9
};

var t92 = {
	"No results found.": "Geen resultaten gevonden."
};

var Blog$9 = "Blog";
var Careers$9 = "Carrières";
var Contact$9 = "Contact";
var Coverage$9 = "Dekking";
var Guides$9 = "Gidsen";
var Imprint$9 = "Impressum";
var Parking$9 = "Parkeren";
var eSIM$9 = "eSIM";
var t93 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. Handelsmerken en merken zijn eigendom van hun respectievelijke eigenaren.",
	Blog: Blog$9,
	Careers: Careers$9,
	Contact: Contact$9,
	Coverage: Coverage$9,
	Guides: Guides$9,
	Imprint: Imprint$9,
	Parking: Parking$9,
	"Privacy Policy": "Privacybeleid",
	"Terms Of Services": "Servicevoorwaarden",
	eSIM: eSIM$9,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} De handelsmerken en merknamen van Mozio Inc. zijn eigendom van hun respectievelijke eigenaren."
};

var Filters$9 = "Filters";
var t94 = {
	"Clear All": "Alles wissen",
	Filters: Filters$9,
	"Show results": "Resultaten weergeven"
};

var Done$s = "Gereed";
var Hours$9 = "Uren";
var Minutes$9 = "Minuten";
var t95 = {
	"AM/PM": "AM/PM",
	Done: Done$s,
	Hours: Hours$9,
	Minutes: Minutes$9
};

var Done$r = "Klaar";
var t96 = {
	Done: Done$r
};

var Afghanistan$9 = "Afghanistan";
var Albania$9 = "Albanië";
var Algeria$9 = "Algerije";
var AmericanSamoa$9 = "Amerikaans Samoa";
var Andorra$9 = "Andorra";
var Angola$9 = "Angola";
var Anguilla$9 = "Anguilla";
var Argentina$9 = "Argentinië";
var Armenia$9 = "Armenië";
var Aruba$9 = "Aruba";
var Australia$9 = "Australië";
var Austria$9 = "Oostenrijk";
var Azerbaijan$9 = "Azerbeidzjan";
var Bahamas$9 = "Bahamas";
var Bahrain$9 = "Bahrein";
var Bangladesh$9 = "Bangladesh";
var Barbados$9 = "Barbados";
var Belarus$9 = "Wit-Rusland";
var Belgium$9 = "België";
var Belize$9 = "Belize";
var Benin$9 = "Benin";
var Bermuda$9 = "Bermuda";
var Bhutan$9 = "Bhutan";
var Botswana$9 = "Botswana";
var Brazil$9 = "Brazilië";
var Bulgaria$9 = "Bulgarije";
var Burundi$9 = "Boeroendi";
var Cambodia$9 = "Cambodja";
var Cameroon$9 = "Kameroen";
var Canada$9 = "Canada";
var Chad$9 = "Tsjaad";
var Chile$9 = "Chili";
var China$9 = "China";
var Colombia$9 = "Colombia";
var Comoros$9 = "Comoren";
var Congo$9 = "Congo";
var Croatia$9 = "Kroatië";
var Curacao$9 = "Curacao";
var Cyprus$9 = "Cyprus";
var Denmark$9 = "Denemarken";
var Djibouti$9 = "Djibouti";
var Dominica$9 = "Dominica";
var Ecuador$9 = "Ecuador";
var Egypt$9 = "Egypte";
var Eritrea$9 = "Eritrea";
var Estonia$9 = "Estland";
var Ethiopia$9 = "Ethiopië";
var Fiji$9 = "Fiji";
var Finland$9 = "Finland";
var France$9 = "Frankrijk";
var Gabon$9 = "Gabon";
var Gambia$9 = "Gambia";
var Georgia$9 = "Georgië";
var Germany$9 = "Duitsland";
var Ghana$9 = "Ghana";
var Gibraltar$9 = "Gibraltar";
var Greece$9 = "Griekenland";
var Greenland$9 = "Groenland";
var Grenada$9 = "Grenada";
var Guadeloupe$9 = "Guadeloupe";
var Guam$9 = "Guam";
var Guatemala$9 = "Guatemala";
var Guernsey$9 = "Guernsey";
var Guinea$9 = "Guinea";
var Guyana$9 = "Guyana";
var Haiti$9 = "Haïti";
var Honduras$9 = "Honduras";
var Hungary$9 = "Hongarije";
var Iceland$9 = "IJsland";
var India$9 = "Indië";
var Indonesia$9 = "Indonesië";
var Iraq$9 = "Irak";
var Ireland$9 = "Ierland";
var Israel$9 = "Israël";
var Italy$9 = "Italië";
var Jamaica$9 = "Jamaica";
var Japan$9 = "Japan";
var Jersey$9 = "Jersey";
var Jordan$9 = "Jordanië";
var Kazakhstan$9 = "Kazachstan";
var Kenya$9 = "Kenia";
var Kiribati$9 = "Kiribati";
var Kuwait$9 = "Koeweit";
var Kyrgyzstan$9 = "Kirgizië";
var Latvia$9 = "Letland";
var Lebanon$9 = "Libanon";
var Lesotho$9 = "Lesotho";
var Liberia$9 = "Liberia";
var Liechtenstein$9 = "Liechtenstein";
var Lithuania$9 = "Litouwen";
var Luxembourg$9 = "Luxemburg";
var Macao$9 = "Macau";
var Madagascar$9 = "Madagascar";
var Malawi$9 = "Malawi";
var Malaysia$9 = "Maleisië";
var Maldives$9 = "Maldiven";
var Mali$9 = "Mali";
var Malta$9 = "Malta";
var Martinique$9 = "Martinique";
var Mauritania$9 = "Mauritanië";
var Mauritius$9 = "Mauritius";
var Mayotte$9 = "Mayotte";
var Mexico$9 = "Mexico";
var Monaco$9 = "Monaco";
var Mongolia$9 = "Mongolië";
var Montenegro$9 = "Montenegro";
var Montserrat$9 = "Montserrat";
var Morocco$9 = "Marokko";
var Mozambique$9 = "Mozambique";
var Myanmar$9 = "Myanmar";
var Namibia$9 = "Namibië";
var Nauru$9 = "Nauru";
var Nepal$9 = "Nepal";
var Netherlands$9 = "Nederland";
var Nicaragua$9 = "Nicaragua";
var Niger$9 = "Niger";
var Nigeria$9 = "Nigeria";
var Niue$9 = "Niue";
var Norway$9 = "Noorwegen";
var Oman$9 = "Oman";
var Pakistan$9 = "Pakistan";
var Palau$9 = "Palau";
var Panama$9 = "Panama";
var Paraguay$9 = "Paraguay";
var Peru$9 = "Peru";
var Philippines$9 = "Filippijnen";
var Pitcairn$9 = "Pitcairn";
var Poland$9 = "Polen";
var Portugal$9 = "Portugal";
var Qatar$9 = "Qatar";
var Romania$9 = "Roemenië";
var Russia$9 = "Rusland";
var Rwanda$9 = "Rwanda";
var Samoa$9 = "Samoa";
var Senegal$9 = "Senegal";
var Serbia$9 = "Servië";
var Seychelles$9 = "Seychellen";
var Singapore$9 = "Singapore";
var Slovakia$9 = "Slowakije";
var Slovenia$9 = "Slovenië";
var Somalia$9 = "Somalië";
var Spain$9 = "Spanje";
var Suriname$9 = "Suriname";
var Swaziland$9 = "Swaziland";
var Sweden$9 = "Zweden";
var Switzerland$9 = "Zwitserland";
var Tajikistan$9 = "Tadzjikistan";
var Thailand$9 = "Thailand";
var Togo$9 = "Togo";
var Tokelau$9 = "Tokelau";
var Tonga$9 = "Tonga";
var Tunisia$9 = "Tunesië";
var Turkey$9 = "Kalkoen";
var Turkmenistan$9 = "Turkmenistan";
var Tuvalu$9 = "Tuvalu";
var Uganda$9 = "Oeganda";
var Ukraine$9 = "Oekraïne";
var Uruguay$9 = "Uruguay";
var Uzbekistan$9 = "Oezbekistan";
var Vanuatu$9 = "Vanuatu";
var Yemen$9 = "Jemen";
var Zambia$9 = "Zambia";
var Zimbabwe$9 = "Zimbabwe";
var t97 = {
	Afghanistan: Afghanistan$9,
	Albania: Albania$9,
	Algeria: Algeria$9,
	AmericanSamoa: AmericanSamoa$9,
	Andorra: Andorra$9,
	Angola: Angola$9,
	Anguilla: Anguilla$9,
	"Antigua and Barbuda": "Antigua en Barbuda",
	Argentina: Argentina$9,
	Armenia: Armenia$9,
	Aruba: Aruba$9,
	Australia: Australia$9,
	Austria: Austria$9,
	Azerbaijan: Azerbaijan$9,
	Bahamas: Bahamas$9,
	Bahrain: Bahrain$9,
	Bangladesh: Bangladesh$9,
	Barbados: Barbados$9,
	Belarus: Belarus$9,
	Belgium: Belgium$9,
	Belize: Belize$9,
	Benin: Benin$9,
	Bermuda: Bermuda$9,
	Bhutan: Bhutan$9,
	"Bolivia, Plurinational State of": "Bolivia, Plurinationale Staat van",
	"Bosnia and Herzegovina": "Bosnië en Herzegovina",
	Botswana: Botswana$9,
	Brazil: Brazil$9,
	"British Indian Ocean Territory": "Brits Territorium in de Indische Oceaan",
	"Brunei Darussalam": "Brunei Darussalam",
	Bulgaria: Bulgaria$9,
	"Burkina Faso": "Burkina Faso",
	Burundi: Burundi$9,
	Cambodia: Cambodia$9,
	Cameroon: Cameroon$9,
	Canada: Canada$9,
	"Cape Verde": "Kaapverdië",
	"Cayman Islands": "Kaaimaneilanden",
	"Central African Republic": "Centraal-Afrikaanse Republiek",
	Chad: Chad$9,
	Chile: Chile$9,
	China: China$9,
	"Christmas Island": "Christmaseiland",
	"Cocos (Keeling) Islands": "Cocos (Keeling) Eilanden",
	Colombia: Colombia$9,
	Comoros: Comoros$9,
	Congo: Congo$9,
	"Congo, The Democratic Republic of the": "Congo, Democratische Republiek van de",
	"Cook Islands": "Cookeilanden",
	"Costa Rica": "Costa Rica",
	"Cote d'Ivoire": "Ivoorkust",
	Croatia: Croatia$9,
	Curacao: Curacao$9,
	Cyprus: Cyprus$9,
	"Czech Republic": "Tsjechische Republiek",
	Denmark: Denmark$9,
	Djibouti: Djibouti$9,
	Dominica: Dominica$9,
	"Dominican Republic": "Dominicaanse Republiek",
	Ecuador: Ecuador$9,
	Egypt: Egypt$9,
	"El Salvador": "El Salvador",
	"Equatorial Guinea": "Equatoriaal-Guinea",
	Eritrea: Eritrea$9,
	Estonia: Estonia$9,
	Ethiopia: Ethiopia$9,
	"Falkland Islands (Malvinas)": "Falklandeilanden (Malvinas)",
	"Faroe Islands": "Faeröer Eilanden",
	Fiji: Fiji$9,
	Finland: Finland$9,
	France: France$9,
	"French Guiana": "Frans-Guyana",
	"French Polynesia": "Frans-Polynesië",
	Gabon: Gabon$9,
	Gambia: Gambia$9,
	Georgia: Georgia$9,
	Germany: Germany$9,
	Ghana: Ghana$9,
	Gibraltar: Gibraltar$9,
	Greece: Greece$9,
	Greenland: Greenland$9,
	Grenada: Grenada$9,
	Guadeloupe: Guadeloupe$9,
	Guam: Guam$9,
	Guatemala: Guatemala$9,
	Guernsey: Guernsey$9,
	Guinea: Guinea$9,
	"Guinea-Bissau": "Guinee-Bissau",
	Guyana: Guyana$9,
	Haiti: Haiti$9,
	"Holy See (Vatican City State)": "Heilige Stoel (Vaticaanstad)",
	Honduras: Honduras$9,
	"Hong Kong": "Hongkong",
	Hungary: Hungary$9,
	Iceland: Iceland$9,
	India: India$9,
	Indonesia: Indonesia$9,
	Iraq: Iraq$9,
	Ireland: Ireland$9,
	"Isle of Man": "Eiland Man",
	Israel: Israel$9,
	Italy: Italy$9,
	Jamaica: Jamaica$9,
	Japan: Japan$9,
	Jersey: Jersey$9,
	Jordan: Jordan$9,
	Kazakhstan: Kazakhstan$9,
	Kenya: Kenya$9,
	Kiribati: Kiribati$9,
	"Korea, Republic of": "Korea, Republiek van",
	Kuwait: Kuwait$9,
	Kyrgyzstan: Kyrgyzstan$9,
	"Lao People's Democratic Republic": "Laotiaanse Democratische Volksrepubliek",
	Latvia: Latvia$9,
	Lebanon: Lebanon$9,
	Lesotho: Lesotho$9,
	Liberia: Liberia$9,
	"Libyan Arab Jamahiriya": "Libisch-Arabische Jamahiriya",
	Liechtenstein: Liechtenstein$9,
	Lithuania: Lithuania$9,
	Luxembourg: Luxembourg$9,
	Macao: Macao$9,
	"Macedonia, The Former Yugoslav Republic of": "Macedonië, de voormalige Joegoslavische Republiek van",
	Madagascar: Madagascar$9,
	Malawi: Malawi$9,
	Malaysia: Malaysia$9,
	Maldives: Maldives$9,
	Mali: Mali$9,
	Malta: Malta$9,
	"Marshall Islands": "Marshalleilanden",
	Martinique: Martinique$9,
	Mauritania: Mauritania$9,
	Mauritius: Mauritius$9,
	Mayotte: Mayotte$9,
	Mexico: Mexico$9,
	"Micronesia, Federated States of": "Micronesië, Federale Staten van",
	"Moldova, Republic of": "Moldavië, Republiek van",
	Monaco: Monaco$9,
	Mongolia: Mongolia$9,
	Montenegro: Montenegro$9,
	Montserrat: Montserrat$9,
	Morocco: Morocco$9,
	Mozambique: Mozambique$9,
	Myanmar: Myanmar$9,
	Namibia: Namibia$9,
	Nauru: Nauru$9,
	Nepal: Nepal$9,
	Netherlands: Netherlands$9,
	"New Caledonia": "Nieuw-Caledonië",
	"New Zealand": "Nieuw-Zeeland",
	Nicaragua: Nicaragua$9,
	Niger: Niger$9,
	Nigeria: Nigeria$9,
	Niue: Niue$9,
	"Norfolk Island": "Norfolkeiland",
	"Northern Mariana Islands": "Noordelijke Marianen",
	Norway: Norway$9,
	Oman: Oman$9,
	Pakistan: Pakistan$9,
	Palau: Palau$9,
	"Palestinian Territory, Occupied": "Palestijns gebied, bezet",
	Panama: Panama$9,
	"Papua New Guinea": "Papoea-Nieuw-Guinea",
	Paraguay: Paraguay$9,
	Peru: Peru$9,
	Philippines: Philippines$9,
	Pitcairn: Pitcairn$9,
	Poland: Poland$9,
	Portugal: Portugal$9,
	"Puerto Rico": "Puerto Rico",
	Qatar: Qatar$9,
	Romania: Romania$9,
	Russia: Russia$9,
	Rwanda: Rwanda$9,
	"Réunion": "Bijeenkomst",
	"Saint Barthélemy": "Sint-Barthélemy",
	"Saint Helena, Ascension and Tristan Da Cunha": "Sint-Helena, Hemelvaart en Tristan Da Cunha",
	"Saint Kitts and Nevis": "Saint Kitts en Nevis",
	"Saint Lucia": "Sint Lucia",
	"Saint Martin": "Sint Maarten",
	"Saint Pierre and Miquelon": "Saint-Pierre en Miquelon",
	"Saint Vincent and the Grenadines": "Saint Vincent en de Grenadines",
	Samoa: Samoa$9,
	"San Marino": "San Marino",
	"Sao Tome and Principe": "Sao Tomé en Principe",
	"Saudi Arabia": "Saoedi-Arabië",
	Senegal: Senegal$9,
	Serbia: Serbia$9,
	Seychelles: Seychelles$9,
	"Sierra Leone": "Sierra Leone",
	Singapore: Singapore$9,
	Slovakia: Slovakia$9,
	Slovenia: Slovenia$9,
	"Solomon Islands": "Salomonseilanden",
	Somalia: Somalia$9,
	"South Africa": "Zuid-Afrika",
	"South Georgia and the South Sandwich Islands": "Zuid-Georgia en de Zuidelijke Sandwicheilanden",
	Spain: Spain$9,
	"Sri Lanka": "Sri Lanka",
	Suriname: Suriname$9,
	"Svalbard and Jan Mayen": "Spitsbergen en Jan Mayen",
	Swaziland: Swaziland$9,
	Sweden: Sweden$9,
	Switzerland: Switzerland$9,
	"Taiwan, Province of China": "Taiwan, provincie van China",
	Tajikistan: Tajikistan$9,
	"Tanzania, United Republic of": "Tanzania, Verenigde Republiek van",
	Thailand: Thailand$9,
	"Timor-Leste": "Oost-Timor",
	Togo: Togo$9,
	Tokelau: Tokelau$9,
	Tonga: Tonga$9,
	"Trinidad and Tobago": "Trinidad en Tobago",
	Tunisia: Tunisia$9,
	Turkey: Turkey$9,
	Turkmenistan: Turkmenistan$9,
	"Turks and Caicos Islands": "Turks- en Caicoseilanden",
	Tuvalu: Tuvalu$9,
	Uganda: Uganda$9,
	Ukraine: Ukraine$9,
	"United Arab Emirates": "Verenigde Arabische Emiraten",
	"United Kingdom": "Verenigd Koninkrijk",
	"United States": "Verenigde Staten",
	Uruguay: Uruguay$9,
	Uzbekistan: Uzbekistan$9,
	Vanuatu: Vanuatu$9,
	"Venezuela, Bolivarian Republic of": "Venezuela, Bolivariaanse Republiek van",
	"Viet Nam": "Vietnam",
	"Virgin Islands, British": "Britse Maagdeneilanden",
	"Virgin Islands, U.S.": "Maagdeneilanden, VS",
	"Wallis and Futuna": "Wallis en Futuna",
	Yemen: Yemen$9,
	Zambia: Zambia$9,
	Zimbabwe: Zimbabwe$9,
	"Åland Islands": "Åland-eilanden"
};

var t98 = {
	"No results found.": "Geen resultaten gevonden."
};

var Done$q = "완료";
var t99 = {
	Done: Done$q
};

var t100 = {
	"No results found.": "검색 결과가 없습니다."
};

var t101 = {
	"Clear all": "모두 지우기",
	"No results found.": "검색 결과가 없습니다."
};

var Passengers$8 = "탑승객";
var t102 = {
	"Number of passengers": "탑승객 수",
	Passengers: Passengers$8
};

var t103 = {
	"No results found.": "검색 결과가 없습니다."
};

var Blog$8 = "블로그";
var Careers$8 = "경력";
var Contact$8 = "연락하다";
var Coverage$8 = "적용 범위";
var Guides$8 = "가이드";
var Imprint$8 = "날인";
var Parking$8 = "주차";
var eSIM$8 = "eSIM";
var t104 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. 상표 및 브랜드는 해당 소유자의 자산입니다.",
	Blog: Blog$8,
	Careers: Careers$8,
	Contact: Contact$8,
	Coverage: Coverage$8,
	Guides: Guides$8,
	Imprint: Imprint$8,
	Parking: Parking$8,
	"Privacy Policy": "개인정보 보호정책",
	"Terms Of Services": "서비스 약관",
	eSIM: eSIM$8,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Mozio Inc.의 상표 및 브랜드는 해당 소유자의 자산입니다."
};

var Filters$8 = "필터";
var t105 = {
	"Clear All": "모두 지우기",
	Filters: Filters$8,
	"Show results": "결과 표시"
};

var Done$p = "완료";
var Hours$8 = "시";
var Minutes$8 = "분";
var t106 = {
	"AM/PM": "오전/오후",
	Done: Done$p,
	Hours: Hours$8,
	Minutes: Minutes$8
};

var Done$o = "완료";
var t107 = {
	Done: Done$o
};

var Afghanistan$8 = "아프가니스탄";
var Albania$8 = "알바니아";
var Algeria$8 = "알제리";
var AmericanSamoa$8 = "아메리칸사모아";
var Andorra$8 = "안도라";
var Angola$8 = "앙골라";
var Anguilla$8 = "앵귈라";
var Argentina$8 = "아르헨티나";
var Armenia$8 = "아르메니아";
var Aruba$8 = "아루바";
var Australia$8 = "호주";
var Austria$8 = "오스트리아";
var Azerbaijan$8 = "아제르바이잔";
var Bahamas$8 = "바하마";
var Bahrain$8 = "바레인";
var Bangladesh$8 = "방글라데시";
var Barbados$8 = "바베이도스";
var Belarus$8 = "벨라루스";
var Belgium$8 = "벨기에";
var Belize$8 = "벨리즈";
var Benin$8 = "베냉";
var Bermuda$8 = "버뮤다";
var Bhutan$8 = "부탄";
var Botswana$8 = "보츠와나";
var Brazil$8 = "브라질";
var Bulgaria$8 = "불가리아";
var Burundi$8 = "부룬디";
var Cambodia$8 = "캄보디아";
var Cameroon$8 = "카메룬";
var Canada$8 = "캐나다";
var Chad$8 = "차드";
var Chile$8 = "칠레";
var China$8 = "중국";
var Colombia$8 = "콜롬비아";
var Comoros$8 = "코모로";
var Congo$8 = "콩고";
var Croatia$8 = "크로아티아";
var Curacao$8 = "퀴라소";
var Cyprus$8 = "키프로스";
var Denmark$8 = "덴마크";
var Djibouti$8 = "지부티";
var Dominica$8 = "도미니카";
var Ecuador$8 = "에콰도르";
var Egypt$8 = "이집트";
var Eritrea$8 = "에리트레아";
var Estonia$8 = "에스토니아";
var Ethiopia$8 = "에티오피아";
var Fiji$8 = "피지";
var Finland$8 = "핀란드";
var France$8 = "프랑스";
var Gabon$8 = "가봉";
var Gambia$8 = "감비아";
var Georgia$8 = "그루지야";
var Germany$8 = "독일";
var Ghana$8 = "가나";
var Gibraltar$8 = "지브롤터";
var Greece$8 = "그리스";
var Greenland$8 = "그린란드";
var Grenada$8 = "그레나다";
var Guadeloupe$8 = "과들루프";
var Guam$8 = "괌";
var Guatemala$8 = "과테말라";
var Guernsey$8 = "건지섬";
var Guinea$8 = "기니";
var Guyana$8 = "가이아나";
var Haiti$8 = "아이티";
var Honduras$8 = "온두라스";
var Hungary$8 = "헝가리";
var Iceland$8 = "아이슬란드";
var India$8 = "인도";
var Indonesia$8 = "인도네시아 공화국";
var Iraq$8 = "이라크";
var Ireland$8 = "아일랜드";
var Israel$8 = "이스라엘";
var Italy$8 = "이탈리아";
var Jamaica$8 = "자메이카";
var Japan$8 = "일본";
var Jersey$8 = "저지";
var Jordan$8 = "요르단";
var Kazakhstan$8 = "카자흐스탄";
var Kenya$8 = "케냐";
var Kiribati$8 = "키리바시";
var Kuwait$8 = "쿠웨이트";
var Kyrgyzstan$8 = "키르기스스탄";
var Latvia$8 = "라트비아";
var Lebanon$8 = "레바논";
var Lesotho$8 = "레소토";
var Liberia$8 = "라이베리아";
var Liechtenstein$8 = "리히텐슈타인";
var Lithuania$8 = "리투아니아";
var Luxembourg$8 = "룩셈부르크";
var Macao$8 = "마카오";
var Madagascar$8 = "마다가스카르";
var Malawi$8 = "말라위";
var Malaysia$8 = "말레이시아";
var Maldives$8 = "몰디브";
var Mali$8 = "말리";
var Malta$8 = "몰타";
var Martinique$8 = "마르티니크";
var Mauritania$8 = "모리타니";
var Mauritius$8 = "모리셔스";
var Mayotte$8 = "마요트";
var Mexico$8 = "멕시코";
var Monaco$8 = "모나코";
var Mongolia$8 = "몽골리아";
var Montenegro$8 = "몬테네그로";
var Montserrat$8 = "몬세라트";
var Morocco$8 = "모로코 가죽";
var Mozambique$8 = "모잠비크";
var Myanmar$8 = "미얀마";
var Namibia$8 = "나미비아";
var Nauru$8 = "나우루";
var Nepal$8 = "네팔";
var Netherlands$8 = "네덜란드";
var Nicaragua$8 = "니카라과";
var Niger$8 = "니제르";
var Nigeria$8 = "나이지리아";
var Niue$8 = "니우에";
var Norway$8 = "노르웨이";
var Oman$8 = "오만";
var Pakistan$8 = "파키스탄";
var Palau$8 = "팔라우";
var Panama$8 = "파나마";
var Paraguay$8 = "파라과이";
var Peru$8 = "페루";
var Philippines$8 = "필리핀 제도";
var Pitcairn$8 = "피트케언";
var Poland$8 = "폴란드";
var Portugal$8 = "포르투갈";
var Qatar$8 = "카타르";
var Romania$8 = "루마니아";
var Russia$8 = "러시아 제국";
var Rwanda$8 = "르완다";
var Samoa$8 = "사모아";
var Senegal$8 = "세네갈";
var Serbia$8 = "세르비아";
var Seychelles$8 = "세이셸";
var Singapore$8 = "싱가포르";
var Slovakia$8 = "슬로바키아";
var Slovenia$8 = "슬로베니아";
var Somalia$8 = "소말리아";
var Spain$8 = "스페인";
var Suriname$8 = "수리남";
var Swaziland$8 = "스와질랜드";
var Sweden$8 = "스웨덴";
var Switzerland$8 = "스위스";
var Tajikistan$8 = "타지키스탄";
var Thailand$8 = "태국";
var Togo$8 = "토고";
var Tokelau$8 = "토켈라우";
var Tonga$8 = "통가";
var Tunisia$8 = "튀니지";
var Turkey$8 = "칠면조";
var Turkmenistan$8 = "투르크메니스탄";
var Tuvalu$8 = "투발루";
var Uganda$8 = "우간다";
var Ukraine$8 = "우크라이나";
var Uruguay$8 = "우루과이";
var Uzbekistan$8 = "우즈베키스탄";
var Vanuatu$8 = "바누아투";
var Yemen$8 = "예멘 아랍 공화국";
var Zambia$8 = "잠비아";
var Zimbabwe$8 = "짐바브웨";
var t108 = {
	Afghanistan: Afghanistan$8,
	Albania: Albania$8,
	Algeria: Algeria$8,
	AmericanSamoa: AmericanSamoa$8,
	Andorra: Andorra$8,
	Angola: Angola$8,
	Anguilla: Anguilla$8,
	"Antigua and Barbuda": "앤티가 바부다",
	Argentina: Argentina$8,
	Armenia: Armenia$8,
	Aruba: Aruba$8,
	Australia: Australia$8,
	Austria: Austria$8,
	Azerbaijan: Azerbaijan$8,
	Bahamas: Bahamas$8,
	Bahrain: Bahrain$8,
	Bangladesh: Bangladesh$8,
	Barbados: Barbados$8,
	Belarus: Belarus$8,
	Belgium: Belgium$8,
	Belize: Belize$8,
	Benin: Benin$8,
	Bermuda: Bermuda$8,
	Bhutan: Bhutan$8,
	"Bolivia, Plurinational State of": "볼리비아, 다민족 국가",
	"Bosnia and Herzegovina": "보스니아 헤르체고비나",
	Botswana: Botswana$8,
	Brazil: Brazil$8,
	"British Indian Ocean Territory": "영국령 인도양 지역",
	"Brunei Darussalam": "브루나이 다루살람",
	Bulgaria: Bulgaria$8,
	"Burkina Faso": "부르키나파소",
	Burundi: Burundi$8,
	Cambodia: Cambodia$8,
	Cameroon: Cameroon$8,
	Canada: Canada$8,
	"Cape Verde": "카보베르데",
	"Cayman Islands": "케이맨 제도",
	"Central African Republic": "중앙아프리카 공화국",
	Chad: Chad$8,
	Chile: Chile$8,
	China: China$8,
	"Christmas Island": "크리스마스 섬",
	"Cocos (Keeling) Islands": "코코스(킬링) 제도",
	Colombia: Colombia$8,
	Comoros: Comoros$8,
	Congo: Congo$8,
	"Congo, The Democratic Republic of the": "콩고 민주 공화국",
	"Cook Islands": "쿡 제도",
	"Costa Rica": "코스타리카",
	"Cote d'Ivoire": "코트디부아르",
	Croatia: Croatia$8,
	Curacao: Curacao$8,
	Cyprus: Cyprus$8,
	"Czech Republic": "체코 공화국",
	Denmark: Denmark$8,
	Djibouti: Djibouti$8,
	Dominica: Dominica$8,
	"Dominican Republic": "도미니카 공화국",
	Ecuador: Ecuador$8,
	Egypt: Egypt$8,
	"El Salvador": "엘살바도르",
	"Equatorial Guinea": "적도 기니",
	Eritrea: Eritrea$8,
	Estonia: Estonia$8,
	Ethiopia: Ethiopia$8,
	"Falkland Islands (Malvinas)": "포클랜드 제도(말비나스)",
	"Faroe Islands": "페로 제도",
	Fiji: Fiji$8,
	Finland: Finland$8,
	France: France$8,
	"French Guiana": "프랑스령 기아나",
	"French Polynesia": "프랑스령 폴리네시아",
	Gabon: Gabon$8,
	Gambia: Gambia$8,
	Georgia: Georgia$8,
	Germany: Germany$8,
	Ghana: Ghana$8,
	Gibraltar: Gibraltar$8,
	Greece: Greece$8,
	Greenland: Greenland$8,
	Grenada: Grenada$8,
	Guadeloupe: Guadeloupe$8,
	Guam: Guam$8,
	Guatemala: Guatemala$8,
	Guernsey: Guernsey$8,
	Guinea: Guinea$8,
	"Guinea-Bissau": "기니비사우",
	Guyana: Guyana$8,
	Haiti: Haiti$8,
	"Holy See (Vatican City State)": "바티칸 시국",
	Honduras: Honduras$8,
	"Hong Kong": "홍콩",
	Hungary: Hungary$8,
	Iceland: Iceland$8,
	India: India$8,
	Indonesia: Indonesia$8,
	Iraq: Iraq$8,
	Ireland: Ireland$8,
	"Isle of Man": "맨 섬",
	Israel: Israel$8,
	Italy: Italy$8,
	Jamaica: Jamaica$8,
	Japan: Japan$8,
	Jersey: Jersey$8,
	Jordan: Jordan$8,
	Kazakhstan: Kazakhstan$8,
	Kenya: Kenya$8,
	Kiribati: Kiribati$8,
	"Korea, Republic of": "대한민국",
	Kuwait: Kuwait$8,
	Kyrgyzstan: Kyrgyzstan$8,
	"Lao People's Democratic Republic": "라오 인민 민주 공화국",
	Latvia: Latvia$8,
	Lebanon: Lebanon$8,
	Lesotho: Lesotho$8,
	Liberia: Liberia$8,
	"Libyan Arab Jamahiriya": "리비아 아랍 자마히리야",
	Liechtenstein: Liechtenstein$8,
	Lithuania: Lithuania$8,
	Luxembourg: Luxembourg$8,
	Macao: Macao$8,
	"Macedonia, The Former Yugoslav Republic of": "마케도니아, 구 유고슬라비아 공화국",
	Madagascar: Madagascar$8,
	Malawi: Malawi$8,
	Malaysia: Malaysia$8,
	Maldives: Maldives$8,
	Mali: Mali$8,
	Malta: Malta$8,
	"Marshall Islands": "마셜 제도",
	Martinique: Martinique$8,
	Mauritania: Mauritania$8,
	Mauritius: Mauritius$8,
	Mayotte: Mayotte$8,
	Mexico: Mexico$8,
	"Micronesia, Federated States of": "미크로네시아 연방",
	"Moldova, Republic of": "몰도바 공화국",
	Monaco: Monaco$8,
	Mongolia: Mongolia$8,
	Montenegro: Montenegro$8,
	Montserrat: Montserrat$8,
	Morocco: Morocco$8,
	Mozambique: Mozambique$8,
	Myanmar: Myanmar$8,
	Namibia: Namibia$8,
	Nauru: Nauru$8,
	Nepal: Nepal$8,
	Netherlands: Netherlands$8,
	"New Caledonia": "뉴칼레도니아",
	"New Zealand": "뉴질랜드",
	Nicaragua: Nicaragua$8,
	Niger: Niger$8,
	Nigeria: Nigeria$8,
	Niue: Niue$8,
	"Norfolk Island": "노퍽 섬",
	"Northern Mariana Islands": "북마리아나 제도",
	Norway: Norway$8,
	Oman: Oman$8,
	Pakistan: Pakistan$8,
	Palau: Palau$8,
	"Palestinian Territory, Occupied": "팔레스타인 영토, 점령됨",
	Panama: Panama$8,
	"Papua New Guinea": "파푸아뉴기니",
	Paraguay: Paraguay$8,
	Peru: Peru$8,
	Philippines: Philippines$8,
	Pitcairn: Pitcairn$8,
	Poland: Poland$8,
	Portugal: Portugal$8,
	"Puerto Rico": "푸에르토리코",
	Qatar: Qatar$8,
	Romania: Romania$8,
	Russia: Russia$8,
	Rwanda: Rwanda$8,
	"Réunion": "재결합",
	"Saint Barthélemy": "생바르텔레미",
	"Saint Helena, Ascension and Tristan Da Cunha": "세인트 헬레나, 어센션, 트리스탄다쿠냐",
	"Saint Kitts and Nevis": "세인트키츠 네비스",
	"Saint Lucia": "세인트 루시아",
	"Saint Martin": "생마르탱",
	"Saint Pierre and Miquelon": "생피에르 미클롱",
	"Saint Vincent and the Grenadines": "세인트빈센트 그레나딘",
	Samoa: Samoa$8,
	"San Marino": "산마리노",
	"Sao Tome and Principe": "상투메 프린시페",
	"Saudi Arabia": "사우디 아라비아",
	Senegal: Senegal$8,
	Serbia: Serbia$8,
	Seychelles: Seychelles$8,
	"Sierra Leone": "시에라리온",
	Singapore: Singapore$8,
	Slovakia: Slovakia$8,
	Slovenia: Slovenia$8,
	"Solomon Islands": "솔로몬 제도",
	Somalia: Somalia$8,
	"South Africa": "남아프리카 공화국",
	"South Georgia and the South Sandwich Islands": "사우스조지아 및 사우스샌드위치 제도",
	Spain: Spain$8,
	"Sri Lanka": "스리랑카",
	Suriname: Suriname$8,
	"Svalbard and Jan Mayen": "스발바르와 얀마옌 제도",
	Swaziland: Swaziland$8,
	Sweden: Sweden$8,
	Switzerland: Switzerland$8,
	"Taiwan, Province of China": "중국 타이완성",
	Tajikistan: Tajikistan$8,
	"Tanzania, United Republic of": "탄자니아 연합 공화국",
	Thailand: Thailand$8,
	"Timor-Leste": "동티모르",
	Togo: Togo$8,
	Tokelau: Tokelau$8,
	Tonga: Tonga$8,
	"Trinidad and Tobago": "트리니다드 토바고",
	Tunisia: Tunisia$8,
	Turkey: Turkey$8,
	Turkmenistan: Turkmenistan$8,
	"Turks and Caicos Islands": "터크스 케이커스 제도",
	Tuvalu: Tuvalu$8,
	Uganda: Uganda$8,
	Ukraine: Ukraine$8,
	"United Arab Emirates": "아랍에미리트",
	"United Kingdom": "영국",
	"United States": "미국",
	Uruguay: Uruguay$8,
	Uzbekistan: Uzbekistan$8,
	Vanuatu: Vanuatu$8,
	"Venezuela, Bolivarian Republic of": "베네수엘라, 볼리바르 공화국",
	"Viet Nam": "베트남",
	"Virgin Islands, British": "영국령 버진아일랜드",
	"Virgin Islands, U.S.": "미국령 버진아일랜드",
	"Wallis and Futuna": "월리스 푸투나",
	Yemen: Yemen$8,
	Zambia: Zambia$8,
	Zimbabwe: Zimbabwe$8,
	"Åland Islands": "올란드 제도"
};

var t109 = {
	"No results found.": "검색 결과가 없습니다."
};

var Done$n = "終わり";
var t110 = {
	Done: Done$n
};

var t111 = {
	"No results found.": "結果が見つかりませんでした。"
};

var t112 = {
	"Clear all": "すべてクリア",
	"No results found.": "結果が見つかりませんでした。"
};

var Passengers$7 = "乗客";
var t113 = {
	"Number of passengers": "乗車人数",
	Passengers: Passengers$7
};

var t114 = {
	"No results found.": "結果が見つかりませんでした。"
};

var Blog$7 = "ブログ";
var Careers$7 = "キャリア";
var Contact$7 = "接触";
var Coverage$7 = "カバレッジ";
var Guides$7 = "ガイド";
var Imprint$7 = "インプリント";
var Parking$7 = "駐車場";
var eSIM$7 = "電子SIM";
var t115 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. 商標およびブランドはそれぞれの所有者の財産です。",
	Blog: Blog$7,
	Careers: Careers$7,
	Contact: Contact$7,
	Coverage: Coverage$7,
	Guides: Guides$7,
	Imprint: Imprint$7,
	Parking: Parking$7,
	"Privacy Policy": "プライバシーポリシー",
	"Terms Of Services": "利用規約",
	eSIM: eSIM$7,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Mozio Inc. の商標およびブランドはそれぞれの所有者の財産です。"
};

var Filters$7 = "フィルター";
var t116 = {
	"Clear All": "すべてクリア",
	Filters: Filters$7,
	"Show results": "結果を表示"
};

var Done$m = "完了";
var Hours$7 = "時";
var Minutes$7 = "分";
var t117 = {
	"AM/PM": "午前/午後",
	Done: Done$m,
	Hours: Hours$7,
	Minutes: Minutes$7
};

var Done$l = "終わり";
var t118 = {
	Done: Done$l
};

var Afghanistan$7 = "アフガニスタン";
var Albania$7 = "アルバニア";
var Algeria$7 = "アルジェリア";
var AmericanSamoa$7 = "アメリカ領サモア";
var Andorra$7 = "アンドラ";
var Angola$7 = "アンゴラ";
var Anguilla$7 = "アンギラ";
var Argentina$7 = "アルゼンチン";
var Armenia$7 = "アルメニア";
var Aruba$7 = "アルバ";
var Australia$7 = "オーストラリア";
var Austria$7 = "オーストリア";
var Azerbaijan$7 = "アゼルバイジャン";
var Bahamas$7 = "バハマ";
var Bahrain$7 = "バーレーン";
var Bangladesh$7 = "バングラデシュ";
var Barbados$7 = "バルバドス";
var Belarus$7 = "ベラルーシ";
var Belgium$7 = "ベルギー";
var Belize$7 = "ベリーズ";
var Benin$7 = "ベナン";
var Bermuda$7 = "バミューダ";
var Bhutan$7 = "ブータン";
var Botswana$7 = "ボツワナ";
var Brazil$7 = "ブラジル";
var Bulgaria$7 = "ブルガリア";
var Burundi$7 = "ブルンジ";
var Cambodia$7 = "カンボジア";
var Cameroon$7 = "カメルーン";
var Canada$7 = "カナダ";
var Chad$7 = "チャド";
var Chile$7 = "チリ";
var China$7 = "中国";
var Colombia$7 = "コロンビア";
var Comoros$7 = "コモロ";
var Congo$7 = "コンゴ";
var Croatia$7 = "クロアチア";
var Curacao$7 = "キュラソー";
var Cyprus$7 = "キプロス";
var Denmark$7 = "デンマーク";
var Djibouti$7 = "ジブチ";
var Dominica$7 = "ドミニカ";
var Ecuador$7 = "エクアドル";
var Egypt$7 = "エジプト";
var Eritrea$7 = "エリトリア";
var Estonia$7 = "エストニア";
var Ethiopia$7 = "エチオピア";
var Fiji$7 = "フィジー";
var Finland$7 = "フィンランド";
var France$7 = "フランス";
var Gabon$7 = "ガボン";
var Gambia$7 = "ガンビア";
var Georgia$7 = "ジョージア";
var Germany$7 = "ドイツ";
var Ghana$7 = "ガーナ";
var Gibraltar$7 = "ジブラルタル";
var Greece$7 = "ギリシャ";
var Greenland$7 = "グリーンランド";
var Grenada$7 = "グレナダ";
var Guadeloupe$7 = "グアドループ";
var Guam$7 = "グアム";
var Guatemala$7 = "グアテマラ";
var Guernsey$7 = "ガーンジー島";
var Guinea$7 = "ギニア";
var Guyana$7 = "ガイアナ";
var Haiti$7 = "ハイチ";
var Honduras$7 = "ホンジュラス";
var Hungary$7 = "ハンガリー";
var Iceland$7 = "アイスランド";
var India$7 = "インド";
var Indonesia$7 = "インドネシア";
var Iraq$7 = "イラク";
var Ireland$7 = "アイルランド";
var Israel$7 = "イスラエル";
var Italy$7 = "イタリア";
var Jamaica$7 = "ジャマイカ";
var Japan$7 = "日本";
var Jersey$7 = "ジャージー";
var Jordan$7 = "ヨルダン";
var Kazakhstan$7 = "カザフスタン";
var Kenya$7 = "ケニア";
var Kiribati$7 = "キリバス";
var Kuwait$7 = "クウェート";
var Kyrgyzstan$7 = "キルギスタン";
var Latvia$7 = "ラトビア";
var Lebanon$7 = "レバノン";
var Lesotho$7 = "レソト";
var Liberia$7 = "リベリア";
var Liechtenstein$7 = "リヒテンシュタイン";
var Lithuania$7 = "リトアニア";
var Luxembourg$7 = "ルクセンブルク";
var Macao$7 = "マカオ";
var Madagascar$7 = "マダガスカル";
var Malawi$7 = "マラウイ";
var Malaysia$7 = "マレーシア";
var Maldives$7 = "モルディブ";
var Mali$7 = "マリ";
var Malta$7 = "マルタ";
var Martinique$7 = "マルティニーク";
var Mauritania$7 = "モーリタニア";
var Mauritius$7 = "モーリシャス";
var Mayotte$7 = "マヨット";
var Mexico$7 = "メキシコ";
var Monaco$7 = "モナコ";
var Mongolia$7 = "モンゴル";
var Montenegro$7 = "モンテネグロ";
var Montserrat$7 = "モントセラト";
var Morocco$7 = "モロッコ";
var Mozambique$7 = "モザンビーク";
var Myanmar$7 = "ミャンマー";
var Namibia$7 = "ナミビア";
var Nauru$7 = "ナウル";
var Nepal$7 = "ネパール";
var Netherlands$7 = "オランダ";
var Nicaragua$7 = "ニカラグア";
var Niger$7 = "ニジェール";
var Nigeria$7 = "ナイジェリア";
var Niue$7 = "ニウエ";
var Norway$7 = "ノルウェー";
var Oman$7 = "オマーン";
var Pakistan$7 = "パキスタン";
var Palau$7 = "パラオ";
var Panama$7 = "パナマ";
var Paraguay$7 = "パラグアイ";
var Peru$7 = "ペルー";
var Philippines$7 = "フィリピン";
var Pitcairn$7 = "ピトケアン";
var Poland$7 = "ポーランド";
var Portugal$7 = "ポルトガル";
var Qatar$7 = "カタール";
var Romania$7 = "ルーマニア";
var Russia$7 = "ロシア";
var Rwanda$7 = "ルワンダ";
var Samoa$7 = "サモア";
var Senegal$7 = "セネガル";
var Serbia$7 = "セルビア";
var Seychelles$7 = "セイシェル";
var Singapore$7 = "シンガポール";
var Slovakia$7 = "スロバキア";
var Slovenia$7 = "スロベニア";
var Somalia$7 = "ソマリア";
var Spain$7 = "スペイン";
var Suriname$7 = "スリナム";
var Swaziland$7 = "スワジランド";
var Sweden$7 = "スウェーデン";
var Switzerland$7 = "スイス";
var Tajikistan$7 = "タジキスタン";
var Thailand$7 = "タイ";
var Togo$7 = "持ち帰り";
var Tokelau$7 = "トケラウ";
var Tonga$7 = "トンガ";
var Tunisia$7 = "チュニジア";
var Turkey$7 = "七面鳥";
var Turkmenistan$7 = "トルクメニスタン";
var Tuvalu$7 = "ツバル";
var Uganda$7 = "ウガンダ";
var Ukraine$7 = "ウクライナ";
var Uruguay$7 = "ウルグアイ";
var Uzbekistan$7 = "ウズベキスタン";
var Vanuatu$7 = "バヌアツ";
var Yemen$7 = "イエメン";
var Zambia$7 = "ザンビア";
var Zimbabwe$7 = "ジンバブエ";
var t119 = {
	Afghanistan: Afghanistan$7,
	Albania: Albania$7,
	Algeria: Algeria$7,
	AmericanSamoa: AmericanSamoa$7,
	Andorra: Andorra$7,
	Angola: Angola$7,
	Anguilla: Anguilla$7,
	"Antigua and Barbuda": "アンティグア・バーブーダ",
	Argentina: Argentina$7,
	Armenia: Armenia$7,
	Aruba: Aruba$7,
	Australia: Australia$7,
	Austria: Austria$7,
	Azerbaijan: Azerbaijan$7,
	Bahamas: Bahamas$7,
	Bahrain: Bahrain$7,
	Bangladesh: Bangladesh$7,
	Barbados: Barbados$7,
	Belarus: Belarus$7,
	Belgium: Belgium$7,
	Belize: Belize$7,
	Benin: Benin$7,
	Bermuda: Bermuda$7,
	Bhutan: Bhutan$7,
	"Bolivia, Plurinational State of": "ボリビア多民族国",
	"Bosnia and Herzegovina": "ボスニア・ヘルツェゴビナ",
	Botswana: Botswana$7,
	Brazil: Brazil$7,
	"British Indian Ocean Territory": "イギリス領インド洋地域",
	"Brunei Darussalam": "ブルネイ・ダルサラーム",
	Bulgaria: Bulgaria$7,
	"Burkina Faso": "ブルキナファソ",
	Burundi: Burundi$7,
	Cambodia: Cambodia$7,
	Cameroon: Cameroon$7,
	Canada: Canada$7,
	"Cape Verde": "カーボベルデ",
	"Cayman Islands": "ケイマン諸島",
	"Central African Republic": "中央アフリカ共和国",
	Chad: Chad$7,
	Chile: Chile$7,
	China: China$7,
	"Christmas Island": "クリスマス島",
	"Cocos (Keeling) Islands": "ココス（キーリング）諸島",
	Colombia: Colombia$7,
	Comoros: Comoros$7,
	Congo: Congo$7,
	"Congo, The Democratic Republic of the": "コンゴ民主共和国",
	"Cook Islands": "クック諸島",
	"Costa Rica": "コスタリカ",
	"Cote d'Ivoire": "コートジボワール",
	Croatia: Croatia$7,
	Curacao: Curacao$7,
	Cyprus: Cyprus$7,
	"Czech Republic": "チェコ共和国",
	Denmark: Denmark$7,
	Djibouti: Djibouti$7,
	Dominica: Dominica$7,
	"Dominican Republic": "ドミニカ共和国",
	Ecuador: Ecuador$7,
	Egypt: Egypt$7,
	"El Salvador": "エルサルバドル",
	"Equatorial Guinea": "赤道ギニア",
	Eritrea: Eritrea$7,
	Estonia: Estonia$7,
	Ethiopia: Ethiopia$7,
	"Falkland Islands (Malvinas)": "フォークランド諸島（マルビナス諸島）",
	"Faroe Islands": "フェロー諸島",
	Fiji: Fiji$7,
	Finland: Finland$7,
	France: France$7,
	"French Guiana": "フランス領ギアナ",
	"French Polynesia": "フランス領ポリネシア",
	Gabon: Gabon$7,
	Gambia: Gambia$7,
	Georgia: Georgia$7,
	Germany: Germany$7,
	Ghana: Ghana$7,
	Gibraltar: Gibraltar$7,
	Greece: Greece$7,
	Greenland: Greenland$7,
	Grenada: Grenada$7,
	Guadeloupe: Guadeloupe$7,
	Guam: Guam$7,
	Guatemala: Guatemala$7,
	Guernsey: Guernsey$7,
	Guinea: Guinea$7,
	"Guinea-Bissau": "ギニアビサウ",
	Guyana: Guyana$7,
	Haiti: Haiti$7,
	"Holy See (Vatican City State)": "聖座（バチカン市国）",
	Honduras: Honduras$7,
	"Hong Kong": "香港",
	Hungary: Hungary$7,
	Iceland: Iceland$7,
	India: India$7,
	Indonesia: Indonesia$7,
	Iraq: Iraq$7,
	Ireland: Ireland$7,
	"Isle of Man": "マン島",
	Israel: Israel$7,
	Italy: Italy$7,
	Jamaica: Jamaica$7,
	Japan: Japan$7,
	Jersey: Jersey$7,
	Jordan: Jordan$7,
	Kazakhstan: Kazakhstan$7,
	Kenya: Kenya$7,
	Kiribati: Kiribati$7,
	"Korea, Republic of": "大韓民国",
	Kuwait: Kuwait$7,
	Kyrgyzstan: Kyrgyzstan$7,
	"Lao People's Democratic Republic": "ラオス人民民主共和国",
	Latvia: Latvia$7,
	Lebanon: Lebanon$7,
	Lesotho: Lesotho$7,
	Liberia: Liberia$7,
	"Libyan Arab Jamahiriya": "リビア・アラブ・ジャマーヒリーヤ",
	Liechtenstein: Liechtenstein$7,
	Lithuania: Lithuania$7,
	Luxembourg: Luxembourg$7,
	Macao: Macao$7,
	"Macedonia, The Former Yugoslav Republic of": "マケドニア旧ユーゴスラビア共和国",
	Madagascar: Madagascar$7,
	Malawi: Malawi$7,
	Malaysia: Malaysia$7,
	Maldives: Maldives$7,
	Mali: Mali$7,
	Malta: Malta$7,
	"Marshall Islands": "マーシャル諸島",
	Martinique: Martinique$7,
	Mauritania: Mauritania$7,
	Mauritius: Mauritius$7,
	Mayotte: Mayotte$7,
	Mexico: Mexico$7,
	"Micronesia, Federated States of": "ミクロネシア連邦",
	"Moldova, Republic of": "モルドバ共和国",
	Monaco: Monaco$7,
	Mongolia: Mongolia$7,
	Montenegro: Montenegro$7,
	Montserrat: Montserrat$7,
	Morocco: Morocco$7,
	Mozambique: Mozambique$7,
	Myanmar: Myanmar$7,
	Namibia: Namibia$7,
	Nauru: Nauru$7,
	Nepal: Nepal$7,
	Netherlands: Netherlands$7,
	"New Caledonia": "ニューカレドニア",
	"New Zealand": "ニュージーランド",
	Nicaragua: Nicaragua$7,
	Niger: Niger$7,
	Nigeria: Nigeria$7,
	Niue: Niue$7,
	"Norfolk Island": "ノーフォーク島",
	"Northern Mariana Islands": "北マリアナ諸島",
	Norway: Norway$7,
	Oman: Oman$7,
	Pakistan: Pakistan$7,
	Palau: Palau$7,
	"Palestinian Territory, Occupied": "パレスチナ占領地",
	Panama: Panama$7,
	"Papua New Guinea": "パプアニューギニア",
	Paraguay: Paraguay$7,
	Peru: Peru$7,
	Philippines: Philippines$7,
	Pitcairn: Pitcairn$7,
	Poland: Poland$7,
	Portugal: Portugal$7,
	"Puerto Rico": "プエルトリコ",
	Qatar: Qatar$7,
	Romania: Romania$7,
	Russia: Russia$7,
	Rwanda: Rwanda$7,
	"Réunion": "再会",
	"Saint Barthélemy": "サン・バルテルミー島",
	"Saint Helena, Ascension and Tristan Da Cunha": "セントヘレナ、アセンション、トリスタンダクーニャ",
	"Saint Kitts and Nevis": "セントクリストファー・ネイビス",
	"Saint Lucia": "セントルシア",
	"Saint Martin": "サン・マルタン",
	"Saint Pierre and Miquelon": "サンピエール島とミクロン島",
	"Saint Vincent and the Grenadines": "セントビンセントおよびグレナディーン諸島",
	Samoa: Samoa$7,
	"San Marino": "サンマリノ",
	"Sao Tome and Principe": "サントメ・プリンシペ",
	"Saudi Arabia": "サウジアラビア",
	Senegal: Senegal$7,
	Serbia: Serbia$7,
	Seychelles: Seychelles$7,
	"Sierra Leone": "シエラレオネ",
	Singapore: Singapore$7,
	Slovakia: Slovakia$7,
	Slovenia: Slovenia$7,
	"Solomon Islands": "ソロモン諸島",
	Somalia: Somalia$7,
	"South Africa": "南アフリカ",
	"South Georgia and the South Sandwich Islands": "サウスジョージア島およびサウスサンドウィッチ諸島",
	Spain: Spain$7,
	"Sri Lanka": "スリランカ",
	Suriname: Suriname$7,
	"Svalbard and Jan Mayen": "スヴァールバル諸島とヤンマイエン島",
	Swaziland: Swaziland$7,
	Sweden: Sweden$7,
	Switzerland: Switzerland$7,
	"Taiwan, Province of China": "台湾、中国の省",
	Tajikistan: Tajikistan$7,
	"Tanzania, United Republic of": "タンザニア連合共和国",
	Thailand: Thailand$7,
	"Timor-Leste": "東ティモール",
	Togo: Togo$7,
	Tokelau: Tokelau$7,
	Tonga: Tonga$7,
	"Trinidad and Tobago": "トリニダード・トバゴ",
	Tunisia: Tunisia$7,
	Turkey: Turkey$7,
	Turkmenistan: Turkmenistan$7,
	"Turks and Caicos Islands": "タークス・カイコス諸島",
	Tuvalu: Tuvalu$7,
	Uganda: Uganda$7,
	Ukraine: Ukraine$7,
	"United Arab Emirates": "アラブ首長国連邦",
	"United Kingdom": "イギリス",
	"United States": "アメリカ合衆国",
	Uruguay: Uruguay$7,
	Uzbekistan: Uzbekistan$7,
	Vanuatu: Vanuatu$7,
	"Venezuela, Bolivarian Republic of": "ベネズエラ・ボリバル共和国",
	"Viet Nam": "ベトナム",
	"Virgin Islands, British": "イギリス領ヴァージン諸島",
	"Virgin Islands, U.S.": "米国領バージン諸島",
	"Wallis and Futuna": "ウォリス・フツナ",
	Yemen: Yemen$7,
	Zambia: Zambia$7,
	Zimbabwe: Zimbabwe$7,
	"Åland Islands": "オーランド諸島"
};

var t120 = {
	"No results found.": "結果が見つかりませんでした。"
};

var Done$k = "Fatto";
var t121 = {
	Done: Done$k
};

var t122 = {
	"No results found.": "Nessun risultato trovato."
};

var t123 = {
	"Clear all": "Cancella tutto",
	"No results found.": "Nessun risultato trovato."
};

var Passengers$6 = "Passeggeri";
var t124 = {
	"Number of passengers": "Numero di passeggeri",
	Passengers: Passengers$6
};

var t125 = {
	"No results found.": "Nessun risultato trovato."
};

var Blog$6 = "Blog";
var Careers$6 = "Carriere";
var Contact$6 = "Contatto";
var Coverage$6 = "Copertura";
var Guides$6 = "Guide";
var Imprint$6 = "Impronta";
var Parking$6 = "Parcheggio";
var eSIM$6 = "eSIM";
var t126 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. I marchi e i brand sono di proprietà dei rispettivi proprietari.",
	Blog: Blog$6,
	Careers: Careers$6,
	Contact: Contact$6,
	Coverage: Coverage$6,
	Guides: Guides$6,
	Imprint: Imprint$6,
	Parking: Parking$6,
	"Privacy Policy": "politica sulla riservatezza",
	"Terms Of Services": "Termini di servizio",
	eSIM: eSIM$6,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} I marchi e i brand di Mozio Inc. sono di proprietà dei rispettivi titolari."
};

var Filters$6 = "Filtri";
var t127 = {
	"Clear All": "Cancella tutto",
	Filters: Filters$6,
	"Show results": "Mostra risultati"
};

var Done$j = "Fine";
var Hours$6 = "Ore";
var Minutes$6 = "Minuti";
var t128 = {
	"AM/PM": "AM/PM",
	Done: Done$j,
	Hours: Hours$6,
	Minutes: Minutes$6
};

var Done$i = "Fatto";
var t129 = {
	Done: Done$i
};

var Afghanistan$6 = "Afghanistan";
var Albania$6 = "Albania";
var Algeria$6 = "Algeria";
var AmericanSamoa$6 = "Samoa americane";
var Andorra$6 = "Andorra";
var Angola$6 = "Angola";
var Anguilla$6 = "Anguilla";
var Argentina$6 = "Argentina";
var Armenia$6 = "Armenia";
var Aruba$6 = "Aruba";
var Australia$6 = "Australia";
var Austria$6 = "Austria";
var Azerbaijan$6 = "Azerbaigian";
var Bahamas$6 = "Bahamas";
var Bahrain$6 = "Bahrein";
var Bangladesh$6 = "Bangladesh";
var Barbados$6 = "Barbados";
var Belarus$6 = "Bielorussia";
var Belgium$6 = "Belgio";
var Belize$6 = "Belize";
var Benin$6 = "Benin";
var Bermuda$6 = "Bermuda";
var Bhutan$6 = "Il Bhutan";
var Botswana$6 = "Il Botswana";
var Brazil$6 = "Brasile";
var Bulgaria$6 = "Bulgaria";
var Burundi$6 = "Burundi";
var Cambodia$6 = "Cambogia";
var Cameroon$6 = "Camerun";
var Canada$6 = "Canada";
var Chad$6 = "Chad";
var Chile$6 = "Chile";
var China$6 = "Cina";
var Colombia$6 = "Colombia";
var Comoros$6 = "Comore";
var Congo$6 = "Congo";
var Croatia$6 = "Croazia";
var Curacao$6 = "Curaçao";
var Cyprus$6 = "Cipro";
var Denmark$6 = "Danimarca";
var Djibouti$6 = "Gibuti";
var Dominica$6 = "Dominica";
var Ecuador$6 = "Ecuador";
var Egypt$6 = "Egitto";
var Eritrea$6 = "Eritrea";
var Estonia$6 = "Estonia";
var Ethiopia$6 = "Etiopia";
var Fiji$6 = "Figi";
var Finland$6 = "Finlandia";
var France$6 = "Francia";
var Gabon$6 = "Gabon";
var Gambia$6 = "Gambia";
var Georgia$6 = "Georgia";
var Germany$6 = "Germania";
var Ghana$6 = "Ghana";
var Gibraltar$6 = "Gibilterra";
var Greece$6 = "Grecia";
var Greenland$6 = "Groenlandia";
var Grenada$6 = "Grenada";
var Guadeloupe$6 = "Guadalupa";
var Guam$6 = "Guam";
var Guatemala$6 = "Guatemala";
var Guernsey$6 = "Guernsey";
var Guinea$6 = "Guinea";
var Guyana$6 = "Guyana";
var Haiti$6 = "Haiti";
var Honduras$6 = "Honduras";
var Hungary$6 = "Ungheria";
var Iceland$6 = "Islanda";
var India$6 = "India";
var Indonesia$6 = "Indonesia";
var Iraq$6 = "Iraq";
var Ireland$6 = "Irlanda";
var Israel$6 = "Israele";
var Italy$6 = "Italia";
var Jamaica$6 = "Giamaica";
var Japan$6 = "Giappone";
var Jersey$6 = "Maglia";
var Jordan$6 = "Giordania";
var Kazakhstan$6 = "Kazakistan";
var Kenya$6 = "Kenia";
var Kiribati$6 = "Kiribati";
var Kuwait$6 = "Kuwait";
var Kyrgyzstan$6 = "Kirghizistan";
var Latvia$6 = "Lettonia";
var Lebanon$6 = "Libano";
var Lesotho$6 = "Lesotho";
var Liberia$6 = "Liberia";
var Liechtenstein$6 = "Liechtenstein";
var Lithuania$6 = "Lituania";
var Luxembourg$6 = "Lussemburgo";
var Macao$6 = "Macao";
var Madagascar$6 = "Madagascar";
var Malawi$6 = "Malawi";
var Malaysia$6 = "Malaysia";
var Maldives$6 = "Maldive";
var Mali$6 = "Mali";
var Malta$6 = "Malta";
var Martinique$6 = "Martinica";
var Mauritania$6 = "Mauritania";
var Mauritius$6 = "Maurizio";
var Mayotte$6 = "Mayotte";
var Mexico$6 = "Messico";
var Monaco$6 = "Monaco";
var Mongolia$6 = "Mongolia";
var Montenegro$6 = "Montenegro";
var Montserrat$6 = "Montserrat";
var Morocco$6 = "Marocco";
var Mozambique$6 = "Mozambico";
var Myanmar$6 = "Birmania";
var Namibia$6 = "Namibia";
var Nauru$6 = "Nauru";
var Nepal$6 = "Nepal";
var Netherlands$6 = "Paesi Bassi";
var Nicaragua$6 = "Nicaragua";
var Niger$6 = "Niger";
var Nigeria$6 = "Nigeria";
var Niue$6 = "Niue";
var Norway$6 = "Norvegia";
var Oman$6 = "Oman";
var Pakistan$6 = "Pakistan";
var Palau$6 = "Palau";
var Panama$6 = "Panama";
var Paraguay$6 = "Il Paraguay";
var Peru$6 = "Perù";
var Philippines$6 = "Filippine";
var Pitcairn$6 = "Pitcairn";
var Poland$6 = "Polonia";
var Portugal$6 = "Portogallo";
var Qatar$6 = "Qatar";
var Romania$6 = "Romania";
var Russia$6 = "Russia";
var Rwanda$6 = "Ruanda";
var Samoa$6 = "Samoa";
var Senegal$6 = "Senegal";
var Serbia$6 = "Serbia";
var Seychelles$6 = "Seychelles";
var Singapore$6 = "Singapore";
var Slovakia$6 = "Slovacchia";
var Slovenia$6 = "Slovenia";
var Somalia$6 = "Somalia";
var Spain$6 = "Spagna";
var Suriname$6 = "Suriname";
var Swaziland$6 = "Swaziland";
var Sweden$6 = "Svezia";
var Switzerland$6 = "Svizzera";
var Tajikistan$6 = "Tagikistan";
var Thailand$6 = "Thailandia";
var Togo$6 = "Togo";
var Tokelau$6 = "Tokelau";
var Tonga$6 = "Tonga";
var Tunisia$6 = "Tunisia";
var Turkey$6 = "Tacchino";
var Turkmenistan$6 = "Turkmenistan";
var Tuvalu$6 = "Tuvalu";
var Uganda$6 = "Uganda";
var Ukraine$6 = "Ucraina";
var Uruguay$6 = "Uruguay";
var Uzbekistan$6 = "Uzbekistan";
var Vanuatu$6 = "Vanuatu";
var Yemen$6 = "Yemen";
var Zambia$6 = "Zambia";
var Zimbabwe$6 = "Zimbabwe";
var t130 = {
	Afghanistan: Afghanistan$6,
	Albania: Albania$6,
	Algeria: Algeria$6,
	AmericanSamoa: AmericanSamoa$6,
	Andorra: Andorra$6,
	Angola: Angola$6,
	Anguilla: Anguilla$6,
	"Antigua and Barbuda": "Antigua e Barbuda",
	Argentina: Argentina$6,
	Armenia: Armenia$6,
	Aruba: Aruba$6,
	Australia: Australia$6,
	Austria: Austria$6,
	Azerbaijan: Azerbaijan$6,
	Bahamas: Bahamas$6,
	Bahrain: Bahrain$6,
	Bangladesh: Bangladesh$6,
	Barbados: Barbados$6,
	Belarus: Belarus$6,
	Belgium: Belgium$6,
	Belize: Belize$6,
	Benin: Benin$6,
	Bermuda: Bermuda$6,
	Bhutan: Bhutan$6,
	"Bolivia, Plurinational State of": "Bolivia, Stato Plurinazionale di",
	"Bosnia and Herzegovina": "Bosnia ed Erzegovina",
	Botswana: Botswana$6,
	Brazil: Brazil$6,
	"British Indian Ocean Territory": "Territorio britannico dell'Oceano Indiano",
	"Brunei Darussalam": "Brunei Darussalam",
	Bulgaria: Bulgaria$6,
	"Burkina Faso": "Burkina Faso",
	Burundi: Burundi$6,
	Cambodia: Cambodia$6,
	Cameroon: Cameroon$6,
	Canada: Canada$6,
	"Cape Verde": "Capo Verde",
	"Cayman Islands": "Isole Cayman",
	"Central African Republic": "Repubblica Centrafricana",
	Chad: Chad$6,
	Chile: Chile$6,
	China: China$6,
	"Christmas Island": "Isola di Natale",
	"Cocos (Keeling) Islands": "Isole Cocos (Keeling)",
	Colombia: Colombia$6,
	Comoros: Comoros$6,
	Congo: Congo$6,
	"Congo, The Democratic Republic of the": "Congo, Repubblica Democratica del",
	"Cook Islands": "Isole Cook",
	"Costa Rica": "Costa Rica",
	"Cote d'Ivoire": "Costa d'Avorio",
	Croatia: Croatia$6,
	Curacao: Curacao$6,
	Cyprus: Cyprus$6,
	"Czech Republic": "Repubblica Ceca",
	Denmark: Denmark$6,
	Djibouti: Djibouti$6,
	Dominica: Dominica$6,
	"Dominican Republic": "Repubblica Dominicana",
	Ecuador: Ecuador$6,
	Egypt: Egypt$6,
	"El Salvador": "El Salvador",
	"Equatorial Guinea": "Guinea Equatoriale",
	Eritrea: Eritrea$6,
	Estonia: Estonia$6,
	Ethiopia: Ethiopia$6,
	"Falkland Islands (Malvinas)": "Isole Falkland (Malvine)",
	"Faroe Islands": "Isole Faroe",
	Fiji: Fiji$6,
	Finland: Finland$6,
	France: France$6,
	"French Guiana": "Guyana francese",
	"French Polynesia": "Polinesia francese",
	Gabon: Gabon$6,
	Gambia: Gambia$6,
	Georgia: Georgia$6,
	Germany: Germany$6,
	Ghana: Ghana$6,
	Gibraltar: Gibraltar$6,
	Greece: Greece$6,
	Greenland: Greenland$6,
	Grenada: Grenada$6,
	Guadeloupe: Guadeloupe$6,
	Guam: Guam$6,
	Guatemala: Guatemala$6,
	Guernsey: Guernsey$6,
	Guinea: Guinea$6,
	"Guinea-Bissau": "Guinea-Bissau",
	Guyana: Guyana$6,
	Haiti: Haiti$6,
	"Holy See (Vatican City State)": "Santa Sede (Stato della Città del Vaticano)",
	Honduras: Honduras$6,
	"Hong Kong": "Hong Kong",
	Hungary: Hungary$6,
	Iceland: Iceland$6,
	India: India$6,
	Indonesia: Indonesia$6,
	Iraq: Iraq$6,
	Ireland: Ireland$6,
	"Isle of Man": "Isola di Man",
	Israel: Israel$6,
	Italy: Italy$6,
	Jamaica: Jamaica$6,
	Japan: Japan$6,
	Jersey: Jersey$6,
	Jordan: Jordan$6,
	Kazakhstan: Kazakhstan$6,
	Kenya: Kenya$6,
	Kiribati: Kiribati$6,
	"Korea, Republic of": "Corea, Repubblica di",
	Kuwait: Kuwait$6,
	Kyrgyzstan: Kyrgyzstan$6,
	"Lao People's Democratic Republic": "Repubblica Democratica Popolare del Laos",
	Latvia: Latvia$6,
	Lebanon: Lebanon$6,
	Lesotho: Lesotho$6,
	Liberia: Liberia$6,
	"Libyan Arab Jamahiriya": "Giamahiria araba libica",
	Liechtenstein: Liechtenstein$6,
	Lithuania: Lithuania$6,
	Luxembourg: Luxembourg$6,
	Macao: Macao$6,
	"Macedonia, The Former Yugoslav Republic of": "Macedonia, ex Repubblica jugoslava di",
	Madagascar: Madagascar$6,
	Malawi: Malawi$6,
	Malaysia: Malaysia$6,
	Maldives: Maldives$6,
	Mali: Mali$6,
	Malta: Malta$6,
	"Marshall Islands": "Isole Marshall",
	Martinique: Martinique$6,
	Mauritania: Mauritania$6,
	Mauritius: Mauritius$6,
	Mayotte: Mayotte$6,
	Mexico: Mexico$6,
	"Micronesia, Federated States of": "Micronesia, Stati Federati di",
	"Moldova, Republic of": "Moldavia, Repubblica di",
	Monaco: Monaco$6,
	Mongolia: Mongolia$6,
	Montenegro: Montenegro$6,
	Montserrat: Montserrat$6,
	Morocco: Morocco$6,
	Mozambique: Mozambique$6,
	Myanmar: Myanmar$6,
	Namibia: Namibia$6,
	Nauru: Nauru$6,
	Nepal: Nepal$6,
	Netherlands: Netherlands$6,
	"New Caledonia": "Nuova Caledonia",
	"New Zealand": "Nuova Zelanda",
	Nicaragua: Nicaragua$6,
	Niger: Niger$6,
	Nigeria: Nigeria$6,
	Niue: Niue$6,
	"Norfolk Island": "Isola Norfolk",
	"Northern Mariana Islands": "Isole Marianne Settentrionali",
	Norway: Norway$6,
	Oman: Oman$6,
	Pakistan: Pakistan$6,
	Palau: Palau$6,
	"Palestinian Territory, Occupied": "Territorio palestinese occupato",
	Panama: Panama$6,
	"Papua New Guinea": "Papua Nuova Guinea",
	Paraguay: Paraguay$6,
	Peru: Peru$6,
	Philippines: Philippines$6,
	Pitcairn: Pitcairn$6,
	Poland: Poland$6,
	Portugal: Portugal$6,
	"Puerto Rico": "Porto Rico",
	Qatar: Qatar$6,
	Romania: Romania$6,
	Russia: Russia$6,
	Rwanda: Rwanda$6,
	"Réunion": "Riunione",
	"Saint Barthélemy": "Saint-Barthélemy",
	"Saint Helena, Ascension and Tristan Da Cunha": "Sant'Elena, Ascensione e Tristano da Cunha",
	"Saint Kitts and Nevis": "Saint Kitts e Nevis",
	"Saint Lucia": "Santa Lucia",
	"Saint Martin": "San Martino",
	"Saint Pierre and Miquelon": "Saint Pierre e Miquelon",
	"Saint Vincent and the Grenadines": "Saint Vincent e Grenadine",
	Samoa: Samoa$6,
	"San Marino": "San Marino",
	"Sao Tome and Principe": "Sao Tomé e Principe",
	"Saudi Arabia": "Arabia Saudita",
	Senegal: Senegal$6,
	Serbia: Serbia$6,
	Seychelles: Seychelles$6,
	"Sierra Leone": "Sierra Leone",
	Singapore: Singapore$6,
	Slovakia: Slovakia$6,
	Slovenia: Slovenia$6,
	"Solomon Islands": "Isole Salomone",
	Somalia: Somalia$6,
	"South Africa": "Sudafrica",
	"South Georgia and the South Sandwich Islands": "Georgia del Sud e Isole Sandwich Australi",
	Spain: Spain$6,
	"Sri Lanka": "Sri Lanka",
	Suriname: Suriname$6,
	"Svalbard and Jan Mayen": "Svalbard e Jan Mayen",
	Swaziland: Swaziland$6,
	Sweden: Sweden$6,
	Switzerland: Switzerland$6,
	"Taiwan, Province of China": "Taiwan, provincia della Cina",
	Tajikistan: Tajikistan$6,
	"Tanzania, United Republic of": "Tanzania, Repubblica Unita di",
	Thailand: Thailand$6,
	"Timor-Leste": "Timor Est",
	Togo: Togo$6,
	Tokelau: Tokelau$6,
	Tonga: Tonga$6,
	"Trinidad and Tobago": "Trinidad e Tobago",
	Tunisia: Tunisia$6,
	Turkey: Turkey$6,
	Turkmenistan: Turkmenistan$6,
	"Turks and Caicos Islands": "Isole Turks e Caicos",
	Tuvalu: Tuvalu$6,
	Uganda: Uganda$6,
	Ukraine: Ukraine$6,
	"United Arab Emirates": "Emirati Arabi Uniti",
	"United Kingdom": "Regno Unito",
	"United States": "Stati Uniti",
	Uruguay: Uruguay$6,
	Uzbekistan: Uzbekistan$6,
	Vanuatu: Vanuatu$6,
	"Venezuela, Bolivarian Republic of": "Venezuela, Repubblica Bolivariana del",
	"Viet Nam": "Vietnam",
	"Virgin Islands, British": "Isole Vergini Britanniche",
	"Virgin Islands, U.S.": "Isole Vergini, Stati Uniti",
	"Wallis and Futuna": "Wallis e Futuna",
	Yemen: Yemen$6,
	Zambia: Zambia$6,
	Zimbabwe: Zimbabwe$6,
	"Åland Islands": "Isole Åland"
};

var t131 = {
	"No results found.": "Nessun risultato trovato."
};

var Done$h = "Selesai";
var t132 = {
	Done: Done$h
};

var t133 = {
	"No results found.": "Tidak ada hasil ditemukan."
};

var t134 = {
	"Clear all": "Hapus semua",
	"No results found.": "Tidak ada hasil ditemukan."
};

var Passengers$5 = "Penumpang";
var t135 = {
	"Number of passengers": "Jumlah penumpang",
	Passengers: Passengers$5
};

var t136 = {
	"No results found.": "Tidak ada hasil ditemukan."
};

var Blog$5 = "Blog";
var Careers$5 = "Karier";
var Contact$5 = "Kontak";
var Coverage$5 = "Cakupan";
var Guides$5 = "Panduan";
var Imprint$5 = "Jejak";
var Parking$5 = "Parkir";
var eSIM$5 = "eSIM";
var t137 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. Merek dagang dan merek adalah milik pemiliknya masing-masing.",
	Blog: Blog$5,
	Careers: Careers$5,
	Contact: Contact$5,
	Coverage: Coverage$5,
	Guides: Guides$5,
	Imprint: Imprint$5,
	Parking: Parking$5,
	"Privacy Policy": "Kebijakan Privasi",
	"Terms Of Services": "Ketentuan Layanan",
	eSIM: eSIM$5,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Merek dagang dan merek Mozio Inc. adalah hak milik dari pemiliknya masing-masing."
};

var Filters$5 = "Filter";
var t138 = {
	"Clear All": "Hapus Semua",
	Filters: Filters$5,
	"Show results": "Tampilkan hasil"
};

var Done$g = "Selesai";
var Hours$5 = "Jam";
var Minutes$5 = "Menit";
var t139 = {
	"AM/PM": "AM/PM",
	Done: Done$g,
	Hours: Hours$5,
	Minutes: Minutes$5
};

var Done$f = "Selesai";
var t140 = {
	Done: Done$f
};

var Afghanistan$5 = "Afganistan";
var Albania$5 = "Albania";
var Algeria$5 = "Aljazair";
var AmericanSamoa$5 = "Samoa Amerika";
var Andorra$5 = "Andorra";
var Angola$5 = "Angola";
var Anguilla$5 = "Anguilla";
var Argentina$5 = "Argentina";
var Armenia$5 = "Armenia";
var Aruba$5 = "Aruba";
var Australia$5 = "Australia";
var Austria$5 = "Austria";
var Azerbaijan$5 = "Azerbaijan";
var Bahamas$5 = "Bahama";
var Bahrain$5 = "Bahrain";
var Bangladesh$5 = "Bangladesh";
var Barbados$5 = "Barbados";
var Belarus$5 = "Belarusia";
var Belgium$5 = "Belgia";
var Belize$5 = "Belize";
var Benin$5 = "Benin";
var Bermuda$5 = "Bermuda";
var Bhutan$5 = "Bhutan";
var Botswana$5 = "Botswana";
var Brazil$5 = "Brazil";
var Bulgaria$5 = "Bulgaria";
var Burundi$5 = "Burundi";
var Cambodia$5 = "Kamboja";
var Cameroon$5 = "Kamerun";
var Canada$5 = "Kanada";
var Chad$5 = "Chad";
var Chile$5 = "Chili";
var China$5 = "Cina";
var Colombia$5 = "Kolumbia";
var Comoros$5 = "Komoro";
var Congo$5 = "Kongo";
var Croatia$5 = "Kroasia";
var Curacao$5 = "Curacao";
var Cyprus$5 = "Siprus";
var Denmark$5 = "Denmark";
var Djibouti$5 = "Jibuti";
var Dominica$5 = "Dominika";
var Ecuador$5 = "Ekuador";
var Egypt$5 = "Mesir";
var Eritrea$5 = "Eritrea";
var Estonia$5 = "Estonia";
var Ethiopia$5 = "Etiopia";
var Fiji$5 = "Fiji";
var Finland$5 = "Finlandia";
var France$5 = "Perancis";
var Gabon$5 = "Gabon";
var Gambia$5 = "Gambia";
var Georgia$5 = "Georgia";
var Germany$5 = "Jerman";
var Ghana$5 = "Ghana";
var Gibraltar$5 = "Gibraltar";
var Greece$5 = "Yunani";
var Greenland$5 = "Tanah penggembalaan";
var Grenada$5 = "Grenada";
var Guadeloupe$5 = "Guadeloupe";
var Guam$5 = "Guam";
var Guatemala$5 = "Guatemala";
var Guernsey$5 = "Guernsey";
var Guinea$5 = "Guinea";
var Guyana$5 = "Guyana";
var Haiti$5 = "Haiti";
var Honduras$5 = "Honduras";
var Hungary$5 = "Hongaria";
var Iceland$5 = "Islandia";
var India$5 = "India";
var Indonesia$5 = "Indonesia";
var Iraq$5 = "Irak";
var Ireland$5 = "Irlandia";
var Israel$5 = "Israel";
var Italy$5 = "Italia";
var Jamaica$5 = "Jamaika";
var Japan$5 = "Jepang";
var Jersey$5 = "Jersey";
var Jordan$5 = "Yordania";
var Kazakhstan$5 = "Kazakstan";
var Kenya$5 = "Kenya";
var Kiribati$5 = "Kiribati";
var Kuwait$5 = "Kuwait";
var Kyrgyzstan$5 = "Kirgistan";
var Latvia$5 = "Latvia";
var Lebanon$5 = "Libanon";
var Lesotho$5 = "Lesotho";
var Liberia$5 = "Liberia";
var Liechtenstein$5 = "Liechtenstein";
var Lithuania$5 = "Lithuania";
var Luxembourg$5 = "Luksemburg";
var Macao$5 = "Makau";
var Madagascar$5 = "Madagaskar";
var Malawi$5 = "Malawi";
var Malaysia$5 = "Malaysia";
var Maldives$5 = "Maladewa";
var Mali$5 = "Mali";
var Malta$5 = "Malta";
var Martinique$5 = "Martinik";
var Mauritania$5 = "Mauritania";
var Mauritius$5 = "Mauritius";
var Mayotte$5 = "Mayotte";
var Mexico$5 = "Meksiko";
var Monaco$5 = "Monako";
var Mongolia$5 = "Mongolia";
var Montenegro$5 = "Montenegro";
var Montserrat$5 = "Montserrat";
var Morocco$5 = "Maroko";
var Mozambique$5 = "Mozambik";
var Myanmar$5 = "Myanmar";
var Namibia$5 = "Namibia";
var Nauru$5 = "Nauru";
var Nepal$5 = "Nepal";
var Netherlands$5 = "Belanda";
var Nicaragua$5 = "Nikaragua";
var Niger$5 = "Nigeria";
var Nigeria$5 = "Nigeria";
var Niue$5 = "Niue";
var Norway$5 = "Norwegia";
var Oman$5 = "Oman";
var Pakistan$5 = "Pakistan";
var Palau$5 = "Palau";
var Panama$5 = "Panama";
var Paraguay$5 = "Paraguay";
var Peru$5 = "Peru";
var Philippines$5 = "Filipina";
var Pitcairn$5 = "Pitcairn";
var Poland$5 = "Polandia";
var Portugal$5 = "Portugal";
var Qatar$5 = "Qatar";
var Romania$5 = "Rumania";
var Russia$5 = "Rusia";
var Rwanda$5 = "Rwanda";
var Samoa$5 = "Samoa";
var Senegal$5 = "Senegal";
var Serbia$5 = "Serbia";
var Seychelles$5 = "Seychelles";
var Singapore$5 = "Singapura";
var Slovakia$5 = "Slowakia";
var Slovenia$5 = "Slovenia";
var Somalia$5 = "Somalia";
var Spain$5 = "Spanyol";
var Suriname$5 = "Suriname";
var Swaziland$5 = "Swaziland";
var Sweden$5 = "Swedia";
var Switzerland$5 = "Swiss";
var Tajikistan$5 = "Tajikistan";
var Thailand$5 = "Thailand";
var Togo$5 = "Togo";
var Tokelau$5 = "Tokelau";
var Tonga$5 = "Tonga";
var Tunisia$5 = "Tunisia";
var Turkey$5 = "Turki";
var Turkmenistan$5 = "Turkmenistan";
var Tuvalu$5 = "Tuvalu";
var Uganda$5 = "Uganda";
var Ukraine$5 = "Ukraina";
var Uruguay$5 = "Uruguay";
var Uzbekistan$5 = "Uzbekistan";
var Vanuatu$5 = "Vanuatu";
var Yemen$5 = "Yaman";
var Zambia$5 = "Zambia";
var Zimbabwe$5 = "Zimbabwe";
var t141 = {
	Afghanistan: Afghanistan$5,
	Albania: Albania$5,
	Algeria: Algeria$5,
	AmericanSamoa: AmericanSamoa$5,
	Andorra: Andorra$5,
	Angola: Angola$5,
	Anguilla: Anguilla$5,
	"Antigua and Barbuda": "Antigua dan Barbuda",
	Argentina: Argentina$5,
	Armenia: Armenia$5,
	Aruba: Aruba$5,
	Australia: Australia$5,
	Austria: Austria$5,
	Azerbaijan: Azerbaijan$5,
	Bahamas: Bahamas$5,
	Bahrain: Bahrain$5,
	Bangladesh: Bangladesh$5,
	Barbados: Barbados$5,
	Belarus: Belarus$5,
	Belgium: Belgium$5,
	Belize: Belize$5,
	Benin: Benin$5,
	Bermuda: Bermuda$5,
	Bhutan: Bhutan$5,
	"Bolivia, Plurinational State of": "Bolivia, Negara Plurinasional",
	"Bosnia and Herzegovina": "Bosnia dan Herzegovina",
	Botswana: Botswana$5,
	Brazil: Brazil$5,
	"British Indian Ocean Territory": "Wilayah Samudra Hindia Britania",
	"Brunei Darussalam": "Brunei Darussalam",
	Bulgaria: Bulgaria$5,
	"Burkina Faso": "Burkina Faso",
	Burundi: Burundi$5,
	Cambodia: Cambodia$5,
	Cameroon: Cameroon$5,
	Canada: Canada$5,
	"Cape Verde": "Tanjung Verde",
	"Cayman Islands": "Kepulauan Cayman",
	"Central African Republic": "Republik Afrika Tengah",
	Chad: Chad$5,
	Chile: Chile$5,
	China: China$5,
	"Christmas Island": "Pulau Natal",
	"Cocos (Keeling) Islands": "Kepulauan Cocos (Keeling)",
	Colombia: Colombia$5,
	Comoros: Comoros$5,
	Congo: Congo$5,
	"Congo, The Democratic Republic of the": "Kongo, Republik Demokratik Kongo",
	"Cook Islands": "Kepulauan Cook",
	"Costa Rica": "Kosta Rika",
	"Cote d'Ivoire": "Pantai Gading",
	Croatia: Croatia$5,
	Curacao: Curacao$5,
	Cyprus: Cyprus$5,
	"Czech Republic": "Republik Ceko",
	Denmark: Denmark$5,
	Djibouti: Djibouti$5,
	Dominica: Dominica$5,
	"Dominican Republic": "Republik Dominika",
	Ecuador: Ecuador$5,
	Egypt: Egypt$5,
	"El Salvador": "El Salvador",
	"Equatorial Guinea": "Guinea Khatulistiwa",
	Eritrea: Eritrea$5,
	Estonia: Estonia$5,
	Ethiopia: Ethiopia$5,
	"Falkland Islands (Malvinas)": "Kepulauan Falkland (Malvinas)",
	"Faroe Islands": "Kepulauan Faroe",
	Fiji: Fiji$5,
	Finland: Finland$5,
	France: France$5,
	"French Guiana": "Guyana Prancis",
	"French Polynesia": "Polinesia Prancis",
	Gabon: Gabon$5,
	Gambia: Gambia$5,
	Georgia: Georgia$5,
	Germany: Germany$5,
	Ghana: Ghana$5,
	Gibraltar: Gibraltar$5,
	Greece: Greece$5,
	Greenland: Greenland$5,
	Grenada: Grenada$5,
	Guadeloupe: Guadeloupe$5,
	Guam: Guam$5,
	Guatemala: Guatemala$5,
	Guernsey: Guernsey$5,
	Guinea: Guinea$5,
	"Guinea-Bissau": "Guinea-Bissau",
	Guyana: Guyana$5,
	Haiti: Haiti$5,
	"Holy See (Vatican City State)": "Tahta Suci (Negara Kota Vatikan)",
	Honduras: Honduras$5,
	"Hong Kong": "Hong Kong",
	Hungary: Hungary$5,
	Iceland: Iceland$5,
	India: India$5,
	Indonesia: Indonesia$5,
	Iraq: Iraq$5,
	Ireland: Ireland$5,
	"Isle of Man": "Pulau Man",
	Israel: Israel$5,
	Italy: Italy$5,
	Jamaica: Jamaica$5,
	Japan: Japan$5,
	Jersey: Jersey$5,
	Jordan: Jordan$5,
	Kazakhstan: Kazakhstan$5,
	Kenya: Kenya$5,
	Kiribati: Kiribati$5,
	"Korea, Republic of": "Korea, Republik",
	Kuwait: Kuwait$5,
	Kyrgyzstan: Kyrgyzstan$5,
	"Lao People's Democratic Republic": "Republik Demokratik Rakyat Laos",
	Latvia: Latvia$5,
	Lebanon: Lebanon$5,
	Lesotho: Lesotho$5,
	Liberia: Liberia$5,
	"Libyan Arab Jamahiriya": "Jamahiriya Arab Libya",
	Liechtenstein: Liechtenstein$5,
	Lithuania: Lithuania$5,
	Luxembourg: Luxembourg$5,
	Macao: Macao$5,
	"Macedonia, The Former Yugoslav Republic of": "Makedonia, Bekas Republik Yugoslavia",
	Madagascar: Madagascar$5,
	Malawi: Malawi$5,
	Malaysia: Malaysia$5,
	Maldives: Maldives$5,
	Mali: Mali$5,
	Malta: Malta$5,
	"Marshall Islands": "Kepulauan Marshall",
	Martinique: Martinique$5,
	Mauritania: Mauritania$5,
	Mauritius: Mauritius$5,
	Mayotte: Mayotte$5,
	Mexico: Mexico$5,
	"Micronesia, Federated States of": "Mikronesia, Negara Federasi",
	"Moldova, Republic of": "Moldova, Republik",
	Monaco: Monaco$5,
	Mongolia: Mongolia$5,
	Montenegro: Montenegro$5,
	Montserrat: Montserrat$5,
	Morocco: Morocco$5,
	Mozambique: Mozambique$5,
	Myanmar: Myanmar$5,
	Namibia: Namibia$5,
	Nauru: Nauru$5,
	Nepal: Nepal$5,
	Netherlands: Netherlands$5,
	"New Caledonia": "Kaledonia Baru",
	"New Zealand": "Selandia Baru",
	Nicaragua: Nicaragua$5,
	Niger: Niger$5,
	Nigeria: Nigeria$5,
	Niue: Niue$5,
	"Norfolk Island": "Pulau Norfolk",
	"Northern Mariana Islands": "Kepulauan Mariana Utara",
	Norway: Norway$5,
	Oman: Oman$5,
	Pakistan: Pakistan$5,
	Palau: Palau$5,
	"Palestinian Territory, Occupied": "Wilayah Palestina, Diduduki",
	Panama: Panama$5,
	"Papua New Guinea": "Papua Nugini",
	Paraguay: Paraguay$5,
	Peru: Peru$5,
	Philippines: Philippines$5,
	Pitcairn: Pitcairn$5,
	Poland: Poland$5,
	Portugal: Portugal$5,
	"Puerto Rico": "Puerto Riko",
	Qatar: Qatar$5,
	Romania: Romania$5,
	Russia: Russia$5,
	Rwanda: Rwanda$5,
	"Réunion": "Reuni",
	"Saint Barthélemy": "Saint Barthélemy",
	"Saint Helena, Ascension and Tristan Da Cunha": "Saint Helena, Ascension dan Tristan Da Cunha",
	"Saint Kitts and Nevis": "Saint Kitts dan Nevis",
	"Saint Lucia": "Saint Lucia",
	"Saint Martin": "Saint Martin",
	"Saint Pierre and Miquelon": "Saint Pierre dan Miquelon",
	"Saint Vincent and the Grenadines": "Saint Vincent dan Grenadines",
	Samoa: Samoa$5,
	"San Marino": "San Marino",
	"Sao Tome and Principe": "Sao Tome dan Principe",
	"Saudi Arabia": "Arab Saudi",
	Senegal: Senegal$5,
	Serbia: Serbia$5,
	Seychelles: Seychelles$5,
	"Sierra Leone": "Sierra Leone",
	Singapore: Singapore$5,
	Slovakia: Slovakia$5,
	Slovenia: Slovenia$5,
	"Solomon Islands": "Kepulauan Solomon",
	Somalia: Somalia$5,
	"South Africa": "Afrika Selatan",
	"South Georgia and the South Sandwich Islands": "Georgia Selatan dan Kepulauan Sandwich Selatan",
	Spain: Spain$5,
	"Sri Lanka": "Sri Lanka",
	Suriname: Suriname$5,
	"Svalbard and Jan Mayen": "Svalbard dan Jan Mayen",
	Swaziland: Swaziland$5,
	Sweden: Sweden$5,
	Switzerland: Switzerland$5,
	"Taiwan, Province of China": "Taiwan, Provinsi Tiongkok",
	Tajikistan: Tajikistan$5,
	"Tanzania, United Republic of": "Tanzania, Republik Bersatu",
	Thailand: Thailand$5,
	"Timor-Leste": "Timor-Leste",
	Togo: Togo$5,
	Tokelau: Tokelau$5,
	Tonga: Tonga$5,
	"Trinidad and Tobago": "Trinidad dan Tobago",
	Tunisia: Tunisia$5,
	Turkey: Turkey$5,
	Turkmenistan: Turkmenistan$5,
	"Turks and Caicos Islands": "Kepulauan Turks dan Caicos",
	Tuvalu: Tuvalu$5,
	Uganda: Uganda$5,
	Ukraine: Ukraine$5,
	"United Arab Emirates": "Uni Emirat Arab",
	"United Kingdom": "Inggris Raya",
	"United States": "Amerika Serikat",
	Uruguay: Uruguay$5,
	Uzbekistan: Uzbekistan$5,
	Vanuatu: Vanuatu$5,
	"Venezuela, Bolivarian Republic of": "Venezuela, Republik Bolivarian",
	"Viet Nam": "Vietnam",
	"Virgin Islands, British": "Kepulauan Virgin, Inggris",
	"Virgin Islands, U.S.": "Kepulauan Virgin, AS",
	"Wallis and Futuna": "Wallis dan Futuna",
	Yemen: Yemen$5,
	Zambia: Zambia$5,
	Zimbabwe: Zimbabwe$5,
	"Åland Islands": "Kepulauan Åland"
};

var t142 = {
	"No results found.": "Tidak ada hasil ditemukan."
};

var Done$e = "Fait";
var t143 = {
	Done: Done$e
};

var t144 = {
	"No results found.": "Aucun résultat trouvé."
};

var t145 = {
	"Clear all": "Effacer tout",
	"No results found.": "Aucun résultat trouvé."
};

var Passengers$4 = "Passagers";
var t146 = {
	"Number of passengers": "Nombre de passagers",
	Passengers: Passengers$4
};

var t147 = {
	"No results found.": "Aucun résultat trouvé."
};

var Blog$4 = "Blog";
var Careers$4 = "Carrières";
var Contact$4 = "Contact";
var Coverage$4 = "Couverture";
var Guides$4 = "Guides";
var Imprint$4 = "Imprimer";
var Parking$4 = "Parking";
var eSIM$4 = "eSIM";
var t148 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. Les marques et marques déposées appartiennent à leurs propriétaires respectifs.",
	Blog: Blog$4,
	Careers: Careers$4,
	Contact: Contact$4,
	Coverage: Coverage$4,
	Guides: Guides$4,
	Imprint: Imprint$4,
	Parking: Parking$4,
	"Privacy Policy": "politique de confidentialité",
	"Terms Of Services": "Conditions d'utilisation",
	eSIM: eSIM$4,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Les marques de commerce et les marques déposées de Mozio Inc. sont la propriété de leurs détenteurs respectifs."
};

var Filters$4 = "Filtres";
var t149 = {
	"Clear All": "Tout effacer",
	Filters: Filters$4,
	"Show results": "Afficher les résultats"
};

var Done$d = "Terminé";
var Hours$4 = "Heures";
var Minutes$4 = "Minutes";
var t150 = {
	"AM/PM": "AM/PM",
	Done: Done$d,
	Hours: Hours$4,
	Minutes: Minutes$4
};

var Done$c = "Fait";
var t151 = {
	Done: Done$c
};

var Afghanistan$4 = "Afghanistan";
var Albania$4 = "Albanie";
var Algeria$4 = "Algérie";
var AmericanSamoa$4 = "Samoa américaines";
var Andorra$4 = "Andorre";
var Angola$4 = "Angola";
var Anguilla$4 = "Anguilla";
var Argentina$4 = "Argentine";
var Armenia$4 = "Arménie";
var Aruba$4 = "Aruba";
var Australia$4 = "Australie";
var Austria$4 = "Autriche";
var Azerbaijan$4 = "Azerbaïdjan";
var Bahamas$4 = "Bahamas";
var Bahrain$4 = "Bahreïn";
var Bangladesh$4 = "Bangladesh";
var Barbados$4 = "Barbade";
var Belarus$4 = "Biélorussie";
var Belgium$4 = "Belgique";
var Belize$4 = "Bélize";
var Benin$4 = "Bénin";
var Bermuda$4 = "Bermudes";
var Bhutan$4 = "Bhoutan";
var Botswana$4 = "Botswana";
var Brazil$4 = "Brésil";
var Bulgaria$4 = "Bulgarie";
var Burundi$4 = "Burundi";
var Cambodia$4 = "Cambodge";
var Cameroon$4 = "Cameroun";
var Canada$4 = "Canada";
var Chad$4 = "Tchad";
var Chile$4 = "Chili";
var China$4 = "Chine";
var Colombia$4 = "Colombie";
var Comoros$4 = "Comores";
var Congo$4 = "Congo";
var Croatia$4 = "Croatie";
var Curacao$4 = "Curaçao";
var Cyprus$4 = "Chypre";
var Denmark$4 = "Danemark";
var Djibouti$4 = "Djibouti";
var Dominica$4 = "Dominique";
var Ecuador$4 = "Équateur";
var Egypt$4 = "Egypte";
var Eritrea$4 = "Érythrée";
var Estonia$4 = "Estonie";
var Ethiopia$4 = "Ethiopie";
var Fiji$4 = "Fidji";
var Finland$4 = "Finlande";
var France$4 = "France";
var Gabon$4 = "Gabon";
var Gambia$4 = "Gambie";
var Georgia$4 = "Géorgie";
var Germany$4 = "Allemagne";
var Ghana$4 = "Ghana";
var Gibraltar$4 = "Gibraltar";
var Greece$4 = "Grèce";
var Greenland$4 = "Groenland";
var Grenada$4 = "Grenade";
var Guadeloupe$4 = "Guadeloupe";
var Guam$4 = "Guam";
var Guatemala$4 = "Guatemala";
var Guernsey$4 = "Guernesey";
var Guinea$4 = "Guinée";
var Guyana$4 = "Guyane";
var Haiti$4 = "Haïti";
var Honduras$4 = "Honduras";
var Hungary$4 = "Hongrie";
var Iceland$4 = "Islande";
var India$4 = "Inde";
var Indonesia$4 = "Indonésie";
var Iraq$4 = "Irak";
var Ireland$4 = "Irlande";
var Israel$4 = "Israël";
var Italy$4 = "Italie";
var Jamaica$4 = "Jamaïque";
var Japan$4 = "Japon";
var Jersey$4 = "Jersey";
var Jordan$4 = "Jordanie";
var Kazakhstan$4 = "Kazakhstan";
var Kenya$4 = "Kenya";
var Kiribati$4 = "Kiribati";
var Kuwait$4 = "Koweit";
var Kyrgyzstan$4 = "Kirghizistan";
var Latvia$4 = "Lettonie";
var Lebanon$4 = "Liban";
var Lesotho$4 = "Lesotho";
var Liberia$4 = "Libéria";
var Liechtenstein$4 = "Liechtenstein";
var Lithuania$4 = "Lituanie";
var Luxembourg$4 = "Luxembourg";
var Macao$4 = "Macao";
var Madagascar$4 = "Madagascar";
var Malawi$4 = "Malawi";
var Malaysia$4 = "Malaisie";
var Maldives$4 = "Maldives";
var Mali$4 = "Mali";
var Malta$4 = "Malte";
var Martinique$4 = "Martinique";
var Mauritania$4 = "Mauritanie";
var Mauritius$4 = "Maurice";
var Mayotte$4 = "Mayotte";
var Mexico$4 = "Mexique";
var Monaco$4 = "Monaco";
var Mongolia$4 = "Mongolie";
var Montenegro$4 = "Monténégro";
var Montserrat$4 = "Montserrat";
var Morocco$4 = "Maroc";
var Mozambique$4 = "Mozambique";
var Myanmar$4 = "Birmanie";
var Namibia$4 = "Namibie";
var Nauru$4 = "Nauru";
var Nepal$4 = "Népal";
var Netherlands$4 = "Pays-Bas";
var Nicaragua$4 = "Nicaragua";
var Niger$4 = "Niger";
var Nigeria$4 = "Nigeria";
var Niue$4 = "Niue";
var Norway$4 = "Norvège";
var Oman$4 = "Oman";
var Pakistan$4 = "Pakistan";
var Palau$4 = "Palaos";
var Panama$4 = "Panama";
var Paraguay$4 = "Paraguay";
var Peru$4 = "Pérou";
var Philippines$4 = "Philippines";
var Pitcairn$4 = "Pitcairn";
var Poland$4 = "Pologne";
var Portugal$4 = "Portugal";
var Qatar$4 = "Qatar";
var Romania$4 = "Roumanie";
var Russia$4 = "Russie";
var Rwanda$4 = "Rwanda";
var Samoa$4 = "Samoa";
var Senegal$4 = "Sénégal";
var Serbia$4 = "Serbie";
var Seychelles$4 = "Seychelles";
var Singapore$4 = "Singapour";
var Slovakia$4 = "Slovaquie";
var Slovenia$4 = "Slovénie";
var Somalia$4 = "Somalie";
var Spain$4 = "Espagne";
var Suriname$4 = "Suriname";
var Swaziland$4 = "Swaziland";
var Sweden$4 = "Suède";
var Switzerland$4 = "Suisse";
var Tajikistan$4 = "Tadjikistan";
var Thailand$4 = "Thaïlande";
var Togo$4 = "Aller";
var Tokelau$4 = "Tokélaou";
var Tonga$4 = "Tonga";
var Tunisia$4 = "Tunisie";
var Turkey$4 = "Turquie";
var Turkmenistan$4 = "Turkménistan";
var Tuvalu$4 = "Tuvalu";
var Uganda$4 = "Ouganda";
var Ukraine$4 = "Ukraine";
var Uruguay$4 = "Uruguay";
var Uzbekistan$4 = "Ouzbékistan";
var Vanuatu$4 = "Vanuatu";
var Yemen$4 = "Yémen";
var Zambia$4 = "Zambie";
var Zimbabwe$4 = "Zimbabwe";
var t152 = {
	Afghanistan: Afghanistan$4,
	Albania: Albania$4,
	Algeria: Algeria$4,
	AmericanSamoa: AmericanSamoa$4,
	Andorra: Andorra$4,
	Angola: Angola$4,
	Anguilla: Anguilla$4,
	"Antigua and Barbuda": "Antigua-et-Barbuda",
	Argentina: Argentina$4,
	Armenia: Armenia$4,
	Aruba: Aruba$4,
	Australia: Australia$4,
	Austria: Austria$4,
	Azerbaijan: Azerbaijan$4,
	Bahamas: Bahamas$4,
	Bahrain: Bahrain$4,
	Bangladesh: Bangladesh$4,
	Barbados: Barbados$4,
	Belarus: Belarus$4,
	Belgium: Belgium$4,
	Belize: Belize$4,
	Benin: Benin$4,
	Bermuda: Bermuda$4,
	Bhutan: Bhutan$4,
	"Bolivia, Plurinational State of": "Bolivie, État plurinational de",
	"Bosnia and Herzegovina": "Bosnie-Herzégovine",
	Botswana: Botswana$4,
	Brazil: Brazil$4,
	"British Indian Ocean Territory": "Territoire britannique de l'océan Indien",
	"Brunei Darussalam": "Brunéi Darussalam",
	Bulgaria: Bulgaria$4,
	"Burkina Faso": "Burkina Faso",
	Burundi: Burundi$4,
	Cambodia: Cambodia$4,
	Cameroon: Cameroon$4,
	Canada: Canada$4,
	"Cape Verde": "Cap-Vert",
	"Cayman Islands": "Îles Caïmans",
	"Central African Republic": "République centrafricaine",
	Chad: Chad$4,
	Chile: Chile$4,
	China: China$4,
	"Christmas Island": "L'île Christmas",
	"Cocos (Keeling) Islands": "Îles Cocos (Keeling)",
	Colombia: Colombia$4,
	Comoros: Comoros$4,
	Congo: Congo$4,
	"Congo, The Democratic Republic of the": "Congo, République démocratique du",
	"Cook Islands": "Îles Cook",
	"Costa Rica": "Costa Rica",
	"Cote d'Ivoire": "Côte d'Ivoire",
	Croatia: Croatia$4,
	Curacao: Curacao$4,
	Cyprus: Cyprus$4,
	"Czech Republic": "République tchèque",
	Denmark: Denmark$4,
	Djibouti: Djibouti$4,
	Dominica: Dominica$4,
	"Dominican Republic": "République dominicaine",
	Ecuador: Ecuador$4,
	Egypt: Egypt$4,
	"El Salvador": "Le Salvador",
	"Equatorial Guinea": "Guinée équatoriale",
	Eritrea: Eritrea$4,
	Estonia: Estonia$4,
	Ethiopia: Ethiopia$4,
	"Falkland Islands (Malvinas)": "Îles Falkland (Malvinas)",
	"Faroe Islands": "Îles Féroé",
	Fiji: Fiji$4,
	Finland: Finland$4,
	France: France$4,
	"French Guiana": "Guyane française",
	"French Polynesia": "Polynésie française",
	Gabon: Gabon$4,
	Gambia: Gambia$4,
	Georgia: Georgia$4,
	Germany: Germany$4,
	Ghana: Ghana$4,
	Gibraltar: Gibraltar$4,
	Greece: Greece$4,
	Greenland: Greenland$4,
	Grenada: Grenada$4,
	Guadeloupe: Guadeloupe$4,
	Guam: Guam$4,
	Guatemala: Guatemala$4,
	Guernsey: Guernsey$4,
	Guinea: Guinea$4,
	"Guinea-Bissau": "Guinée-Bissau",
	Guyana: Guyana$4,
	Haiti: Haiti$4,
	"Holy See (Vatican City State)": "Saint-Siège (État de la Cité du Vatican)",
	Honduras: Honduras$4,
	"Hong Kong": "Hong Kong",
	Hungary: Hungary$4,
	Iceland: Iceland$4,
	India: India$4,
	Indonesia: Indonesia$4,
	Iraq: Iraq$4,
	Ireland: Ireland$4,
	"Isle of Man": "Île de Man",
	Israel: Israel$4,
	Italy: Italy$4,
	Jamaica: Jamaica$4,
	Japan: Japan$4,
	Jersey: Jersey$4,
	Jordan: Jordan$4,
	Kazakhstan: Kazakhstan$4,
	Kenya: Kenya$4,
	Kiribati: Kiribati$4,
	"Korea, Republic of": "Corée, République de",
	Kuwait: Kuwait$4,
	Kyrgyzstan: Kyrgyzstan$4,
	"Lao People's Democratic Republic": "République démocratique populaire lao",
	Latvia: Latvia$4,
	Lebanon: Lebanon$4,
	Lesotho: Lesotho$4,
	Liberia: Liberia$4,
	"Libyan Arab Jamahiriya": "Jamahiriya arabe libyenne",
	Liechtenstein: Liechtenstein$4,
	Lithuania: Lithuania$4,
	Luxembourg: Luxembourg$4,
	Macao: Macao$4,
	"Macedonia, The Former Yugoslav Republic of": "Macédoine, ex-République yougoslave de",
	Madagascar: Madagascar$4,
	Malawi: Malawi$4,
	Malaysia: Malaysia$4,
	Maldives: Maldives$4,
	Mali: Mali$4,
	Malta: Malta$4,
	"Marshall Islands": "Îles Marshall",
	Martinique: Martinique$4,
	Mauritania: Mauritania$4,
	Mauritius: Mauritius$4,
	Mayotte: Mayotte$4,
	Mexico: Mexico$4,
	"Micronesia, Federated States of": "Micronésie, États fédérés de",
	"Moldova, Republic of": "Moldavie, République de",
	Monaco: Monaco$4,
	Mongolia: Mongolia$4,
	Montenegro: Montenegro$4,
	Montserrat: Montserrat$4,
	Morocco: Morocco$4,
	Mozambique: Mozambique$4,
	Myanmar: Myanmar$4,
	Namibia: Namibia$4,
	Nauru: Nauru$4,
	Nepal: Nepal$4,
	Netherlands: Netherlands$4,
	"New Caledonia": "Nouvelle-Calédonie",
	"New Zealand": "Nouvelle-Zélande",
	Nicaragua: Nicaragua$4,
	Niger: Niger$4,
	Nigeria: Nigeria$4,
	Niue: Niue$4,
	"Norfolk Island": "Île Norfolk",
	"Northern Mariana Islands": "Îles Mariannes du Nord",
	Norway: Norway$4,
	Oman: Oman$4,
	Pakistan: Pakistan$4,
	Palau: Palau$4,
	"Palestinian Territory, Occupied": "Territoire palestinien occupé",
	Panama: Panama$4,
	"Papua New Guinea": "Papouasie-Nouvelle-Guinée",
	Paraguay: Paraguay$4,
	Peru: Peru$4,
	Philippines: Philippines$4,
	Pitcairn: Pitcairn$4,
	Poland: Poland$4,
	Portugal: Portugal$4,
	"Puerto Rico": "Porto Rico",
	Qatar: Qatar$4,
	Romania: Romania$4,
	Russia: Russia$4,
	Rwanda: Rwanda$4,
	"Réunion": "Réunion",
	"Saint Barthélemy": "Saint-Barthélemy",
	"Saint Helena, Ascension and Tristan Da Cunha": "Sainte-Hélène, Ascension et Tristan Da Cunha",
	"Saint Kitts and Nevis": "Saint-Kitts-et-Nevis",
	"Saint Lucia": "Sainte-Lucie",
	"Saint Martin": "Saint-Martin",
	"Saint Pierre and Miquelon": "Saint-Pierre-et-Miquelon",
	"Saint Vincent and the Grenadines": "Saint-Vincent-et-les Grenadines",
	Samoa: Samoa$4,
	"San Marino": "Saint-Marin",
	"Sao Tome and Principe": "Sao Tomé-et-Principe",
	"Saudi Arabia": "Arabie Saoudite",
	Senegal: Senegal$4,
	Serbia: Serbia$4,
	Seychelles: Seychelles$4,
	"Sierra Leone": "Sierra Leone",
	Singapore: Singapore$4,
	Slovakia: Slovakia$4,
	Slovenia: Slovenia$4,
	"Solomon Islands": "Îles Salomon",
	Somalia: Somalia$4,
	"South Africa": "Afrique du Sud",
	"South Georgia and the South Sandwich Islands": "Géorgie du Sud et les îles Sandwich du Sud",
	Spain: Spain$4,
	"Sri Lanka": "Sri Lanka",
	Suriname: Suriname$4,
	"Svalbard and Jan Mayen": "Svalbard et Jan Mayen",
	Swaziland: Swaziland$4,
	Sweden: Sweden$4,
	Switzerland: Switzerland$4,
	"Taiwan, Province of China": "Taïwan, province de Chine",
	Tajikistan: Tajikistan$4,
	"Tanzania, United Republic of": "Tanzanie, République-Unie de",
	Thailand: Thailand$4,
	"Timor-Leste": "Timor-Leste",
	Togo: Togo$4,
	Tokelau: Tokelau$4,
	Tonga: Tonga$4,
	"Trinidad and Tobago": "Trinité-et-Tobago",
	Tunisia: Tunisia$4,
	Turkey: Turkey$4,
	Turkmenistan: Turkmenistan$4,
	"Turks and Caicos Islands": "Îles Turques-et-Caïques",
	Tuvalu: Tuvalu$4,
	Uganda: Uganda$4,
	Ukraine: Ukraine$4,
	"United Arab Emirates": "Émirats arabes unis",
	"United Kingdom": "Royaume-Uni",
	"United States": "États-Unis",
	Uruguay: Uruguay$4,
	Uzbekistan: Uzbekistan$4,
	Vanuatu: Vanuatu$4,
	"Venezuela, Bolivarian Republic of": "Venezuela, République bolivarienne du",
	"Viet Nam": "Vietnam",
	"Virgin Islands, British": "Îles Vierges britanniques",
	"Virgin Islands, U.S.": "Îles Vierges américaines",
	"Wallis and Futuna": "Wallis-et-Futuna",
	Yemen: Yemen$4,
	Zambia: Zambia$4,
	Zimbabwe: Zimbabwe$4,
	"Åland Islands": "Îles Åland"
};

var t153 = {
	"No results found.": "Aucun résultat trouvé."
};

var Done$b = "Hecho";
var t154 = {
	Done: Done$b
};

var t155 = {
	"No results found.": "No se encontraron resultados."
};

var t156 = {
	"Clear all": "Borrar todo",
	"No results found.": "No se encontraron resultados."
};

var Passengers$3 = "Pasajeros";
var t157 = {
	"Number of passengers": "Número de pasajeros",
	Passengers: Passengers$3
};

var t158 = {
	"No results found.": "No se encontraron resultados."
};

var Blog$3 = "Blog";
var Careers$3 = "Carreras";
var Contact$3 = "Contacto";
var Coverage$3 = "Cobertura";
var Guides$3 = "Guías";
var Imprint$3 = "Imprimir";
var Parking$3 = "Aparcamiento";
var eSIM$3 = "eSIM";
var t159 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. Las marcas comerciales y nombres comerciales son propiedad de sus respectivos dueños.",
	Blog: Blog$3,
	Careers: Careers$3,
	Contact: Contact$3,
	Coverage: Coverage$3,
	Guides: Guides$3,
	Imprint: Imprint$3,
	Parking: Parking$3,
	"Privacy Policy": "política de privacidad",
	"Terms Of Services": "Condiciones de servicio",
	eSIM: eSIM$3,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Mozio Inc. Las marcas comerciales y nombres comerciales son propiedad de sus respectivos dueños."
};

var Filters$3 = "Filtros";
var t160 = {
	"Clear All": "Borrar todo",
	Filters: Filters$3,
	"Show results": "Mostrar resultados"
};

var Done$a = "Listo";
var Hours$3 = "Horas";
var Minutes$3 = "Minutos";
var t161 = {
	"AM/PM": "AM/PM",
	Done: Done$a,
	Hours: Hours$3,
	Minutes: Minutes$3
};

var Done$9 = "Hecho";
var t162 = {
	Done: Done$9
};

var Afghanistan$3 = "Afganistán";
var Albania$3 = "Albania";
var Algeria$3 = "Argelia";
var AmericanSamoa$3 = "Samoa Americana";
var Andorra$3 = "Andorra";
var Angola$3 = "Angola";
var Anguilla$3 = "Anguila";
var Argentina$3 = "Argentina";
var Armenia$3 = "Armenia";
var Aruba$3 = "Aruba";
var Australia$3 = "Australia";
var Austria$3 = "Austria";
var Azerbaijan$3 = "Azerbaiyán";
var Bahamas$3 = "Bahamas";
var Bahrain$3 = "Baréin";
var Bangladesh$3 = "Bangladés";
var Barbados$3 = "Barbados";
var Belarus$3 = "Bielorrusia";
var Belgium$3 = "Bélgica";
var Belize$3 = "Belice";
var Benin$3 = "Benín";
var Bermuda$3 = "islas Bermudas";
var Bhutan$3 = "Bután";
var Botswana$3 = "Botsuana";
var Brazil$3 = "Brasil";
var Bulgaria$3 = "Bulgaria";
var Burundi$3 = "Burundi";
var Cambodia$3 = "Camboya";
var Cameroon$3 = "Camerún";
var Canada$3 = "Canadá";
var Chad$3 = "Chad";
var Chile$3 = "Chile";
var China$3 = "Porcelana";
var Colombia$3 = "Colombia";
var Comoros$3 = "Comoras";
var Congo$3 = "Congo";
var Croatia$3 = "Croacia";
var Curacao$3 = "Curazao";
var Cyprus$3 = "Chipre";
var Denmark$3 = "Dinamarca";
var Djibouti$3 = "Yibuti";
var Dominica$3 = "Dominica";
var Ecuador$3 = "Ecuador";
var Egypt$3 = "Egipto";
var Eritrea$3 = "Eritrea";
var Estonia$3 = "Estonia";
var Ethiopia$3 = "Etiopía";
var Fiji$3 = "Fiyi";
var Finland$3 = "Finlandia";
var France$3 = "Francia";
var Gabon$3 = "Gabón";
var Gambia$3 = "Gambia";
var Georgia$3 = "Georgia";
var Germany$3 = "Alemania";
var Ghana$3 = "Ghana";
var Gibraltar$3 = "Gibraltar";
var Greece$3 = "Grecia";
var Greenland$3 = "Tierra Verde";
var Grenada$3 = "Granada";
var Guadeloupe$3 = "Guadalupe";
var Guam$3 = "Guam";
var Guatemala$3 = "Guatemala";
var Guernsey$3 = "Guernsey";
var Guinea$3 = "Guinea";
var Guyana$3 = "Guayana";
var Haiti$3 = "Haití";
var Honduras$3 = "Honduras";
var Hungary$3 = "Hungría";
var Iceland$3 = "Islandia";
var India$3 = "India";
var Indonesia$3 = "Indonesia";
var Iraq$3 = "Irak";
var Ireland$3 = "Irlanda";
var Israel$3 = "Israel";
var Italy$3 = "Italia";
var Jamaica$3 = "Jamaica";
var Japan$3 = "Japón";
var Jersey$3 = "Jersey";
var Jordan$3 = "Jordán";
var Kazakhstan$3 = "Kazajstán";
var Kenya$3 = "Kenia";
var Kiribati$3 = "Kiribati";
var Kuwait$3 = "Kuwait";
var Kyrgyzstan$3 = "Kirguistán";
var Latvia$3 = "Letonia";
var Lebanon$3 = "Líbano";
var Lesotho$3 = "Lesoto";
var Liberia$3 = "Liberia";
var Liechtenstein$3 = "Liechtenstein";
var Lithuania$3 = "Lituania";
var Luxembourg$3 = "Luxemburgo";
var Macao$3 = "Macao";
var Madagascar$3 = "Madagascar";
var Malawi$3 = "Malawi";
var Malaysia$3 = "Malasia";
var Maldives$3 = "Maldivas";
var Mali$3 = "Malí";
var Malta$3 = "Malta";
var Martinique$3 = "Martinica";
var Mauritania$3 = "Mauritania";
var Mauritius$3 = "Mauricio";
var Mayotte$3 = "Mayotte";
var Mexico$3 = "México";
var Monaco$3 = "Mónaco";
var Mongolia$3 = "Mongolia";
var Montenegro$3 = "Montenegro";
var Montserrat$3 = "Montserrat";
var Morocco$3 = "Marruecos";
var Mozambique$3 = "Mozambique";
var Myanmar$3 = "Myanmar";
var Namibia$3 = "Namibia";
var Nauru$3 = "Nauru";
var Nepal$3 = "Nepal";
var Netherlands$3 = "Países Bajos";
var Nicaragua$3 = "Nicaragua";
var Niger$3 = "Níger";
var Nigeria$3 = "Nigeria";
var Niue$3 = "Niue";
var Norway$3 = "Noruega";
var Oman$3 = "Omán";
var Pakistan$3 = "Pakistán";
var Palau$3 = "Palaos";
var Panama$3 = "Panamá";
var Paraguay$3 = "Paraguay";
var Peru$3 = "Perú";
var Philippines$3 = "Filipinas";
var Pitcairn$3 = "Pitcairn";
var Poland$3 = "Polonia";
var Portugal$3 = "Portugal";
var Qatar$3 = "Katar";
var Romania$3 = "Rumania";
var Russia$3 = "Rusia";
var Rwanda$3 = "Ruanda";
var Samoa$3 = "Samoa";
var Senegal$3 = "Senegal";
var Serbia$3 = "Serbia";
var Seychelles$3 = "Seychelles";
var Singapore$3 = "Singapur";
var Slovakia$3 = "Eslovaquia";
var Slovenia$3 = "Eslovenia";
var Somalia$3 = "Somalia";
var Spain$3 = "España";
var Suriname$3 = "Surinam";
var Swaziland$3 = "Suazilandia";
var Sweden$3 = "Suecia";
var Switzerland$3 = "Suiza";
var Tajikistan$3 = "Tayikistán";
var Thailand$3 = "Tailandia";
var Togo$3 = "Ir";
var Tokelau$3 = "Tokelau";
var Tonga$3 = "Tonga";
var Tunisia$3 = "Túnez";
var Turkey$3 = "Pavo";
var Turkmenistan$3 = "Turkmenistán";
var Tuvalu$3 = "Tuvalu";
var Uganda$3 = "Uganda";
var Ukraine$3 = "Ucrania";
var Uruguay$3 = "Uruguay";
var Uzbekistan$3 = "Uzbekistán";
var Vanuatu$3 = "Vanuatu";
var Yemen$3 = "Yemen";
var Zambia$3 = "Zambia";
var Zimbabwe$3 = "Zimbabue";
var t163 = {
	Afghanistan: Afghanistan$3,
	Albania: Albania$3,
	Algeria: Algeria$3,
	AmericanSamoa: AmericanSamoa$3,
	Andorra: Andorra$3,
	Angola: Angola$3,
	Anguilla: Anguilla$3,
	"Antigua and Barbuda": "Antigua y Barbuda",
	Argentina: Argentina$3,
	Armenia: Armenia$3,
	Aruba: Aruba$3,
	Australia: Australia$3,
	Austria: Austria$3,
	Azerbaijan: Azerbaijan$3,
	Bahamas: Bahamas$3,
	Bahrain: Bahrain$3,
	Bangladesh: Bangladesh$3,
	Barbados: Barbados$3,
	Belarus: Belarus$3,
	Belgium: Belgium$3,
	Belize: Belize$3,
	Benin: Benin$3,
	Bermuda: Bermuda$3,
	Bhutan: Bhutan$3,
	"Bolivia, Plurinational State of": "Bolivia, Estado Plurinacional de",
	"Bosnia and Herzegovina": "Bosnia y Herzegovina",
	Botswana: Botswana$3,
	Brazil: Brazil$3,
	"British Indian Ocean Territory": "Territorio Británico del Océano Índico",
	"Brunei Darussalam": "Brunéi Darussalam",
	Bulgaria: Bulgaria$3,
	"Burkina Faso": "Burkina Faso",
	Burundi: Burundi$3,
	Cambodia: Cambodia$3,
	Cameroon: Cameroon$3,
	Canada: Canada$3,
	"Cape Verde": "Cabo Verde",
	"Cayman Islands": "Islas Caimán",
	"Central African Republic": "República Centroafricana",
	Chad: Chad$3,
	Chile: Chile$3,
	China: China$3,
	"Christmas Island": "Isla de Navidad",
	"Cocos (Keeling) Islands": "Islas Cocos (Keeling)",
	Colombia: Colombia$3,
	Comoros: Comoros$3,
	Congo: Congo$3,
	"Congo, The Democratic Republic of the": "Congo, República Democrática del",
	"Cook Islands": "Islas Cook",
	"Costa Rica": "Costa Rica",
	"Cote d'Ivoire": "Costa de Marfil",
	Croatia: Croatia$3,
	Curacao: Curacao$3,
	Cyprus: Cyprus$3,
	"Czech Republic": "República Checa",
	Denmark: Denmark$3,
	Djibouti: Djibouti$3,
	Dominica: Dominica$3,
	"Dominican Republic": "República Dominicana",
	Ecuador: Ecuador$3,
	Egypt: Egypt$3,
	"El Salvador": "El Salvador",
	"Equatorial Guinea": "Guinea Ecuatorial",
	Eritrea: Eritrea$3,
	Estonia: Estonia$3,
	Ethiopia: Ethiopia$3,
	"Falkland Islands (Malvinas)": "Islas Malvinas (Falkland Islands)",
	"Faroe Islands": "Islas Feroe",
	Fiji: Fiji$3,
	Finland: Finland$3,
	France: France$3,
	"French Guiana": "Guayana Francesa",
	"French Polynesia": "Polinesia Francesa",
	Gabon: Gabon$3,
	Gambia: Gambia$3,
	Georgia: Georgia$3,
	Germany: Germany$3,
	Ghana: Ghana$3,
	Gibraltar: Gibraltar$3,
	Greece: Greece$3,
	Greenland: Greenland$3,
	Grenada: Grenada$3,
	Guadeloupe: Guadeloupe$3,
	Guam: Guam$3,
	Guatemala: Guatemala$3,
	Guernsey: Guernsey$3,
	Guinea: Guinea$3,
	"Guinea-Bissau": "Guinea-Bissau",
	Guyana: Guyana$3,
	Haiti: Haiti$3,
	"Holy See (Vatican City State)": "Santa Sede (Estado de la Ciudad del Vaticano)",
	Honduras: Honduras$3,
	"Hong Kong": "Hong Kong",
	Hungary: Hungary$3,
	Iceland: Iceland$3,
	India: India$3,
	Indonesia: Indonesia$3,
	Iraq: Iraq$3,
	Ireland: Ireland$3,
	"Isle of Man": "Isla de Man",
	Israel: Israel$3,
	Italy: Italy$3,
	Jamaica: Jamaica$3,
	Japan: Japan$3,
	Jersey: Jersey$3,
	Jordan: Jordan$3,
	Kazakhstan: Kazakhstan$3,
	Kenya: Kenya$3,
	Kiribati: Kiribati$3,
	"Korea, Republic of": "Corea, República de",
	Kuwait: Kuwait$3,
	Kyrgyzstan: Kyrgyzstan$3,
	"Lao People's Democratic Republic": "República Democrática Popular Lao",
	Latvia: Latvia$3,
	Lebanon: Lebanon$3,
	Lesotho: Lesotho$3,
	Liberia: Liberia$3,
	"Libyan Arab Jamahiriya": "Jamahiriya Árabe Libia",
	Liechtenstein: Liechtenstein$3,
	Lithuania: Lithuania$3,
	Luxembourg: Luxembourg$3,
	Macao: Macao$3,
	"Macedonia, The Former Yugoslav Republic of": "Macedonia, ex República Yugoslava de",
	Madagascar: Madagascar$3,
	Malawi: Malawi$3,
	Malaysia: Malaysia$3,
	Maldives: Maldives$3,
	Mali: Mali$3,
	Malta: Malta$3,
	"Marshall Islands": "Islas Marshall",
	Martinique: Martinique$3,
	Mauritania: Mauritania$3,
	Mauritius: Mauritius$3,
	Mayotte: Mayotte$3,
	Mexico: Mexico$3,
	"Micronesia, Federated States of": "Micronesia, Estados Federados de",
	"Moldova, Republic of": "República de Moldavia",
	Monaco: Monaco$3,
	Mongolia: Mongolia$3,
	Montenegro: Montenegro$3,
	Montserrat: Montserrat$3,
	Morocco: Morocco$3,
	Mozambique: Mozambique$3,
	Myanmar: Myanmar$3,
	Namibia: Namibia$3,
	Nauru: Nauru$3,
	Nepal: Nepal$3,
	Netherlands: Netherlands$3,
	"New Caledonia": "Nueva Caledonia",
	"New Zealand": "Nueva Zelanda",
	Nicaragua: Nicaragua$3,
	Niger: Niger$3,
	Nigeria: Nigeria$3,
	Niue: Niue$3,
	"Norfolk Island": "Isla Norfolk",
	"Northern Mariana Islands": "Islas Marianas del Norte",
	Norway: Norway$3,
	Oman: Oman$3,
	Pakistan: Pakistan$3,
	Palau: Palau$3,
	"Palestinian Territory, Occupied": "Territorio Palestino Ocupado",
	Panama: Panama$3,
	"Papua New Guinea": "Papúa Nueva Guinea",
	Paraguay: Paraguay$3,
	Peru: Peru$3,
	Philippines: Philippines$3,
	Pitcairn: Pitcairn$3,
	Poland: Poland$3,
	Portugal: Portugal$3,
	"Puerto Rico": "Puerto Rico",
	Qatar: Qatar$3,
	Romania: Romania$3,
	Russia: Russia$3,
	Rwanda: Rwanda$3,
	"Réunion": "Reunión",
	"Saint Barthélemy": "San Bartolomé",
	"Saint Helena, Ascension and Tristan Da Cunha": "Santa Elena, Ascensión y Tristán Da Cunha",
	"Saint Kitts and Nevis": "San Cristóbal y Nieves",
	"Saint Lucia": "Santa Lucía",
	"Saint Martin": "San Martín",
	"Saint Pierre and Miquelon": "San Pedro y Miquelón",
	"Saint Vincent and the Grenadines": "San Vicente y las Granadinas",
	Samoa: Samoa$3,
	"San Marino": "San Marino",
	"Sao Tome and Principe": "Santo Tomé y Príncipe",
	"Saudi Arabia": "Arabia Saudita",
	Senegal: Senegal$3,
	Serbia: Serbia$3,
	Seychelles: Seychelles$3,
	"Sierra Leone": "Sierra Leona",
	Singapore: Singapore$3,
	Slovakia: Slovakia$3,
	Slovenia: Slovenia$3,
	"Solomon Islands": "Islas Salomón",
	Somalia: Somalia$3,
	"South Africa": "Sudáfrica",
	"South Georgia and the South Sandwich Islands": "Islas Georgias del Sur y Sandwich del Sur",
	Spain: Spain$3,
	"Sri Lanka": "Sri Lanka",
	Suriname: Suriname$3,
	"Svalbard and Jan Mayen": "Svalbard y Jan Mayen",
	Swaziland: Swaziland$3,
	Sweden: Sweden$3,
	Switzerland: Switzerland$3,
	"Taiwan, Province of China": "Taiwán, provincia de China",
	Tajikistan: Tajikistan$3,
	"Tanzania, United Republic of": "República Unida de Tanzania",
	Thailand: Thailand$3,
	"Timor-Leste": "Timor Oriental",
	Togo: Togo$3,
	Tokelau: Tokelau$3,
	Tonga: Tonga$3,
	"Trinidad and Tobago": "Trinidad y Tobago",
	Tunisia: Tunisia$3,
	Turkey: Turkey$3,
	Turkmenistan: Turkmenistan$3,
	"Turks and Caicos Islands": "Islas Turcas y Caicos",
	Tuvalu: Tuvalu$3,
	Uganda: Uganda$3,
	Ukraine: Ukraine$3,
	"United Arab Emirates": "Emiratos Árabes Unidos",
	"United Kingdom": "Reino Unido",
	"United States": "Estados Unidos",
	Uruguay: Uruguay$3,
	Uzbekistan: Uzbekistan$3,
	Vanuatu: Vanuatu$3,
	"Venezuela, Bolivarian Republic of": "Venezuela, República Bolivariana de",
	"Viet Nam": "Vietnam",
	"Virgin Islands, British": "Islas Vírgenes Británicas",
	"Virgin Islands, U.S.": "Islas Vírgenes de los Estados Unidos",
	"Wallis and Futuna": "Wallis y Futuna",
	Yemen: Yemen$3,
	Zambia: Zambia$3,
	Zimbabwe: Zimbabwe$3,
	"Åland Islands": "Islas Åland"
};

var t164 = {
	"No results found.": "No se encontraron resultados."
};

var Done$8 = "Done";
var t165 = {
	Done: Done$8
};

var t166 = {
	"No results found.": "No results found."
};

var t167 = {
	"Clear all": "Clear all",
	"No results found.": "No results found."
};

var Passengers$2 = "Passengers";
var t168 = {
	"Number of passengers": "Number of passengers",
	Passengers: Passengers$2
};

var t169 = {
	"No results found.": "No results found."
};

var Blog$2 = "Blog";
var Careers$2 = "Careers";
var Contact$2 = "Contact";
var Coverage$2 = "Coverage";
var Guides$2 = "Guides";
var Imprint$2 = "Imprint";
var Parking$2 = "Parking";
var eSIM$2 = "eSIM";
var t170 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. Trademarks and brands are property of their respective owners.",
	Blog: Blog$2,
	Careers: Careers$2,
	Contact: Contact$2,
	Coverage: Coverage$2,
	Guides: Guides$2,
	Imprint: Imprint$2,
	Parking: Parking$2,
	"Privacy Policy": "Privacy Policy",
	"Terms Of Services": "Terms Of Services",
	eSIM: eSIM$2,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Mozio Inc. Trademarks and brands are property of their respective owners."
};

var Filters$2 = "Filters";
var t171 = {
	"Clear All": "Clear All",
	Filters: Filters$2,
	"Show results": "Show results"
};

var Done$7 = "Done";
var Hours$2 = "Hours";
var Minutes$2 = "Minutes";
var t172 = {
	"AM/PM": "AM/PM",
	Done: Done$7,
	Hours: Hours$2,
	Minutes: Minutes$2
};

var Done$6 = "Done";
var t173 = {
	Done: Done$6
};

var Afghanistan$2 = "Afghanistan";
var Albania$2 = "Albania";
var Algeria$2 = "Algeria";
var AmericanSamoa$2 = "AmericanSamoa";
var Andorra$2 = "Andorra";
var Angola$2 = "Angola";
var Anguilla$2 = "Anguilla";
var Argentina$2 = "Argentina";
var Armenia$2 = "Armenia";
var Aruba$2 = "Aruba";
var Australia$2 = "Australia";
var Austria$2 = "Austria";
var Azerbaijan$2 = "Azerbaijan";
var Bahamas$2 = "Bahamas";
var Bahrain$2 = "Bahrain";
var Bangladesh$2 = "Bangladesh";
var Barbados$2 = "Barbados";
var Belarus$2 = "Belarus";
var Belgium$2 = "Belgium";
var Belize$2 = "Belize";
var Benin$2 = "Benin";
var Bermuda$2 = "Bermuda";
var Bhutan$2 = "Bhutan";
var Botswana$2 = "Botswana";
var Brazil$2 = "Brazil";
var Bulgaria$2 = "Bulgaria";
var Burundi$2 = "Burundi";
var Cambodia$2 = "Cambodia";
var Cameroon$2 = "Cameroon";
var Canada$2 = "Canada";
var Chad$2 = "Chad";
var Chile$2 = "Chile";
var China$2 = "China";
var Colombia$2 = "Colombia";
var Comoros$2 = "Comoros";
var Congo$2 = "Congo";
var Croatia$2 = "Croatia";
var Curacao$2 = "Curacao";
var Cyprus$2 = "Cyprus";
var Denmark$2 = "Denmark";
var Djibouti$2 = "Djibouti";
var Dominica$2 = "Dominica";
var Ecuador$2 = "Ecuador";
var Egypt$2 = "Egypt";
var Eritrea$2 = "Eritrea";
var Estonia$2 = "Estonia";
var Ethiopia$2 = "Ethiopia";
var Fiji$2 = "Fiji";
var Finland$2 = "Finland";
var France$2 = "France";
var Gabon$2 = "Gabon";
var Gambia$2 = "Gambia";
var Georgia$2 = "Georgia";
var Germany$2 = "Germany";
var Ghana$2 = "Ghana";
var Gibraltar$2 = "Gibraltar";
var Greece$2 = "Greece";
var Greenland$2 = "Greenland";
var Grenada$2 = "Grenada";
var Guadeloupe$2 = "Guadeloupe";
var Guam$2 = "Guam";
var Guatemala$2 = "Guatemala";
var Guernsey$2 = "Guernsey";
var Guinea$2 = "Guinea";
var Guyana$2 = "Guyana";
var Haiti$2 = "Haiti";
var Honduras$2 = "Honduras";
var Hungary$2 = "Hungary";
var Iceland$2 = "Iceland";
var India$2 = "India";
var Indonesia$2 = "Indonesia";
var Iraq$2 = "Iraq";
var Ireland$2 = "Ireland";
var Israel$2 = "Israel";
var Italy$2 = "Italy";
var Jamaica$2 = "Jamaica";
var Japan$2 = "Japan";
var Jersey$2 = "Jersey";
var Jordan$2 = "Jordan";
var Kazakhstan$2 = "Kazakhstan";
var Kenya$2 = "Kenya";
var Kiribati$2 = "Kiribati";
var Kuwait$2 = "Kuwait";
var Kyrgyzstan$2 = "Kyrgyzstan";
var Latvia$2 = "Latvia";
var Lebanon$2 = "Lebanon";
var Lesotho$2 = "Lesotho";
var Liberia$2 = "Liberia";
var Liechtenstein$2 = "Liechtenstein";
var Lithuania$2 = "Lithuania";
var Luxembourg$2 = "Luxembourg";
var Macao$2 = "Macao";
var Madagascar$2 = "Madagascar";
var Malawi$2 = "Malawi";
var Malaysia$2 = "Malaysia";
var Maldives$2 = "Maldives";
var Mali$2 = "Mali";
var Malta$2 = "Malta";
var Martinique$2 = "Martinique";
var Mauritania$2 = "Mauritania";
var Mauritius$2 = "Mauritius";
var Mayotte$2 = "Mayotte";
var Mexico$2 = "Mexico";
var Monaco$2 = "Monaco";
var Mongolia$2 = "Mongolia";
var Montenegro$2 = "Montenegro";
var Montserrat$2 = "Montserrat";
var Morocco$2 = "Morocco";
var Mozambique$2 = "Mozambique";
var Myanmar$2 = "Myanmar";
var Namibia$2 = "Namibia";
var Nauru$2 = "Nauru";
var Nepal$2 = "Nepal";
var Netherlands$2 = "Netherlands";
var Nicaragua$2 = "Nicaragua";
var Niger$2 = "Niger";
var Nigeria$2 = "Nigeria";
var Niue$2 = "Niue";
var Norway$2 = "Norway";
var Oman$2 = "Oman";
var Pakistan$2 = "Pakistan";
var Palau$2 = "Palau";
var Panama$2 = "Panama";
var Paraguay$2 = "Paraguay";
var Peru$2 = "Peru";
var Philippines$2 = "Philippines";
var Pitcairn$2 = "Pitcairn";
var Poland$2 = "Poland";
var Portugal$2 = "Portugal";
var Qatar$2 = "Qatar";
var Romania$2 = "Romania";
var Russia$2 = "Russia";
var Rwanda$2 = "Rwanda";
var Samoa$2 = "Samoa";
var Senegal$2 = "Senegal";
var Serbia$2 = "Serbia";
var Seychelles$2 = "Seychelles";
var Singapore$2 = "Singapore";
var Slovakia$2 = "Slovakia";
var Slovenia$2 = "Slovenia";
var Somalia$2 = "Somalia";
var Spain$2 = "Spain";
var Suriname$2 = "Suriname";
var Swaziland$2 = "Swaziland";
var Sweden$2 = "Sweden";
var Switzerland$2 = "Switzerland";
var Tajikistan$2 = "Tajikistan";
var Thailand$2 = "Thailand";
var Togo$2 = "Togo";
var Tokelau$2 = "Tokelau";
var Tonga$2 = "Tonga";
var Tunisia$2 = "Tunisia";
var Turkey$2 = "Turkey";
var Turkmenistan$2 = "Turkmenistan";
var Tuvalu$2 = "Tuvalu";
var Uganda$2 = "Uganda";
var Ukraine$2 = "Ukraine";
var Uruguay$2 = "Uruguay";
var Uzbekistan$2 = "Uzbekistan";
var Vanuatu$2 = "Vanuatu";
var Yemen$2 = "Yemen";
var Zambia$2 = "Zambia";
var Zimbabwe$2 = "Zimbabwe";
var t174 = {
	Afghanistan: Afghanistan$2,
	Albania: Albania$2,
	Algeria: Algeria$2,
	AmericanSamoa: AmericanSamoa$2,
	Andorra: Andorra$2,
	Angola: Angola$2,
	Anguilla: Anguilla$2,
	"Antigua and Barbuda": "Antigua and Barbuda",
	Argentina: Argentina$2,
	Armenia: Armenia$2,
	Aruba: Aruba$2,
	Australia: Australia$2,
	Austria: Austria$2,
	Azerbaijan: Azerbaijan$2,
	Bahamas: Bahamas$2,
	Bahrain: Bahrain$2,
	Bangladesh: Bangladesh$2,
	Barbados: Barbados$2,
	Belarus: Belarus$2,
	Belgium: Belgium$2,
	Belize: Belize$2,
	Benin: Benin$2,
	Bermuda: Bermuda$2,
	Bhutan: Bhutan$2,
	"Bolivia, Plurinational State of": "Bolivia, Plurinational State of",
	"Bosnia and Herzegovina": "Bosnia and Herzegovina",
	Botswana: Botswana$2,
	Brazil: Brazil$2,
	"British Indian Ocean Territory": "British Indian Ocean Territory",
	"Brunei Darussalam": "Brunei Darussalam",
	Bulgaria: Bulgaria$2,
	"Burkina Faso": "Burkina Faso",
	Burundi: Burundi$2,
	Cambodia: Cambodia$2,
	Cameroon: Cameroon$2,
	Canada: Canada$2,
	"Cape Verde": "Cape Verde",
	"Cayman Islands": "Cayman Islands",
	"Central African Republic": "Central African Republic",
	Chad: Chad$2,
	Chile: Chile$2,
	China: China$2,
	"Christmas Island": "Christmas Island",
	"Cocos (Keeling) Islands": "Cocos (Keeling) Islands",
	Colombia: Colombia$2,
	Comoros: Comoros$2,
	Congo: Congo$2,
	"Congo, The Democratic Republic of the": "Congo, The Democratic Republic of the",
	"Cook Islands": "Cook Islands",
	"Costa Rica": "Costa Rica",
	"Cote d'Ivoire": "Cote d'Ivoire",
	Croatia: Croatia$2,
	Curacao: Curacao$2,
	Cyprus: Cyprus$2,
	"Czech Republic": "Czech Republic",
	Denmark: Denmark$2,
	Djibouti: Djibouti$2,
	Dominica: Dominica$2,
	"Dominican Republic": "Dominican Republic",
	Ecuador: Ecuador$2,
	Egypt: Egypt$2,
	"El Salvador": "El Salvador",
	"Equatorial Guinea": "Equatorial Guinea",
	Eritrea: Eritrea$2,
	Estonia: Estonia$2,
	Ethiopia: Ethiopia$2,
	"Falkland Islands (Malvinas)": "Falkland Islands (Malvinas)",
	"Faroe Islands": "Faroe Islands",
	Fiji: Fiji$2,
	Finland: Finland$2,
	France: France$2,
	"French Guiana": "French Guiana",
	"French Polynesia": "French Polynesia",
	Gabon: Gabon$2,
	Gambia: Gambia$2,
	Georgia: Georgia$2,
	Germany: Germany$2,
	Ghana: Ghana$2,
	Gibraltar: Gibraltar$2,
	Greece: Greece$2,
	Greenland: Greenland$2,
	Grenada: Grenada$2,
	Guadeloupe: Guadeloupe$2,
	Guam: Guam$2,
	Guatemala: Guatemala$2,
	Guernsey: Guernsey$2,
	Guinea: Guinea$2,
	"Guinea-Bissau": "Guinea-Bissau",
	Guyana: Guyana$2,
	Haiti: Haiti$2,
	"Holy See (Vatican City State)": "Holy See (Vatican City State)",
	Honduras: Honduras$2,
	"Hong Kong": "Hong Kong",
	Hungary: Hungary$2,
	Iceland: Iceland$2,
	India: India$2,
	Indonesia: Indonesia$2,
	Iraq: Iraq$2,
	Ireland: Ireland$2,
	"Isle of Man": "Isle of Man",
	Israel: Israel$2,
	Italy: Italy$2,
	Jamaica: Jamaica$2,
	Japan: Japan$2,
	Jersey: Jersey$2,
	Jordan: Jordan$2,
	Kazakhstan: Kazakhstan$2,
	Kenya: Kenya$2,
	Kiribati: Kiribati$2,
	"Korea, Republic of": "Korea, Republic of",
	Kuwait: Kuwait$2,
	Kyrgyzstan: Kyrgyzstan$2,
	"Lao People's Democratic Republic": "Lao People's Democratic Republic",
	Latvia: Latvia$2,
	Lebanon: Lebanon$2,
	Lesotho: Lesotho$2,
	Liberia: Liberia$2,
	"Libyan Arab Jamahiriya": "Libyan Arab Jamahiriya",
	Liechtenstein: Liechtenstein$2,
	Lithuania: Lithuania$2,
	Luxembourg: Luxembourg$2,
	Macao: Macao$2,
	"Macedonia, The Former Yugoslav Republic of": "Macedonia, The Former Yugoslav Republic of",
	Madagascar: Madagascar$2,
	Malawi: Malawi$2,
	Malaysia: Malaysia$2,
	Maldives: Maldives$2,
	Mali: Mali$2,
	Malta: Malta$2,
	"Marshall Islands": "Marshall Islands",
	Martinique: Martinique$2,
	Mauritania: Mauritania$2,
	Mauritius: Mauritius$2,
	Mayotte: Mayotte$2,
	Mexico: Mexico$2,
	"Micronesia, Federated States of": "Micronesia, Federated States of",
	"Moldova, Republic of": "Moldova, Republic of",
	Monaco: Monaco$2,
	Mongolia: Mongolia$2,
	Montenegro: Montenegro$2,
	Montserrat: Montserrat$2,
	Morocco: Morocco$2,
	Mozambique: Mozambique$2,
	Myanmar: Myanmar$2,
	Namibia: Namibia$2,
	Nauru: Nauru$2,
	Nepal: Nepal$2,
	Netherlands: Netherlands$2,
	"New Caledonia": "New Caledonia",
	"New Zealand": "New Zealand",
	Nicaragua: Nicaragua$2,
	Niger: Niger$2,
	Nigeria: Nigeria$2,
	Niue: Niue$2,
	"Norfolk Island": "Norfolk Island",
	"Northern Mariana Islands": "Northern Mariana Islands",
	Norway: Norway$2,
	Oman: Oman$2,
	Pakistan: Pakistan$2,
	Palau: Palau$2,
	"Palestinian Territory, Occupied": "Palestinian Territory, Occupied",
	Panama: Panama$2,
	"Papua New Guinea": "Papua New Guinea",
	Paraguay: Paraguay$2,
	Peru: Peru$2,
	Philippines: Philippines$2,
	Pitcairn: Pitcairn$2,
	Poland: Poland$2,
	Portugal: Portugal$2,
	"Puerto Rico": "Puerto Rico",
	Qatar: Qatar$2,
	Romania: Romania$2,
	Russia: Russia$2,
	Rwanda: Rwanda$2,
	"Réunion": "Réunion",
	"Saint Barthélemy": "Saint Barthélemy",
	"Saint Helena, Ascension and Tristan Da Cunha": "Saint Helena, Ascension and Tristan Da Cunha",
	"Saint Kitts and Nevis": "Saint Kitts and Nevis",
	"Saint Lucia": "Saint Lucia",
	"Saint Martin": "Saint Martin",
	"Saint Pierre and Miquelon": "Saint Pierre and Miquelon",
	"Saint Vincent and the Grenadines": "Saint Vincent and the Grenadines",
	Samoa: Samoa$2,
	"San Marino": "San Marino",
	"Sao Tome and Principe": "Sao Tome and Principe",
	"Saudi Arabia": "Saudi Arabia",
	Senegal: Senegal$2,
	Serbia: Serbia$2,
	Seychelles: Seychelles$2,
	"Sierra Leone": "Sierra Leone",
	Singapore: Singapore$2,
	Slovakia: Slovakia$2,
	Slovenia: Slovenia$2,
	"Solomon Islands": "Solomon Islands",
	Somalia: Somalia$2,
	"South Africa": "South Africa",
	"South Georgia and the South Sandwich Islands": "South Georgia and the South Sandwich Islands",
	Spain: Spain$2,
	"Sri Lanka": "Sri Lanka",
	Suriname: Suriname$2,
	"Svalbard and Jan Mayen": "Svalbard and Jan Mayen",
	Swaziland: Swaziland$2,
	Sweden: Sweden$2,
	Switzerland: Switzerland$2,
	"Taiwan, Province of China": "Taiwan, Province of China",
	Tajikistan: Tajikistan$2,
	"Tanzania, United Republic of": "Tanzania, United Republic of",
	Thailand: Thailand$2,
	"Timor-Leste": "Timor-Leste",
	Togo: Togo$2,
	Tokelau: Tokelau$2,
	Tonga: Tonga$2,
	"Trinidad and Tobago": "Trinidad and Tobago",
	Tunisia: Tunisia$2,
	Turkey: Turkey$2,
	Turkmenistan: Turkmenistan$2,
	"Turks and Caicos Islands": "Turks and Caicos Islands",
	Tuvalu: Tuvalu$2,
	Uganda: Uganda$2,
	Ukraine: Ukraine$2,
	"United Arab Emirates": "United Arab Emirates",
	"United Kingdom": "United Kingdom",
	"United States": "United States",
	Uruguay: Uruguay$2,
	Uzbekistan: Uzbekistan$2,
	Vanuatu: Vanuatu$2,
	"Venezuela, Bolivarian Republic of": "Venezuela, Bolivarian Republic of",
	"Viet Nam": "Viet Nam",
	"Virgin Islands, British": "Virgin Islands, British",
	"Virgin Islands, U.S.": "Virgin Islands, U.S.",
	"Wallis and Futuna": "Wallis and Futuna",
	Yemen: Yemen$2,
	Zambia: Zambia$2,
	Zimbabwe: Zimbabwe$2,
	"Åland Islands": "Åland Islands"
};

var t175 = {
	"No results found.": "No results found."
};

var Done$5 = "Erledigt";
var t176 = {
	Done: Done$5
};

var t177 = {
	"No results found.": "Keine Ergebnisse gefunden."
};

var t178 = {
	"Clear all": "Alles löschen",
	"No results found.": "Keine Ergebnisse gefunden."
};

var Passengers$1 = "Fahrgäste";
var t179 = {
	"Number of passengers": "Anzahl der Fahrgäste",
	Passengers: Passengers$1
};

var t180 = {
	"No results found.": "Keine Ergebnisse gefunden."
};

var Blog$1 = "Der Blog";
var Careers$1 = "Karriere";
var Contact$1 = "Kontakt";
var Coverage$1 = "Abdeckung";
var Guides$1 = "Anleitungen";
var Imprint$1 = "Impressum";
var Parking$1 = "Parken";
var eSIM$1 = "eSIM";
var t181 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. Handelsmarken und Marken sind Eigentum ihrer jeweiligen Inhaber.",
	Blog: Blog$1,
	Careers: Careers$1,
	Contact: Contact$1,
	Coverage: Coverage$1,
	Guides: Guides$1,
	Imprint: Imprint$1,
	Parking: Parking$1,
	"Privacy Policy": "Datenschutzrichtlinie",
	"Terms Of Services": "Servicebedingungen",
	eSIM: eSIM$1,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Die Marken und Warenzeichen von Mozio Inc. sind Eigentum ihrer jeweiligen Inhaber."
};

var Filters$1 = "Filter";
var t182 = {
	"Clear All": "Alles löschen",
	Filters: Filters$1,
	"Show results": "Ergebnisse anzeigen"
};

var Done$4 = "Fertig";
var Hours$1 = "Stunden";
var Minutes$1 = "Minuten";
var t183 = {
	"AM/PM": "AM/PM",
	Done: Done$4,
	Hours: Hours$1,
	Minutes: Minutes$1
};

var Done$3 = "Erledigt";
var t184 = {
	Done: Done$3
};

var Afghanistan$1 = "Afghanistan";
var Albania$1 = "Albanien";
var Algeria$1 = "Algerien";
var AmericanSamoa$1 = "Amerikanisch-Samoa";
var Andorra$1 = "Andorra";
var Angola$1 = "Angola";
var Anguilla$1 = "Anguilla";
var Argentina$1 = "Argentinien";
var Armenia$1 = "Armenien";
var Aruba$1 = "Aruba";
var Australia$1 = "Australien";
var Austria$1 = "Österreich";
var Azerbaijan$1 = "Aserbaidschan";
var Bahamas$1 = "Bahamas";
var Bahrain$1 = "Bahrain";
var Bangladesh$1 = "Bangladesch";
var Barbados$1 = "Barbados";
var Belarus$1 = "Weißrussland";
var Belgium$1 = "Belgien";
var Belize$1 = "Belize";
var Benin$1 = "Benin";
var Bermuda$1 = "Bermuda";
var Bhutan$1 = "Bhutan";
var Botswana$1 = "Botswana";
var Brazil$1 = "Brasilien";
var Bulgaria$1 = "Bulgarien";
var Burundi$1 = "Burundi";
var Cambodia$1 = "Kambodscha";
var Cameroon$1 = "Kamerun";
var Canada$1 = "Kanada";
var Chad$1 = "Tschad";
var Chile$1 = "Chile";
var China$1 = "China";
var Colombia$1 = "Kolumbien";
var Comoros$1 = "Komoren";
var Congo$1 = "Kongo";
var Croatia$1 = "Kroatien";
var Curacao$1 = "Curaçao";
var Cyprus$1 = "Zypern";
var Denmark$1 = "Dänemark";
var Djibouti$1 = "Dschibuti";
var Dominica$1 = "Dominica";
var Ecuador$1 = "Ecuador";
var Egypt$1 = "Ägypten";
var Eritrea$1 = "Eritrea";
var Estonia$1 = "Estland";
var Ethiopia$1 = "Äthiopien";
var Fiji$1 = "Fidschi";
var Finland$1 = "Finnland";
var France$1 = "Frankreich";
var Gabon$1 = "Gabun";
var Gambia$1 = "Gambia";
var Georgia$1 = "Georgia";
var Germany$1 = "Deutschland";
var Ghana$1 = "Ghana";
var Gibraltar$1 = "Gibraltar";
var Greece$1 = "Griechenland";
var Greenland$1 = "Grönland";
var Grenada$1 = "Grenada";
var Guadeloupe$1 = "Guadeloupe";
var Guam$1 = "Guam";
var Guatemala$1 = "Guatemala";
var Guernsey$1 = "Guernsey";
var Guinea$1 = "Guinea";
var Guyana$1 = "Guyana";
var Haiti$1 = "Haiti";
var Honduras$1 = "Honduras";
var Hungary$1 = "Ungarn";
var Iceland$1 = "Island";
var India$1 = "Indien";
var Indonesia$1 = "Indonesien";
var Iraq$1 = "Irak";
var Ireland$1 = "Irland";
var Israel$1 = "Israel";
var Italy$1 = "Italien";
var Jamaica$1 = "Jamaika";
var Japan$1 = "Japan";
var Jersey$1 = "Jersey";
var Jordan$1 = "Jordanien";
var Kazakhstan$1 = "Kasachstan";
var Kenya$1 = "Kenia";
var Kiribati$1 = "Kiribati";
var Kuwait$1 = "Kuwait";
var Kyrgyzstan$1 = "Kirgisistan";
var Latvia$1 = "Lettland";
var Lebanon$1 = "Libanon";
var Lesotho$1 = "Lesotho";
var Liberia$1 = "Liberia";
var Liechtenstein$1 = "Liechtenstein";
var Lithuania$1 = "Litauen";
var Luxembourg$1 = "Luxemburg";
var Macao$1 = "Macau";
var Madagascar$1 = "Madagaskar";
var Malawi$1 = "Malawi";
var Malaysia$1 = "Malaysia";
var Maldives$1 = "Malediven";
var Mali$1 = "Mali";
var Malta$1 = "Malta";
var Martinique$1 = "Martinique";
var Mauritania$1 = "Mauretanien";
var Mauritius$1 = "Mauritius";
var Mayotte$1 = "Mayotte";
var Mexico$1 = "Mexiko";
var Monaco$1 = "Monaco";
var Mongolia$1 = "Mongolei";
var Montenegro$1 = "Montenegro";
var Montserrat$1 = "Montserrat";
var Morocco$1 = "Marokko";
var Mozambique$1 = "Mosambik";
var Myanmar$1 = "Myanmar";
var Namibia$1 = "Namibia";
var Nauru$1 = "Nauru";
var Nepal$1 = "Nepal";
var Netherlands$1 = "Niederlande";
var Nicaragua$1 = "Nicaragua";
var Niger$1 = "Niger";
var Nigeria$1 = "Nigeria";
var Niue$1 = "Niue";
var Norway$1 = "Norwegen";
var Oman$1 = "Oman";
var Pakistan$1 = "Pakistan";
var Palau$1 = "Palau";
var Panama$1 = "Panama";
var Paraguay$1 = "Paraguay";
var Peru$1 = "Peru";
var Philippines$1 = "Philippinen";
var Pitcairn$1 = "Pitcairn";
var Poland$1 = "Polen";
var Portugal$1 = "Portugal";
var Qatar$1 = "Katar";
var Romania$1 = "Rumänien";
var Russia$1 = "Russland";
var Rwanda$1 = "Ruanda";
var Samoa$1 = "Samoa";
var Senegal$1 = "Senegal";
var Serbia$1 = "Serbien";
var Seychelles$1 = "Seychellen";
var Singapore$1 = "Singapur";
var Slovakia$1 = "Slowakei";
var Slovenia$1 = "Slowenien";
var Somalia$1 = "Somalia";
var Spain$1 = "Spanien";
var Suriname$1 = "Suriname";
var Swaziland$1 = "Swasiland";
var Sweden$1 = "Schweden";
var Switzerland$1 = "Schweiz";
var Tajikistan$1 = "Tadschikistan";
var Thailand$1 = "Thailand";
var Togo$1 = "Togo";
var Tokelau$1 = "Tokelau";
var Tonga$1 = "Tonga";
var Tunisia$1 = "Tunesien";
var Turkey$1 = "Truthahn";
var Turkmenistan$1 = "Turkmenistan";
var Tuvalu$1 = "Tuvalu";
var Uganda$1 = "Uganda";
var Ukraine$1 = "Ukraine";
var Uruguay$1 = "Uruguay";
var Uzbekistan$1 = "Usbekistan";
var Vanuatu$1 = "Vanuatu";
var Yemen$1 = "Jemen";
var Zambia$1 = "Sambia";
var Zimbabwe$1 = "Simbabwe";
var t185 = {
	Afghanistan: Afghanistan$1,
	Albania: Albania$1,
	Algeria: Algeria$1,
	AmericanSamoa: AmericanSamoa$1,
	Andorra: Andorra$1,
	Angola: Angola$1,
	Anguilla: Anguilla$1,
	"Antigua and Barbuda": "Antigua und Barbuda",
	Argentina: Argentina$1,
	Armenia: Armenia$1,
	Aruba: Aruba$1,
	Australia: Australia$1,
	Austria: Austria$1,
	Azerbaijan: Azerbaijan$1,
	Bahamas: Bahamas$1,
	Bahrain: Bahrain$1,
	Bangladesh: Bangladesh$1,
	Barbados: Barbados$1,
	Belarus: Belarus$1,
	Belgium: Belgium$1,
	Belize: Belize$1,
	Benin: Benin$1,
	Bermuda: Bermuda$1,
	Bhutan: Bhutan$1,
	"Bolivia, Plurinational State of": "Bolivien, Plurinationaler Staat",
	"Bosnia and Herzegovina": "Bosnien und Herzegowina",
	Botswana: Botswana$1,
	Brazil: Brazil$1,
	"British Indian Ocean Territory": "Britisches Territorium im Indischen Ozean",
	"Brunei Darussalam": "Brunei Darussalam",
	Bulgaria: Bulgaria$1,
	"Burkina Faso": "Burkina Faso",
	Burundi: Burundi$1,
	Cambodia: Cambodia$1,
	Cameroon: Cameroon$1,
	Canada: Canada$1,
	"Cape Verde": "Kap Verde",
	"Cayman Islands": "Kaimaninseln",
	"Central African Republic": "Zentralafrikanische Republik",
	Chad: Chad$1,
	Chile: Chile$1,
	China: China$1,
	"Christmas Island": "Weihnachtsinsel",
	"Cocos (Keeling) Islands": "Kokosinseln (Keelinginseln)",
	Colombia: Colombia$1,
	Comoros: Comoros$1,
	Congo: Congo$1,
	"Congo, The Democratic Republic of the": "Kongo, Demokratische Republik",
	"Cook Islands": "Cookinseln",
	"Costa Rica": "Costa Rica",
	"Cote d'Ivoire": "Elfenbeinküste",
	Croatia: Croatia$1,
	Curacao: Curacao$1,
	Cyprus: Cyprus$1,
	"Czech Republic": "Tschechische Republik",
	Denmark: Denmark$1,
	Djibouti: Djibouti$1,
	Dominica: Dominica$1,
	"Dominican Republic": "Dominikanische Republik",
	Ecuador: Ecuador$1,
	Egypt: Egypt$1,
	"El Salvador": "El Salvador",
	"Equatorial Guinea": "Äquatorialguinea",
	Eritrea: Eritrea$1,
	Estonia: Estonia$1,
	Ethiopia: Ethiopia$1,
	"Falkland Islands (Malvinas)": "Falklandinseln (Malvinas)",
	"Faroe Islands": "Färöer Inseln",
	Fiji: Fiji$1,
	Finland: Finland$1,
	France: France$1,
	"French Guiana": "Französisch-Guayana",
	"French Polynesia": "Französisch-Polynesien",
	Gabon: Gabon$1,
	Gambia: Gambia$1,
	Georgia: Georgia$1,
	Germany: Germany$1,
	Ghana: Ghana$1,
	Gibraltar: Gibraltar$1,
	Greece: Greece$1,
	Greenland: Greenland$1,
	Grenada: Grenada$1,
	Guadeloupe: Guadeloupe$1,
	Guam: Guam$1,
	Guatemala: Guatemala$1,
	Guernsey: Guernsey$1,
	Guinea: Guinea$1,
	"Guinea-Bissau": "Guinea-Bissau",
	Guyana: Guyana$1,
	Haiti: Haiti$1,
	"Holy See (Vatican City State)": "Heiliger Stuhl (Vatikanstadt)",
	Honduras: Honduras$1,
	"Hong Kong": "Hongkong",
	Hungary: Hungary$1,
	Iceland: Iceland$1,
	India: India$1,
	Indonesia: Indonesia$1,
	Iraq: Iraq$1,
	Ireland: Ireland$1,
	"Isle of Man": "Isle of Man",
	Israel: Israel$1,
	Italy: Italy$1,
	Jamaica: Jamaica$1,
	Japan: Japan$1,
	Jersey: Jersey$1,
	Jordan: Jordan$1,
	Kazakhstan: Kazakhstan$1,
	Kenya: Kenya$1,
	Kiribati: Kiribati$1,
	"Korea, Republic of": "Korea, Republik",
	Kuwait: Kuwait$1,
	Kyrgyzstan: Kyrgyzstan$1,
	"Lao People's Democratic Republic": "Demokratische Volksrepublik Laos",
	Latvia: Latvia$1,
	Lebanon: Lebanon$1,
	Lesotho: Lesotho$1,
	Liberia: Liberia$1,
	"Libyan Arab Jamahiriya": "Libysch-Arabische Dschamahirija",
	Liechtenstein: Liechtenstein$1,
	Lithuania: Lithuania$1,
	Luxembourg: Luxembourg$1,
	Macao: Macao$1,
	"Macedonia, The Former Yugoslav Republic of": "Mazedonien, die ehemalige jugoslawische Republik",
	Madagascar: Madagascar$1,
	Malawi: Malawi$1,
	Malaysia: Malaysia$1,
	Maldives: Maldives$1,
	Mali: Mali$1,
	Malta: Malta$1,
	"Marshall Islands": "Marshallinseln",
	Martinique: Martinique$1,
	Mauritania: Mauritania$1,
	Mauritius: Mauritius$1,
	Mayotte: Mayotte$1,
	Mexico: Mexico$1,
	"Micronesia, Federated States of": "Mikronesien, Föderierte Staaten von",
	"Moldova, Republic of": "Moldawien, Republik",
	Monaco: Monaco$1,
	Mongolia: Mongolia$1,
	Montenegro: Montenegro$1,
	Montserrat: Montserrat$1,
	Morocco: Morocco$1,
	Mozambique: Mozambique$1,
	Myanmar: Myanmar$1,
	Namibia: Namibia$1,
	Nauru: Nauru$1,
	Nepal: Nepal$1,
	Netherlands: Netherlands$1,
	"New Caledonia": "Neukaledonien",
	"New Zealand": "Neuseeland",
	Nicaragua: Nicaragua$1,
	Niger: Niger$1,
	Nigeria: Nigeria$1,
	Niue: Niue$1,
	"Norfolk Island": "Norfolkinsel",
	"Northern Mariana Islands": "Nördliche Marianen",
	Norway: Norway$1,
	Oman: Oman$1,
	Pakistan: Pakistan$1,
	Palau: Palau$1,
	"Palestinian Territory, Occupied": "Palästinensisches Gebiet, besetzt",
	Panama: Panama$1,
	"Papua New Guinea": "Papua-Neuguinea",
	Paraguay: Paraguay$1,
	Peru: Peru$1,
	Philippines: Philippines$1,
	Pitcairn: Pitcairn$1,
	Poland: Poland$1,
	Portugal: Portugal$1,
	"Puerto Rico": "Puerto Rico",
	Qatar: Qatar$1,
	Romania: Romania$1,
	Russia: Russia$1,
	Rwanda: Rwanda$1,
	"Réunion": "Wiedervereinigung",
	"Saint Barthélemy": "Saint-Barthélemy",
	"Saint Helena, Ascension and Tristan Da Cunha": "St. Helena, Ascension und Tristan Da Cunha",
	"Saint Kitts and Nevis": "St. Kitts und Nevis",
	"Saint Lucia": "St. Lucia",
	"Saint Martin": "St. Martin",
	"Saint Pierre and Miquelon": "Saint-Pierre und Miquelon",
	"Saint Vincent and the Grenadines": "St. Vincent und die Grenadinen",
	Samoa: Samoa$1,
	"San Marino": "San Marino",
	"Sao Tome and Principe": "São Tomé und Príncipe",
	"Saudi Arabia": "Saudi-Arabien",
	Senegal: Senegal$1,
	Serbia: Serbia$1,
	Seychelles: Seychelles$1,
	"Sierra Leone": "Sierra Leone",
	Singapore: Singapore$1,
	Slovakia: Slovakia$1,
	Slovenia: Slovenia$1,
	"Solomon Islands": "Salomonen",
	Somalia: Somalia$1,
	"South Africa": "Südafrika",
	"South Georgia and the South Sandwich Islands": "Südgeorgien und die Südlichen Sandwichinseln",
	Spain: Spain$1,
	"Sri Lanka": "Sri Lanka",
	Suriname: Suriname$1,
	"Svalbard and Jan Mayen": "Spitzbergen und Jan Mayen",
	Swaziland: Swaziland$1,
	Sweden: Sweden$1,
	Switzerland: Switzerland$1,
	"Taiwan, Province of China": "Taiwan, Provinz Chinas",
	Tajikistan: Tajikistan$1,
	"Tanzania, United Republic of": "Tansania, Vereinigte Republik",
	Thailand: Thailand$1,
	"Timor-Leste": "Timor-Leste",
	Togo: Togo$1,
	Tokelau: Tokelau$1,
	Tonga: Tonga$1,
	"Trinidad and Tobago": "Trinidad und Tobago",
	Tunisia: Tunisia$1,
	Turkey: Turkey$1,
	Turkmenistan: Turkmenistan$1,
	"Turks and Caicos Islands": "Turks- und Caicosinseln",
	Tuvalu: Tuvalu$1,
	Uganda: Uganda$1,
	Ukraine: Ukraine$1,
	"United Arab Emirates": "Vereinigte Arabische Emirate",
	"United Kingdom": "Vereinigtes Königreich",
	"United States": "Vereinigte Staaten",
	Uruguay: Uruguay$1,
	Uzbekistan: Uzbekistan$1,
	Vanuatu: Vanuatu$1,
	"Venezuela, Bolivarian Republic of": "Venezuela, Bolivarische Republik",
	"Viet Nam": "Vietnam",
	"Virgin Islands, British": "Britische Jungferninseln",
	"Virgin Islands, U.S.": "Amerikanische Jungferninseln",
	"Wallis and Futuna": "Wallis und Futuna",
	Yemen: Yemen$1,
	Zambia: Zambia$1,
	Zimbabwe: Zimbabwe$1,
	"Åland Islands": "Åland-Inseln"
};

var t186 = {
	"No results found.": "Keine Ergebnisse gefunden."
};

var Done$2 = "Fet";
var t187 = {
	Done: Done$2
};

var t188 = {
	"No results found.": "No s'han trobat resultats."
};

var t189 = {
	"Clear all": "Esborra-ho tot",
	"No results found.": "No s'han trobat resultats."
};

var Passengers = "Passatgers";
var t190 = {
	"Number of passengers": "Nombre de passatgers",
	Passengers: Passengers
};

var t191 = {
	"No results found.": "No s'han trobat resultats."
};

var Blog = "Bloc";
var Careers = "Carreres professionals";
var Contact = "Contacte";
var Coverage = "Cobertura";
var Guides = "Guies";
var Imprint = "Empremta";
var Parking = "Aparcament";
var eSIM = "eSIM";
var t192 = {
	"2025 Mozio Inc. Trademarks and brands are property of their respective owners.": "2025 Mozio Inc. Les marques comercials són propietat dels seus respectius propietaris.",
	Blog: Blog,
	Careers: Careers,
	Contact: Contact,
	Coverage: Coverage,
	Guides: Guides,
	Imprint: Imprint,
	Parking: Parking,
	"Privacy Policy": "Política de privacitat",
	"Terms Of Services": "Condicions del servei",
	eSIM: eSIM,
	"{{year}} Mozio Inc. Trademarks and brands are property of their respective owners.": "{{year}} Les marques comercials i els noms comercials de Mozio Inc. són propietat dels seus respectius propietaris."
};

var Filters = "Filtres";
var t193 = {
	"Clear All": "Esborra-ho tot",
	Filters: Filters,
	"Show results": "Mostra els resultats"
};

var Done$1 = "Fet";
var Hours = "Hores";
var Minutes = "Minuts";
var t194 = {
	"AM/PM": "AM/PM",
	Done: Done$1,
	Hours: Hours,
	Minutes: Minutes
};

var Done = "Fet";
var t195 = {
	Done: Done
};

var Afghanistan = "Afganistan";
var Albania = "Albània";
var Algeria = "Algèria";
var AmericanSamoa = "Samoa Americana";
var Andorra = "Andorra";
var Angola = "Angola";
var Anguilla = "Anguilla";
var Argentina = "Argentina";
var Armenia = "Armènia";
var Aruba = "Aruba";
var Australia = "Austràlia";
var Austria = "Àustria";
var Azerbaijan = "Azerbaidjan";
var Bahamas = "Bahames";
var Bahrain = "Bahrain";
var Bangladesh = "Bangladesh";
var Barbados = "Barbados";
var Belarus = "Bielorússia";
var Belgium = "Bèlgica";
var Belize = "Belize";
var Benin = "Benín";
var Bermuda = "Bermudes";
var Bhutan = "Bhutan";
var Botswana = "Botswana";
var Brazil = "Brasil";
var Bulgaria = "Bulgària";
var Burundi = "Burundi";
var Cambodia = "Cambodja";
var Cameroon = "Camerun";
var Canada = "Canadà";
var Chad = "Txad";
var Chile = "Xile";
var China = "Xina";
var Colombia = "Colòmbia";
var Comoros = "Comores";
var Congo = "Congo";
var Croatia = "Croàcia";
var Curacao = "Curaçao";
var Cyprus = "Xipre";
var Denmark = "Dinamarca";
var Djibouti = "Djibouti";
var Dominica = "Dominica";
var Ecuador = "Equador";
var Egypt = "Egipte";
var Eritrea = "Eritrea";
var Estonia = "Estònia";
var Ethiopia = "Etiòpia";
var Fiji = "Fiji";
var Finland = "Finlàndia";
var France = "França";
var Gabon = "Gabon";
var Gambia = "Gàmbia";
var Georgia = "Geòrgia";
var Germany = "Alemanya";
var Ghana = "Ghana";
var Gibraltar = "Gibraltar";
var Greece = "Grècia";
var Greenland = "Groenlàndia";
var Grenada = "Grenada";
var Guadeloupe = "Guadalupe";
var Guam = "Guam";
var Guatemala = "Guatemala";
var Guernsey = "Guernsey";
var Guinea = "Guinea";
var Guyana = "Guyana";
var Haiti = "Haití";
var Honduras = "Hondures";
var Hungary = "Hongria";
var Iceland = "Islàndia";
var India = "Índia";
var Indonesia = "Indonèsia";
var Iraq = "Iraq";
var Ireland = "Irlanda";
var Israel = "Israel";
var Italy = "Itàlia";
var Jamaica = "Jamaica";
var Japan = "Japó";
var Jersey = "Jersey";
var Jordan = "Jordània";
var Kazakhstan = "Kazakhstan";
var Kenya = "Kenya";
var Kiribati = "Kiribati";
var Kuwait = "Kuwait";
var Kyrgyzstan = "Kirguizistan";
var Latvia = "Letònia";
var Lebanon = "Líban";
var Lesotho = "Lesotho";
var Liberia = "Libèria";
var Liechtenstein = "Liechtenstein";
var Lithuania = "Lituània";
var Luxembourg = "Luxemburg";
var Macao = "Macau";
var Madagascar = "Madagascar";
var Malawi = "Malawi";
var Malaysia = "Malàisia";
var Maldives = "Maldives";
var Mali = "Mali";
var Malta = "Malta";
var Martinique = "Martinica";
var Mauritania = "Mauritània";
var Mauritius = "Maurici";
var Mayotte = "Mayotte";
var Mexico = "Mèxic";
var Monaco = "Mònaco";
var Mongolia = "Mongòlia";
var Montenegro = "Montenegro";
var Montserrat = "Montserrat";
var Morocco = "Marroc";
var Mozambique = "Moçambic";
var Myanmar = "Myanmar";
var Namibia = "Namíbia";
var Nauru = "Nauru";
var Nepal = "Nepal";
var Netherlands = "Països Baixos";
var Nicaragua = "Nicaragua";
var Niger = "Níger";
var Nigeria = "Nigèria";
var Niue = "Niue";
var Norway = "Noruega";
var Oman = "Oman";
var Pakistan = "Pakistan";
var Palau = "Palau";
var Panama = "Panamà";
var Paraguay = "Paraguai";
var Peru = "Perú";
var Philippines = "Filipines";
var Pitcairn = "Pitcairn";
var Poland = "Polònia";
var Portugal = "Portugal";
var Qatar = "Qatar";
var Romania = "Romania";
var Russia = "Rússia";
var Rwanda = "Ruanda";
var Samoa = "Samoa";
var Senegal = "Senegal";
var Serbia = "Sèrbia";
var Seychelles = "Seychelles";
var Singapore = "Singapur";
var Slovakia = "Eslovàquia";
var Slovenia = "Eslovènia";
var Somalia = "Somàlia";
var Spain = "Espanya";
var Suriname = "Surinam";
var Swaziland = "Swazilàndia";
var Sweden = "Suècia";
var Switzerland = "Suïssa";
var Tajikistan = "Tadjikistan";
var Thailand = "Tailàndia";
var Togo = "Togo";
var Tokelau = "Tokelau";
var Tonga = "Tonga";
var Tunisia = "Tunísia";
var Turkey = "Turquia";
var Turkmenistan = "Turkmenistan";
var Tuvalu = "Tuvalu";
var Uganda = "Uganda";
var Ukraine = "Ucraïna";
var Uruguay = "Uruguai";
var Uzbekistan = "Uzbekistan";
var Vanuatu = "Vanuatu";
var Yemen = "Iemen";
var Zambia = "Zàmbia";
var Zimbabwe = "Zimbàbue";
var t196 = {
	Afghanistan: Afghanistan,
	Albania: Albania,
	Algeria: Algeria,
	AmericanSamoa: AmericanSamoa,
	Andorra: Andorra,
	Angola: Angola,
	Anguilla: Anguilla,
	"Antigua and Barbuda": "Antigua i Barbuda",
	Argentina: Argentina,
	Armenia: Armenia,
	Aruba: Aruba,
	Australia: Australia,
	Austria: Austria,
	Azerbaijan: Azerbaijan,
	Bahamas: Bahamas,
	Bahrain: Bahrain,
	Bangladesh: Bangladesh,
	Barbados: Barbados,
	Belarus: Belarus,
	Belgium: Belgium,
	Belize: Belize,
	Benin: Benin,
	Bermuda: Bermuda,
	Bhutan: Bhutan,
	"Bolivia, Plurinational State of": "Bolívia, Estat Plurinacional de",
	"Bosnia and Herzegovina": "Bòsnia i Hercegovina",
	Botswana: Botswana,
	Brazil: Brazil,
	"British Indian Ocean Territory": "Territori Britànic de l'Oceà Índic",
	"Brunei Darussalam": "Brunei Darussalam",
	Bulgaria: Bulgaria,
	"Burkina Faso": "Burkina Faso",
	Burundi: Burundi,
	Cambodia: Cambodia,
	Cameroon: Cameroon,
	Canada: Canada,
	"Cape Verde": "Cap Verd",
	"Cayman Islands": "Illes Caiman",
	"Central African Republic": "República Centreafricana",
	Chad: Chad,
	Chile: Chile,
	China: China,
	"Christmas Island": "Illa de Nadal",
	"Cocos (Keeling) Islands": "Illes Cocos (Keeling)",
	Colombia: Colombia,
	Comoros: Comoros,
	Congo: Congo,
	"Congo, The Democratic Republic of the": "Congo, República Democràtica del",
	"Cook Islands": "Illes Cook",
	"Costa Rica": "Costa Rica",
	"Cote d'Ivoire": "Costa d'Ivori",
	Croatia: Croatia,
	Curacao: Curacao,
	Cyprus: Cyprus,
	"Czech Republic": "República Txeca",
	Denmark: Denmark,
	Djibouti: Djibouti,
	Dominica: Dominica,
	"Dominican Republic": "República Dominicana",
	Ecuador: Ecuador,
	Egypt: Egypt,
	"El Salvador": "El Salvador",
	"Equatorial Guinea": "Guinea Equatorial",
	Eritrea: Eritrea,
	Estonia: Estonia,
	Ethiopia: Ethiopia,
	"Falkland Islands (Malvinas)": "Illes Malvines",
	"Faroe Islands": "Illes Fèroe",
	Fiji: Fiji,
	Finland: Finland,
	France: France,
	"French Guiana": "Guaiana Francesa",
	"French Polynesia": "Polinèsia Francesa",
	Gabon: Gabon,
	Gambia: Gambia,
	Georgia: Georgia,
	Germany: Germany,
	Ghana: Ghana,
	Gibraltar: Gibraltar,
	Greece: Greece,
	Greenland: Greenland,
	Grenada: Grenada,
	Guadeloupe: Guadeloupe,
	Guam: Guam,
	Guatemala: Guatemala,
	Guernsey: Guernsey,
	Guinea: Guinea,
	"Guinea-Bissau": "Guinea Bissau",
	Guyana: Guyana,
	Haiti: Haiti,
	"Holy See (Vatican City State)": "Santa Seu (Estat de la Ciutat del Vaticà)",
	Honduras: Honduras,
	"Hong Kong": "Hong Kong",
	Hungary: Hungary,
	Iceland: Iceland,
	India: India,
	Indonesia: Indonesia,
	Iraq: Iraq,
	Ireland: Ireland,
	"Isle of Man": "Illa de Man",
	Israel: Israel,
	Italy: Italy,
	Jamaica: Jamaica,
	Japan: Japan,
	Jersey: Jersey,
	Jordan: Jordan,
	Kazakhstan: Kazakhstan,
	Kenya: Kenya,
	Kiribati: Kiribati,
	"Korea, Republic of": "República de Corea",
	Kuwait: Kuwait,
	Kyrgyzstan: Kyrgyzstan,
	"Lao People's Democratic Republic": "República Democràtica Popular de Laos",
	Latvia: Latvia,
	Lebanon: Lebanon,
	Lesotho: Lesotho,
	Liberia: Liberia,
	"Libyan Arab Jamahiriya": "Jamahiriya Àrab Líbia",
	Liechtenstein: Liechtenstein,
	Lithuania: Lithuania,
	Luxembourg: Luxembourg,
	Macao: Macao,
	"Macedonia, The Former Yugoslav Republic of": "Macedònia, Antiga República Iugoslava de",
	Madagascar: Madagascar,
	Malawi: Malawi,
	Malaysia: Malaysia,
	Maldives: Maldives,
	Mali: Mali,
	Malta: Malta,
	"Marshall Islands": "Illes Marshall",
	Martinique: Martinique,
	Mauritania: Mauritania,
	Mauritius: Mauritius,
	Mayotte: Mayotte,
	Mexico: Mexico,
	"Micronesia, Federated States of": "Micronèsia, Estats Federats de",
	"Moldova, Republic of": "Moldàvia, República de",
	Monaco: Monaco,
	Mongolia: Mongolia,
	Montenegro: Montenegro,
	Montserrat: Montserrat,
	Morocco: Morocco,
	Mozambique: Mozambique,
	Myanmar: Myanmar,
	Namibia: Namibia,
	Nauru: Nauru,
	Nepal: Nepal,
	Netherlands: Netherlands,
	"New Caledonia": "Nova Caledònia",
	"New Zealand": "Nova Zelanda",
	Nicaragua: Nicaragua,
	Niger: Niger,
	Nigeria: Nigeria,
	Niue: Niue,
	"Norfolk Island": "Illa Norfolk",
	"Northern Mariana Islands": "Illes Marianes del Nord",
	Norway: Norway,
	Oman: Oman,
	Pakistan: Pakistan,
	Palau: Palau,
	"Palestinian Territory, Occupied": "Territori palestí, ocupat",
	Panama: Panama,
	"Papua New Guinea": "Papua Nova Guinea",
	Paraguay: Paraguay,
	Peru: Peru,
	Philippines: Philippines,
	Pitcairn: Pitcairn,
	Poland: Poland,
	Portugal: Portugal,
	"Puerto Rico": "Puerto Rico",
	Qatar: Qatar,
	Romania: Romania,
	Russia: Russia,
	Rwanda: Rwanda,
	"Réunion": "Reunió",
	"Saint Barthélemy": "Sant Bartomeu",
	"Saint Helena, Ascension and Tristan Da Cunha": "Santa Helena, Ascensió i Tristan da Cunha",
	"Saint Kitts and Nevis": "Saint Kitts i Nevis",
	"Saint Lucia": "Santa Llúcia",
	"Saint Martin": "Sant Martí",
	"Saint Pierre and Miquelon": "Saint-Pierre i Miquelon",
	"Saint Vincent and the Grenadines": "Saint Vincent i les Grenadines",
	Samoa: Samoa,
	"San Marino": "San Marino",
	"Sao Tome and Principe": "São Tomé i Príncipe",
	"Saudi Arabia": "Aràbia Saudita",
	Senegal: Senegal,
	Serbia: Serbia,
	Seychelles: Seychelles,
	"Sierra Leone": "Sierra Leone",
	Singapore: Singapore,
	Slovakia: Slovakia,
	Slovenia: Slovenia,
	"Solomon Islands": "Illes Salomó",
	Somalia: Somalia,
	"South Africa": "Sud-àfrica",
	"South Georgia and the South Sandwich Islands": "Illes Geòrgia del Sud i Sandwich del Sud",
	Spain: Spain,
	"Sri Lanka": "Sri Lanka",
	Suriname: Suriname,
	"Svalbard and Jan Mayen": "Svalbard i Jan Mayen",
	Swaziland: Swaziland,
	Sweden: Sweden,
	Switzerland: Switzerland,
	"Taiwan, Province of China": "Taiwan, Província de la Xina",
	Tajikistan: Tajikistan,
	"Tanzania, United Republic of": "Tanzània, República Unida de",
	Thailand: Thailand,
	"Timor-Leste": "Timor Oriental",
	Togo: Togo,
	Tokelau: Tokelau,
	Tonga: Tonga,
	"Trinidad and Tobago": "Trinitat i Tobago",
	Tunisia: Tunisia,
	Turkey: Turkey,
	Turkmenistan: Turkmenistan,
	"Turks and Caicos Islands": "Illes Turks i Caicos",
	Tuvalu: Tuvalu,
	Uganda: Uganda,
	Ukraine: Ukraine,
	"United Arab Emirates": "Emirats Àrabs Units",
	"United Kingdom": "Regne Unit",
	"United States": "Estats Units",
	Uruguay: Uruguay,
	Uzbekistan: Uzbekistan,
	Vanuatu: Vanuatu,
	"Venezuela, Bolivarian Republic of": "Veneçuela, República Bolivariana de",
	"Viet Nam": "Vietnam",
	"Virgin Islands, British": "Illes Verges Britàniques",
	"Virgin Islands, U.S.": "Illes Verges, EUA",
	"Wallis and Futuna": "Wallis i Futuna",
	Yemen: Yemen,
	Zambia: Zambia,
	Zimbabwe: Zimbabwe,
	"Åland Islands": "Illes Åland"
};

var t197 = {
	"No results found.": "No s'han trobat resultats."
};

const locales = {
  zh_TW: {
    "ui:time-picker": t0,
    "ui:responsive-combo-box": t1,
    "ui:responsive-autocomplete": t2,
    "ui:passenger-input": t3,
    "ui:header-combobox": t4,
    "ui:footer": t5,
    "ui:filter": t6,
    "ui:date-time-picker": t7,
    "ui:date-picker": t8,
    "ui:country-input": t9,
    "ui:command": t10
  },
  zh_CN: {
    "ui:time-picker": t11,
    "ui:responsive-combo-box": t12,
    "ui:responsive-autocomplete": t13,
    "ui:passenger-input": t14,
    "ui:header-combobox": t15,
    "ui:footer": t16,
    "ui:filter": t17,
    "ui:date-time-picker": t18,
    "ui:date-picker": t19,
    "ui:country-input": t20,
    "ui:command": t21
  },
  vi_VN: {
    "ui:time-picker": t22,
    "ui:responsive-combo-box": t23,
    "ui:responsive-autocomplete": t24,
    "ui:passenger-input": t25,
    "ui:header-combobox": t26,
    "ui:footer": t27,
    "ui:filter": t28,
    "ui:date-time-picker": t29,
    "ui:date-picker": t30,
    "ui:country-input": t31,
    "ui:command": t32
  },
  th_TH: {
    "ui:time-picker": t33,
    "ui:responsive-combo-box": t34,
    "ui:responsive-autocomplete": t35,
    "ui:passenger-input": t36,
    "ui:header-combobox": t37,
    "ui:footer": t38,
    "ui:filter": t39,
    "ui:date-time-picker": t40,
    "ui:date-picker": t41,
    "ui:country-input": t42,
    "ui:command": t43
  },
  sv: {
    "ui:time-picker": t44,
    "ui:responsive-combo-box": t45,
    "ui:responsive-autocomplete": t46,
    "ui:passenger-input": t47,
    "ui:header-combobox": t48,
    "ui:footer": t49,
    "ui:filter": t50,
    "ui:date-time-picker": t51,
    "ui:date-picker": t52,
    "ui:country-input": t53,
    "ui:command": t54
  },
  ru_RU: {
    "ui:time-picker": t55,
    "ui:responsive-combo-box": t56,
    "ui:responsive-autocomplete": t57,
    "ui:passenger-input": t58,
    "ui:header-combobox": t59,
    "ui:footer": t60,
    "ui:filter": t61,
    "ui:date-time-picker": t62,
    "ui:date-picker": t63,
    "ui:country-input": t64,
    "ui:command": t65
  },
  pt_BR: {
    "ui:time-picker": t66,
    "ui:responsive-combo-box": t67,
    "ui:responsive-autocomplete": t68,
    "ui:passenger-input": t69,
    "ui:header-combobox": t70,
    "ui:footer": t71,
    "ui:filter": t72,
    "ui:date-time-picker": t73,
    "ui:date-picker": t74,
    "ui:country-input": t75,
    "ui:command": t76
  },
  pl: {
    "ui:time-picker": t77,
    "ui:responsive-combo-box": t78,
    "ui:responsive-autocomplete": t79,
    "ui:passenger-input": t80,
    "ui:header-combobox": t81,
    "ui:footer": t82,
    "ui:filter": t83,
    "ui:date-time-picker": t84,
    "ui:date-picker": t85,
    "ui:country-input": t86,
    "ui:command": t87
  },
  nl: {
    "ui:time-picker": t88,
    "ui:responsive-combo-box": t89,
    "ui:responsive-autocomplete": t90,
    "ui:passenger-input": t91,
    "ui:header-combobox": t92,
    "ui:footer": t93,
    "ui:filter": t94,
    "ui:date-time-picker": t95,
    "ui:date-picker": t96,
    "ui:country-input": t97,
    "ui:command": t98
  },
  ko_KR: {
    "ui:time-picker": t99,
    "ui:responsive-combo-box": t100,
    "ui:responsive-autocomplete": t101,
    "ui:passenger-input": t102,
    "ui:header-combobox": t103,
    "ui:footer": t104,
    "ui:filter": t105,
    "ui:date-time-picker": t106,
    "ui:date-picker": t107,
    "ui:country-input": t108,
    "ui:command": t109
  },
  ja_JP: {
    "ui:time-picker": t110,
    "ui:responsive-combo-box": t111,
    "ui:responsive-autocomplete": t112,
    "ui:passenger-input": t113,
    "ui:header-combobox": t114,
    "ui:footer": t115,
    "ui:filter": t116,
    "ui:date-time-picker": t117,
    "ui:date-picker": t118,
    "ui:country-input": t119,
    "ui:command": t120
  },
  it_IT: {
    "ui:time-picker": t121,
    "ui:responsive-combo-box": t122,
    "ui:responsive-autocomplete": t123,
    "ui:passenger-input": t124,
    "ui:header-combobox": t125,
    "ui:footer": t126,
    "ui:filter": t127,
    "ui:date-time-picker": t128,
    "ui:date-picker": t129,
    "ui:country-input": t130,
    "ui:command": t131
  },
  id_ID: {
    "ui:time-picker": t132,
    "ui:responsive-combo-box": t133,
    "ui:responsive-autocomplete": t134,
    "ui:passenger-input": t135,
    "ui:header-combobox": t136,
    "ui:footer": t137,
    "ui:filter": t138,
    "ui:date-time-picker": t139,
    "ui:date-picker": t140,
    "ui:country-input": t141,
    "ui:command": t142
  },
  fr: {
    "ui:time-picker": t143,
    "ui:responsive-combo-box": t144,
    "ui:responsive-autocomplete": t145,
    "ui:passenger-input": t146,
    "ui:header-combobox": t147,
    "ui:footer": t148,
    "ui:filter": t149,
    "ui:date-time-picker": t150,
    "ui:date-picker": t151,
    "ui:country-input": t152,
    "ui:command": t153
  },
  es_ES: {
    "ui:time-picker": t154,
    "ui:responsive-combo-box": t155,
    "ui:responsive-autocomplete": t156,
    "ui:passenger-input": t157,
    "ui:header-combobox": t158,
    "ui:footer": t159,
    "ui:filter": t160,
    "ui:date-time-picker": t161,
    "ui:date-picker": t162,
    "ui:country-input": t163,
    "ui:command": t164
  },
  en_US: {
    "ui:time-picker": t165,
    "ui:responsive-combo-box": t166,
    "ui:responsive-autocomplete": t167,
    "ui:passenger-input": t168,
    "ui:header-combobox": t169,
    "ui:footer": t170,
    "ui:filter": t171,
    "ui:date-time-picker": t172,
    "ui:date-picker": t173,
    "ui:country-input": t174,
    "ui:command": t175
  },
  de_DE: {
    "ui:time-picker": t176,
    "ui:responsive-combo-box": t177,
    "ui:responsive-autocomplete": t178,
    "ui:passenger-input": t179,
    "ui:header-combobox": t180,
    "ui:footer": t181,
    "ui:filter": t182,
    "ui:date-time-picker": t183,
    "ui:date-picker": t184,
    "ui:country-input": t185,
    "ui:command": t186
  },
  ca: {
    "ui:time-picker": t187,
    "ui:responsive-combo-box": t188,
    "ui:responsive-autocomplete": t189,
    "ui:passenger-input": t190,
    "ui:header-combobox": t191,
    "ui:footer": t192,
    "ui:filter": t193,
    "ui:date-time-picker": t194,
    "ui:date-picker": t195,
    "ui:country-input": t196,
    "ui:command": t197
  }
};

export { Alert, AspectRatio, BUTTON_COLOR, BUTTON_SIZE, BUTTON_VARIANT, Badge, Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Checkbox, CheckboxCard, CheckboxCardButton, CheckboxCardDescription, CheckboxCardTitle, CheckboxIndicatorWrapper, Collapsible, CollapsibleContent, CollapsibleTrigger, _ComboBox as ComboBox, _ComboBoxButton as ComboBoxButton, _ComboBoxContent as ComboBoxContent, ComboBoxContentContext, ComboBoxContentStateContext, ComboBoxContext, _ComboBoxInput as ComboBoxInput, ComboBoxInputContext, _ComboBoxItem as ComboBoxItem, _ComboBoxLabel as ComboBoxLabel, ComboBoxLabelContext, _ComboBoxPopover as ComboBoxPopover, ComboBoxPopoverContext, ComboBoxStateContext, CommandSheet, CommandSheetContent, CommandSheetItem, CommandSheetList, CommandSheetTrigger, Container, CountryInput, DatePicker, DateTimePicker, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogPortal, DialogScrollableContent, DialogTitle, DialogTrigger, Filter, Flag, FloatingInput, FloatingInputArrow, FloatingInputClearButton, FloatingInputGroup, FloatingInputLeftContent, FloatingInputRightContent, FloatingLabel, Footer, Header, HeaderAvatar, HeaderButton, HeaderCombobox, HeaderContent, HeaderCreateAccountButton, HeaderLogo, HeaderSignInButton, HeaderUserAvatar, IconButton, Input, InputClearButton, InputGroup, InputLeftContent, Link, Loading, LocaleInput, MultiSelectAutocomplete, NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, Pagination, PassengerInput, PhoneInput, Popover, PopoverClose, PopoverContent, PopoverTrigger, RadioButtonGroup, RadioButtonGroupItem, RadioGroup, RadioGroupItem, ResponsiveAutocomplete, ResponsiveCombobox, SHEET_CONTENT_SIDE, ScrollArea, ScrollBar, Select, SelectContent, SelectGroup, SelectIcon, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue, Sheet, SheetClose, SheetContent, SheetTrigger, Skeleton, Slider, Switch, TAB_VARIANT, Tabs, TabsContent, TabsList, TabsTrigger, TimePicker, Timeline, Toaster, Tooltip, TooltipContent, TooltipTrigger, callingCodeToCountryCode, cn, getTheme, locales, mediaQueries, setTheme, toThemeVars, useCountries, useCountriesAllowedOnStripe, useToast, useTranslation };
